import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
  Grid,
  Tab,
  Tabs,
  Typography,
  Button,
  IconButton,
  Drawer,
  makeStyles,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  SnackbarContent,
  MenuItem,
  Paper
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import SaveIcon from '@material-ui/icons/Save';
import ScheduleIcon from '@material-ui/icons/Schedule';
import ReceiptIcon from '@material-ui/icons/Receipt';
import NotificationsIcon from '@material-ui/icons/Notifications';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddIcon from '@material-ui/icons/Add';
import BusinessIcon from '@material-ui/icons/Business';
import PeopleIcon from '@material-ui/icons/People';
import CloseIcon from '@material-ui/icons/Close';
import RemoveIcon from '@material-ui/icons/Remove';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Popover from '@material-ui/core/Popover';

const POLICY_TYPES = ['health', 'accident', 'other'];

const EXERCISE_ADJUSTMENT_MODE_OPTIONS = [
  {
    value: 'auto_replace',
    label: 'Adaptive (Auto)',
    description: 'Automatically replace exercises when the adaptive rules trigger.',
  },
  {
    value: 'disabled',
    label: 'Manual Only',
    description: 'Disable adaptive adjustments and keep exercise changes fully manual.',
  },
];

const PHASE_PROGRESSION_OPERATOR_OPTIONS = [
  { value: 'gte', label: 'At least' },
  { value: 'lte', label: 'At most' },
  { value: 'eq', label: 'Exactly' },
];

const PHASE_PROGRESSION_COMBO_JOIN_OPTIONS = [
  { value: 'and', label: 'AND' },
  { value: 'or', label: 'OR' },
];

const PHASE_PROGRESSION_SELECT_MENU_PROPS = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: {
    style: {
      borderRadius: 14,
      marginTop: 8,
    },
  },
};

const SELECT_MENU_PROPS = PHASE_PROGRESSION_SELECT_MENU_PROPS;

const createBlankInsurerDraft = () => ({
  insurance_provider_name: '',
  address: '',
  postal_code: '',
  city: '',
  email: '',
  ean_number: '',
  person_reference: '',
  phone: '',
  business_registration_number: '',
  effective_from: '',
  effective_until: '',
  payer_details: {
    health: { cvr_number: '', ean_number: '', billing_email: '', payment_terms_days: '14', max_coverage_per_appointment: '' },
    accident: { cvr_number: '', ean_number: '', billing_email: '', payment_terms_days: '14', max_coverage_per_appointment: '' },
    other: { cvr_number: '', ean_number: '', billing_email: '', payment_terms_days: '14', max_coverage_per_appointment: '' },
  },
});

const createBlankOtherPayerDraft = () => ({
  name: '',
  address: '',
  postal_code: '',
  city: '',
  billing_email: '',
  cvr_number: '',
  ean_number: '',
  person_reference: '',
  phone: '',
});

const DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS = [
  { key: 'kpi/pain_baseline/current', label: 'Pain (current)', default_operator: 'lte', metric_source: 'kpi_template', kpi_type: 'pain_baseline', kpi_subtype: 'current' },
  { key: 'kpi/pain_baseline/best', label: 'Pain (at best)', default_operator: 'lte', metric_source: 'kpi_template', kpi_type: 'pain_baseline', kpi_subtype: 'best' },
  { key: 'kpi/pain_baseline/worst', label: 'Pain (at worst)', default_operator: 'lte', metric_source: 'kpi_template', kpi_type: 'pain_baseline', kpi_subtype: 'worst' },
  { key: 'kpi/pain_baseline/frequency', label: 'Frequency', default_operator: 'lte', metric_source: 'kpi_template', kpi_type: 'pain_baseline', kpi_subtype: 'frequency' },
  { key: 'kpi/function_goal/main_goal', label: 'Function (PSFS)', default_operator: 'gte', metric_source: 'kpi_template', kpi_type: 'function_goal', kpi_subtype: 'main_goal' },
  { key: 'average_exercise_pain', label: 'Average exercise pain', default_operator: 'lte' },
  { key: 'exercise_level_progressions', label: 'Exercise level progressions', default_operator: 'gte' },
  { key: 'minimum_compliance', label: 'Compliance (%)', default_operator: 'gte' },
  { key: 'sessions_completed', label: 'Sessions completed', default_operator: 'gte' },
  { key: 'minimum_duration_weeks', label: 'Duration in phase (weeks)', default_operator: 'gte' },
];

const PHASE_PROGRESSION_ANCHOR_KEYS = ['sessions_completed', 'minimum_duration_weeks'];

const PHASE_PROGRESSION_PAIN_METRIC_KEYS = new Set([
  'pain_reduction_percent',
  'pain_frequency_reduction_percent',
  'custom_pain_baseline',
  'average_exercise_pain',
]);

const PHASE_PROGRESSION_FUNCTION_METRIC_KEYS = new Set([
  'psfs_score',
  'custom_functional_goal',
]);

const PHASE_PROGRESSION_LEGACY_TO_KPI_MAP = {
  pain_reduction_percent: { kpi_type: 'pain_baseline', kpi_subtype: 'current' },
  custom_pain_baseline: { kpi_type: 'pain_baseline', kpi_subtype: 'current' },
  average_exercise_pain: { kpi_type: 'pain_baseline', kpi_subtype: 'current' },
  pain_frequency_reduction_percent: { kpi_type: 'pain_baseline', kpi_subtype: 'frequency' },
  psfs_score: { kpi_type: 'function_goal', kpi_subtype: 'main_goal' },
  custom_functional_goal: { kpi_type: 'function_goal', kpi_subtype: 'main_goal' },
};

const findMappedClinicPhaseMetricOption = (legacyMetricKey, metricOptions = DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const mapping = PHASE_PROGRESSION_LEGACY_TO_KPI_MAP[legacyMetricKey];
  if (!mapping) {
    return null;
  }

  return metricOptions.find((option) => (
    String(option.kpi_type || '').toLowerCase() === mapping.kpi_type
    && String(option.kpi_subtype || '').toLowerCase() === mapping.kpi_subtype
  )) || null;
};

const normalizeClinicPhaseMetricKey = (metricKey, metricOptions = DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const resolvedMetricKey = String(metricKey || '').trim();
  if (!resolvedMetricKey) {
    return '';
  }

  if (PHASE_PROGRESSION_ANCHOR_KEYS.includes(resolvedMetricKey)) {
    return resolvedMetricKey;
  }

  if (metricOptions.some((option) => option.key === resolvedMetricKey)) {
    return resolvedMetricKey;
  }

  const mappedMetric = findMappedClinicPhaseMetricOption(resolvedMetricKey, metricOptions);
  return mappedMetric ? mappedMetric.key : '';
};

const createPhaseProgressionLocalId = () => `phase-rule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const createPhaseProgressionComboLocalId = () => `phase-combo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizePhaseProgressionMetricOptions = (items) => (
  Array.isArray(items) && items.length > 0
    ? items.map((item) => ({
        key: item.key,
        label: item.label,
        default_operator: item.default_operator || 'gte',
        metric_source: item.metric_source || '',
        kpi_type: item.kpi_type || '',
        kpi_subtype: item.kpi_subtype || '',
      }))
      .filter((item) => PHASE_PROGRESSION_ANCHOR_KEYS.includes(item.key) || String(item.metric_source || '').toLowerCase().includes('kpi') || item.key.startsWith('kpi/'))
    : DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS.map((item) => ({ ...item }))
);

const buildClinicPhaseConditionDraft = (
  metricKey = 'sessions_completed',
  source = {},
  metricOptions = DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS,
) => {
  const normalizedMetricKey = normalizeClinicPhaseMetricKey(metricKey, metricOptions);
  const metric = metricOptions.find((option) => option.key === normalizedMetricKey)
    || metricOptions[0]
    || DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS[0];
  return {
    metric_key: metric.key,
    operator: source.operator || metric.default_operator || 'gte',
    threshold_value: source.threshold_value ?? '',
    exercise_identifiers_text: Array.isArray(source.exercise_identifiers) ? source.exercise_identifiers.join(', ') : '',
  };
};

const buildClinicDefaultPhaseConditionDrafts = (metricOptions = DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const anchorMetricKey = metricOptions.find((option) => PHASE_PROGRESSION_ANCHOR_KEYS.includes(option.key))?.key || 'sessions_completed';
  const additionalMetricKey = metricOptions.find((option) => option.key !== anchorMetricKey)?.key || anchorMetricKey;
  return [
    buildClinicPhaseConditionDraft(anchorMetricKey, {}, metricOptions),
    buildClinicPhaseConditionDraft(additionalMetricKey, {}, metricOptions),
  ];
};

const buildClinicDefaultPhaseConditionCombo = (metricOptions = DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS, comboJoinOperator = 'and') => ({
  localId: createPhaseProgressionComboLocalId(),
  combo_join_operator: comboJoinOperator,
  conditions: buildClinicDefaultPhaseConditionDrafts(metricOptions),
});

const DEFAULT_VIDEOCONSULTATION_CATALOG = {
  recommendationMode: 'recommendation_only',
  staffOverrideMode: 'narrow_only',
  specialityOptions: [],
  symptomTagOptions: [],
  staffOptions: [],
};

const DEFAULT_VIDEOCONSULTATION_SETTINGS = {
  enabled: true,
  defaultRequiresNoRedFlags: true,
  excludedSpecialities: [],
  excludedSymptomTags: [],
  staffOverrides: [],
};

const DEFAULT_INSURANCE_SUPPORT_CATALOG = {
  networkOptions: [],
  staffOptions: [],
};

const DEFAULT_INSURANCE_SUPPORT_SETTINGS = {
  supportedNetworks: [],
  staffOverrides: [],
};

const WEEKDAY_OPTIONS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const ALLOCATION_RECURRENCE_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'tri-weekly', label: 'Tri-weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Resource utilization analytics: the clinic owner can measure how effectively
// calendar resources (rooms) are being used, overall and per room, including the
// unused/idle hours. Three calculation bases are offered.
const UTILIZATION_BASIS_OPTIONS = [
  {
    value: 'allocation_vs_opening',
    label: 'Allocated staff hours vs room opening hours',
    capacityLabel: 'Open hours',
    usedLabel: 'Allocated hours',
    unusedLabel: 'Unstaffed open hours',
    description: "How much of each room's opening hours are covered by staff room allocations.",
  },
  {
    value: 'bookings_vs_allocation',
    label: 'Booked appointments vs allocated hours',
    capacityLabel: 'Allocated hours',
    usedLabel: 'Booked hours',
    unusedLabel: 'Unbooked allocated hours',
    description: 'How much of the allocated staff time was actually filled with appointments.',
  },
  {
    value: 'bookings_vs_opening',
    label: 'Booked appointments vs room opening hours',
    capacityLabel: 'Open hours',
    usedLabel: 'Booked hours',
    unusedLabel: 'Unbooked open hours',
    description: 'End-to-end room occupancy: booked appointments against total opening hours.',
  },
];

const UTILIZATION_PERIOD_OPTIONS = [
  { value: 'last_week', label: 'Last week', weeks: 1 },
  { value: 'last_two_weeks', label: 'Last two weeks', weeks: 2 },
  { value: 'last_month', label: 'Last month', months: 1 },
  { value: 'last_quarter', label: 'Last quarter', months: 3 },
  { value: 'last_year', label: 'Last year', months: 12 },
];

const DEFAULT_UTILIZATION_SANDBOX_WEEKDAYS = WEEKDAY_OPTIONS
  .filter(({ key }) => ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(key))
  .map(({ key }) => key);

const DEFAULT_UTILIZATION_SANDBOX_START_TIME = '08:00';
const DEFAULT_UTILIZATION_SANDBOX_END_TIME = '18:00';
const DEFAULT_UTILIZATION_CONTINUITY_MIN_HOURS = '4';
const DEFAULT_UTILIZATION_CONTINUITY_MAX_GAP_MINUTES = '60';
const UTILIZATION_PREFERENCES_STORAGE_KEY = 'virtus.clinicSettings.utilizationPreferences.v1';

const normalizeUtilizationSandboxWeekdays = (weekdayKeys = DEFAULT_UTILIZATION_SANDBOX_WEEKDAYS) => (
  Array.isArray(weekdayKeys)
    ? Array.from(new Set(weekdayKeys.map((weekdayKey) => String(weekdayKey || '').trim()).filter((weekdayKey) => WEEKDAY_OPTIONS.some((option) => option.key === weekdayKey))))
    : DEFAULT_UTILIZATION_SANDBOX_WEEKDAYS.slice()
);

const getUtilizationSandboxWindow = (startTimeValue, endTimeValue) => {
  const startMinutes = parseTimeValueToMinutes(startTimeValue);
  const endMinutes = parseTimeValueToMinutes(endTimeValue);
  const fallbackStart = parseTimeValueToMinutes(DEFAULT_UTILIZATION_SANDBOX_START_TIME);
  const fallbackEnd = parseTimeValueToMinutes(DEFAULT_UTILIZATION_SANDBOX_END_TIME);

  if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
    return { start: fallbackStart, end: fallbackEnd };
  }

  return { start: startMinutes, end: endMinutes };
};

// Size (in minutes) of each column in the weekday-by-time utilization heatmap.
const UTILIZATION_BUCKET_MINUTES = 60;

const ROOM_SCHEDULE_START_MINUTES = 6 * 60;
const ROOM_SCHEDULE_END_MINUTES = 22 * 60;
const ROOM_SCHEDULE_SLOT_MINUTES = 30;
const ROOM_SCHEDULE_ROW_HEIGHT = 24;
const ROOM_SCHEDULE_WEEKDAY_LABEL_FONT_SIZE = 16;
const ROOM_SCHEDULE_WEEKDAY_SUBLABEL_FONT_SIZE = 11;
const ROOM_SCHEDULE_SLOT_COUNT = (ROOM_SCHEDULE_END_MINUTES - ROOM_SCHEDULE_START_MINUTES) / ROOM_SCHEDULE_SLOT_MINUTES;
const ROOM_SCHEDULE_BODY_HEIGHT = ROOM_SCHEDULE_SLOT_COUNT * ROOM_SCHEDULE_ROW_HEIGHT;
const ROOM_SCHEDULE_HOUR_MARKERS = Array.from(
  { length: ((ROOM_SCHEDULE_END_MINUTES - ROOM_SCHEDULE_START_MINUTES) / 60) + 1 },
  (_, index) => ROOM_SCHEDULE_START_MINUTES + (index * 60),
);

// The static 32-slot background grid for a single room/weekday column is pure and never
// changes, but it sits inside columns that re-render on every drag/resize preview update.
// Memoizing it keeps those background cells from re-rendering during interaction.
const RoomScheduleSlotBackground = React.memo(function RoomScheduleSlotBackground({ slotClassName, hourSlotClassName }) {
  return (
    <>
      {Array.from({ length: ROOM_SCHEDULE_SLOT_COUNT }, (_, slotIndex) => (
        <Box
          key={`slot-${slotIndex}`}
          data-room-schedule-slot="true"
          className={`${slotClassName} ${slotIndex % 2 === 1 ? hourSlotClassName : ''}`}
        />
      ))}
    </>
  );
});

const GeneralSettingsDrawer = ({ classes, expanded, onChange, icon, title, children }) => (
  <Accordion
    className={`${classes.stackedAccordion} ${classes.insuranceAccordion}`}
    expanded={expanded}
    onChange={onChange}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.insuranceAccordionSummary}>
      <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
        {icon}
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails style={{ display: 'block', padding: 16 }}>
      {children}
    </AccordionDetails>
  </Accordion>
);

const parseScheduleTimeToMinutes = (timeValue) => {
  const [hours, minutes] = String(timeValue || '').split(':').map((part) => Number.parseInt(part, 10));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  return (hours * 60) + minutes;
};

const colorWithScheduleAlpha = (hexColor, alpha) => {
  const sanitized = String(hexColor || '').trim();
  const normalized = sanitized.startsWith('#') ? sanitized.slice(1) : sanitized;
  const expanded = normalized.length === 3
    ? normalized.split('').map((part) => `${part}${part}`).join('')
    : normalized;

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    return `rgba(49, 116, 173, ${alpha})`;
  }

  const red = Number.parseInt(expanded.slice(0, 2), 16);
  const green = Number.parseInt(expanded.slice(2, 4), 16);
  const blue = Number.parseInt(expanded.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const formatScheduleMinutesLabel = (minutes) => {
  if (!Number.isFinite(minutes)) {
    return '';
  }

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const formatContinuityIntervalCompact = (startMinutes, endMinutes) => {
  const formatPart = (value) => {
    if (!Number.isFinite(value)) {
      return '';
    }
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    return minutes === 0 ? String(hours) : `${hours}:${String(minutes).padStart(2, '0')}`;
  };
  return `[${formatPart(startMinutes)}-${formatPart(endMinutes)}]`;
};

const getUtilizationContinuityRules = (minHoursValue, maxGapValue) => {
  const parsedMinHours = Number.parseFloat(String(minHoursValue || '').trim());
  const parsedMaxGap = Number.parseInt(String(maxGapValue || '').trim(), 10);
  const minHours = Number.isFinite(parsedMinHours) && parsedMinHours >= 0
    ? parsedMinHours
    : Number.parseFloat(DEFAULT_UTILIZATION_CONTINUITY_MIN_HOURS);
  const maxGapMinutes = Number.isFinite(parsedMaxGap) && parsedMaxGap >= 0
    ? parsedMaxGap
    : Number.parseInt(DEFAULT_UTILIZATION_CONTINUITY_MAX_GAP_MINUTES, 10);
  return {
    minHours,
    maxGapMinutes,
    minDurationMinutes: Math.round(minHours * 60),
  };
};

const loadUtilizationPreferences = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(UTILIZATION_PREFERENCES_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return {
      basis: String(parsed.basis || 'allocation_vs_opening'),
      period: String(parsed.period || 'last_month'),
      locationId: String(parsed.locationId || ''),
      sandboxWeekdays: normalizeUtilizationSandboxWeekdays(parsed.sandboxWeekdays),
      sandboxStartTime: String(parsed.sandboxStartTime || DEFAULT_UTILIZATION_SANDBOX_START_TIME),
      sandboxEndTime: String(parsed.sandboxEndTime || DEFAULT_UTILIZATION_SANDBOX_END_TIME),
      continuityMinHours: String(parsed.continuityMinHours || DEFAULT_UTILIZATION_CONTINUITY_MIN_HOURS),
      continuityMaxGapMinutes: String(parsed.continuityMaxGapMinutes || DEFAULT_UTILIZATION_CONTINUITY_MAX_GAP_MINUTES),
    };
  } catch {
    return null;
  }
};

const persistUtilizationPreferences = (preferences = {}) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(UTILIZATION_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Ignore storage failures (private mode/quota), UI still works with in-memory state.
  }
};

const ALLOCATION_COLOR_PALETTE = [
  '#2F6B99',
  '#4A7C59',
  '#8A5A44',
  '#6B5B95',
  '#9B4F64',
  '#2B7A78',
  '#7A6A4F',
  '#5C6F91',
];

const normalizeAllocationColor = (colorValue) => {
  const sanitized = String(colorValue || '').trim();
  if (!sanitized) {
    return '';
  }

  const prefixed = sanitized.startsWith('#') ? sanitized : `#${sanitized}`;
  const expanded = prefixed.slice(1).length === 3
    ? prefixed.slice(1).split('').map((part) => `${part}${part}`).join('')
    : prefixed.slice(1);

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    return '';
  }

  return `#${expanded.toUpperCase()}`;
};

const getAllocationDefaultColor = (seedValue) => {
  const normalizedSeed = String(seedValue || '').trim();
  if (!normalizedSeed) {
    return ALLOCATION_COLOR_PALETTE[0];
  }

  const hashValue = normalizedSeed.split('').reduce((accumulator, character) => ((accumulator * 31) + character.charCodeAt(0)) >>> 0, 0);
  return ALLOCATION_COLOR_PALETTE[hashValue % ALLOCATION_COLOR_PALETTE.length];
};

const getScheduleWeekdayIndex = (dateValue) => {
  if (!dateValue) {
    return null;
  }

  const [year, month, day] = String(dateValue).split('-').map((part) => Number.parseInt(part, 10));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  const weekday = new Date(year, month - 1, day).getDay();
  return (weekday + 6) % 7;
};

const formatScheduleDateValue = (dateValue) => {
  let date = null;
  if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    const resolvedDate = String(dateValue || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(resolvedDate)) {
      const [year, month, day] = resolvedDate.split('-').map((part) => Number.parseInt(part, 10));
      date = new Date(year, month - 1, day);
    } else if (resolvedDate) {
      date = new Date(resolvedDate);
    }
  }

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftScheduleDateByDays = (dateValue, dayOffset) => {
  const resolvedDate = String(dateValue || '').trim();
  const baseDate = resolvedDate ? new Date(`${resolvedDate}T00:00:00`) : new Date();
  if (Number.isNaN(baseDate.getTime())) {
    return formatScheduleDateValue(new Date());
  }

  baseDate.setDate(baseDate.getDate() + Number(dayOffset || 0));
  return formatScheduleDateValue(baseDate);
};

const getScheduleWeekRangeLabel = (dateValue) => {
  const resolvedDate = String(dateValue || '').trim();
  if (!resolvedDate) {
    return '';
  }

  const date = new Date(`${resolvedDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const startOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return `${formatScheduleDateValue(startOfWeek)} - ${formatScheduleDateValue(endOfWeek)}`;
};

const getScheduleWeekStartDateValue = (dateValue) => {
  const resolvedDate = String(dateValue || '').trim();
  if (!resolvedDate) {
    return '';
  }

  const date = new Date(`${resolvedDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  return formatScheduleDateValue(date);
};

const getScheduleIsoWeekNumber = (dateValue) => {
  const resolvedDate = dateValue instanceof Date
    ? new Date(dateValue)
    : new Date(`${String(dateValue || '').trim()}T00:00:00`);

  if (Number.isNaN(resolvedDate.getTime())) {
    return null;
  }

  const date = new Date(resolvedDate.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));

  const firstThursday = new Date(date.getFullYear(), 0, 4);
  firstThursday.setDate(firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7));

  return 1 + Math.round((date - firstThursday) / (7 * 24 * 60 * 60 * 1000));
};

const getScheduleWeekdayDateLabel = (weekStartDate, weekdayIndex) => {
  const resolvedWeekStart = getScheduleWeekStartDateValue(weekStartDate);
  if (!resolvedWeekStart) {
    return '';
  }

  const dayOffset = Number(weekdayIndex);
  if (!Number.isFinite(dayOffset)) {
    return '';
  }

  return formatScheduleDateValue(shiftScheduleDateByDays(resolvedWeekStart, dayOffset));
};

// --- Resource utilization helpers (pure) ---

// Resolve the inclusive [start, end] date window for a utilization period option.
const getUtilizationPeriodRange = (period, referenceDate = new Date()) => {
  const end = referenceDate instanceof Date && !Number.isNaN(referenceDate.getTime())
    ? new Date(referenceDate.getTime())
    : new Date();
  const option = UTILIZATION_PERIOD_OPTIONS.find((item) => item.value === period)
    || UTILIZATION_PERIOD_OPTIONS[0];
  const start = new Date(end.getTime());
  if (option.weeks) {
    start.setDate(start.getDate() - (option.weeks * 7));
  } else if (option.months) {
    start.setMonth(start.getMonth() - option.months);
  }
  return {
    startDateValue: formatScheduleDateValue(start),
    endDateValue: formatScheduleDateValue(end),
  };
};

// Enumerate every calendar date (YYYY-MM-DD) in an inclusive range. Capped to keep
// the loop bounded even for the "last year" window.
const enumerateScheduleDates = (startDateValue, endDateValue) => {
  const dates = [];
  const start = new Date(`${startDateValue}T00:00:00`);
  const end = new Date(`${endDateValue}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return dates;
  }
  const cursor = new Date(start.getTime());
  let guard = 0;
  while (cursor <= end && guard < 1000) {
    dates.push(formatScheduleDateValue(cursor));
    cursor.setDate(cursor.getDate() + 1);
    guard += 1;
  }
  return dates;
};

// Clinic opening window (in minutes) for a Monday-indexed weekday, or null when closed.
const getOpeningHoursWindowForWeekday = (openingHours, weekdayIndex) => {
  const option = WEEKDAY_OPTIONS[weekdayIndex];
  if (!option) {
    return null;
  }
  const day = openingHours && openingHours[option.key];
  if (!day || !day.enabled) {
    return null;
  }
  const start = parseScheduleTimeToMinutes(day.start);
  const end = parseScheduleTimeToMinutes(day.end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return null;
  }
  return { start, end };
};

// Clamp a minute interval into [lowerBound, upperBound]; null when nothing remains.
const clampScheduleInterval = (interval, lowerBound, upperBound) => {
  if (!interval) {
    return null;
  }
  const start = Math.max(interval.start, lowerBound);
  const end = Math.min(interval.end, upperBound);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return null;
  }
  return { start, end };
};

// Total covered minutes of a set of intervals, merging any overlaps so room time is
// never double-counted (e.g. two staff allocated to the same room at the same time).
const getUnionIntervalsMinutes = (intervals = []) => {
  const valid = intervals
    .filter((interval) => interval
      && Number.isFinite(interval.start)
      && Number.isFinite(interval.end)
      && interval.end > interval.start)
    .sort((a, b) => a.start - b.start);
  let total = 0;
  let currentStart = null;
  let currentEnd = null;
  valid.forEach((interval) => {
    if (currentEnd === null || interval.start > currentEnd) {
      if (currentEnd !== null) {
        total += currentEnd - currentStart;
      }
      currentStart = interval.start;
      currentEnd = interval.end;
    } else if (interval.end > currentEnd) {
      currentEnd = interval.end;
    }
  });
  if (currentEnd !== null) {
    total += currentEnd - currentStart;
  }
  return total;
};

// Resolve a booking's local date and minute interval. Bookings crossing midnight are
// clamped to the end of the start day so the minutes stay attributed to one date.
const getBookingScheduleInterval = (booking) => {
  if (!booking || !booking.start_datetime || !booking.end_datetime) {
    return null;
  }
  const start = new Date(booking.start_datetime);
  const end = new Date(booking.end_datetime);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return null;
  }
  const dateValue = formatScheduleDateValue(start);
  const startMinutes = (start.getHours() * 60) + start.getMinutes();
  let endMinutes = (end.getHours() * 60) + end.getMinutes();
  if (formatScheduleDateValue(end) !== dateValue) {
    endMinutes = 24 * 60;
  }
  return { dateValue, start: startMinutes, end: endMinutes };
};

const formatUtilizationHours = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return '0h';
  }
  const totalMinutes = Math.round(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainder = totalMinutes % 60;
  if (hours === 0) {
    return `${remainder}m`;
  }
  if (remainder === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainder}m`;
};

const getUtilizationPercent = (used, capacity) => {
  if (!Number.isFinite(used) || !Number.isFinite(capacity) || capacity <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((used / capacity) * 1000) / 10);
};

const getRoomAllocationBlockLayout = (allocation) => {
  const startMinutes = parseScheduleTimeToMinutes(allocation?.start_time);
  const endMinutes = parseScheduleTimeToMinutes(allocation?.end_time);
  if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
    return null;
  }

  const clampedStart = Math.max(startMinutes, ROOM_SCHEDULE_START_MINUTES);
  const clampedEnd = Math.min(endMinutes, ROOM_SCHEDULE_END_MINUTES);
  if (clampedEnd <= clampedStart) {
    return null;
  }

  return {
    top: ((clampedStart - ROOM_SCHEDULE_START_MINUTES) / ROOM_SCHEDULE_SLOT_MINUTES) * ROOM_SCHEDULE_ROW_HEIGHT,
    height: Math.max(((clampedEnd - clampedStart) / ROOM_SCHEDULE_SLOT_MINUTES) * ROOM_SCHEDULE_ROW_HEIGHT, 28),
  };
};

const doScheduleTimeRangesOverlap = (leftStartTime, leftEndTime, rightStartTime, rightEndTime) => {
  const leftStartMinutes = parseScheduleTimeToMinutes(leftStartTime);
  const leftEndMinutes = parseScheduleTimeToMinutes(leftEndTime);
  const rightStartMinutes = parseScheduleTimeToMinutes(rightStartTime);
  const rightEndMinutes = parseScheduleTimeToMinutes(rightEndTime);

  if (
    !Number.isFinite(leftStartMinutes)
    || !Number.isFinite(leftEndMinutes)
    || !Number.isFinite(rightStartMinutes)
    || !Number.isFinite(rightEndMinutes)
  ) {
    return false;
  }

  return leftStartMinutes < rightEndMinutes && leftEndMinutes > rightStartMinutes;
};

const getRoomScheduleSlotIndexFromClientY = (clientY, rect) => {
  if (!rect) {
    return 0;
  }

  const relativeY = clientY - rect.top;
  return Math.max(0, Math.min(ROOM_SCHEDULE_SLOT_COUNT - 1, Math.floor(relativeY / ROOM_SCHEDULE_ROW_HEIGHT)));
};

const buildRoomScheduleRangeFromIndices = (anchorIndex, currentIndex) => {
  const safeAnchorIndex = Number.isFinite(anchorIndex) ? anchorIndex : 0;
  const safeCurrentIndex = Number.isFinite(currentIndex) ? currentIndex : safeAnchorIndex;
  const startIndex = Math.min(safeAnchorIndex, safeCurrentIndex);
  const endIndex = Math.max(safeAnchorIndex, safeCurrentIndex) + 1;

  return {
    startMinutes: ROOM_SCHEDULE_START_MINUTES + (startIndex * ROOM_SCHEDULE_SLOT_MINUTES),
    endMinutes: ROOM_SCHEDULE_START_MINUTES + (endIndex * ROOM_SCHEDULE_SLOT_MINUTES),
  };
};

const STANDARD_OPENING_HOURS_TEMPLATE = {
  monday: { enabled: true, start: '09:00', end: '17:00' },
  tuesday: { enabled: true, start: '09:00', end: '17:00' },
  wednesday: { enabled: true, start: '09:00', end: '17:00' },
  thursday: { enabled: true, start: '09:00', end: '17:00' },
  friday: { enabled: true, start: '09:00', end: '17:00' },
  saturday: { enabled: false, start: '09:00', end: '17:00' },
  sunday: { enabled: false, start: '09:00', end: '17:00' },
};

const createStandardOpeningHours = () => WEEKDAY_OPTIONS.reduce((accumulator, { key }) => ({
  ...accumulator,
  [key]: { ...STANDARD_OPENING_HOURS_TEMPLATE[key] },
}), {});

const normalizeOpeningHoursForForm = (openingHours = {}) => WEEKDAY_OPTIONS.reduce((accumulator, { key }) => {
  const fallback = STANDARD_OPENING_HOURS_TEMPLATE[key];
  const rawDay = openingHours && typeof openingHours === 'object' ? openingHours[key] : null;

  accumulator[key] = {
    enabled: rawDay?.enabled !== undefined ? Boolean(rawDay.enabled) : fallback.enabled,
    start: String(rawDay?.start || fallback.start),
    end: String(rawDay?.end || fallback.end),
  };

  return accumulator;
}, createStandardOpeningHours());

const buildOpeningHoursPayload = (openingHours = {}) => WEEKDAY_OPTIONS.reduce((payload, { key }) => {
  const day = openingHours[key] || {};
  payload[key] = {
    enabled: day.enabled !== undefined ? Boolean(day.enabled) : false,
    start: String(day.start || STANDARD_OPENING_HOURS_TEMPLATE[key].start),
    end: String(day.end || STANDARD_OPENING_HOURS_TEMPLATE[key].end),
  };
  return payload;
}, {});

const openingHoursHaveActiveDay = (openingHours = {}) => WEEKDAY_OPTIONS.some(({ key }) => Boolean(openingHours?.[key]?.enabled));

const normalizeStaffOpeningHoursForForm = (staffOpeningHours = {}, staffOptions = [], fallbackOpeningHours = createStandardOpeningHours()) => (
  Array.isArray(staffOptions)
    ? staffOptions.reduce((accumulator, staff) => {
        const rawOpeningHours = staffOpeningHours && typeof staffOpeningHours === 'object'
          ? staffOpeningHours[staff.id]
          : null;

        accumulator[staff.id] = normalizeOpeningHoursForForm(rawOpeningHours || fallbackOpeningHours);
        return accumulator;
      }, {})
    : {}
);

const buildStaffOpeningHoursPayload = (staffOpeningHours = {}, staffOptions = [], fallbackOpeningHours = createStandardOpeningHours()) => (
  Array.isArray(staffOptions)
    ? staffOptions.reduce((payload, staff) => {
        const rawOpeningHours = staffOpeningHours && typeof staffOpeningHours === 'object'
          ? staffOpeningHours[staff.id]
          : null;
        const normalizedHours = normalizeOpeningHoursForForm(rawOpeningHours || fallbackOpeningHours);

        payload[staff.id] = buildOpeningHoursPayload(normalizedHours);
        return payload;
      }, {})
    : {}
);

const formatStaffTabInitials = (staff = {}) => {
  const explicitInitials = String(staff.initials || '').trim().toUpperCase();
  if (explicitInitials) {
    return explicitInitials;
  }

  const parts = String(staff.label || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '??';
  }

  return parts.map((part) => part[0]).join('').slice(0, 3).toUpperCase();
};

const validateOpeningHoursSchedule = (schedule = {}, scheduleLabel = '', requireActiveDay = false) => {
  if (requireActiveDay && !openingHoursHaveActiveDay(schedule)) {
    throw new Error('Enable at least one opening day before turning on patient booking opening hours.');
  }

  WEEKDAY_OPTIONS.forEach(({ key, label }) => {
    const day = schedule[key] || {};
    if (!day.enabled) {
      return;
    }

    const startMinutes = parseTimeValueToMinutes(day.start);
    const endMinutes = parseTimeValueToMinutes(day.end);
    const prefix = scheduleLabel ? `${scheduleLabel} ` : '';
    if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) {
      throw new Error(`${prefix}${label} opening hours need valid start and end times.`);
    }

    if (endMinutes <= startMinutes) {
      throw new Error(`${prefix}${label} opening hours must end after they start.`);
    }
  });
};

const parseTimeValueToMinutes = (timeValue) => {
  const [hoursPart, minutesPart] = String(timeValue || '').trim().split(':');
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }

  return (hours * 60) + minutes;
};

const normalizeVideoconsultationOptionList = (items = [], idKey = 'value') => (
  Array.isArray(items)
    ? items
      .map((item) => ({
        ...item,
        [idKey]: String(item?.[idKey] || '').trim(),
        label: String(item?.label || '').trim(),
      }))
      .filter((item) => item[idKey])
    : []
);

const sortValuesByCatalogOrder = (values = [], options = [], optionValueKey = 'value') => {
  const orderMap = new Map(options.map((option, index) => [String(option?.[optionValueKey] || ''), index]));
  return Array.from(new Set((Array.isArray(values) ? values : []).map((item) => String(item || '').trim()).filter(Boolean)))
    .sort((left, right) => {
      const leftIndex = orderMap.has(left) ? orderMap.get(left) : Number.MAX_SAFE_INTEGER;
      const rightIndex = orderMap.has(right) ? orderMap.get(right) : Number.MAX_SAFE_INTEGER;
      if (leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
      }
      return left.localeCompare(right);
    });
};

const normalizeVideoconsultationCatalog = (catalog = {}) => ({
  recommendationMode: String(catalog.recommendation_mode || 'recommendation_only'),
  staffOverrideMode: String(catalog.staff_override_mode || 'narrow_only'),
  specialityOptions: normalizeVideoconsultationOptionList(catalog.speciality_options, 'value'),
  symptomTagOptions: normalizeVideoconsultationOptionList(catalog.symptom_tag_options, 'value'),
  staffOptions: normalizeVideoconsultationOptionList(catalog.staff_options, 'id'),
});

const normalizeVideoconsultationSettings = (settings = {}, catalog = DEFAULT_VIDEOCONSULTATION_CATALOG) => {
  const specialityOptions = catalog.specialityOptions || [];
  const symptomTagOptions = catalog.symptomTagOptions || [];
  const staffOptions = catalog.staffOptions || [];
  const allowedSpecialities = new Set(specialityOptions.map((item) => item.value));
  const allowedSymptomTags = new Set(symptomTagOptions.map((item) => item.value));
  const allowedStaffIds = new Set(staffOptions.map((item) => item.id));

  const staffOverrides = Array.isArray(settings.staff_overrides)
    ? settings.staff_overrides
      .map((item) => ({
        staffId: String(item?.staff_id || '').trim(),
        enabled: item?.enabled !== undefined ? Boolean(item.enabled) : true,
        excludedSpecialities: sortValuesByCatalogOrder(
          (Array.isArray(item?.excluded_specialities) ? item.excluded_specialities : []).filter((value) => allowedSpecialities.has(String(value))),
          specialityOptions,
          'value'
        ),
        excludedSymptomTags: sortValuesByCatalogOrder(
          (Array.isArray(item?.excluded_symptom_tags) ? item.excluded_symptom_tags : []).filter((value) => allowedSymptomTags.has(String(value))),
          symptomTagOptions,
          'value'
        ),
      }))
      .filter((item) => item.staffId && allowedStaffIds.has(item.staffId))
    : [];

  return {
    enabled: settings.enabled !== undefined ? Boolean(settings.enabled) : true,
    defaultRequiresNoRedFlags: settings.default_requires_no_red_flags !== undefined
      ? Boolean(settings.default_requires_no_red_flags)
      : true,
    excludedSpecialities: sortValuesByCatalogOrder(
      (Array.isArray(settings.excluded_specialities) ? settings.excluded_specialities : []).filter((value) => allowedSpecialities.has(String(value))),
      specialityOptions,
      'value'
    ),
    excludedSymptomTags: sortValuesByCatalogOrder(
      (Array.isArray(settings.excluded_symptom_tags) ? settings.excluded_symptom_tags : []).filter((value) => allowedSymptomTags.has(String(value))),
      symptomTagOptions,
      'value'
    ),
    staffOverrides: sortValuesByCatalogOrder(staffOverrides.map((item) => item.staffId), staffOptions, 'id')
      .map((staffId) => staffOverrides.find((item) => item.staffId === staffId))
      .filter(Boolean),
  };
};

const buildVideoconsultationPayload = (settings = DEFAULT_VIDEOCONSULTATION_SETTINGS) => ({
  enabled: Boolean(settings.enabled),
  default_requires_no_red_flags: Boolean(settings.defaultRequiresNoRedFlags),
  excluded_specialities: Array.isArray(settings.excludedSpecialities) ? settings.excludedSpecialities : [],
  excluded_symptom_tags: Array.isArray(settings.excludedSymptomTags) ? settings.excludedSymptomTags : [],
  staff_overrides: Array.isArray(settings.staffOverrides)
    ? settings.staffOverrides.map((item) => ({
        staff_id: item.staffId,
        enabled: item.enabled !== undefined ? Boolean(item.enabled) : true,
        excluded_specialities: Array.isArray(item.excludedSpecialities) ? item.excludedSpecialities : [],
        excluded_symptom_tags: Array.isArray(item.excludedSymptomTags) ? item.excludedSymptomTags : [],
      }))
    : [],
});

const toggleVideoconsultationValue = (values = [], value, options = [], optionValueKey = 'value') => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) {
    return sortValuesByCatalogOrder(values, options, optionValueKey);
  }

  const currentValues = Array.isArray(values) ? values.map((item) => String(item || '').trim()).filter(Boolean) : [];
  const nextValues = currentValues.includes(normalizedValue)
    ? currentValues.filter((item) => item !== normalizedValue)
    : [...currentValues, normalizedValue];
  return sortValuesByCatalogOrder(nextValues, options, optionValueKey);
};

const normalizeInsuranceSupportCatalog = (catalog = {}) => ({
  networkOptions: normalizeVideoconsultationOptionList(catalog.network_options, 'value'),
  staffOptions: normalizeVideoconsultationOptionList(catalog.staff_options, 'id'),
  partnerTemplates: Array.isArray(catalog.partner_templates) ? catalog.partner_templates.map((item) => ({
    ...item,
    value: String(item.value || '').trim(),
    label: String(item.label || item.name || '').trim(),
  })).filter((item) => item.value && item.label) : [],
});

const normalizeInsuranceSupportSettings = (settings = {}, catalog = DEFAULT_INSURANCE_SUPPORT_CATALOG) => {
  const networkOptions = catalog.networkOptions || [];
  const staffOptions = catalog.staffOptions || [];
  const allowedNetworks = new Set(networkOptions.map((item) => item.value));
  const allowedStaffIds = new Set(staffOptions.map((item) => item.id));

  const staffOverrides = Array.isArray(settings.staff_overrides)
    ? settings.staff_overrides
      .map((item) => ({
        staffId: String(item?.staff_id || '').trim(),
        excludedNetworks: sortValuesByCatalogOrder(
          (Array.isArray(item?.excluded_networks) ? item.excluded_networks : []).filter((value) => allowedNetworks.has(String(value))),
          networkOptions,
          'value'
        ),
      }))
      .filter((item) => item.staffId && allowedStaffIds.has(item.staffId))
    : [];

  return {
    supportedNetworks: sortValuesByCatalogOrder(
      (Array.isArray(settings.supported_networks) ? settings.supported_networks : []).filter((value) => allowedNetworks.has(String(value))),
      networkOptions,
      'value'
    ),
    staffOverrides: sortValuesByCatalogOrder(staffOverrides.map((item) => item.staffId), staffOptions, 'id')
      .map((staffId) => staffOverrides.find((item) => item.staffId === staffId))
      .filter(Boolean),
  };
};

const buildInsuranceSupportPayload = (settings = DEFAULT_INSURANCE_SUPPORT_SETTINGS) => ({
  supported_networks: Array.isArray(settings.supportedNetworks) ? settings.supportedNetworks : [],
  staff_overrides: Array.isArray(settings.staffOverrides)
    ? settings.staffOverrides.map((item) => ({
        staff_id: item.staffId,
        excluded_networks: Array.isArray(item.excludedNetworks) ? item.excludedNetworks : [],
      }))
    : [],
});

const normalizeClinicPhaseConditionCombos = (
  conditions = [],
  metricOptions = DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS,
) => {
  if (!Array.isArray(conditions) || conditions.length === 0) {
    return [buildClinicDefaultPhaseConditionCombo(metricOptions)];
  }

  const groupedConditions = conditions.reduce((groups, condition) => {
    const rawComboIndex = Number.isFinite(Number(condition?.combo_index)) ? Number(condition.combo_index) : 0;
    const comboIndex = rawComboIndex >= 0 ? rawComboIndex : 0;
    if (!groups.has(comboIndex)) {
      groups.set(comboIndex, {
        localId: createPhaseProgressionComboLocalId(),
        combo_join_operator: comboIndex === 0 ? 'and' : (String(condition?.combo_join_operator || 'or').toLowerCase() === 'and' ? 'and' : 'or'),
        sourceConditions: [],
      });
    }
    groups.get(comboIndex).sourceConditions.push(condition || {});
    return groups;
  }, new Map());

  const orderedCombos = Array.from(groupedConditions.entries())
    .sort((left, right) => left[0] - right[0])
    .map(([_comboIndex, combo], normalizedIndex) => ({
      localId: combo.localId,
      combo_join_operator: normalizedIndex === 0 ? 'and' : combo.combo_join_operator,
      conditions: combo.sourceConditions
        .map((condition) => {
          const normalizedMetricKey = normalizeClinicPhaseMetricKey(condition.metric_key, metricOptions);
          if (!normalizedMetricKey) {
            return null;
          }
          return buildClinicPhaseConditionDraft(normalizedMetricKey, condition, metricOptions);
        })
        .filter(Boolean)
        .sort((left, right) => {
          const leftIsAnchor = PHASE_PROGRESSION_ANCHOR_KEYS.includes(left.metric_key);
          const rightIsAnchor = PHASE_PROGRESSION_ANCHOR_KEYS.includes(right.metric_key);
          if (leftIsAnchor === rightIsAnchor) return 0;
          return leftIsAnchor ? -1 : 1;
        })
        .slice(0, 3),
    }));

  return orderedCombos.length > 0 ? orderedCombos : [buildClinicDefaultPhaseConditionCombo(metricOptions)];
};

const getAvailableClinicPhaseMetricOptions = (conditions = [], conditionIndex = 0, metricOptions = DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const currentMetricKey = conditions[conditionIndex]?.metric_key;
  const usedMetricKeys = new Set(
    conditions
      .filter((_condition, index) => index !== conditionIndex)
      .map((condition) => condition.metric_key)
      .filter(Boolean)
  );
  const optionPool = conditionIndex === 0
    ? metricOptions.filter((option) => PHASE_PROGRESSION_ANCHOR_KEYS.includes(option.key))
    : metricOptions;
  return optionPool.filter((option) => option.key === currentMetricKey || !usedMetricKeys.has(option.key));
};

const getNextClinicPhaseMetricKey = (conditions = [], conditionIndex = 0, metricOptions = DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const availableOptions = getAvailableClinicPhaseMetricOptions(conditions, conditionIndex, metricOptions);
  return availableOptions.length > 0
    ? availableOptions[0].key
    : (
      conditionIndex === 0
        ? (metricOptions.find((option) => PHASE_PROGRESSION_ANCHOR_KEYS.includes(option.key))?.key || 'sessions_completed')
        : (metricOptions.find((option) => !PHASE_PROGRESSION_ANCHOR_KEYS.includes(option.key))?.key || metricOptions[0]?.key || 'sessions_completed')
    );
};

const getClinicPhaseMetricType = (condition = {}) => {
  const metricKey = String(condition.metric_key || '').trim();

  if (PHASE_PROGRESSION_PAIN_METRIC_KEYS.has(metricKey) || metricKey.startsWith('kpi/pain_baseline/')) {
    return 'pain';
  }

  if (PHASE_PROGRESSION_FUNCTION_METRIC_KEYS.has(metricKey) || metricKey.startsWith('kpi/function_goal/')) {
    return 'function';
  }

  return 'default';
};

const getClinicPhaseThresholdLabel = (condition = {}) => {
  const metricType = getClinicPhaseMetricType(condition);
  if (metricType === 'pain') return 'Min. reduction (%)';
  if (metricType === 'function') return 'Min. score (0-100)';
  return 'Threshold';
};

const normalizeClinicPhaseProgressionDefaults = (
  items = [],
  metricOptions = DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS,
) => (
  Array.isArray(items)
    ? items.map((item, index) => ({
        localId: item.localId || item.id || `${index}-${createPhaseProgressionLocalId()}`,
        source_phase_index: item.source_phase_index !== undefined && item.source_phase_index !== null ? String(item.source_phase_index) : String(index + 1),
        target_phase_index: item.target_phase_index !== undefined && item.target_phase_index !== null ? String(item.target_phase_index) : String(index + 2),
        conditionCombos: normalizeClinicPhaseConditionCombos(item.conditions, metricOptions),
      }))
    : []
);

const normalizeClinicPhaseFallbackPresets = (items = []) => (
  Array.isArray(items)
    ? items
      .filter(Boolean)
      .map((item, index) => ({
        localId: item.localId || item.title || `fallback-${index}-${createPhaseProgressionLocalId()}`,
        source_phase_index: item.source_phase_index,
        target_phase_index: item.target_phase_index,
        title: item.title || `Legacy fallback preset: Phase ${item.source_phase_index} -> Phase ${item.target_phase_index}`,
        summary: item.summary || '',
        conditions: Array.isArray(item.conditions) ? item.conditions : [],
        notes: Array.isArray(item.notes) ? item.notes : [],
        unrepresented_checks: Array.isArray(item.unrepresented_checks) ? item.unrepresented_checks : [],
        is_exact_match: Boolean(item.is_exact_match),
      }))
    : []
);

const sanitizeClinicAdjustmentMode = (mode, fallback = 'auto_replace') => {
  const resolvedMode = mode || fallback;
  return resolvedMode === 'suggest_only' ? 'disabled' : resolvedMode;
};

const policyTypeLabel = (value) => {
  if (value === 'health') return 'Health Insurance';
  if (value === 'accident') return 'Accident Insurance';
  return 'Other';
};

const normalizePayerDetails = (item) => {
  if (Array.isArray(item.payer_details) && item.payer_details.length > 0) {
    const details = item.payer_details.map((detail) => ({
      policy_type: detail.policy_type || 'health',
      custom_payment_terms_days: detail.custom_payment_terms_days ?? '',
      billing_email: detail.billing_email ?? '',
      ean_number: detail.ean_number ?? '',
      business_registration_number: detail.business_registration_number ?? '',
      reminder_1_days_before_due: detail.reminder_1_days_before_due ?? '',
      reminder_2_days_after_due: detail.reminder_2_days_after_due ?? '',
      reminder_3_days_after_due: detail.reminder_3_days_after_due ?? '',
      inkasso_days_after_due: detail.inkasso_days_after_due ?? '',
    }));

    POLICY_TYPES.forEach((policyType) => {
      if (!details.some((detail) => detail.policy_type === policyType)) {
        details.push({
          policy_type: policyType,
          custom_payment_terms_days: '',
          billing_email: '',
          ean_number: '',
          business_registration_number: '',
          reminder_1_days_before_due: '',
          reminder_2_days_after_due: '',
          reminder_3_days_after_due: '',
          inkasso_days_after_due: '',
        });
      }
    });
    return details;
  }

  return [
    {
      policy_type: 'health',
      custom_payment_terms_days: item.custom_payment_terms_days ?? '',
      billing_email: item.billing_email ?? '',
      ean_number: item.ean_number ?? '',
      business_registration_number: item.business_registration_number ?? '',
      reminder_1_days_before_due: item.reminder_1_days_before_due ?? '',
      reminder_2_days_after_due: item.reminder_2_days_after_due ?? '',
      reminder_3_days_after_due: item.reminder_3_days_after_due ?? '',
      inkasso_days_after_due: item.inkasso_days_after_due ?? '',
    },
    {
      policy_type: 'accident',
      custom_payment_terms_days: '',
      billing_email: '',
      ean_number: '',
      business_registration_number: '',
      reminder_1_days_before_due: '',
      reminder_2_days_after_due: '',
      reminder_3_days_after_due: '',
      inkasso_days_after_due: '',
    },
  ];
};

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#f7faff',
    borderRadius: 14,
    boxShadow: '0 6px 32px rgba(0,45,85,0.18)',
  },
  cardHeader: {
    paddingTop: 0,
    paddingBottom: 0,
    '& .MuiCardHeader-content': {
      margin: 0,
    },
  },
  cardHeaderTitle: {
    color: '#002D55',
    fontWeight: 'bold',
    fontSize: 22,
  },
  tabsIndicator: {
    backgroundColor: '#FF7F27',
    height: 3,
    borderRadius: 18,
    bottom: 0,
  },
  tabRoot: {
    fontWeight: 600,
    fontSize: 'clamp(0.75rem, 1.04vw, 0.95rem)',
    textTransform: 'uppercase',
    color: '#002D55',
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    minWidth: 'clamp(60px, 6.9vw, 100px)',
    padding: '0 clamp(4px, 0.7vw, 12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    '&.Mui-selected': {
      color: '#002D55',
    },
    '&:hover': {
      color: '#FF7F27',
    },
  },
  tabsWrapper: {
    backgroundColor: '#f7faff',
    borderRadius: 12,
    marginBottom: '0.6%',
    height: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,45,85,0.12)',
    [theme.breakpoints.only('md')]: {
      height: 'clamp(24px, calc(4px + 4.5vw), 38px)',
    },
  },
  tabsControlRoot: {
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    '& .MuiTabs-flexContainer': {
      height: '100%',
      alignItems: 'center',
    },
    [theme.breakpoints.only('md')]: {
      minHeight: 'clamp(24px, calc(4px + 4.5vw), 38px)',
      height: 'clamp(24px, calc(4px + 4.5vw), 38px)',
    },
  },
  cardContent: {
    padding: 0,
  },
  tabContent: {
    padding: '0 16px 16px',
    '& $settingSection': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  settingSection: {
    marginBottom: theme.spacing(4),
    padding: 16,
  },
  footerSection: {
    marginBottom: 0,
    paddingBottom: 0,
  },
  sectionDivider: {
    margin: '24px 0',
  },
  stackedAccordion: {
    marginBottom: 8,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  insuranceAccordion: {
    backgroundColor: '#fff',
    borderRadius: 14,
    overflow: 'hidden',
    border: '1px solid #e5e9ef',
    boxShadow: 'none',
    '&.MuiPaper-rounded': {
      borderRadius: 14,
    },
    '&::before': {
      display: 'none',
    },
  },
  insuranceAccordionSummary: {
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0',
  },
  insuranceDetailCard: {
    marginBottom: 20,
    border: '1px solid #e5e9ef',
    borderRadius: 14,
    padding: 16,
    backgroundColor: '#fff',
  },
  roomScheduleWrapper: {
    marginTop: 12,
  },
  roomScheduleHeader: {
    color: '#002D55',
    fontWeight: 700,
    marginBottom: 8,
  },
  roomScheduleToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  roomScheduleHint: {
    color: '#6f7b87',
    fontSize: 12,
  },
  roomScheduleGrid: {
    minWidth: 980,
    display: 'grid',
    gridTemplateColumns: '64px repeat(7, minmax(120px, 1fr))',
    gap: 0,
    border: '1px solid #dbe7f2',
    borderRadius: 14,
    overflow: 'hidden',
    background: '#fff',
  },
  roomScheduleScroll: {
    overflowX: 'auto',
    paddingBottom: 4,
  },
  roomScheduleCorner: {
    borderRight: '1px solid #dbe7f2',
    borderBottom: '1px solid #dbe7f2',
    background: '#f7fafc',
  },
  roomDayHeader: {
    padding: '10px 8px',
    borderBottom: '1px solid #dbe7f2',
    borderRight: '1px solid #dbe7f2',
    background: '#f7fafc',
    minHeight: 58,
  },
  roomDayHeaderLabel: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: ROOM_SCHEDULE_WEEKDAY_LABEL_FONT_SIZE,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    lineHeight: 1.25,
  },
  roomDayHeaderSubLabel: {
    color: '#6f7b87',
    fontSize: ROOM_SCHEDULE_WEEKDAY_SUBLABEL_FONT_SIZE,
    marginTop: 3,
    lineHeight: 1.25,
  },
  roomTimeAxis: {
    position: 'relative',
    height: ROOM_SCHEDULE_BODY_HEIGHT,
    borderRight: '1px solid #dbe7f2',
    background: '#fafcff',
  },
  roomTimeAxisLabel: {
    position: 'absolute',
    left: 8,
    color: '#6f7b87',
    fontSize: 11,
    transform: 'translateY(-50%)',
    whiteSpace: 'nowrap',
  },
  roomTimeColumn: {
    position: 'relative',
    height: ROOM_SCHEDULE_BODY_HEIGHT,
    borderRight: '1px solid #dbe7f2',
    background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
    cursor: 'crosshair',
  },
  roomTimeSlot: {
    height: ROOM_SCHEDULE_ROW_HEIGHT,
    borderBottom: '1px solid #edf2f7',
  },
  roomTimeSlotHour: {
    borderBottomColor: '#d8e2ec',
  },
  roomAllocationCard: {
    position: 'absolute',
    left: 6,
    right: 6,
    borderRadius: 10,
    padding: '6px 8px 7px',
    overflow: 'hidden',
    boxShadow: '0 6px 18px rgba(0, 45, 85, 0.08)',
    borderLeft: '5px solid var(--room-allocation-color, #3174ad)',
    borderTop: '1px solid rgba(0, 45, 85, 0.05)',
    borderRight: '1px solid rgba(0, 45, 85, 0.05)',
    borderBottom: '1px solid rgba(0, 45, 85, 0.05)',
    background: 'var(--room-allocation-background, rgba(49, 116, 173, 0.12))',
    cursor: 'grab',
    userSelect: 'none',
    touchAction: 'none',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  roomSelectionPreview: {
    position: 'absolute',
    left: 6,
    right: 6,
    borderRadius: 10,
    border: '1px dashed rgba(0, 45, 85, 0.35)',
    background: 'rgba(255, 127, 39, 0.14)',
    boxShadow: 'inset 0 0 0 1px rgba(255, 127, 39, 0.08)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 8px',
    zIndex: 1,
  },
  roomSelectionPreviewLabel: {
    color: '#002D55',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
  },
  roomAllocationActions: {
    position: 'absolute',
    top: 5,
    right: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    zIndex: 2,
  },
  roomAllocationActionButton: {
    minWidth: 0,
    padding: '2px 6px',
    fontSize: 10,
    lineHeight: 1.2,
    borderRadius: 8,
    textTransform: 'none',
  },
  roomAllocationCardOverride: {
    boxShadow: '0 0 0 2px rgba(255, 127, 39, 0.18), 0 8px 20px rgba(0, 45, 85, 0.12)',
  },
  roomAllocationTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  roomAllocationMeta: {
    color: '#46607a',
    fontSize: 11,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  roomAllocationResizeHandle: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    width: 30,
    padding: '3px 0',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.72)',
    cursor: 'ns-resize',
    zIndex: 3,
  },
  roomAllocationResizeHandleTop: {
    top: 3,
  },
  roomAllocationResizeHandleBottom: {
    bottom: 3,
  },
  roomAllocationResizeGrip: {
    width: 14,
    height: 2,
    borderRadius: 999,
    background: 'rgba(0, 45, 85, 0.45)',
  },
  roomSpecificDateSection: {
    marginTop: 12,
  },
  roomSpecificDateLabel: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 12,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  footerDivider: {
    marginTop: 52,
    marginleft: 8
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '16px 0',
    paddingBottom: 0
  },
  footerActionButton: {
    marginTop: 10,
    marginBottom: 10,
    marginLeft: 0,
    marginRight: 0,
  },
  sectionTitle: {
    color: '#002D55',
    fontWeight: 600,
    fontSize: 16,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  description: {
    color: '#666',
    fontSize: 13,
    marginBottom: theme.spacing(2),
    lineHeight: 1.6,
  },
  radioLabel: {
    color: '#002D55',
    fontSize: 14,
    '& .MuiRadio-root': {
      color: '#002D55',
    },
    '& .Mui-checked': {
      color: '#FF7F27',
    },
  },
  saveButton: {
    backgroundColor: '#002D55',
    color: '#fff',
    fontWeight: 600,
    padding: '10px 24px',
    borderRadius: 18,
    '&:hover': {
      backgroundColor: '#FF7F27',
    },
  },
  cancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 600,
    padding: '10px 24px',
    borderRadius: 18,
    border: '2px solid transparent',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      borderColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  roomWeekNavButton: {
    width: 32,
    height: 32,
    padding: 0,
    minWidth: 32,
    borderRadius: 16,
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    border: '2px solid transparent',
    boxShadow: 'none',
    lineHeight: 1,
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      borderColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  exampleBox: {
    backgroundColor: '#f0f4f8',
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(2),
    border: '1px solid #d0d8e0',
  },
  exampleTitle: {
    color: '#002D55',
    fontWeight: 600,
    fontSize: 13,
    marginBottom: theme.spacing(1),
  },
  exampleTimes: {
    color: '#666',
    fontSize: 13,
    fontFamily: 'monospace',
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      backgroundColor: '#fff',
      overflow: 'hidden',
      color: '#002D55',
      '& fieldset': {
        borderColor: '#ccc',
      },
      '&:hover fieldset': {
        borderColor: '#FF7F27',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#FF7F27',
      },
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'transparent',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
      borderRadius: 14,
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink:not(.Mui-focused)': {
      color: '#002D55',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#002D55',
    },
    [theme.breakpoints.down('md')]: {
      '& .MuiOutlinedInput-input': {
        fontSize: 'clamp(0.68rem, 1vw, 0.82rem)',
      },
      '& .MuiInputLabel-root': {
        fontSize: 'clamp(0.68rem, 1vw, 0.82rem)',
      },
      '& .MuiInputLabel-shrink': {
        fontSize: 'clamp(0.62rem, 0.9vw, 0.72rem)',
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 12,
      },
      '& .MuiOutlinedInput-input': {
        paddingTop: 10,
        paddingBottom: 10,
        fontSize: 'clamp(0.72rem, 1.26vw, 0.85rem)',
      },
      '& .MuiInputLabel-root': {
        fontSize: 'clamp(0.72rem, 1.26vw, 0.85rem)',
      },
      '& .MuiInputLabel-shrink': {
        fontSize: 'clamp(0.68rem, 1.06vw, 0.75rem)',
      },
    },
  },
  phaseProgressionSelectField: {
    '& .MuiSelect-select': {
      color: '#002D55',
      fontWeight: 500,
      fontSize: 'clamp(0.69rem, 1.04vw, 0.90rem)',
    },
  },
  phaseProgressionCompactField: {
    '& .MuiOutlinedInput-root': {
      height: 30,
      borderRadius: 14,
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: 6.5,
      paddingBottom: 6.5,
    },
    '& .MuiSelect-select.MuiSelect-select': {
      minHeight: 14,
      paddingTop: 6,
      paddingBottom: 6,
      display: 'flex',
      alignItems: 'center',
    },
  },
  dialogPaper: {
    borderRadius: 18,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 12,
      },
      '& .MuiOutlinedInput-input': {
        paddingTop: 10,
        paddingBottom: 10,
        fontSize: '0.92rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.92rem',
      },
      '& .MuiInputLabel-shrink': {
        fontSize: '0.78rem',
      },
    },
    boxShadow: '0 18px 40px rgba(0,45,85,0.18)',
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
      boxShadow: 'none',
      width: '100%',
      maxWidth: '100%',
      margin: 0,
    },
  },
  dialogTitle: {
    borderBottom: '1px solid #eeeeee',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
    [theme.breakpoints.down('sm')]: {
      padding: '12px 16px',
    },
  },
  dialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      alignItems: 'flex-start',
    },
  },
  dialogTitleText: {
    flex: 1,
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.95rem, calc(4px + 0.72vw), 1.15rem)',
    textTransform: 'uppercase',
    [theme.breakpoints.down('md')]: {
      fontSize: 'clamp(0.82rem, 1.3vw, 1rem)',
      lineHeight: 1.15,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 'clamp(0.75rem, 1.04vw, 0.95rem)',
      lineHeight: 1.2,
    },
  },
  closeButton: {
    color: '#64748B',
    padding: 6,
    '&:hover': {
      backgroundColor: '#edf5fc',
    },
  },
  dialogContent: {
    padding: '24px !important',
    backgroundColor: '#e6eaf0',
    '&.MuiDialogContent-dividers': {
      borderTop: 'none',
      borderBottom: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '16px !important',
    },
  },
  dialogSectionLabel: {
    fontSize: 12,
    color: '#666666',
    marginBottom: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    [theme.breakpoints.down('md')]: {
      fontSize: 'clamp(0.62rem, 0.9vw, 0.7rem)',
      marginBottom: 8,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 'clamp(0.68rem, 1.04vw, 0.72rem)',
      marginBottom: 8,
    },
  },
  dialogActions: {
    padding: 16,
    gap: 8,
    justifyContent: 'flex-end',
    borderTop: '1px solid #eeeeee',
    backgroundColor: '#ffffff',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      padding: 12,
    },
  },
  dialogActionButton: {
    borderRadius: 50,
  },
}));

const ClinicSettings = ({ className, ...rest }) => {
  const classes = useStyles();
  const token = useSelector(state => state.auth.token);
  const user = useSelector(state => state.auth.user);
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const normalizeInsurancePaymentTermsList = (items = []) => (
    Array.isArray(items)
      ? items.map((item) => ({
          ...item,
          payer_details: normalizePayerDetails(item).map((detail) => ({ ...detail })),
        }))
      : []
  );

  const resolveDocumentSrc = (value) => {
    if (!value) {
      return '';
    }

    const stringValue = String(value);
    if (stringValue.startsWith('data:') || stringValue.startsWith('http')) {
      return stringValue;
    }

    return `data:application/pdf;base64,${stringValue}`;
  };

  const normalizePendingPatientInsuranceList = (items = []) => (
    Array.isArray(items)
      ? items.map((item) => ({
          ...item,
          patient_display_name: String(item.patient_display_name || '').trim(),
          insurance_provider: String(item.insurance_provider || '').trim(),
          billing_email: String(item.billing_email || '').trim(),
          plan_type: String(item.plan_type || '').trim(),
          policy_number: String(item.policy_number || '').trim(),
          member_id: String(item.member_id || '').trim(),
          group_number: String(item.group_number || '').trim(),
          pending_reason: String(item.pending_reason || '').trim(),
          review_notes: String(item.review_notes || '').trim(),
          reviewed_by_name: String(item.reviewed_by_name || '').trim(),
          reviewed_at: item.reviewed_at || '',
          patient: item.patient || '',
          policy_evidence_file: resolveDocumentSrc(item.policy_evidence_file || ''),
          referral_letters: Array.isArray(item.referral_letters)
            ? item.referral_letters.map((letter) => ({
                ...letter,
                title: String(letter.title || '').trim(),
                referral_id: String(letter.referral_id || '').trim(),
                file_url: resolveDocumentSrc(letter.file_url || ''),
              }))
            : [],
          agreement: item.agreement || null,
          agreement_details: item.agreement_details || null,
          is_pending_review: item.is_pending_review !== undefined ? item.is_pending_review : false,
        }))
      : []
  );

  const buildSettingsSnapshot = (data = {}) => {
    const metricOptions = normalizePhaseProgressionMetricOptions(data.phase_progression_available_metrics);
    const videoconsultationCatalog = normalizeVideoconsultationCatalog(data.videoconsultation_catalog);
    const insuranceSupportCatalog = normalizeInsuranceSupportCatalog(data.insurance_support_catalog);
    const openingHours = normalizeOpeningHoursForForm(data.opening_hours);
    const staffOpeningHours = normalizeStaffOpeningHoursForForm(data.staff_opening_hours, videoconsultationCatalog.staffOptions, openingHours);

    return {
      phaseProgressionAvailableMetrics: metricOptions,
      appointmentInterval: String(data.appointment_interval || 30),
      sameDayMaxGapHours: String(data.same_day_max_gap_hours || 0),
      lateCancellationThresholdHours: String(data.late_cancellation_threshold_hours || 24),
      lateCancellationFeeAmount: String(data.late_cancellation_fee_amount || 0),
      publicListingEnabled: data.public_listing_enabled !== undefined ? data.public_listing_enabled : false,
      patientBookingOpeningHoursEnabled: data.patient_booking_opening_hours_enabled !== undefined ? data.patient_booking_opening_hours_enabled : false,
      openingHours,
      staffOpeningHours,
      exerciseAdjustmentModeDefault: sanitizeClinicAdjustmentMode(data.exercise_adjustment_mode_default),
      videoconsultationCatalog,
      videoconsultationSettings: normalizeVideoconsultationSettings(data.videoconsultation_settings, videoconsultationCatalog),
      insuranceSupportCatalog,
      insuranceSupportSettings: normalizeInsuranceSupportSettings(data.insurance_support_settings, insuranceSupportCatalog),
      phaseProgressionDefaults: normalizeClinicPhaseProgressionDefaults(
        data.phase_progression_defaults,
        metricOptions
      ),
      phaseProgressionFallbackPresets: normalizeClinicPhaseFallbackPresets(data.phase_progression_fallback_presets),
      paymentTermsDefault: String(data.payment_terms_default || 14),
      paymentTermsPatient: String(data.payment_terms_patient || 14),
      paymentTermsInsurance: String(data.payment_terms_insurance || 14),
      stripeFeeDisplayMode: data.stripe_fee_display_mode || 'platform_fee_deduction',
      enablePaymentReminders: data.enable_payment_reminders !== undefined ? data.enable_payment_reminders : true,
      reminder1DaysBeforeDue: String(data.reminder_1_days_before_due || 7),
      reminder2DaysAfterDue: String(data.reminder_2_days_after_due || 3),
      reminder3DaysAfterDue: String(data.reminder_3_days_after_due || 14),
      inkassoDaysAfterDue: String(data.inkasso_days_after_due || 30),
      insurancePaymentTerms: normalizeInsurancePaymentTermsList(data.insurance_payment_terms),
    };
  };

  const applyGeneralSettingsSnapshot = (snapshot) => {
    if (!snapshot) return;
    setPhaseProgressionAvailableMetrics(normalizePhaseProgressionMetricOptions(snapshot.phaseProgressionAvailableMetrics));
    setAppointmentInterval(snapshot.appointmentInterval);
    setSameDayMaxGapHours(snapshot.sameDayMaxGapHours);
    setLateCancellationThresholdHours(snapshot.lateCancellationThresholdHours);
    setLateCancellationFeeAmount(snapshot.lateCancellationFeeAmount);
    setPublicListingEnabled(snapshot.publicListingEnabled);
    setPatientBookingOpeningHoursEnabled(snapshot.patientBookingOpeningHoursEnabled);
    setOpeningHours(normalizeOpeningHoursForForm(snapshot.openingHours));
    setStaffOpeningHours(normalizeStaffOpeningHoursForForm(snapshot.staffOpeningHours, snapshot.videoconsultationCatalog?.staffOptions || [], snapshot.openingHours));
    setExerciseAdjustmentModeDefault(snapshot.exerciseAdjustmentModeDefault);
    setVideoconsultationCatalog(snapshot.videoconsultationCatalog || DEFAULT_VIDEOCONSULTATION_CATALOG);
    setVideoconsultationSettings(snapshot.videoconsultationSettings || DEFAULT_VIDEOCONSULTATION_SETTINGS);
    setInsuranceSupportCatalog(snapshot.insuranceSupportCatalog || DEFAULT_INSURANCE_SUPPORT_CATALOG);
    setInsuranceSupportSettings(snapshot.insuranceSupportSettings || DEFAULT_INSURANCE_SUPPORT_SETTINGS);
    setPhaseProgressionDefaults(normalizeClinicPhaseProgressionDefaults(snapshot.phaseProgressionDefaults, snapshot.phaseProgressionAvailableMetrics));
    setPhaseProgressionFallbackPresets(normalizeClinicPhaseFallbackPresets(snapshot.phaseProgressionFallbackPresets));
    setPaymentTermsDefault(snapshot.paymentTermsDefault);
    setPaymentTermsPatient(snapshot.paymentTermsPatient);
    setPaymentTermsInsurance(snapshot.paymentTermsInsurance);
    setStripeFeeDisplayMode(snapshot.stripeFeeDisplayMode);
    setEnablePaymentReminders(snapshot.enablePaymentReminders);
    setReminder1DaysBeforeDue(snapshot.reminder1DaysBeforeDue);
    setReminder2DaysAfterDue(snapshot.reminder2DaysAfterDue);
    setReminder3DaysAfterDue(snapshot.reminder3DaysAfterDue);
    setInkassoDaysAfterDue(snapshot.inkassoDaysAfterDue);
  };

  const cloneInsuranceOverrides = (items = []) => normalizeInsurancePaymentTermsList(items);

  const [appointmentInterval, setAppointmentInterval] = useState('30');
  const [sameDayMaxGapHours, setSameDayMaxGapHours] = useState('0');
  const [lateCancellationThresholdHours, setLateCancellationThresholdHours] = useState('24');
  const [lateCancellationFeeAmount, setLateCancellationFeeAmount] = useState('0');
  const [publicListingEnabled, setPublicListingEnabled] = useState(false);
  const [patientBookingOpeningHoursEnabled, setPatientBookingOpeningHoursEnabled] = useState(false);
  const [openingHours, setOpeningHours] = useState(createStandardOpeningHours());
  const [staffOpeningHours, setStaffOpeningHours] = useState({});
  const [openingHoursTab, setOpeningHoursTab] = useState('clinic');
  const [exerciseAdjustmentModeDefault, setExerciseAdjustmentModeDefault] = useState('auto_replace');
  const [videoconsultationCatalog, setVideoconsultationCatalog] = useState(DEFAULT_VIDEOCONSULTATION_CATALOG);
  const [videoconsultationSettings, setVideoconsultationSettings] = useState(DEFAULT_VIDEOCONSULTATION_SETTINGS);
  const [insuranceSupportCatalog, setInsuranceSupportCatalog] = useState(DEFAULT_INSURANCE_SUPPORT_CATALOG);
  const [insuranceSupportSettings, setInsuranceSupportSettings] = useState(DEFAULT_INSURANCE_SUPPORT_SETTINGS);
  const [phaseProgressionAvailableMetrics, setPhaseProgressionAvailableMetrics] = useState(DEFAULT_PHASE_PROGRESSION_METRIC_OPTIONS);
  const [phaseProgressionDefaults, setPhaseProgressionDefaults] = useState([]);
  const [phaseProgressionFallbackPresets, setPhaseProgressionFallbackPresets] = useState([]);
  const [expandedPhaseProgressionRule, setExpandedPhaseProgressionRule] = useState(null);
  const [stripeConnectStatus, setStripeConnectStatus] = useState({
    loading: false,
    error: '',
    stripeAccountId: '',
    chargesEnabled: false,
    payoutsEnabled: false,
    onboardingComplete: false,
  });
  
    // Billing configuration state
    const [paymentTermsDefault, setPaymentTermsDefault] = useState('14');
    const [paymentTermsPatient, setPaymentTermsPatient] = useState('14');
    const [paymentTermsInsurance, setPaymentTermsInsurance] = useState('14');
    const [stripeFeeDisplayMode, setStripeFeeDisplayMode] = useState('platform_fee_deduction');
    const [insurancePaymentTerms, setInsurancePaymentTerms] = useState([]);
    const [enablePaymentReminders, setEnablePaymentReminders] = useState(true);
    const [reminder1DaysBeforeDue, setReminder1DaysBeforeDue] = useState('7');
    const [reminder2DaysAfterDue, setReminder2DaysAfterDue] = useState('3');
    const [reminder3DaysAfterDue, setReminder3DaysAfterDue] = useState('14');
    const [inkassoDaysAfterDue, setInkassoDaysAfterDue] = useState('30');
    const [expandedInsurance, setExpandedInsurance] = useState(null);
    const [pendingPatientInsurances, setPendingPatientInsurances] = useState([]);
    const [pendingPatientInsurancesLoading, setPendingPatientInsurancesLoading] = useState(false);
    const [pendingPatientInsuranceDrawerOpen, setPendingPatientInsuranceDrawerOpen] = useState(false);
    const [pendingPatientInsuranceSelectedId, setPendingPatientInsuranceSelectedId] = useState('');
    const [pendingPatientInsuranceReviewNotes, setPendingPatientInsuranceReviewNotes] = useState('');
    const [pendingPatientInsuranceActionId, setPendingPatientInsuranceActionId] = useState('');
    const [pendingPatientInsurancePreview, setPendingPatientInsurancePreview] = useState({ type: '', label: '', url: '' });

  // Agreement (Insurance Payers tab) state
  const [activeTab, setActiveTab] = useState('general');
  const [expandedGeneralSetting, setExpandedGeneralSetting] = useState(false);
  const [agreements, setAgreements] = useState([]);
  const [agreementsLoading, setAgreementsLoading] = useState(false);
  const [expandedAgreement, setExpandedAgreement] = useState(null);
  const [agreementDetailEdits, setAgreementDetailEdits] = useState({});
  const [savingAgreementDetail, setSavingAgreementDetail] = useState({});
  const [addInsurerOpen, setAddInsurerOpen] = useState(false);
  const [editingInsurerId, setEditingInsurerId] = useState('');
  const [selectedInsuranceTemplate, setSelectedInsuranceTemplate] = useState('');
  const [newInsurer, setNewInsurer] = useState(createBlankInsurerDraft());
  const [otherPayers, setOtherPayers] = useState([]);
  const [otherPayersLoading, setOtherPayersLoading] = useState(false);
  const [otherPayerDialogOpen, setOtherPayerDialogOpen] = useState(false);
  const [editingOtherPayerId, setEditingOtherPayerId] = useState('');
  const [newOtherPayer, setNewOtherPayer] = useState(createBlankOtherPayerDraft());
  const [savingOtherPayer, setSavingOtherPayer] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState('');
  const [expandedLocationId, setExpandedLocationId] = useState(null);
  const [expandedLocationSection, setExpandedLocationSection] = useState(null);
  const [locationSnackbar, setLocationSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingRoom, setEditingRoom] = useState({}); // { locationId: 'newRoomName' }
  const [roomSaving, setRoomSaving] = useState({});
  const [newLocation, setNewLocation] = useState(null); // inline add-location form
  const [allStaff, setAllStaff] = useState([]); // all staff for the care provider
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [allocationDrafts, setAllocationDrafts] = useState({});
  const [roomScheduleDates, setRoomScheduleDates] = useState({});
  const [roomScheduleRoomFilters, setRoomScheduleRoomFilters] = useState({});
  const [roomScheduleStaffFilters, setRoomScheduleStaffFilters] = useState({});
  const [roomScheduleShowClassRooms, setRoomScheduleShowClassRooms] = useState({});
  const roomAllocationDragRef = useRef(null);
  const roomAllocationClickSuppressedRef = useRef(false);
  const roomScheduleSelectionRef = useRef(null);
  const roomAllocationResizeRef = useRef(null);
  const roomAllocationResizePreviewRef = useRef(null);
  const openAllocationDialogFromSelectionRef = useRef(null);
  const persistRoomAllocationChangeRef = useRef(null);
  const roomAllocationLongPressTimerRef = useRef(null);
  const roomAllocationLongPressMovedRef = useRef(false);
  const roomAllocationLongPressTargetRef = useRef(null);
  // Caches the Locations & Rooms data so re-navigating to the tab does not refetch.
  // Invalidated by any mutation handler that calls fetchLocations()/fetchAvailabilitySlots().
  const locationsDataLoadedRef = useRef(false);
  // Resource utilization analytics state.
  const [utilizationBasis, setUtilizationBasis] = useState('allocation_vs_opening');
  const [utilizationPeriod, setUtilizationPeriod] = useState('last_month');
  const [utilizationLocationId, setUtilizationLocationId] = useState('');
  const [utilizationSandboxWeekdays, setUtilizationSandboxWeekdays] = useState(DEFAULT_UTILIZATION_SANDBOX_WEEKDAYS);
  const [utilizationSandboxStartTime, setUtilizationSandboxStartTime] = useState(DEFAULT_UTILIZATION_SANDBOX_START_TIME);
  const [utilizationSandboxEndTime, setUtilizationSandboxEndTime] = useState(DEFAULT_UTILIZATION_SANDBOX_END_TIME);
  const [utilizationContinuityMinHours, setUtilizationContinuityMinHours] = useState(DEFAULT_UTILIZATION_CONTINUITY_MIN_HOURS);
  const [utilizationContinuityMaxGapMinutes, setUtilizationContinuityMaxGapMinutes] = useState(DEFAULT_UTILIZATION_CONTINUITY_MAX_GAP_MINUTES);
  const [utilizationSandboxWeekdaysDraft, setUtilizationSandboxWeekdaysDraft] = useState(DEFAULT_UTILIZATION_SANDBOX_WEEKDAYS);
  const [utilizationSandboxStartTimeDraft, setUtilizationSandboxStartTimeDraft] = useState(DEFAULT_UTILIZATION_SANDBOX_START_TIME);
  const [utilizationSandboxEndTimeDraft, setUtilizationSandboxEndTimeDraft] = useState(DEFAULT_UTILIZATION_SANDBOX_END_TIME);
  const [utilizationContinuityMinHoursDraft, setUtilizationContinuityMinHoursDraft] = useState(DEFAULT_UTILIZATION_CONTINUITY_MIN_HOURS);
  const [utilizationContinuityMaxGapMinutesDraft, setUtilizationContinuityMaxGapMinutesDraft] = useState(DEFAULT_UTILIZATION_CONTINUITY_MAX_GAP_MINUTES);
  const [utilizationPanelTab, setUtilizationPanelTab] = useState('utilization');
  const [absenceStatsTab, setAbsenceStatsTab] = useState('monthly');
  const [absenceStaffGroupFilter, setAbsenceStaffGroupFilter] = useState('all');
  const [absenceStaffMemberFilter, setAbsenceStaffMemberFilter] = useState('all');
  const [utilizationBookings, setUtilizationBookings] = useState([]);
  const [utilizationBookingsLoading, setUtilizationBookingsLoading] = useState(false);
  const [utilizationBookingsError, setUtilizationBookingsError] = useState('');
  const utilizationBookingsLoadedRef = useRef(false);
  const utilizationPreferencesHydratedRef = useRef(false);
  const areWeekdaySelectionsEqual = (left = [], right = []) => {
    const normalizedLeft = normalizeUtilizationSandboxWeekdays(left).slice().sort();
    const normalizedRight = normalizeUtilizationSandboxWeekdays(right).slice().sort();
    if (normalizedLeft.length !== normalizedRight.length) {
      return false;
    }
    return normalizedLeft.every((value, index) => value === normalizedRight[index]);
  };

  const utilizationSettingsDirty = (
    !areWeekdaySelectionsEqual(utilizationSandboxWeekdaysDraft, utilizationSandboxWeekdays)
    || String(utilizationSandboxStartTimeDraft || '') !== String(utilizationSandboxStartTime || '')
    || String(utilizationSandboxEndTimeDraft || '') !== String(utilizationSandboxEndTime || '')
    || String(utilizationContinuityMinHoursDraft || '') !== String(utilizationContinuityMinHours || '')
    || String(utilizationContinuityMaxGapMinutesDraft || '') !== String(utilizationContinuityMaxGapMinutes || '')
  );

  const handleUtilizationSandboxWeekdayToggle = (weekdayKey) => {
    const resolvedWeekdayKey = String(weekdayKey || '').trim();
    if (!resolvedWeekdayKey) {
      return;
    }

    setUtilizationSandboxWeekdaysDraft((prev) => {
      const currentWeekdays = normalizeUtilizationSandboxWeekdays(prev);
      if (currentWeekdays.includes(resolvedWeekdayKey)) {
        const nextWeekdays = currentWeekdays.filter((item) => item !== resolvedWeekdayKey);
        return nextWeekdays.length > 0 ? nextWeekdays : currentWeekdays;
      }
      return [...currentWeekdays, resolvedWeekdayKey];
    });
  };

  const applyUtilizationHeatmapSettings = () => {
    const nextSandboxWeekdays = normalizeUtilizationSandboxWeekdays(utilizationSandboxWeekdaysDraft);
    const nextSandboxStartTime = String(utilizationSandboxStartTimeDraft || DEFAULT_UTILIZATION_SANDBOX_START_TIME);
    const nextSandboxEndTime = String(utilizationSandboxEndTimeDraft || DEFAULT_UTILIZATION_SANDBOX_END_TIME);
    const nextContinuityMinHours = String(utilizationContinuityMinHoursDraft || DEFAULT_UTILIZATION_CONTINUITY_MIN_HOURS);
    const nextContinuityMaxGapMinutes = String(utilizationContinuityMaxGapMinutesDraft || DEFAULT_UTILIZATION_CONTINUITY_MAX_GAP_MINUTES);

    setUtilizationSandboxWeekdays(nextSandboxWeekdays);
    setUtilizationSandboxStartTime(nextSandboxStartTime);
    setUtilizationSandboxEndTime(nextSandboxEndTime);
    setUtilizationContinuityMinHours(nextContinuityMinHours);
    setUtilizationContinuityMaxGapMinutes(nextContinuityMaxGapMinutes);

    persistUtilizationPreferences({
      basis: utilizationBasis,
      period: utilizationPeriod,
      locationId: utilizationLocationId,
      sandboxWeekdays: nextSandboxWeekdays,
      sandboxStartTime: nextSandboxStartTime,
      sandboxEndTime: nextSandboxEndTime,
      continuityMinHours: nextContinuityMinHours,
      continuityMaxGapMinutes: nextContinuityMaxGapMinutes,
    });
  };

  const resetUtilizationHeatmapSettingsDraft = () => {
    setUtilizationSandboxWeekdaysDraft(normalizeUtilizationSandboxWeekdays(utilizationSandboxWeekdays));
    setUtilizationSandboxStartTimeDraft(String(utilizationSandboxStartTime || DEFAULT_UTILIZATION_SANDBOX_START_TIME));
    setUtilizationSandboxEndTimeDraft(String(utilizationSandboxEndTime || DEFAULT_UTILIZATION_SANDBOX_END_TIME));
    setUtilizationContinuityMinHoursDraft(String(utilizationContinuityMinHours || DEFAULT_UTILIZATION_CONTINUITY_MIN_HOURS));
    setUtilizationContinuityMaxGapMinutesDraft(String(utilizationContinuityMaxGapMinutes || DEFAULT_UTILIZATION_CONTINUITY_MAX_GAP_MINUTES));
  };

  useEffect(() => {
    if (activeTab !== 'utilization') {
      return;
    }

    const storedPreferences = loadUtilizationPreferences();
    if (storedPreferences) {
      setUtilizationBasis(storedPreferences.basis);
      setUtilizationPeriod(storedPreferences.period);
      setUtilizationLocationId(storedPreferences.locationId);

      setUtilizationSandboxWeekdays(storedPreferences.sandboxWeekdays);
      setUtilizationSandboxStartTime(storedPreferences.sandboxStartTime);
      setUtilizationSandboxEndTime(storedPreferences.sandboxEndTime);
      setUtilizationContinuityMinHours(storedPreferences.continuityMinHours);
      setUtilizationContinuityMaxGapMinutes(storedPreferences.continuityMaxGapMinutes);

      setUtilizationSandboxWeekdaysDraft(storedPreferences.sandboxWeekdays);
      setUtilizationSandboxStartTimeDraft(storedPreferences.sandboxStartTime);
      setUtilizationSandboxEndTimeDraft(storedPreferences.sandboxEndTime);
      setUtilizationContinuityMinHoursDraft(storedPreferences.continuityMinHours);
      setUtilizationContinuityMaxGapMinutesDraft(storedPreferences.continuityMaxGapMinutes);
    }

    utilizationPreferencesHydratedRef.current = true;
  }, [activeTab]);

  useEffect(() => {
    if (!utilizationPreferencesHydratedRef.current) {
      return;
    }

    persistUtilizationPreferences({
      basis: utilizationBasis,
      period: utilizationPeriod,
      locationId: utilizationLocationId,
      sandboxWeekdays: normalizeUtilizationSandboxWeekdays(utilizationSandboxWeekdays),
      sandboxStartTime: String(utilizationSandboxStartTime || DEFAULT_UTILIZATION_SANDBOX_START_TIME),
      sandboxEndTime: String(utilizationSandboxEndTime || DEFAULT_UTILIZATION_SANDBOX_END_TIME),
      continuityMinHours: String(utilizationContinuityMinHours || DEFAULT_UTILIZATION_CONTINUITY_MIN_HOURS),
      continuityMaxGapMinutes: String(utilizationContinuityMaxGapMinutes || DEFAULT_UTILIZATION_CONTINUITY_MAX_GAP_MINUTES),
    });
  }, [
    utilizationBasis,
    utilizationPeriod,
    utilizationLocationId,
    utilizationSandboxWeekdays,
    utilizationSandboxStartTime,
    utilizationSandboxEndTime,
    utilizationContinuityMinHours,
    utilizationContinuityMaxGapMinutes,
  ]);
  // Staff vacation planner state.
  const [staffVacations, setStaffVacations] = useState([]);
  const [vacationsLoading, setVacationsLoading] = useState(false);
  const [vacationActionId, setVacationActionId] = useState(null);
  const [vacationPlannerMonth, setVacationPlannerMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const vacationsLoadedRef = useRef(false);
  const [roomScheduleSelectionPreview, setRoomScheduleSelectionPreview] = useState(null);
  const [roomAllocationResizePreview, setRoomAllocationResizePreview] = useState(null);
  const [roomAllocationAdjustAnchor, setRoomAllocationAdjustAnchor] = useState(null);
  const [roomAllocationAdjustLocationId, setRoomAllocationAdjustLocationId] = useState('');
  const [roomAllocationAdjustSlot, setRoomAllocationAdjustSlot] = useState(null);
  const [roomAllocationAdjustStart, setRoomAllocationAdjustStart] = useState('');
  const [roomAllocationAdjustEnd, setRoomAllocationAdjustEnd] = useState('');
  const [roomAllocationAdjustSaving, setRoomAllocationAdjustSaving] = useState(false);
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [allocationDialogLocationId, setAllocationDialogLocationId] = useState('');
  const [allocationConflictDialogOpen, setAllocationConflictDialogOpen] = useState(false);
  const [allocationConflictDetails, setAllocationConflictDetails] = useState(null);
  const [allocationRoomConflictOpen, setAllocationRoomConflictOpen] = useState(false);
  const [allocationRoomConflictDetails, setAllocationRoomConflictDetails] = useState(null);
  const [openingHoursConflictOpen, setOpeningHoursConflictOpen] = useState(false);
  const [openingHoursConflictDetails, setOpeningHoursConflictDetails] = useState(null);
  const [openingHoursConflictSaving, setOpeningHoursConflictSaving] = useState(false);
  const [calendarDraft, setCalendarDraft] = useState(null);
  const [calendarSaving, setCalendarSaving] = useState(false);
  const [calendarError, setCalendarError] = useState('');
  const [originalSettings, setOriginalSettings] = useState(null);
  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
  const allocationSpecificDateInputRef = useRef(null);
  const allocationOpeningHoursBypassRef = useRef(false);
  const insurancePartnerTemplates = insuranceSupportCatalog.partnerTemplates || [];

  const updateOpeningHoursDay = (scopeId, dayKey, field, value) => {
    if (scopeId === 'clinic') {
      setOpeningHours((prev) => ({
        ...prev,
        [dayKey]: {
          ...(prev[dayKey] || {}),
          [field]: value,
        },
      }));
      return;
    }

    setStaffOpeningHours((prev) => {
      const currentHours = prev[scopeId] || openingHours;
      return {
        ...prev,
        [scopeId]: {
          ...currentHours,
          [dayKey]: {
            ...(currentHours[dayKey] || {}),
            [field]: value,
          },
        },
      };
    });
  };

  const handleApplyStandardOpeningHours = (scopeId = 'clinic') => {
    const standardOpeningHours = createStandardOpeningHours();
    if (scopeId === 'clinic') {
      setOpeningHours(standardOpeningHours);
      return;
    }

    setStaffOpeningHours((prev) => ({
      ...prev,
      [scopeId]: standardOpeningHours,
    }));
  };

  const openingHoursTabs = useMemo(() => ([
    { id: 'clinic', label: 'Clinic', initials: 'Clinic' },
    ...(videoconsultationCatalog.staffOptions || []).map((staff) => ({
      id: staff.id,
      label: staff.label,
      initials: formatStaffTabInitials(staff),
    })),
  ]), [videoconsultationCatalog.staffOptions]);

  const activeOpeningHours = openingHoursTab === 'clinic'
    ? openingHours
    : (staffOpeningHours[openingHoursTab] || openingHours);

  useEffect(() => {
    if (openingHoursTab === 'clinic') {
      return;
    }

    if (!openingHoursTabs.some((tab) => tab.id === openingHoursTab)) {
      setOpeningHoursTab('clinic');
    }
  }, [openingHoursTab, openingHoursTabs]);

  const findVideoconsultationStaffOverride = (staffId) => (
    (videoconsultationSettings.staffOverrides || []).find((item) => item.staffId === String(staffId)) || null
  );

  const handleVideoconsultationClinicToggle = (field, value, options, optionValueKey = 'value') => {
    setVideoconsultationSettings((prev) => ({
      ...prev,
      [field]: toggleVideoconsultationValue(prev[field], value, options, optionValueKey),
    }));
  };

  const handleVideoconsultationStaffOverrideEnabled = (staffId, enabled) => {
    const normalizedStaffId = String(staffId || '').trim();
    if (!normalizedStaffId) {
      return;
    }

    setVideoconsultationSettings((prev) => {
      const existingOverrides = Array.isArray(prev.staffOverrides) ? prev.staffOverrides : [];
      const nextOverrides = enabled
        ? existingOverrides.some((item) => item.staffId === normalizedStaffId)
          ? existingOverrides.map((item) => (
              item.staffId === normalizedStaffId ? { ...item, enabled: true } : item
            ))
          : [...existingOverrides, {
              staffId: normalizedStaffId,
              enabled: true,
              excludedSpecialities: [],
              excludedSymptomTags: [],
            }]
        : existingOverrides.filter((item) => item.staffId !== normalizedStaffId);

      const orderedStaffIds = sortValuesByCatalogOrder(
        nextOverrides.map((item) => item.staffId),
        videoconsultationCatalog.staffOptions,
        'id'
      );

      return {
        ...prev,
        staffOverrides: orderedStaffIds
          .map((orderedStaffId) => nextOverrides.find((item) => item.staffId === orderedStaffId))
          .filter(Boolean),
      };
    });
  };

  const handleVideoconsultationStaffToggle = (staffId, field, value, options, optionValueKey = 'value') => {
    const normalizedStaffId = String(staffId || '').trim();
    if (!normalizedStaffId) {
      return;
    }

    setVideoconsultationSettings((prev) => ({
      ...prev,
      staffOverrides: (prev.staffOverrides || []).map((item) => (
        item.staffId === normalizedStaffId
          ? {
              ...item,
              [field]: toggleVideoconsultationValue(item[field], value, options, optionValueKey),
            }
          : item
      )),
    }));
  };

  const findInsuranceSupportStaffOverride = (staffId) => (
    (insuranceSupportSettings.staffOverrides || []).find((item) => item.staffId === String(staffId)) || null
  );

  const handleInsuranceSupportClinicToggle = (networkValue) => {
    setInsuranceSupportSettings((prev) => ({
      ...prev,
      supportedNetworks: toggleVideoconsultationValue(prev.supportedNetworks, networkValue, insuranceSupportCatalog.networkOptions, 'value'),
      staffOverrides: (prev.staffOverrides || []).map((item) => ({
        ...item,
        excludedNetworks: toggleVideoconsultationValue(item.excludedNetworks, '', insuranceSupportCatalog.networkOptions, 'value').filter((value) => value !== networkValue),
      })),
    }));
  };

  const handleInsuranceSupportStaffOverrideEnabled = (staffId, enabled) => {
    const normalizedStaffId = String(staffId || '').trim();
    if (!normalizedStaffId) {
      return;
    }

    setInsuranceSupportSettings((prev) => {
      const existingOverrides = Array.isArray(prev.staffOverrides) ? prev.staffOverrides : [];
      const nextOverrides = enabled
        ? existingOverrides.some((item) => item.staffId === normalizedStaffId)
          ? existingOverrides
          : [...existingOverrides, { staffId: normalizedStaffId, excludedNetworks: [] }]
        : existingOverrides.filter((item) => item.staffId !== normalizedStaffId);

      const orderedStaffIds = sortValuesByCatalogOrder(nextOverrides.map((item) => item.staffId), insuranceSupportCatalog.staffOptions, 'id');
      return {
        ...prev,
        staffOverrides: orderedStaffIds.map((orderedStaffId) => nextOverrides.find((item) => item.staffId === orderedStaffId)).filter(Boolean),
      };
    });
  };

  const handleInsuranceSupportStaffToggle = (staffId, networkValue) => {
    const normalizedStaffId = String(staffId || '').trim();
    if (!normalizedStaffId) {
      return;
    }

    setInsuranceSupportSettings((prev) => ({
      ...prev,
      staffOverrides: (prev.staffOverrides || []).map((item) => (
        item.staffId === normalizedStaffId
          ? {
              ...item,
              excludedNetworks: toggleVideoconsultationValue(item.excludedNetworks, networkValue, insuranceSupportCatalog.networkOptions, 'value'),
            }
          : item
      )),
    }));
  };

  const handlePhaseProgressionRuleChange = (ruleLocalId, field, value) => {
    setPhaseProgressionDefaults((prev) => prev.map((rule) => (
      rule.localId === ruleLocalId ? { ...rule, [field]: value } : rule
    )));
  };

  const handlePhaseProgressionComboJoinChange = (ruleLocalId, comboLocalId, value) => {
    setPhaseProgressionDefaults((prev) => prev.map((rule) => {
      if (rule.localId !== ruleLocalId) {
        return rule;
      }

      return {
        ...rule,
        conditionCombos: rule.conditionCombos.map((combo, comboIndex) => {
          if (combo.localId !== comboLocalId || comboIndex === 0) {
            return combo;
          }

          return {
            ...combo,
            combo_join_operator: value === 'and' ? 'and' : 'or',
          };
        }),
      };
    }));
  };

  const handlePhaseProgressionConditionChange = (ruleLocalId, comboLocalId, conditionIndex, field, value) => {
    setPhaseProgressionDefaults((prev) => prev.map((rule) => {
      if (rule.localId !== ruleLocalId) {
        return rule;
      }

      return {
        ...rule,
        conditionCombos: rule.conditionCombos.map((combo) => {
          if (combo.localId !== comboLocalId) {
            return combo;
          }

          return {
            ...combo,
            conditions: combo.conditions.map((condition, index) => {
              if (index !== conditionIndex) {
                return condition;
              }

              if (field === 'metric_key') {
                return buildClinicPhaseConditionDraft(value, { ...condition, metric_key: value }, phaseProgressionAvailableMetrics);
              }

              return { ...condition, [field]: value };
            }),
          };
        }),
      };
    }));
  };

  const handleAddPhaseProgressionRule = () => {
    const nextRule = {
      localId: createPhaseProgressionLocalId(),
      source_phase_index: phaseProgressionDefaults.length > 0 ? String(phaseProgressionDefaults.length + 1) : '1',
      target_phase_index: phaseProgressionDefaults.length > 0 ? String(phaseProgressionDefaults.length + 2) : '2',
      conditionCombos: [buildClinicDefaultPhaseConditionCombo(phaseProgressionAvailableMetrics)],
    };

    setPhaseProgressionDefaults((prev) => [...prev, nextRule]);
    setExpandedPhaseProgressionRule(nextRule.localId);
  };

  const handleRemovePhaseProgressionRule = (ruleLocalId) => {
    setPhaseProgressionDefaults((prev) => prev.filter((rule) => rule.localId !== ruleLocalId));
    setExpandedPhaseProgressionRule((prev) => (prev === ruleLocalId ? null : prev));
  };

  const handleUseFallbackPreset = (preset) => {
    const nextRule = {
      localId: createPhaseProgressionLocalId(),
      source_phase_index: String(preset.source_phase_index || ''),
      target_phase_index: String(preset.target_phase_index || ''),
      conditionCombos: normalizeClinicPhaseConditionCombos(preset.conditions, phaseProgressionAvailableMetrics),
    };

    setPhaseProgressionDefaults((prev) => [...prev, nextRule]);
    setExpandedPhaseProgressionRule(nextRule.localId);
  };

  const handleAddPhaseProgressionCondition = (ruleLocalId, comboLocalId) => {
    setPhaseProgressionDefaults((prev) => prev.map((rule) => {
      if (rule.localId !== ruleLocalId) {
        return rule;
      }

      return {
        ...rule,
        conditionCombos: rule.conditionCombos.map((combo) => {
          if (combo.localId !== comboLocalId || combo.conditions.length >= 3) {
            return combo;
          }

          return {
            ...combo,
            conditions: [
              ...combo.conditions,
              buildClinicPhaseConditionDraft(
                getNextClinicPhaseMetricKey(combo.conditions, combo.conditions.length, phaseProgressionAvailableMetrics),
                {},
                phaseProgressionAvailableMetrics
              )
            ],
          };
        }),
      };
    }));
  };

  const handleRemovePhaseProgressionCondition = (ruleLocalId, comboLocalId, conditionIndex) => {
    setPhaseProgressionDefaults((prev) => prev.map((rule) => {
      if (rule.localId !== ruleLocalId) {
        return rule;
      }

      return {
        ...rule,
        conditionCombos: rule.conditionCombos.map((combo) => {
          if (combo.localId !== comboLocalId || combo.conditions.length <= 1 || conditionIndex === 0) {
            return combo;
          }

          return {
            ...combo,
            conditions: combo.conditions.filter((_, index) => index !== conditionIndex),
          };
        }),
      };
    }));
  };

  const handleAddPhaseProgressionConditionCombo = (ruleLocalId) => {
    setPhaseProgressionDefaults((prev) => prev.map((rule) => {
      if (rule.localId !== ruleLocalId) {
        return rule;
      }

      return {
        ...rule,
        conditionCombos: [
          ...rule.conditionCombos,
          buildClinicDefaultPhaseConditionCombo(phaseProgressionAvailableMetrics, 'or'),
        ],
      };
    }));
  };

  const handleRemovePhaseProgressionConditionCombo = (ruleLocalId, comboLocalId) => {
    setPhaseProgressionDefaults((prev) => prev.map((rule) => {
      if (rule.localId !== ruleLocalId || rule.conditionCombos.length <= 1) {
        return rule;
      }

      return {
        ...rule,
        conditionCombos: rule.conditionCombos.filter((combo) => combo.localId !== comboLocalId),
      };
    }));
  };

  const handleInsuranceTermChange = (insuranceProvider, policyType, value) => {
    setInsurancePaymentTerms((prev) =>
      prev.map((item) =>
        item.insurance_provider === insuranceProvider
          ? {
              ...item,
              payer_details: normalizePayerDetails(item).map((detail) =>
                detail.policy_type === policyType ? { ...detail, custom_payment_terms_days: value } : detail
              ),
            }
          : item
      )
    );
  };

  const handleInsuranceBillingEmailChange = (insuranceProvider, policyType, value) => {
    setInsurancePaymentTerms((prev) =>
      prev.map((item) =>
        item.insurance_provider === insuranceProvider
          ? {
              ...item,
              payer_details: normalizePayerDetails(item).map((detail) =>
                detail.policy_type === policyType ? { ...detail, billing_email: value } : detail
              ),
            }
          : item
      )
    );
  };

  const handleInsuranceEanNumberChange = (insuranceProvider, policyType, value) => {
    setInsurancePaymentTerms((prev) =>
      prev.map((item) =>
        item.insurance_provider === insuranceProvider
          ? {
              ...item,
              payer_details: normalizePayerDetails(item).map((detail) =>
                detail.policy_type === policyType ? { ...detail, ean_number: value } : detail
              ),
            }
          : item
      )
    );
  };

  const handleInsuranceBusinessRegistrationNumberChange = (insuranceProvider, policyType, value) => {
    setInsurancePaymentTerms((prev) =>
      prev.map((item) =>
        item.insurance_provider === insuranceProvider
          ? {
              ...item,
              payer_details: normalizePayerDetails(item).map((detail) =>
                detail.policy_type === policyType ? { ...detail, business_registration_number: value } : detail
              ),
            }
          : item
      )
    );
  };

  const handleInsuranceReminderChange = (insuranceProvider, policyType, reminderField, value) => {
    setInsurancePaymentTerms((prev) =>
      prev.map((item) =>
        item.insurance_provider === insuranceProvider
          ? {
              ...item,
              payer_details: normalizePayerDetails(item).map((detail) =>
                detail.policy_type === policyType ? { ...detail, [reminderField]: value } : detail
              ),
            }
          : item
      )
    );
  };

  // Fetch current settings
  const fetchAgreements = async () => {
    setAgreementsLoading(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/insurance-agreements/`, {
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setAgreements(Array.isArray(data) ? data : []);
        // Hydrate edit state from loaded payer details
        const edits = {};
        (Array.isArray(data) ? data : []).forEach((agreement) => {
          POLICY_TYPES.forEach((policyType) => {
            const key = `${agreement.id}-${policyType}`;
            const detail = (agreement.payer_details || []).find((d) => d.policy_type === policyType);
            edits[key] = {
              cvr_number: detail?.cvr_number || '',
              ean_number: detail?.ean_number || '',
              billing_email: detail?.billing_email || '',
              payment_terms_days: detail?.payment_terms_days ?? 14,
              max_coverage_per_appointment: detail?.max_coverage_per_appointment || '',
            };
          });
        });
        setAgreementDetailEdits(edits);
      }
    } catch (error) {
      console.error('Error fetching agreements:', error);
    } finally {
      setAgreementsLoading(false);
    }
  };

  const fetchOtherPayers = async () => {
    setOtherPayersLoading(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/other-payers/`, {
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setOtherPayers(Array.isArray(data) ? data : []);
      } else {
        setOtherPayers([]);
      }
    } catch (error) {
      console.error('Error fetching other payers:', error);
      setOtherPayers([]);
    } finally {
      setOtherPayersLoading(false);
    }
  };

  const handleAgreementDetailChange = (agreementId, policyType, field, value) => {
    const key = `${agreementId}-${policyType}`;
    setAgreementDetailEdits((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const handleSaveAgreementDetail = async (agreementId, policyType) => {
    const key = `${agreementId}-${policyType}`;
    const formData = agreementDetailEdits[key] || {};
    setSavingAgreementDetail((prev) => ({ ...prev, [key]: true }));
    try {
      await fetch(`${backendBaseUrl}/api/profile/clinic/insurance-agreements/${agreementId}/payer-details/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_type: policyType,
          cvr_number: formData.cvr_number || null,
          ean_number: formData.ean_number || null,
          billing_email: formData.billing_email || null,
          payment_terms_days: formData.payment_terms_days !== '' ? parseInt(formData.payment_terms_days) : 14,
          max_coverage_per_appointment: formData.max_coverage_per_appointment !== '' ? parseFloat(formData.max_coverage_per_appointment) : null,
        }),
      });
      await fetchAgreements();
    } catch (error) {
      console.error('Error saving payer detail:', error);
    } finally {
      setSavingAgreementDetail((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleToggleAgreementActive = async (agreementId, currentActive) => {
    try {
      await fetch(`${backendBaseUrl}/api/profile/clinic/insurance-agreements/${agreementId}/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentActive }),
      });
      await fetchAgreements();
    } catch (error) {
      console.error('Error toggling agreement status:', error);
    }
  };

  const buildInsuranceAgreementPayload = (source) => ({
    insurance_provider_name: String(source.insurance_provider_name || '').trim(),
    address: source.address || null,
    postal_code: source.postal_code || null,
    city: source.city || null,
    email: source.email || null,
    ean_number: source.ean_number || null,
    person_reference: source.person_reference || null,
    phone: source.phone || null,
    business_registration_number: source.business_registration_number || null,
    effective_from: source.effective_from || null,
    effective_until: source.effective_until || null,
    payer_details: POLICY_TYPES.map((policyType) => {
      const detail = source.payer_details?.[policyType] || {};
      return {
        policy_type: policyType,
        cvr_number: detail.cvr_number || null,
        ean_number: detail.ean_number || null,
        billing_email: detail.billing_email || null,
        payment_terms_days: detail.payment_terms_days !== '' ? parseInt(detail.payment_terms_days, 10) : 14,
        max_coverage_per_appointment: detail.max_coverage_per_appointment !== '' ? parseFloat(detail.max_coverage_per_appointment) : null,
      };
    }),
  });

  const buildInsuranceAgreementDraft = (agreement = {}) => ({
    insurance_provider_name: String(agreement.insurance_provider_name || '').trim(),
    address: agreement.address || '',
    postal_code: agreement.postal_code || '',
    city: agreement.city || '',
    email: agreement.email || '',
    ean_number: agreement.ean_number || '',
    person_reference: agreement.person_reference || '',
    phone: agreement.phone || '',
    business_registration_number: agreement.business_registration_number || '',
    effective_from: agreement.effective_from || '',
    effective_until: agreement.effective_until || '',
    payer_details: POLICY_TYPES.reduce((accumulator, policyType) => {
      const detail = normalizePayerDetails(agreement).find((item) => item.policy_type === policyType) || {};
      accumulator[policyType] = {
        cvr_number: detail.cvr_number || '',
        ean_number: detail.ean_number || '',
        billing_email: detail.billing_email || '',
        payment_terms_days: detail.custom_payment_terms_days !== undefined && detail.custom_payment_terms_days !== ''
          ? String(detail.custom_payment_terms_days)
          : '14',
        max_coverage_per_appointment: detail.max_coverage_per_appointment || '',
      };
      return accumulator;
    }, {}),
  });

  const buildPatientInsuranceAgreementPayload = (record = {}) => ({
    insurance_provider_name: String(record.insurance_provider || '').trim(),
    address: '',
    postal_code: '',
    city: '',
    email: String(record.billing_email || '').trim(),
    ean_number: '',
    person_reference: '',
    phone: '',
    business_registration_number: '',
    effective_from: '',
    effective_until: '',
    payer_details: {
      health: {
        billing_email: String(record.billing_email || '').trim(),
        payment_terms_days: String(paymentTermsInsurance || '14'),
      },
    },
  });

  const resetInsurerDialog = () => {
    setEditingInsurerId('');
    setSelectedInsuranceTemplate('');
    setNewInsurer(createBlankInsurerDraft());
  };

  const closeInsurerDialog = () => {
    setAddInsurerOpen(false);
    resetInsurerDialog();
  };

  const openEditInsurerDialog = (agreement) => {
    if (!agreement) return;
    setEditingInsurerId(String(agreement.id || ''));
    setSelectedInsuranceTemplate('');
    setNewInsurer(buildInsuranceAgreementDraft(agreement));
    setAddInsurerOpen(true);
  };

  const handleSaveInsuranceAgreement = async (payloadSource) => {
    if (!String(payloadSource?.insurance_provider_name || '').trim()) return;
    setSaving(true);
    try {
      const payload = buildInsuranceAgreementPayload(payloadSource);
      const isEditing = Boolean(editingInsurerId && payloadSource === newInsurer);
      const response = await fetch(
        isEditing
          ? `${backendBaseUrl}/api/profile/clinic/insurance-agreements/${editingInsurerId}/`
          : `${backendBaseUrl}/api/profile/clinic/insurance-agreements/`,
        {
        method: isEditing ? 'PATCH' : 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
      );
      if (response.ok) {
        if (payloadSource === newInsurer) {
          setAddInsurerOpen(false);
          resetInsurerDialog();
        }
        setEditingInsurerId('');
        await fetchAgreements();
      } else {
        alert('Failed to save insurer. Please try again.');
      }
    } catch (error) {
      console.error('Error saving insurer:', error);
      alert('Error saving insurer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNewInsurer = async () => {
    await handleSaveInsuranceAgreement(newInsurer);
  };

  const openAddInsurerDialog = () => {
    resetInsurerDialog();
    setAddInsurerOpen(true);
  };

  const openPendingPatientInsuranceDrawer = async () => {
    setPendingPatientInsuranceDrawerOpen(true);
    await fetchPendingPatientInsurances();
  };

  const closePendingPatientInsuranceDrawer = () => {
    setPendingPatientInsuranceDrawerOpen(false);
    setPendingPatientInsuranceActionId('');
  };

  const handlePromotePendingInsuranceToClinic = async (record) => {
    if (!record?.id || !String(record.insurance_provider || '').trim()) return;
    if (record.agreement || record.agreement_details) {
      return;
    }

    const actionKey = `promote-${record.id}`;
    setPendingPatientInsuranceActionId(actionKey);
    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/insurance-agreements/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPatientInsuranceAgreementPayload(record)),
      });

      if (!response.ok) {
        throw new Error('Failed to add insurer to clinic registry');
      }

      await fetchAgreements();
      await fetchPendingPatientInsurances();
    } catch (error) {
      console.error('Error promoting patient insurance to clinic agreement:', error);
    } finally {
      setPendingPatientInsuranceActionId('');
    }
  };

  const openSelectedPatientInsuranceReview = (record) => {
    if (!record?.patient) return;

    const selectedPatientId = String(record.patient);
    try {
      localStorage.setItem('vp_selectedPatientId', selectedPatientId);
      if (user?.id) {
        localStorage.setItem(`vp_selectedPatientId_${user.id}`, selectedPatientId);
      }
      localStorage.setItem('vp_clinicDetailsTabIndex', '2');
    } catch (error) {
      console.error('Failed to persist selected patient for review:', error);
    }

    history.push('/my_clinic_account');
  };

  const handleApprovePendingInsuranceBilling = async (record) => {
    if (!record?.id) return;

    const actionKey = `approve-${record.id}`;
    setPendingPatientInsuranceActionId(actionKey);
    try {
      let agreementId = record.agreement ? String(record.agreement) : '';

      if (!agreementId && !record.agreement_details) {
        const agreementResponse = await fetch(`${backendBaseUrl}/api/profile/clinic/insurance-agreements/`, {
          method: 'POST',
          headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPatientInsuranceAgreementPayload(record)),
        });

        if (agreementResponse.ok) {
          const agreementData = await agreementResponse.json();
          agreementId = String(agreementData?.id || '');
        }
      }

      const reviewResponse = await fetch(`${backendBaseUrl}/api/profile/patient/insurance/${record.id}/review/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'approve',
          notes: pendingPatientInsuranceReviewNotes || record.review_notes || '',
        }),
      });

      if (!reviewResponse.ok) {
        throw new Error('Failed to approve insurance billing');
      }

      if (agreementId) {
        await fetch(`${backendBaseUrl}/api/profile/patient/insurance/${record.id}/`, {
          method: 'PATCH',
          headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ agreement: agreementId }),
        });
      }

      await fetchAgreements();
      await fetchPendingPatientInsurances();
    } catch (error) {
      console.error('Error approving patient insurance billing:', error);
    } finally {
      setPendingPatientInsuranceActionId('');
    }
  };

  const openNewOtherPayerDialog = () => {
    setEditingOtherPayerId('');
    setNewOtherPayer(createBlankOtherPayerDraft());
    setOtherPayerDialogOpen(true);
  };

  const openEditOtherPayerDialog = (payer) => {
    if (!payer) return;
    setEditingOtherPayerId(String(payer.id || ''));
    setNewOtherPayer({
      name: payer.name || '',
      address: payer.address || '',
      postal_code: payer.postal_code || '',
      city: payer.city || '',
      billing_email: payer.billing_email || payer.email || '',
      cvr_number: payer.cvr_number || '',
      ean_number: payer.ean_number || '',
      person_reference: payer.person_reference || '',
      phone: payer.phone || '',
    });
    setOtherPayerDialogOpen(true);
  };

  const handleSaveOtherPayer = async () => {
    if (!String(newOtherPayer.name || '').trim()) return;
    setSavingOtherPayer(true);
    try {
      const isEditing = Boolean(editingOtherPayerId);
      const response = await fetch(
        isEditing
          ? `${backendBaseUrl}/api/profile/clinic/other-payers/${editingOtherPayerId}/`
          : `${backendBaseUrl}/api/profile/clinic/other-payers/`,
        {
          method: isEditing ? 'PATCH' : 'POST',
          headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(newOtherPayer),
        }
      );
      if (response.ok) {
        setOtherPayerDialogOpen(false);
        setEditingOtherPayerId('');
        setNewOtherPayer(createBlankOtherPayerDraft());
        await fetchOtherPayers();
      } else {
        alert('Failed to save other payer. Please try again.');
      }
    } catch (error) {
      console.error('Error saving other payer:', error);
      alert('Error saving other payer. Please try again.');
    } finally {
      setSavingOtherPayer(false);
    }
  };

  const handleDeleteOtherPayer = async (payerId) => {
    if (!payerId) return;
    if (!window.confirm('Delete this other payer?')) return;
    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/other-payers/${payerId}/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        await fetchOtherPayers();
      } else {
        alert('Failed to delete other payer. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting other payer:', error);
      alert('Error deleting other payer. Please try again.');
    }
  };

  const resetNewInsurerForm = () => {
    setSelectedInsuranceTemplate('');
    setNewInsurer(createBlankInsurerDraft());
  };

  const applyInsurancePartnerTemplateToForm = (template) => {
    const partnerTemplate = template || {};
    setSelectedInsuranceTemplate(String(partnerTemplate.value || ''));
    setNewInsurer((prev) => ({
      ...createBlankInsurerDraft(),
      insurance_provider_name: partnerTemplate.name || partnerTemplate.label || '',
      business_registration_number: partnerTemplate.cvr || '',
      address: partnerTemplate.address || '',
      postal_code: partnerTemplate.postal_code || '',
      city: partnerTemplate.city || '',
      email: partnerTemplate.email || '',
      ean_number: partnerTemplate.ean || '',
      person_reference: partnerTemplate.person_reference || '',
      phone: partnerTemplate.phone || '',
      payer_details: prev.payer_details,
    }));
  };

  const handleOpenInsurancePartnerTemplate = (template) => {
    applyInsurancePartnerTemplateToForm(template);
    setAddInsurerOpen(true);
  };

  const fetchStripeConnectStatus = async () => {
    if (!token) return;

    setStripeConnectStatus((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/stripe/connect/status/?account_type=careprovider`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Unable to load Stripe Connect status');
      }

      setStripeConnectStatus({
        loading: false,
        error: '',
        stripeAccountId: data.stripe_account_id || '',
        chargesEnabled: Boolean(data.charges_enabled),
        payoutsEnabled: Boolean(data.payouts_enabled),
        onboardingComplete: Boolean(data.onboarding_complete),
      });
    } catch (error) {
      setStripeConnectStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Unable to load Stripe Connect status',
      }));
    }
  };

  const handleStartStripeOnboarding = async () => {
    if (!token) return;

    setStripeConnectStatus((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/stripe/connect/onboarding/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ account_type: 'careprovider' }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Unable to start Stripe onboarding');
      }

      if (data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }

      await fetchStripeConnectStatus();
    } catch (error) {
      setStripeConnectStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Unable to start Stripe onboarding',
      }));
    }
  };

  const handleOpenStripeDashboard = async () => {
    if (!token) return;

    setStripeConnectStatus((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/stripe/connect/dashboard-link/?account_type=careprovider`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Unable to open Stripe dashboard');
      }

      if (data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      setStripeConnectStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Unable to open Stripe dashboard',
      }));
    } finally {
      setStripeConnectStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/settings/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const snapshot = buildSettingsSnapshot(data);
        applyGeneralSettingsSnapshot(snapshot);
        setInsurancePaymentTerms(cloneInsuranceOverrides(snapshot.insurancePaymentTerms));
        setPendingPatientInsurances(normalizePendingPatientInsuranceList(data.pending_patient_insurances));
        setOriginalSettings(snapshot);
      }
    } catch (error) {
      console.error('Error fetching clinic settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingPatientInsurances = async () => {
    setPendingPatientInsurancesLoading(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/settings/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPendingPatientInsurances(normalizePendingPatientInsuranceList(data.pending_patient_insurances));
      }
    } catch (error) {
      console.error('Error fetching pending patient insurances:', error);
    } finally {
      setPendingPatientInsurancesLoading(false);
    }
  };

  // Fetch current settings
  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [token, backendBaseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pendingPatientInsurances.length) {
      setPendingPatientInsuranceSelectedId('');
      setPendingPatientInsuranceReviewNotes('');
      setPendingPatientInsurancePreview({ type: '', label: '', url: '' });
      return;
    }

    const selectedRecord = pendingPatientInsurances.find((item) => String(item.id) === String(pendingPatientInsuranceSelectedId)) || pendingPatientInsurances[0];
    if (!selectedRecord) {
      return;
    }

    if (!pendingPatientInsuranceSelectedId || !pendingPatientInsurances.some((item) => String(item.id) === String(pendingPatientInsuranceSelectedId))) {
      setPendingPatientInsuranceSelectedId(String(selectedRecord.id));
      setPendingPatientInsuranceReviewNotes(selectedRecord.review_notes || '');
      setPendingPatientInsurancePreview(() => {
        if (selectedRecord.policy_evidence_file) {
          return { type: 'policy', label: 'Policy document', url: selectedRecord.policy_evidence_file };
        }
        const firstLetter = (selectedRecord.referral_letters || []).find((letter) => letter.file_url);
        return firstLetter ? { type: 'referral', label: firstLetter.title || firstLetter.referral_id || 'Referral letter', url: firstLetter.file_url } : { type: '', label: '', url: '' };
      });
      return;
    }

    if (String(selectedRecord.id) === String(pendingPatientInsuranceSelectedId)) {
      setPendingPatientInsuranceReviewNotes((prev) => (prev || selectedRecord.review_notes || ''));
      setPendingPatientInsurancePreview((prev) => {
        if (prev.url) {
          return prev;
        }
        if (selectedRecord.policy_evidence_file) {
          return { type: 'policy', label: 'Policy document', url: selectedRecord.policy_evidence_file };
        }
        const firstLetter = (selectedRecord.referral_letters || []).find((letter) => letter.file_url);
        return firstLetter ? { type: 'referral', label: firstLetter.title || firstLetter.referral_id || 'Referral letter', url: firstLetter.file_url } : { type: '', label: '', url: '' };
      });
    }
  }, [pendingPatientInsurances, pendingPatientInsuranceSelectedId]);

  useEffect(() => {
    if (token && activeTab === 'general') {
      fetchStripeConnectStatus();
    }
  }, [token, activeTab, backendBaseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token && activeTab === 'insurance') {
      fetchAgreements();
    }
  }, [token, activeTab, backendBaseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token && activeTab === 'other') {
      fetchOtherPayers();
    }
  }, [token, activeTab, backendBaseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Locations & Rooms --
  const fetchLocations = async () => {
    setLocationsLoading(true);
    setLocationsError('');
    try {
      const res = await fetch(`${backendBaseUrl}/api/profile/clinic/locations/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (res.ok) {
        setLocations(await res.json());
      } else {
        const errorData = await res.json().catch(() => ({}));
        setLocations([]);
        setLocationsError(errorData.error || 'Unable to load locations.');
      }
    } catch {
      setLocations([]);
      setLocationsError('Unable to load locations.');
    } finally { setLocationsLoading(false); }
  };

  const fetchAvailabilitySlots = async () => {
    setAvailabilityLoading(true);
    try {
      const res = await fetch(`${backendBaseUrl}/api/calendar/availability/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAvailabilitySlots(Array.isArray(data.availability_slots) ? data.availability_slots : []);
      } else {
        setAvailabilitySlots([]);
      }
    } catch {
      setAvailabilitySlots([]);
    } finally { setAvailabilityLoading(false); }
  };

  const fetchStaffList = async () => {
    try {
      const res = await fetch(`${backendBaseUrl}/profile/my_careprovider_staff/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAllStaff((data.staff || []).map(s => ({
          id: s.user_id,
          name: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email,
          roleType: s.role_type || '',
          staffEngagementType: s.staff_engagement_type || '',
        })));
      }
    } catch { /* ignore */ }
  };

  // Loads all appointments once for the booking-based utilization bases. The API has no
  // documented date-range params, so we fetch all and filter client-side by start date.
  const fetchUtilizationBookings = async () => {
    setUtilizationBookingsLoading(true);
    setUtilizationBookingsError('');
    try {
      const res = await fetch(`${backendBaseUrl}/api/calendar/appointments/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) {
        throw new Error('Unable to load appointments for utilization analysis.');
      }
      const data = await res.json().catch(() => ({}));
      setUtilizationBookings(Array.isArray(data.appointments) ? data.appointments : []);
    } catch (error) {
      setUtilizationBookings([]);
      setUtilizationBookingsError(error.message || 'Unable to load appointments.');
    } finally {
      setUtilizationBookingsLoading(false);
    }
  };

  // Loads reported staff vacations for the planner + owner banner.
  const fetchStaffVacations = async () => {
    setVacationsLoading(true);
    try {
      const res = await fetch(`${backendBaseUrl}/api/calendar/vacations/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setStaffVacations(Array.isArray(data.vacations) ? data.vacations : []);
      } else {
        setStaffVacations([]);
      }
    } catch {
      setStaffVacations([]);
    } finally {
      setVacationsLoading(false);
    }
  };

  const acknowledgeVacation = async (vacationId) => {
    if (!vacationId) return;
    setVacationActionId(vacationId);
    try {
      const res = await fetch(`${backendBaseUrl}/api/calendar/vacations/${vacationId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({ status: 'acknowledged' }),
      });
      if (res.ok) {
        const updated = await res.json().catch(() => null);
        setStaffVacations((prev) => prev.map((v) => (
          String(v.id) === String(vacationId) ? { ...v, ...(updated || { status: 'acknowledged' }) } : v
        )));
      }
    } catch { /* ignore */ } finally {
      setVacationActionId(null);
    }
  };

  const deleteVacation = async (vacationId) => {
    if (!vacationId) return;
    setVacationActionId(vacationId);
    try {
      const res = await fetch(`${backendBaseUrl}/api/calendar/vacations/${vacationId}/delete/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${token}` },
      });
      if (res.ok) {
        setStaffVacations((prev) => prev.filter((v) => String(v.id) !== String(vacationId)));
        // Removing a vacation restores freed allocations, so refresh the heatmaps.
        fetchAvailabilitySlots();
      }
    } catch { /* ignore */ } finally {
      setVacationActionId(null);
    }
  };

  useEffect(() => {
    if (token && (activeTab === 'locations' || activeTab === 'utilization') && !locationsDataLoadedRef.current) {
      locationsDataLoadedRef.current = true;
      fetchLocations();
      fetchStaffList();
      fetchAvailabilitySlots();
    }
    if (token && activeTab === 'utilization' && !utilizationBookingsLoadedRef.current) {
      utilizationBookingsLoadedRef.current = true;
      fetchUtilizationBookings();
    }
    if (token && activeTab === 'utilization' && !vacationsLoadedRef.current) {
      vacationsLoadedRef.current = true;
      fetchStaffVacations();
    }
  }, [token, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Computes overall + per-room resource utilization for the selected basis/period/location.
  // Recurring allocations are treated as a weekly template (the common case); specific-date
  // allocations override the recurring template for that day. The capacity window comes from
  // the clinic-defined sandbox weekdays and hours so the report can drive hiring decisions.
  const utilizationReport = useMemo(() => {
    const basisOption = UTILIZATION_BASIS_OPTIONS.find((option) => option.value === utilizationBasis)
      || UTILIZATION_BASIS_OPTIONS[0];
    const { startDateValue, endDateValue } = getUtilizationPeriodRange(utilizationPeriod);
    const dateList = enumerateScheduleDates(startDateValue, endDateValue);
    const usesBookings = utilizationBasis !== 'allocation_vs_opening';
    const sandboxWeekdayKeys = normalizeUtilizationSandboxWeekdays(utilizationSandboxWeekdays);
    const sandboxWeekdayIndexSet = new Set(
      WEEKDAY_OPTIONS
        .map(({ key }, weekdayIndex) => (sandboxWeekdayKeys.includes(key) ? weekdayIndex : null))
        .filter((weekdayIndex) => weekdayIndex !== null),
    );
    const { start: sandboxStartMinutes, end: sandboxEndMinutes } = getUtilizationSandboxWindow(
      utilizationSandboxStartTime,
      utilizationSandboxEndTime,
    );
    const continuityRules = getUtilizationContinuityRules(
      utilizationContinuityMinHours,
      utilizationContinuityMaxGapMinutes,
    );

    const availableLocations = locations || [];
    const primaryLocation = availableLocations.find((loc) => Boolean(loc.is_primary)) || null;
    const hasExpandedLocation = expandedLocationId
      && availableLocations.some((loc) => String(loc.id) === String(expandedLocationId));
    const scopedLocationId = hasExpandedLocation
      ? String(expandedLocationId)
      : (utilizationLocationId
        ? String(utilizationLocationId)
        : (primaryLocation ? String(primaryLocation.id) : ''));
    const targetLocations = availableLocations.filter((loc) => !scopedLocationId
      || String(loc.id) === scopedLocationId);
    const scopedLocation = scopedLocationId
      ? (availableLocations.find((loc) => String(loc.id) === scopedLocationId) || null)
      : null;

    // Build the time-of-day buckets from the clinic-defined sandbox so the heatmap can be
    // used as a planning window for capacity and hiring decisions.
    const timeBuckets = [];
    if (sandboxEndMinutes > sandboxStartMinutes) {
      const bucketStart = Math.floor(sandboxStartMinutes / UTILIZATION_BUCKET_MINUTES) * UTILIZATION_BUCKET_MINUTES;
      const bucketEnd = Math.ceil(sandboxEndMinutes / UTILIZATION_BUCKET_MINUTES) * UTILIZATION_BUCKET_MINUTES;
      for (let start = bucketStart; start < bucketEnd; start += UTILIZATION_BUCKET_MINUTES) {
        const end = Math.min(start + UTILIZATION_BUCKET_MINUTES, bucketEnd);
        timeBuckets.push({
          start,
          end,
          label: formatScheduleMinutesLabel(start),
        });
      }
    }

    // Accumulator: weekdayCells[weekdayIndex][bucketIndex] = { capacity, used } across the period.
    const weekdayCells = WEEKDAY_OPTIONS.map(() => timeBuckets.map(() => ({ capacity: 0, used: 0 })));

    // Accumulator: per ISO-week (keyed by Monday week-start) capacity/used totals.
    const weeklyTotals = new Map();

    // Room-level weekday/time accumulators so the heatmap can be rendered per room as well.
    const roomHeatmapByKey = new Map();

    // Index bookings by location|room|date for quick per-day lookup.
    const bookingsByKey = new Map();
    if (usesBookings) {
      (utilizationBookings || []).forEach((booking) => {
        const status = String(booking.status || '').toLowerCase();
        if (status.includes('cancel') || status.includes('no_show') || status.includes('noshow')) {
          return;
        }
        const interval = getBookingScheduleInterval(booking);
        if (!interval) {
          return;
        }
        const roomId = String((booking.room && booking.room.id) || booking.room || '');
        const locationId = String(booking.clinic_location || '');
        if (!roomId || !locationId) {
          return;
        }
        const key = `${locationId}|${roomId}|${interval.dateValue}`;
        if (!bookingsByKey.has(key)) {
          bookingsByKey.set(key, []);
        }
        bookingsByKey.get(key).push({ start: interval.start, end: interval.end });
      });
    }

    const roomRows = [];
    let totalCapacity = 0;
    let totalUsed = 0;

    targetLocations.forEach((loc) => {
      const activeRooms = (loc.rooms || []).filter((room) => room && room.is_active !== false);

      // Index this location's allocation slots by room ? recurring (by weekday) / specific date.
      const allocByRoom = new Map();
      (availabilitySlots || []).forEach((slot) => {
        if (String(slot.clinic_location || '') !== String(loc.id)) {
          return;
        }
        if (slot.is_available === false) {
          return;
        }
        const roomId = String((slot.room && slot.room.id) || slot.room || '');
        if (!roomId) {
          return;
        }
        if (!allocByRoom.has(roomId)) {
          allocByRoom.set(roomId, { recurring: {}, specific: {} });
        }
        const bucket = allocByRoom.get(roomId);
        const specificDate = String(slot.specific_date || '').trim();
        if (specificDate) {
          if (!bucket.specific[specificDate]) {
            bucket.specific[specificDate] = [];
          }
          bucket.specific[specificDate].push(slot);
        } else {
          const weekdayIndex = Number(slot.day_of_week);
          if (Number.isFinite(weekdayIndex)) {
            if (!bucket.recurring[weekdayIndex]) {
              bucket.recurring[weekdayIndex] = [];
            }
            bucket.recurring[weekdayIndex].push(slot);
          }
        }
      });

      activeRooms.forEach((room) => {
        const roomId = String(room.id);
        const roomKey = `${loc.id}-${roomId}`;
        const bucket = allocByRoom.get(roomId) || { recurring: {}, specific: {} };
        const roomHeatmapCells = WEEKDAY_OPTIONS.map(() => timeBuckets.map(() => ({ capacity: 0, used: 0 })));
        roomHeatmapByKey.set(roomKey, {
          roomId,
          roomName: room.name || 'Unnamed room',
          locationId: String(loc.id),
          locationName: loc.name || 'Unnamed location',
          roomColor: room.color || '#3174ad',
          cells: roomHeatmapCells,
        });
        let roomCapacity = 0;
        let roomUsed = 0;

        dateList.forEach((dateValue) => {
          const weekdayIndex = getScheduleWeekdayIndex(dateValue);
          if (!sandboxWeekdayIndexSet.has(weekdayIndex)) {
            return; // clinic closed ? no capacity that day
          }
          const sandboxWindow = { start: sandboxStartMinutes, end: sandboxEndMinutes };
          const sandboxMinutes = sandboxWindow.end - sandboxWindow.start;

          // Specific-date allocations override the recurring weekly template for that day.
          const specificSlots = bucket.specific[dateValue] || [];
          const allocationSlots = specificSlots.length
            ? specificSlots
            : (bucket.recurring[weekdayIndex] || []);
          const allocationIntervals = allocationSlots
            .map((slot) => clampScheduleInterval(
              {
                start: parseScheduleTimeToMinutes(slot.start_time),
                end: parseScheduleTimeToMinutes(slot.end_time),
              },
              sandboxWindow.start,
              sandboxWindow.end,
            ))
            .filter(Boolean);
          const allocatedMinutes = getUnionIntervalsMinutes(allocationIntervals);

          let bookingIntervals = [];
          if (usesBookings) {
            const rawBookings = bookingsByKey.get(`${loc.id}|${roomId}|${dateValue}`) || [];
            bookingIntervals = rawBookings
              .map((interval) => clampScheduleInterval(interval, sandboxWindow.start, sandboxWindow.end))
              .filter(Boolean);
          }

          // Bookings that fall within an allocation (used for the bookings-vs-allocation basis).
          let restrictedIntervals = [];
          if (utilizationBasis === 'bookings_vs_allocation') {
            bookingIntervals.forEach((bookingInterval) => {
              allocationIntervals.forEach((allocationInterval) => {
                const overlap = clampScheduleInterval(
                  bookingInterval,
                  allocationInterval.start,
                  allocationInterval.end,
                );
                if (overlap) {
                  restrictedIntervals.push(overlap);
                }
              });
            });
          }

          // Resolve this date's capacity + used intervals for the selected basis.
          let dateCapacityMinutes;
          let dateUsedMinutes;
          let capacityIntervals;
          let usedIntervals;
          if (utilizationBasis === 'allocation_vs_opening') {
            dateCapacityMinutes = sandboxMinutes;
            dateUsedMinutes = allocatedMinutes;
            capacityIntervals = [sandboxWindow];
            usedIntervals = allocationIntervals;
          } else if (utilizationBasis === 'bookings_vs_allocation') {
            dateCapacityMinutes = allocatedMinutes;
            dateUsedMinutes = getUnionIntervalsMinutes(restrictedIntervals);
            capacityIntervals = allocationIntervals;
            usedIntervals = restrictedIntervals;
          } else { // bookings_vs_opening
            dateCapacityMinutes = sandboxMinutes;
            dateUsedMinutes = getUnionIntervalsMinutes(bookingIntervals);
            capacityIntervals = [sandboxWindow];
            usedIntervals = bookingIntervals;
          }

          roomCapacity += dateCapacityMinutes;
          roomUsed += dateUsedMinutes;

          // Accumulate into the week bucket (keyed by the Monday week-start of this date).
          const weekStart = getScheduleWeekStartDateValue(dateValue);
          if (weekStart) {
            const weekEntry = weeklyTotals.get(weekStart) || { capacity: 0, used: 0 };
            weekEntry.capacity += dateCapacityMinutes;
            weekEntry.used += dateUsedMinutes;
            weeklyTotals.set(weekStart, weekEntry);
          }

          // Distribute this date's capacity/used minutes into the weekday-by-time buckets.
          const cellRow = weekdayCells[weekdayIndex];
          timeBuckets.forEach((timeBucket, bucketIndex) => {
            const capacityInBucket = getUnionIntervalsMinutes(
              capacityIntervals
                .map((iv) => clampScheduleInterval(iv, timeBucket.start, timeBucket.end))
                .filter(Boolean),
            );
            if (capacityInBucket <= 0) {
              return;
            }
            const usedInBucket = getUnionIntervalsMinutes(
              usedIntervals
                .map((iv) => clampScheduleInterval(iv, timeBucket.start, timeBucket.end))
                .filter(Boolean),
            );
            cellRow[bucketIndex].capacity += capacityInBucket;
            cellRow[bucketIndex].used += usedInBucket;

            const roomCellRow = roomHeatmapCells[weekdayIndex];
            roomCellRow[bucketIndex].capacity += capacityInBucket;
            roomCellRow[bucketIndex].used += usedInBucket;
          });
        });

        const roomUnused = Math.max(0, roomCapacity - roomUsed);
        totalCapacity += roomCapacity;
        totalUsed += roomUsed;
        roomRows.push({
          key: `${loc.id}-${roomId}`,
          roomId,
          roomName: room.name || 'Unnamed room',
          locationId: String(loc.id),
          locationName: loc.name || 'Unnamed location',
          roomColor: room.color || '#3174ad',
          capacityMinutes: roomCapacity,
          usedMinutes: roomUsed,
          unusedMinutes: roomUnused,
          utilizationPct: getUtilizationPercent(roomUsed, roomCapacity),
        });
      });
    });

    // Surface the least-used rooms first so wasted capacity is easy to spot.
    roomRows.sort((a, b) => a.utilizationPct - b.utilizationPct);
    const totalUnused = Math.max(0, totalCapacity - totalUsed);

    // Build the weekday breakdown grid (rows = weekdays that are open, cols = time buckets),
    // and find the single most-idle weekday/time slot to call out to the owner.
    let peakIdleCell = null;
    const weekdayGrid = WEEKDAY_OPTIONS.map((option, weekdayIndex) => {
      const cells = (weekdayCells[weekdayIndex] || []).map((cell, bucketIndex) => {
        const capacityMinutes = cell.capacity;
        const usedMinutes = Math.min(cell.used, capacityMinutes);
        const unusedMinutes = Math.max(0, capacityMinutes - usedMinutes);
        const cellData = {
          bucketIndex,
          label: timeBuckets[bucketIndex] ? timeBuckets[bucketIndex].label : '',
          capacityMinutes,
          usedMinutes,
          unusedMinutes,
          utilizationPct: getUtilizationPercent(usedMinutes, capacityMinutes),
        };
        if (capacityMinutes > 0 && (!peakIdleCell || unusedMinutes > peakIdleCell.unusedMinutes)) {
          peakIdleCell = {
            weekdayLabel: option.label,
            timeLabel: timeBuckets[bucketIndex]
              ? `${formatScheduleMinutesLabel(timeBuckets[bucketIndex].start)}-${formatScheduleMinutesLabel(timeBuckets[bucketIndex].end)}`
              : '',
            unusedMinutes,
            utilizationPct: cellData.utilizationPct,
          };
        }
        return cellData;
      });
      const weekdayCapacity = cells.reduce((sum, cell) => sum + cell.capacityMinutes, 0);
      const weekdayUsed = cells.reduce((sum, cell) => sum + cell.usedMinutes, 0);
      return {
        weekdayIndex,
        label: option.label,
        hasCapacity: weekdayCapacity > 0,
        capacityMinutes: weekdayCapacity,
        usedMinutes: weekdayUsed,
        unusedMinutes: Math.max(0, weekdayCapacity - weekdayUsed),
        utilizationPct: getUtilizationPercent(weekdayUsed, weekdayCapacity),
        cells,
      };
    });
    const openWeekdayGrid = weekdayGrid.filter((row) => row.hasCapacity);

    // Build the per-week breakdown (chronological), each with its unused hours.
    const weeklyBreakdown = Array.from(weeklyTotals.entries())
      .filter(([, entry]) => entry.capacity > 0)
      .sort(([a], [b]) => (a < b ? -1 : (a > b ? 1 : 0)))
      .map(([weekStart, entry]) => {
        const usedMinutes = Math.min(entry.used, entry.capacity);
        return {
          weekStart,
          weekNumber: getScheduleIsoWeekNumber(weekStart),
          rangeLabel: getScheduleWeekRangeLabel(weekStart),
          capacityMinutes: entry.capacity,
          usedMinutes,
          unusedMinutes: Math.max(0, entry.capacity - usedMinutes),
          utilizationPct: getUtilizationPercent(usedMinutes, entry.capacity),
        };
      });

    const mergeIntervalsWithRules = (intervals = [], { maxGapMinutes = 60, minDurationMinutes = 240 } = {}) => {
      if (!Array.isArray(intervals) || intervals.length === 0) {
        return [];
      }
      const merged = [];
      intervals
        .map((interval) => ({
          start: Number(interval.start),
          end: Number(interval.end),
        }))
        .filter((interval) => Number.isFinite(interval.start) && Number.isFinite(interval.end) && interval.end > interval.start)
        .sort((a, b) => a.start - b.start)
        .forEach((interval) => {
          if (merged.length === 0) {
            merged.push({ ...interval });
            return;
          }
          const last = merged[merged.length - 1];
          if (interval.start - last.end <= maxGapMinutes) {
            last.end = Math.max(last.end, interval.end);
          } else {
            merged.push({ ...interval });
          }
        });
      return merged
        .map((interval) => ({
          ...interval,
          durationMinutes: Math.max(0, interval.end - interval.start),
        }))
        .filter((interval) => interval.durationMinutes >= minDurationMinutes);
    };

    const mergeIntervalsByOverlap = (intervals = []) => {
      if (!Array.isArray(intervals) || intervals.length === 0) {
        return [];
      }
      const merged = [];
      intervals
        .map((interval) => ({
          start: Number(interval.start),
          end: Number(interval.end),
        }))
        .filter((interval) => Number.isFinite(interval.start) && Number.isFinite(interval.end) && interval.end > interval.start)
        .sort((a, b) => a.start - b.start)
        .forEach((interval) => {
          if (merged.length === 0) {
            merged.push({ ...interval });
            return;
          }
          const last = merged[merged.length - 1];
          if (interval.start <= last.end) {
            last.end = Math.max(last.end, interval.end);
          } else {
            merged.push({ ...interval });
          }
        });
      return merged.map((interval) => ({
        ...interval,
        durationMinutes: Math.max(0, interval.end - interval.start),
      }));
    };

    const buildContinuityIntervalsFromCells = (cells = [], { maxGapMinutes = 60, minDurationMinutes = 240 } = {}) => {
      const candidateIntervals = [];
      (cells || []).forEach((cell, bucketIndex) => {
        const bucket = timeBuckets[bucketIndex];
        if (!bucket) {
          return;
        }
        if ((cell.capacityMinutes || 0) <= 0 || (cell.unusedMinutes || 0) <= 0) {
          return;
        }
        candidateIntervals.push({ start: bucket.start, end: bucket.end });
      });
      return mergeIntervalsWithRules(candidateIntervals, { maxGapMinutes, minDurationMinutes });
    };

    const buildAllContinuityIntervalsFromCells = (cells = []) => {
      const candidateIntervals = [];
      (cells || []).forEach((cell, bucketIndex) => {
        const bucket = timeBuckets[bucketIndex];
        if (!bucket) {
          return;
        }
        if ((cell.capacityMinutes || 0) <= 0 || (cell.unusedMinutes || 0) <= 0) {
          return;
        }
        candidateIntervals.push({ start: bucket.start, end: bucket.end });
      });
      return mergeIntervalsByOverlap(candidateIntervals);
    };

    const buildHeatmapSummary = (cellsByWeekday) => {
      let peakIdleCell = null;
      let focusIdleRun = null;
      const focusIdleRuns = [];

      const registerIdleRun = (run) => {
        if (!run) {
          return;
        }
        const durationMinutes = Math.max(0, run.endMinutes - run.startMinutes);
        if (durationMinutes < 5 * 60) {
          return;
        }
        const candidate = {
          weekdayIndex: run.weekdayIndex,
          weekdayLabel: run.weekdayLabel,
          startBucketIndex: run.startBucketIndex,
          endBucketIndex: run.endBucketIndex,
          startMinutes: run.startMinutes,
          endMinutes: run.endMinutes,
          durationMinutes,
          totalUnusedMinutes: run.totalUnusedMinutes,
          timeRangeLabel: `${formatScheduleMinutesLabel(run.startMinutes)}-${formatScheduleMinutesLabel(run.endMinutes)}`,
        };
        focusIdleRuns.push(candidate);
        if (!focusIdleRun
          || candidate.durationMinutes > focusIdleRun.durationMinutes
          || (candidate.durationMinutes === focusIdleRun.durationMinutes && candidate.totalUnusedMinutes > focusIdleRun.totalUnusedMinutes)) {
          focusIdleRun = candidate;
        }
      };

      const weekdayGrid = WEEKDAY_OPTIONS.map((option, weekdayIndex) => {
        let currentIdleRun = null;
        const flushIdleRun = () => {
          registerIdleRun(currentIdleRun);
          currentIdleRun = null;
        };

        const cells = (cellsByWeekday[weekdayIndex] || []).map((cell, bucketIndex) => {
          const bucket = timeBuckets[bucketIndex];
          const capacityMinutes = cell.capacity;
          const usedMinutes = Math.min(cell.used, capacityMinutes);
          const unusedMinutes = Math.max(0, capacityMinutes - usedMinutes);
          const cellData = {
            bucketIndex,
            label: timeBuckets[bucketIndex] ? timeBuckets[bucketIndex].label : '',
            capacityMinutes,
            usedMinutes,
            unusedMinutes,
            utilizationPct: getUtilizationPercent(usedMinutes, capacityMinutes),
          };
          const bucketMinutes = bucket ? Math.max(0, bucket.end - bucket.start) : UTILIZATION_BUCKET_MINUTES;
          const hasUnusedCapacity = capacityMinutes > 0 && unusedMinutes > 0;
          if (hasUnusedCapacity) {
            if (!currentIdleRun) {
              currentIdleRun = {
                weekdayIndex,
                weekdayLabel: option.label,
                startBucketIndex: bucketIndex,
                endBucketIndex: bucketIndex + 1,
                startMinutes: bucket ? bucket.start : (bucketIndex * bucketMinutes),
                endMinutes: bucket ? bucket.end : ((bucketIndex + 1) * bucketMinutes),
                totalUnusedMinutes: 0,
              };
            }
            currentIdleRun.endBucketIndex = bucketIndex + 1;
            currentIdleRun.endMinutes = bucket ? bucket.end : currentIdleRun.endMinutes + bucketMinutes;
            currentIdleRun.totalUnusedMinutes += unusedMinutes;
          } else {
            flushIdleRun();
          }
          if (capacityMinutes > 0 && (!peakIdleCell || unusedMinutes > peakIdleCell.unusedMinutes)) {
            peakIdleCell = {
              weekdayLabel: option.label,
              timeLabel: timeBuckets[bucketIndex]
                ? `${formatScheduleMinutesLabel(timeBuckets[bucketIndex].start)}-${formatScheduleMinutesLabel(timeBuckets[bucketIndex].end)}`
                : '',
              unusedMinutes,
              utilizationPct: cellData.utilizationPct,
            };
          }
          return cellData;
        });
        flushIdleRun();
        const weekdayCapacity = cells.reduce((sum, cell) => sum + cell.capacityMinutes, 0);
        const weekdayUsed = cells.reduce((sum, cell) => sum + cell.usedMinutes, 0);
        return {
          weekdayIndex,
          label: option.label,
          hasCapacity: weekdayCapacity > 0,
          capacityMinutes: weekdayCapacity,
          usedMinutes: weekdayUsed,
          unusedMinutes: Math.max(0, weekdayCapacity - weekdayUsed),
          utilizationPct: getUtilizationPercent(weekdayUsed, weekdayCapacity),
          cells,
        };
      });
      return {
        weekdayGrid: weekdayGrid.filter((row) => row.hasCapacity),
        peakIdleCell,
        focusIdleRun,
        focusIdleRuns,
      };
    };

    const roomHeatmaps = roomRows.map((room) => {
      const roomHeatmap = roomHeatmapByKey.get(room.key);
      if (!roomHeatmap) {
        return null;
      }
      const summary = buildHeatmapSummary(roomHeatmap.cells);
      const continuityByWeekday = summary.weekdayGrid
        .map((row) => {
          const allIntervals = buildAllContinuityIntervalsFromCells(row.cells || []);
          const intervals = buildContinuityIntervalsFromCells(row.cells || [], continuityRules);
          const continuityMinutes = intervals.reduce((sum, interval) => sum + interval.durationMinutes, 0);
          return {
            weekdayIndex: row.weekdayIndex,
            weekdayLabel: row.label,
            continuityMinutes,
            intervals,
            allIntervals,
            allContinuityMinutes: allIntervals.reduce((sum, interval) => sum + interval.durationMinutes, 0),
            intervalLabels: intervals.map((interval) => `${formatScheduleMinutesLabel(interval.start)}-${formatScheduleMinutesLabel(interval.end)}`),
            allIntervalLabels: allIntervals.map((interval) => `${formatScheduleMinutesLabel(interval.start)}-${formatScheduleMinutesLabel(interval.end)}`),
            allIntervalCompactLabels: allIntervals.map((interval) => formatContinuityIntervalCompact(interval.start, interval.end)),
          };
        })
        .filter((entry) => entry.continuityMinutes > 0 || entry.allContinuityMinutes > 0);
      const continuityMinutesTotal = continuityByWeekday.reduce((sum, entry) => sum + entry.continuityMinutes, 0);
      const allContinuityMinutesTotal = continuityByWeekday.reduce((sum, entry) => sum + entry.allContinuityMinutes, 0);
      return {
        ...room,
        weekdayGrid: summary.weekdayGrid,
        peakIdleCell: summary.peakIdleCell,
        focusIdleRun: summary.focusIdleRun,
        focusIdleRuns: summary.focusIdleRuns,
        continuityByWeekday,
        continuityMinutesTotal,
        allContinuityMinutesTotal,
      };
    }).filter(Boolean);

    const combinedContinuityByWeekday = WEEKDAY_OPTIONS.map((option, weekdayIndex) => {
      const dayRoomContinuity = roomHeatmaps.map((room) => {
        const day = (room.continuityByWeekday || []).find((entry) => entry.weekdayIndex === weekdayIndex);
        return {
          room,
          intervals: day ? day.intervals : [],
        };
      });
      const allIntervals = roomHeatmaps.flatMap((room) => {
        const day = (room.continuityByWeekday || []).find((entry) => entry.weekdayIndex === weekdayIndex);
        return day ? day.intervals : [];
      });
      // Combined total should be non-overlapping across rooms; do not merge by 1h gaps here.
      const mergedIntervals = mergeIntervalsByOverlap(allIntervals);
      const intervalRoomDetails = mergedIntervals.map((interval) => {
        const rooms = dayRoomContinuity
          .map(({ room, intervals }) => {
            const matchingIntervals = (intervals || []).filter((roomInterval) => roomInterval.start < interval.end && roomInterval.end > interval.start);
            if (matchingIntervals.length === 0) {
              return null;
            }
            return {
              roomKey: room.key,
              roomName: room.roomName,
              locationName: room.locationName,
              intervalCompactLabels: matchingIntervals.map((roomInterval) => formatContinuityIntervalCompact(roomInterval.start, roomInterval.end)),
            };
          })
          .filter(Boolean);
        return {
          interval,
          rooms,
        };
      });
      const continuityMinutes = mergedIntervals.reduce((sum, interval) => sum + interval.durationMinutes, 0);
      return {
        weekdayIndex,
        weekdayLabel: option.label,
        continuityMinutes,
        intervals: mergedIntervals,
        intervalRoomDetails,
        intervalLabels: mergedIntervals.map((interval) => `${formatScheduleMinutesLabel(interval.start)}-${formatScheduleMinutesLabel(interval.end)}`),
      };
    }).filter((entry) => entry.continuityMinutes > 0);

    const combinedContinuityBookingGuide = combinedContinuityByWeekday.flatMap((entry) => (
      (entry.intervalRoomDetails || []).map((detail) => {
        const intervalLabel = formatContinuityIntervalCompact(detail.interval.start, detail.interval.end);
        const roomGuide = (detail.rooms || [])
          .map((room) => `${room.roomName}${room.intervalCompactLabels.length ? ` ${room.intervalCompactLabels.join(' ')}` : ''}`)
          .join(', ');
        return `${entry.weekdayLabel} ${intervalLabel}: ${roomGuide || 'No room-specific continuity windows found.'}`;
      })
    ));

    const combinedAllContinuityByWeekday = WEEKDAY_OPTIONS.map((option, weekdayIndex) => {
      const allIntervals = roomHeatmaps.flatMap((room) => {
        const day = (room.continuityByWeekday || []).find((entry) => entry.weekdayIndex === weekdayIndex);
        return day ? (day.allIntervals || []) : [];
      });
      // "All intervals" ignores the minimum-hours threshold, but still applies the
      // configured max-gap continuity merge rule so this figure responds to settings.
      const mergedIntervals = mergeIntervalsWithRules(allIntervals, {
        maxGapMinutes: continuityRules.maxGapMinutes,
        minDurationMinutes: 0,
      });
      const continuityMinutes = mergedIntervals.reduce((sum, interval) => sum + interval.durationMinutes, 0);
      return {
        weekdayIndex,
        weekdayLabel: option.label,
        continuityMinutes,
        intervals: mergedIntervals,
        intervalLabels: mergedIntervals.map((interval) => `${formatScheduleMinutesLabel(interval.start)}-${formatScheduleMinutesLabel(interval.end)}`),
        intervalCompactLabels: mergedIntervals.map((interval) => formatContinuityIntervalCompact(interval.start, interval.end)),
      };
    }).filter((entry) => entry.continuityMinutes > 0);

    const combinedContinuityMinutesTotal = combinedContinuityByWeekday
      .reduce((sum, entry) => sum + entry.continuityMinutes, 0);
    const combinedAllContinuityMinutesTotal = combinedAllContinuityByWeekday
      .reduce((sum, entry) => sum + entry.continuityMinutes, 0);

    // Group the per-room heatmaps by location so each clinic location renders as its own
    // collapsible drawer (mirrors the Locations & Rooms tab layout).
    const roomHeatmapGroups = targetLocations
      .map((loc) => {
        const rooms = roomHeatmaps.filter((room) => String(room.locationId) === String(loc.id));
        if (rooms.length === 0) {
          return null;
        }
        const capacityMinutes = rooms.reduce((sum, room) => sum + room.capacityMinutes, 0);
        const usedMinutes = rooms.reduce((sum, room) => sum + room.usedMinutes, 0);
        return {
          locationId: String(loc.id),
          locationName: loc.name || 'Unnamed location',
          isPrimary: Boolean(loc.is_primary),
          capacityMinutes,
          usedMinutes,
          unusedMinutes: Math.max(0, capacityMinutes - usedMinutes),
          utilizationPct: getUtilizationPercent(usedMinutes, capacityMinutes),
          rooms,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.isPrimary && !b.isPrimary) {
          return -1;
        }
        if (!a.isPrimary && b.isPrimary) {
          return 1;
        }
        return a.locationName.localeCompare(b.locationName);
      });

    // Group room results by location so the detailed breakdown reads room-first after the
    // overall clinic summary.
    const roomGroups = targetLocations
      .map((loc) => {
        const rooms = roomRows
          .filter((room) => String(room.locationId) === String(loc.id))
          .sort((a, b) => a.utilizationPct - b.utilizationPct);
        if (rooms.length === 0) {
          return null;
        }
        const locationCapacity = rooms.reduce((sum, room) => sum + room.capacityMinutes, 0);
        const locationUsed = rooms.reduce((sum, room) => sum + room.usedMinutes, 0);
        return {
          locationId: String(loc.id),
          locationName: loc.name || 'Unnamed location',
          capacityMinutes: locationCapacity,
          usedMinutes: locationUsed,
          unusedMinutes: Math.max(0, locationCapacity - locationUsed),
          utilizationPct: getUtilizationPercent(locationUsed, locationCapacity),
          rooms,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.isPrimary && !b.isPrimary) {
          return -1;
        }
        if (!a.isPrimary && b.isPrimary) {
          return 1;
        }
        return a.locationName.localeCompare(b.locationName);
      });

    return {
      basisOption,
      startDateValue,
      endDateValue,
      hasOpenCapacity: totalCapacity > 0,
      multipleLocations: targetLocations.length > 1,
      rooms: roomRows,
      roomGroups,
      roomHeatmaps,
      roomHeatmapGroups,
      timeBuckets,
      weekdayGrid: openWeekdayGrid,
      weeklyBreakdown,
      peakIdleCell,
      scopedLocationId,
      scopedLocationName: scopedLocation
        ? (scopedLocation.name || 'Unnamed location')
        : 'All locations',
      combinedContinuityByWeekday,
      combinedContinuityBookingGuide,
      combinedContinuityMinutesTotal,
      combinedAllContinuityByWeekday,
      combinedAllContinuityMinutesTotal,
      continuityRules,
      totals: {
        capacityMinutes: totalCapacity,
        usedMinutes: totalUsed,
        unusedMinutes: totalUnused,
        utilizationPct: getUtilizationPercent(totalUsed, totalCapacity),
      },
    };
  }, [
    locations,
    availabilitySlots,
    openingHours,
    utilizationBasis,
    utilizationPeriod,
    utilizationLocationId,
    utilizationSandboxWeekdays,
    utilizationSandboxStartTime,
    utilizationSandboxEndTime,
    utilizationContinuityMinHours,
    utilizationContinuityMaxGapMinutes,
    utilizationBookings,
    expandedLocationId,
  ]);

  // Stable color per staff member for the vacation planner visualization.
  const VACATION_STAFF_PALETTE = ['#2F6B99', '#4A7C59', '#8A5A44', '#6B5B95', '#9B4F64', '#2B7A78', '#7A6A4F', '#5C6F91'];
  const vacationStaffColor = (staffId) => {
    const key = String(staffId || '');
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
      hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    return VACATION_STAFF_PALETTE[hash % VACATION_STAFF_PALETTE.length];
  };

  const getAbsenceStaffGroup = (staff) => {
    const roleType = String(staff?.roleType || staff?.role_type || '').toLowerCase();
    const engagementType = String(staff?.staffEngagementType || staff?.staff_engagement_type || '').toLowerCase();
    if (engagementType === 'subcontractor' || roleType === 'subcontractor') {
      return 'subcontractor';
    }
    if (roleType === 'physiotherapist' || roleType === 'care_staff' || roleType === 'careprovider_admin') {
      return 'physiotherapist';
    }
    if (roleType === 'admin' || roleType === 'secretary' || roleType === 'administrative' || roleType === 'clinic_admin') {
      return 'administrative';
    }
    return 'other';
  };

  const getAbsenceStaffGroupLabel = (group) => {
    if (group === 'physiotherapist') return 'Physiotherapists';
    if (group === 'subcontractor') return 'Subcontractors';
    if (group === 'administrative') return 'Administrative employees';
    if (group === 'other') return 'Other staff';
    return 'All staff';
  };

  const staffAbsenceStats = useMemo(() => {
    const monthStart = new Date(vacationPlannerMonth.getFullYear(), vacationPlannerMonth.getMonth(), 1);
    const monthEnd = new Date(vacationPlannerMonth.getFullYear(), vacationPlannerMonth.getMonth() + 1, 0);
    const year = vacationPlannerMonth.getFullYear();
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    const staffIndex = new Map((allStaff || []).map((staff) => [String(staff.id), staff]));
    const statsByStaff = new Map();

    (staffVacations || []).forEach((absence) => {
      const staffId = String(absence.staff_member || '');
      if (!staffId) return;
      const staff = staffIndex.get(staffId) || { id: staffId, name: absence.staff_member_name || 'Staff member' };
      const group = getAbsenceStaffGroup(staff);
      if (absenceStaffGroupFilter !== 'all' && group !== absenceStaffGroupFilter) return;
      if (absenceStaffMemberFilter !== 'all' && staffId !== String(absenceStaffMemberFilter)) return;

      const absenceType = String(absence.absence_type || 'vacation');
      if (!['vacation', 'sick_day', 'paid_leave', 'agreed_absence'].includes(absenceType)) return;
      const startDate = new Date(`${absence.start_date}T00:00:00`);
      const endDate = new Date(`${absence.end_date || absence.start_date}T00:00:00`);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;

      const row = statsByStaff.get(staffId) || {
        staffId,
        staffName: staff.name,
        group,
        vacation: 0,
        sick_day: 0,
        paid_leave: 0,
        agreed_absence: 0,
        monthlyDates: [],
        yearlyDates: Array.from({ length: 12 }, () => []),
        monthly: { vacation: 0, sick_day: 0, paid_leave: 0, agreed_absence: 0 },
        yearly: Array.from({ length: 12 }, () => ({ vacation: 0, sick_day: 0, paid_leave: 0, agreed_absence: 0 })),
      };

      const formatDate = (value) => {
        if (!value) return '';
        const date = new Date(`${value}T00:00:00`);
        return Number.isNaN(date.getTime())
          ? String(value)
          : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      };
      const exactDates = `${formatDate(absence.start_date)}${absence.end_date && absence.end_date !== absence.start_date ? ` - ${formatDate(absence.end_date)}` : ''}`;

      if (endDate >= monthStart && startDate <= monthEnd) {
        row[absenceType] += 1;
        row.monthly[absenceType] += 1;
        row.monthlyDates.push({ type: absenceType, range: exactDates });
      }

      for (let month = 0; month < 12; month += 1) {
        const rangeStart = new Date(year, month, 1);
        const rangeEnd = new Date(year, month + 1, 0);
        if (endDate >= rangeStart && startDate <= rangeEnd) {
          row.yearly[month][absenceType] += 1;
          row.yearlyDates[month].push({ type: absenceType, range: exactDates });
        }
      }

      statsByStaff.set(staffId, row);
    });

    return Array.from(statsByStaff.values()).sort((a, b) => a.staffName.localeCompare(b.staffName));
  }, [allStaff, absenceStaffGroupFilter, absenceStaffMemberFilter, staffVacations, vacationPlannerMonth]);

  const reportedVacations = useMemo(
    () => (staffVacations || []).filter((v) => v.status === 'reported'),
    [staffVacations],
  );

  const reportedVacationReports = useMemo(
    () => reportedVacations.filter((v) => String(v.absence_type || 'vacation') !== 'sick_day'),
    [reportedVacations],
  );

  const reportedSickDayReports = useMemo(
    () => reportedVacations.filter((v) => String(v.absence_type || '') === 'sick_day'),
    [reportedVacations],
  );

  const reportedPaidLeaveReports = useMemo(
    () => reportedVacations.filter((v) => String(v.absence_type || '') === 'paid_leave'),
    [reportedVacations],
  );

  const reportedAgreedAbsenceReports = useMemo(
    () => reportedVacations.filter((v) => String(v.absence_type || '') === 'agreed_absence'),
    [reportedVacations],
  );

  const currentMonthAbsenceStats = useMemo(() => {
    const monthStart = new Date(vacationPlannerMonth.getFullYear(), vacationPlannerMonth.getMonth(), 1);
    const monthEnd = new Date(vacationPlannerMonth.getFullYear(), vacationPlannerMonth.getMonth() + 1, 0);
    const stats = {
      vacation: 0,
      sick_day: 0,
      paid_leave: 0,
      agreed_absence: 0,
    };

    (staffVacations || []).forEach((absence) => {
      const absenceType = String(absence.absence_type || 'vacation');
      if (!(absenceType in stats)) {
        return;
      }

      const startDate = new Date(`${absence.start_date}T00:00:00`);
      const endDate = new Date(`${absence.end_date || absence.start_date}T00:00:00`);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return;
      }

      if (endDate >= monthStart && startDate <= monthEnd) {
        stats[absenceType] += 1;
      }
    });

    return stats;
  }, [staffVacations, vacationPlannerMonth]);

  const currentMonthAbsenceCards = [
    { key: 'vacation', label: 'V', fullLabel: 'Vacation', value: currentMonthAbsenceStats.vacation, color: '#E3F2FD', accent: '#2F6B99' },
    { key: 'sick_day', label: 'S', fullLabel: 'Sick day', value: currentMonthAbsenceStats.sick_day, color: '#FFF3E0', accent: '#FF7F27' },
    { key: 'paid_leave', label: 'PL', fullLabel: 'Paid leave', value: currentMonthAbsenceStats.paid_leave, color: '#E8F5E9', accent: '#2E7D32' },
    { key: 'agreed_absence', label: 'AA', fullLabel: 'Agreed absence', value: currentMonthAbsenceStats.agreed_absence, color: '#F3E5F5', accent: '#8E44AD' },
  ];

  const yearlyAbsenceStats = useMemo(() => {
    const year = vacationPlannerMonth.getFullYear();
    const monthLabels = Array.from({ length: 12 }, (_, index) => new Date(year, index, 1).toLocaleDateString(undefined, { month: 'short' }));
    const statsByMonth = Array.from({ length: 12 }, () => ({ vacation: 0, sick_day: 0, paid_leave: 0, agreed_absence: 0 }));

    (staffVacations || []).forEach((absence) => {
      const absenceType = String(absence.absence_type || 'vacation');
      if (!(absenceType in statsByMonth[0])) {
        return;
      }

      const startDate = new Date(`${absence.start_date}T00:00:00`);
      const endDate = new Date(`${absence.end_date || absence.start_date}T00:00:00`);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return;
      }

      for (let month = 0; month < 12; month += 1) {
        const monthStart = new Date(year, month, 1);
        const monthEnd = new Date(year, month + 1, 0);
        if (endDate >= monthStart && startDate <= monthEnd) {
          statsByMonth[month][absenceType] += 1;
        }
      }
    });

    return monthLabels.map((label, monthIndex) => ({
      key: `${year}-${String(monthIndex + 1).padStart(2, '0')}`,
      label,
      month: monthIndex,
      ...statsByMonth[monthIndex],
    }));
  }, [staffVacations, vacationPlannerMonth]);

  // Builds a Monday-start month grid mapping each visible day to the vacations covering it.
  const vacationCalendar = useMemo(() => {
    const monthStart = vacationPlannerMonth;
    const year = monthStart.getFullYear();
    const month = monthStart.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    // Monday = 0 offset.
    const leadingDays = (firstOfMonth.getDay() + 6) % 7;
    const gridStart = new Date(year, month, 1 - leadingDays);

    const parseDate = (value) => {
      if (!value) return null;
      const parts = String(value).split('-');
      if (parts.length !== 3) return null;
      return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    };

    const vacationRanges = (staffVacations || []).map((v) => ({
      ...v,
      startObj: parseDate(v.start_date),
      endObj: parseDate(v.end_date) || parseDate(v.start_date),
      color: vacationStaffColor(v.staff_member),
    })).filter((v) => v.startObj);

    const weeks = [];
    for (let week = 0; week < 6; week += 1) {
      const days = [];
      for (let day = 0; day < 7; day += 1) {
        const cellDate = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + (week * 7) + day);
        const cellTime = cellDate.getTime();
        const dayVacations = vacationRanges.filter((v) => {
          const start = new Date(v.startObj.getFullYear(), v.startObj.getMonth(), v.startObj.getDate()).getTime();
          const end = new Date(v.endObj.getFullYear(), v.endObj.getMonth(), v.endObj.getDate()).getTime();
          return cellTime >= start && cellTime <= end;
        });
        days.push({
          date: cellDate,
          inMonth: cellDate.getMonth() === month,
          vacations: dayVacations,
        });
      }
      weeks.push(days);
    }
    return {
      label: monthStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
      weeks,
    };
  }, [vacationPlannerMonth, staffVacations]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftVacationPlannerMonth = (delta) => {
    setVacationPlannerMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const formatVacationRange = (vacation) => {
    const fmt = (value) => {
      if (!value) return '';
      const parts = String(value).split('-');
      if (parts.length !== 3) return value;
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    };
    const start = fmt(vacation.start_date);
    const end = fmt(vacation.end_date);
    return start === end || !end ? start : `${start} - ${end}`;
  };

  const handleSaveLocation = async (loc) => {
    setSaving(true);
    try {
      const url = loc.id
        ? `${backendBaseUrl}/api/profile/clinic/locations/${loc.id}/`
        : `${backendBaseUrl}/api/profile/clinic/locations/`;
      const res = await fetch(url, {
        method: loc.id ? 'PATCH' : 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: loc.name, address: loc.address, city: loc.city, postal_code: loc.postal_code, country: loc.country, phone: loc.phone, email: loc.email }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Unable to save location.');
      }
      const data = await res.json().catch(() => ({}));
      showLocationSnackbar(loc.id ? 'Location updated.' : 'Location created.');
      if (!loc.id && data?.id) {
        setExpandedLocationId(String(data.id));
      }
      fetchLocations();
      return data;
    } catch (error) {
      alert(error.message || 'Unable to save location.');
      return null;
    } finally { setSaving(false); }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Remove this location? It will no longer be available for bookings.')) return;
    await fetch(`${backendBaseUrl}/api/profile/clinic/locations/${id}/`, {
      method: 'DELETE',
      headers: { 'Authorization': `Token ${token}` },
    });
    if (String(expandedLocationId || '') === String(id)) {
      setExpandedLocationId(null);
    }
    showLocationSnackbar('Location removed.');
    fetchLocations();
  };

  const handleAddRoom = async (locationId, roomName, supportsClasses = false) => {
    if (!roomName.trim()) return;
    try {
      const res = await fetch(`${backendBaseUrl}/api/profile/clinic/locations/${locationId}/rooms/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: roomName.trim(), supports_classes: supportsClasses === true }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Unable to add room.');
      }
      setEditingRoom((prev) => ({ ...prev, [locationId]: '' }));
      showLocationSnackbar('Room added.');
      fetchLocations();
    } catch (error) {
      alert(error.message || 'Unable to add room.');
    }
  };

  const renderRoomDrawerSection = (loc, sectionKey, title, description, rooms, supportsClassesDefault) => (
    <Accordion
      expanded={expandedLocationSection === `${sectionKey}-${loc.id}`}
      onChange={(_, isExpanded) => setExpandedLocationSection(isExpanded ? `${sectionKey}-${loc.id}` : null)}
      TransitionProps={{ unmountOnExit: true }}
      style={{ marginBottom: 8, borderRadius: 16, border: '1px solid #e5e9ef', boxShadow: 'none', overflow: 'hidden' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography style={{ fontWeight: 600, color: '#002D55' }}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block' }}>
        <Typography variant="body2" style={{ color: '#6f7b87', marginBottom: 12 }}>
          {description}
        </Typography>
        {rooms.length === 0 && (
          <Typography variant="body2" style={{ color: '#999', marginBottom: 8 }}>
            No rooms are configured yet for this drawer.
          </Typography>
        )}
        {rooms.map((room) => (
          <Paper
            key={room.id}
            variant="outlined"
            style={{
              padding: 12,
              marginBottom: 8,
              borderRadius: 10,
              borderColor: room.is_active !== false ? '#dbe7f2' : '#f1d7d7',
              borderLeft: `6px solid ${room.color || '#dbe7f2'}`,
              background: room.is_active !== false ? '#fff' : '#fcf7f7',
              opacity: room.is_active !== false ? 1 : 0.85,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Room name"
                  variant="outlined"
                  size="small"
                  value={room.name || ''}
                  onChange={(e) => handleRoomFieldChange(loc.id, room.id, 'name', e.target.value)}
                  className={classes.orangeFocused}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  label="Order"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={room.order ?? 0}
                  onChange={(e) => handleRoomFieldChange(loc.id, room.id, 'order', e.target.value)}
                  className={classes.orangeFocused}
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={room.is_active !== false}
                      onChange={(e) => handleRoomFieldChange(loc.id, room.id, 'is_active', e.target.checked)}
                      style={{ color: room.is_active !== false ? '#FF7F27' : '#002D55' }}
                    />
                  )}
                  label={<span style={{ color: '#002D55', fontWeight: 600 }}>Active</span>}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Chip
                  label={supportsClassesDefault ? 'Class room' : 'Treatment room'}
                  size="small"
                  style={{
                    backgroundColor: supportsClassesDefault ? '#fff3e0' : '#eef4fb',
                    color: '#002D55',
                    fontWeight: 700,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Chip
                  label={room.is_active !== false ? 'Visible' : 'Hidden'}
                  size="small"
                  style={{
                    backgroundColor: room.is_active !== false ? '#e8f5e9' : '#fce4ec',
                    color: room.is_active !== false ? '#2e7d32' : '#c62828',
                    fontWeight: 700,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                  <Button
                    size="small"
                    variant="contained"
                    className={classes.saveButton}
                    onClick={() => handleSaveRoom(loc.id, { ...room, supports_classes: supportsClassesDefault })}
                    disabled={Boolean(roomSaving[room.id])}
                  >
                    {roomSaving[room.id] ? 'Saving...' : 'Save'}
                  </Button>
                  <Button size="small" onClick={() => handleDeleteRoom(loc.id, room.id)} style={{ color: '#e53935' }}>
                    Deactivate
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                  <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: 12 }}>
                    Room color
                  </Typography>
                  <input
                    type="color"
                    value={room.color || '#3174ad'}
                    onChange={(e) => handleRoomFieldChange(loc.id, room.id, 'color', e.target.value)}
                    style={{ width: 42, height: 32, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
                  />
                  <Typography variant="caption" style={{ color: '#6f7b87', fontFamily: 'monospace' }}>
                    {room.color || '#3174ad'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 8 }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder={supportsClassesDefault ? 'New class room name' : 'New treatment room name'}
            value={editingRoom[loc.id] || ''}
            onChange={(e) => setEditingRoom((prev) => ({ ...prev, [loc.id]: e.target.value }))}
            className={classes.orangeFocused}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddRoom(loc.id, editingRoom[loc.id] || '', supportsClassesDefault); }}
          />
          <Button
            size="small"
            variant="contained"
            className={classes.saveButton}
            onClick={() => handleAddRoom(loc.id, editingRoom[loc.id] || '', supportsClassesDefault)}
            startIcon={<AddIcon />}
          >
            {supportsClassesDefault ? 'Add Class Room' : 'Add Treatment Room'}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const handleRoomFieldChange = (locationId, roomId, field, value) => {
    setLocations((prev) => prev.map((location) => {
      if (location.id !== locationId) {
        return location;
      }

      return {
        ...location,
        rooms: (location.rooms || []).map((room) => (
          room.id === roomId ? { ...room, [field]: value } : room
        )),
      };
    }));
  };

  const handleSaveRoom = async (locationId, room) => {
    setRoomSaving((prev) => ({ ...prev, [room.id]: true }));
    try {
      const res = await fetch(`${backendBaseUrl}/api/profile/clinic/locations/${locationId}/rooms/${room.id}/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: String(room.name || '').trim(),
          color: room.color || '#3174ad',
          order: Number(room.order || 0),
          is_active: room.is_active !== false,
          supports_classes: room.supports_classes === true,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Unable to save room.');
      }
      showLocationSnackbar('Room updated.');
      fetchLocations();
    } catch (error) {
      alert(error.message || 'Unable to save room.');
    } finally {
      setRoomSaving((prev) => ({ ...prev, [room.id]: false }));
    }
  };

  const handleDeleteRoom = async (locationId, roomId) => {
    try {
      const res = await fetch(`${backendBaseUrl}/api/profile/clinic/locations/${locationId}/rooms/${roomId}/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Unable to delete room.');
      }
      showLocationSnackbar('Room updated.');
      fetchLocations();
    } catch (error) {
      alert(error.message || 'Unable to delete room.');
    }
  };

  const handleAllocationDraftChange = (location, field, value) => {
    setAllocationDrafts((prev) => ({
      ...prev,
      [location.id]: (() => {
        const nextDraft = {
          ...(prev[location.id] || buildAllocationDraft(location)),
          [field]: value,
        };

        if (field === 'staff_member') {
          const previousDefaultColor = getAllocationDefaultColor((prev[location.id] || {}).staff_member || '');
          if (!nextDraft.color || normalizeAllocationColor(nextDraft.color) === previousDefaultColor) {
            // Keep the staff color sticky so allocations for the same staff stay visually consistent.
            const existingStaffSlot = (availabilitySlots || []).find(
              (slot) => String(slot.staff_member || '') === String(value || '')
                && String(slot.clinic_location || '') === String(location.id)
                && normalizeAllocationColor(slot.color),
            );
            nextDraft.color = (existingStaffSlot && normalizeAllocationColor(existingStaffSlot.color))
              || getAllocationDefaultColor(value || '');
          }
        }

        return nextDraft;
      })(),
    }));
  };

  const openAllocationDialog = (location, draft = null) => {
    if (!location) {
      return;
    }

    setExpandedLocationSection(`allocations-${location.id}`);
    setAllocationDrafts((prev) => ({
      ...prev,
      [location.id]: draft || prev[location.id] || buildAllocationDraft(location),
    }));
    setAllocationDialogLocationId(String(location.id));
    setAllocationDialogOpen(true);
  };

  const closeAllocationDialog = () => {
    setAllocationDialogOpen(false);
    setAllocationDialogLocationId('');
    setAllocationConflictDialogOpen(false);
    setAllocationConflictDetails(null);
    setAllocationRoomConflictOpen(false);
    setAllocationRoomConflictDetails(null);
    allocationOpeningHoursBypassRef.current = false;
  };

  const closeAllocationConflictDialog = () => {
    setAllocationConflictDialogOpen(false);
    setAllocationConflictDetails(null);
  };

  const closeAllocationRoomConflictDialog = () => {
    setAllocationRoomConflictOpen(false);
    setAllocationRoomConflictDetails(null);
  };

  const handleAllocationRoomConflictAdjust = () => {
    if (!allocationRoomConflictDetails || !allocationRoomConflictDetails.suggestion) {
      closeAllocationRoomConflictDialog();
      return;
    }

    const { location, draft, suggestion } = allocationRoomConflictDetails;
    const adjustedDraft = { ...draft, start_time: suggestion.start_time, end_time: suggestion.end_time };
    setAllocationDrafts((prev) => ({ ...prev, [location.id]: adjustedDraft }));
    closeAllocationRoomConflictDialog();
    showLocationSnackbar(`Adjusted allocation to ${suggestion.start_time} - ${suggestion.end_time} to avoid the room conflict.`, 'success');

    handleCreateAllocation(location, {
      closeDialogOnSuccess: Boolean(allocationDialogOpen),
      skipRoomConflictCheck: true,
      draftOverride: adjustedDraft,
    });
  };

  const handleAllocationRoomConflictOverride = async () => {
    if (!allocationRoomConflictDetails) {
      closeAllocationRoomConflictDialog();
      return;
    }

    const { location, draft, conflicts } = allocationRoomConflictDetails;
    const slotIds = Array.from(new Set((conflicts || []).map((conflict) => conflict.slotId).filter(Boolean)));
    closeAllocationRoomConflictDialog();

    try {
      for (const slotId of slotIds) {
        const res = await fetch(`${backendBaseUrl}/api/calendar/availability/${slotId}/delete/`, {
          method: 'DELETE',
          headers: { 'Authorization': `Token ${token}` },
        });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || 'Unable to remove the conflicting allocation.');
        }
      }
    } catch (error) {
      showLocationSnackbar(error.message || 'Unable to remove the conflicting allocation.', 'error');
      return;
    }

    showLocationSnackbar(`Removed ${slotIds.length} conflicting allocation${slotIds.length === 1 ? '' : 's'}.`, 'success');

    handleCreateAllocation(location, {
      closeDialogOnSuccess: Boolean(allocationDialogOpen),
      skipRoomConflictCheck: true,
      draftOverride: draft,
    });
  };

  const handleAllocationConflictOneOff = () => {
    if (!allocationConflictDetails) {
      closeAllocationConflictDialog();
      return;
    }

    allocationOpeningHoursBypassRef.current = true;
    closeAllocationConflictDialog();

    const hasSpecificDate = Boolean(String(allocationConflictDetails.draft?.specific_date || '').trim());
    if (!hasSpecificDate) {
      allocationSpecificDateInputRef.current?.focus();
      showLocationSnackbar('Set a specific date override to save this as a one-off event.', 'warning');
      return;
    }

    if (typeof allocationConflictDetails.persist === 'function') {
      allocationConflictDetails.persist({ skipOpeningHoursConflictCheck: true });
      allocationOpeningHoursBypassRef.current = false;
      return;
    }

    handleCreateAllocation(allocationConflictDetails.location, {
      closeDialogOnSuccess: true,
      skipOpeningHoursConflictCheck: true,
    });
  };

  const persistStaffOpeningHours = async (adjustedStaffOpeningHours) => {
    const staffOpeningHoursPayload = buildStaffOpeningHoursPayload(
      adjustedStaffOpeningHours,
      videoconsultationCatalog.staffOptions,
      openingHours,
    );

    const response = await fetch(`${backendBaseUrl}/api/profile/clinic/settings/`, {
      method: 'POST',
      headers: {
        'Authorization': `Token ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        opening_hours: buildOpeningHoursPayload(openingHours),
        staff_opening_hours: staffOpeningHoursPayload,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to save staff opening hours.');
    }

    const data = await response.json();
    const snapshot = buildSettingsSnapshot(data);
    setOriginalSettings((previous) => (previous ? { ...previous, openingHours: snapshot.openingHours, staffOpeningHours: snapshot.staffOpeningHours } : snapshot));
    return snapshot;
  };

  const handleAllocationConflictAdjustHours = async () => {
    if (!allocationConflictDetails) {
      closeAllocationConflictDialog();
      return;
    }

    const { location, draft, conflicts } = allocationConflictDetails;
    const adjustedStaffOpeningHours = buildAdjustedStaffOpeningHours(
      draft,
      conflicts,
      staffOpeningHours,
    );

    allocationOpeningHoursBypassRef.current = false;
    setStaffOpeningHours(adjustedStaffOpeningHours);
    setOpeningHoursTab(String(draft?.staff_member || 'clinic'));
    closeAllocationConflictDialog();

    try {
      await persistStaffOpeningHours(adjustedStaffOpeningHours);
      showLocationSnackbar('Staff opening hours updated and saved.', 'success');
    } catch (error) {
      console.error('Error saving adjusted staff opening hours:', error);
      showLocationSnackbar(error.message || 'Could not save the staff opening hours adjustment.', 'error');
      return;
    }

    if (typeof allocationConflictDetails.persist === 'function') {
      allocationConflictDetails.persist({
        skipOpeningHoursConflictCheck: true,
        staffOpeningHoursOverride: adjustedStaffOpeningHours,
      });
      return;
    }

    handleCreateAllocation(location, {
      closeDialogOnSuccess: true,
      skipOpeningHoursConflictCheck: true,
      staffOpeningHoursOverride: adjustedStaffOpeningHours,
    });
  };

  const closeOpeningHoursConflictDialog = () => {
    setOpeningHoursConflictOpen(false);
    setOpeningHoursConflictDetails(null);
  };

  const handleOpeningHoursConflictAdjust = async () => {
    if (!openingHoursConflictDetails?.staffId) {
      closeOpeningHoursConflictDialog();
      return;
    }

    const adjustedStaffOpeningHours = buildAdjustedStaffOpeningHours(
      { staff_member: openingHoursConflictDetails.staffId },
      openingHoursConflictDetails.conflicts || [],
      staffOpeningHours,
    );

    setStaffOpeningHours(adjustedStaffOpeningHours);
    setOpeningHoursTab(String(openingHoursConflictDetails.staffId));
    closeOpeningHoursConflictDialog();

    try {
      await persistStaffOpeningHours(adjustedStaffOpeningHours);
      showLocationSnackbar('Staff opening hours updated to preserve existing room allocations.', 'success');
      await handleSave();
    } catch (error) {
      console.error('Error saving adjusted staff opening hours:', error);
      showLocationSnackbar(error.message || 'Could not save the staff opening hours adjustment.', 'error');
    }
  };

  const handleEditAllocation = (location, allocation, nextAllocation = null) => {
    const draftSource = nextAllocation || allocation;
    openAllocationDialog(location, buildAllocationDraftFromSlot(location, draftSource));
  };

  const handleCreateAllocation = async (location, options = {}) => {
    const draft = options.draftOverride || allocationDrafts[location.id] || buildAllocationDraft(location);
    if (!draft.staff_member) {
      alert('Select a staff member before saving an allocation.');
      return false;
    }
    if (!draft.room) {
      alert('Select a room before saving an allocation.');
      return false;
    }
    if (!draft.start_time || !draft.end_time) {
      alert('Start and end times are required.');
      return false;
    }

    const selectedWeekdays = Array.isArray(draft.day_of_week)
      ? draft.day_of_week.map((value) => String(value)).filter(Boolean)
      : [String(draft.day_of_week || '')].filter(Boolean);

    if (!draft.specific_date && selectedWeekdays.length === 0) {
      alert('Select at least one weekday for a recurring allocation.');
      return false;
    }

    const staffOpeningHoursSnapshot = options.staffOpeningHoursOverride || staffOpeningHours;
    const openingHoursConflicts = getAllocationOpeningHoursConflicts(draft, staffOpeningHoursSnapshot);
    const skipOpeningHoursConflictCheck = Boolean(options.skipOpeningHoursConflictCheck);
    const hasSpecificDate = Boolean(String(draft.specific_date || '').trim());
    const oneOffBypassActive = allocationOpeningHoursBypassRef.current && hasSpecificDate;

    if (allocationOpeningHoursBypassRef.current && !hasSpecificDate) {
      allocationSpecificDateInputRef.current?.focus();
      showLocationSnackbar('Set a specific date override to save this as a one-off event.', 'warning');
      return false;
    }

    if (openingHoursConflicts.length > 0 && !skipOpeningHoursConflictCheck && !oneOffBypassActive) {
      setAllocationConflictDetails({
        location,
        draft,
        conflicts: openingHoursConflicts,
      });
      setAllocationConflictDialogOpen(true);
      return false;
    }

    if (oneOffBypassActive) {
      allocationOpeningHoursBypassRef.current = false;
    }

    if (!options.skipRoomConflictCheck) {
      const roomConflicts = getAllocationRoomConflicts(draft, location);
      if (roomConflicts.length > 0) {
        setAllocationRoomConflictDetails({
          location,
          draft,
          conflicts: roomConflicts,
          suggestion: suggestAllocationFittingInterval(draft, roomConflicts),
        });
        setAllocationRoomConflictOpen(true);
        return false;
      }
    }

    try {
      const isEditing = Boolean(draft.editing_slot_id);
      const weekdaysToCreate = isEditing
        ? [selectedWeekdays[0] || null]
        : (draft.specific_date ? [null] : selectedWeekdays);
      const warningMessages = [];

      for (const weekdayValue of weekdaysToCreate) {
        const payload = {
          staff_member: draft.staff_member,
          clinic_location: location.id,
          room: draft.room,
          color: draft.color || null,
          recurrence: String(draft.recurrence || 'weekly'),
          day_of_week: weekdayValue === null ? undefined : Number(weekdayValue),
          specific_date: draft.specific_date || null,
          start_time: draft.start_time,
          end_time: draft.end_time,
          is_available: draft.is_available !== false,
          is_bookable: draft.is_bookable !== false,
        };

        const res = await fetch(isEditing
          ? `${backendBaseUrl}/api/calendar/availability/${draft.editing_slot_id}/`
          : `${backendBaseUrl}/api/calendar/availability/create/`, {
          method: isEditing ? 'PUT' : 'POST',
          headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          throw new Error(await formatApiErrorMessage(res, 'Unable to save staff room allocation.'));
        }

        const responseData = await res.json().catch(() => ({}));
        warningMessages.push(...extractLocationWarnings(responseData));

        if (isEditing) {
          break;
        }
      }
      setAllocationDrafts((prev) => ({ ...prev, [location.id]: buildAllocationDraft(location) }));
      const uniqueWarnings = Array.from(new Set(warningMessages));
      if (uniqueWarnings.length > 0) {
        showLocationSnackbar(
          `${isEditing ? 'Allocation updated' : (weekdaysToCreate.length > 1 ? 'Allocations saved' : 'Allocation saved')} with warning${uniqueWarnings.length > 1 ? 's' : ''}: ${uniqueWarnings.join(' ')}`,
          'warning',
        );
      } else {
        showLocationSnackbar(isEditing ? 'Allocation updated.' : (weekdaysToCreate.length > 1 ? 'Allocations saved.' : 'Allocation saved.'));
      }
      if (options.closeDialogOnSuccess) {
        closeAllocationDialog();
      }
      const savedColor = normalizeAllocationColor(draft.color);
      if (savedColor) {
        await syncStaffAllocationColors(draft.staff_member, location.id, savedColor);
      } else {
        fetchAvailabilitySlots();
      }
      return true;
    } catch (error) {
      alert(error.message || 'Unable to save staff room allocation.');
      return false;
    }
  };

  const handleDeleteAllocation = async (slotId) => {
    try {
      const res = await fetch(`${backendBaseUrl}/api/calendar/availability/${slotId}/delete/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Unable to delete staff room allocation.');
      }
      showLocationSnackbar('Allocation deleted.');
      setAllocationDrafts((prev) => {
        const nextDrafts = { ...prev };
        Object.keys(nextDrafts).forEach((locationId) => {
          if (String(nextDrafts[locationId]?.editing_slot_id || '') === String(slotId)) {
            nextDrafts[locationId] = buildAllocationDraft({
              staff_members_detail: [],
              rooms: [],
              calendars: [],
            });
          }
        });
        return nextDrafts;
      });
      if (allocationDialogOpen) {
        closeAllocationDialog();
      }
      fetchAvailabilitySlots();
    } catch (error) {
      alert(error.message || 'Unable to delete staff room allocation.');
    }
  };

  const handleAddStaffToLocation = async (locationId, userId) => {
    if (!userId) return;
    await fetch(`${backendBaseUrl}/api/profile/clinic/locations/${locationId}/staff/${userId}/`, {
      method: 'POST',
      headers: { 'Authorization': `Token ${token}` },
    });
    fetchLocations();
  };

  const handleRemoveStaffFromLocation = async (locationId, userId) => {
    await fetch(`${backendBaseUrl}/api/profile/clinic/locations/${locationId}/staff/${userId}/`, {
      method: 'DELETE',
      headers: { 'Authorization': `Token ${token}` },
    });
    fetchLocations();
  };

  const formatCalendarStaffLabel = (staff = {}) => {
    const fullName = `${staff.first_name || ''} ${staff.last_name || ''}`.trim();
    return fullName || staff.display_name || staff.email || 'Unnamed staff';
  };

  const formatCalendarStaffOverlayLabel = (staff = {}) => (
    staff.email
    || staff.display_name
    || `${staff.first_name || ''} ${staff.last_name || ''}`.trim()
    || staff.first_name
    || staff.last_name
    || 'Unnamed staff'
  );

  const showLocationSnackbar = (message, severity = 'success') => {
    setLocationSnackbar({ open: true, message, severity });
  };

  const extractLocationWarnings = (responseData = {}) => {
    const rawWarnings = responseData?.warnings;
    const warnings = Array.isArray(rawWarnings)
      ? rawWarnings
      : (typeof rawWarnings === 'string' && rawWarnings.trim() ? [rawWarnings] : []);

    return Array.from(new Set(warnings.map((warning) => String(warning).trim()).filter(Boolean)));
  };

  const formatApiErrorMessage = async (response, fallbackMessage) => {
    const errorData = await response.json().catch(() => ({}));
    if (typeof errorData.error === 'string' && errorData.error.trim()) {
      return errorData.error;
    }

    if (typeof errorData.detail === 'string' && errorData.detail.trim()) {
      return errorData.detail;
    }

    const fieldMessages = Object.values(errorData)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((value) => typeof value === 'string' && value.trim());

    if (fieldMessages.length > 0) {
      return fieldMessages.join(' ');
    }

    return fallbackMessage;
  };

  const closeLocationSnackbar = () => {
    setLocationSnackbar((prev) => ({ ...prev, open: false }));
  };

  const formatAllocationTimeRange = (allocation = {}) => {
    const start = String(allocation.start_time || '').slice(0, 5);
    const end = String(allocation.end_time || '').slice(0, 5);
    return start && end ? `${start} - ${end}` : 'Time not set';
  };

  const getAllocationStaff = (allocation, location) => (
    (location?.staff_members_detail || []).find((staff) => String(staff.id) === String(allocation.staff_member)) || null
  );

  const getRoomAllocationGroups = (location, allocations = [], selectedDate = '', showClassRooms = false) => {
    const selectedDateWeekday = getScheduleWeekdayIndex(selectedDate);
    const rooms = [...(location?.rooms || [])].sort((left, right) => {
      const activeLeft = left.is_active !== false;
      const activeRight = right.is_active !== false;
      if (activeLeft !== activeRight) {
        return activeLeft ? -1 : 1;
      }

      const orderDiff = Number(left.order || 0) - Number(right.order || 0);
      if (orderDiff !== 0) {
        return orderDiff;
      }

      return String(left.name || '').localeCompare(String(right.name || ''));
    }).filter((room) => showClassRooms || room.supports_classes !== true);

    const allocatedRoomIds = new Set(
      allocations
        .map((allocation) => String(allocation.room || ''))
        .filter(Boolean)
    );

    const groups = rooms.map((room) => {
      const roomAllocations = allocations.filter((allocation) => String(allocation.room || '') === String(room.id));
      const specificDateAllocations = roomAllocations
        .filter((allocation) => allocation.specific_date)
        .sort((left, right) => {
          const dateDiff = String(left.specific_date || '').localeCompare(String(right.specific_date || ''));
          if (dateDiff !== 0) {
            return dateDiff;
          }

          return String(left.start_time || '').localeCompare(String(right.start_time || ''));
        });

      return {
        room,
        weekly: WEEKDAY_OPTIONS.map((weekday, weekdayIndex) => {
          const selectedDateOverrides = selectedDateWeekday === weekdayIndex && selectedDate
            ? specificDateAllocations.filter((allocation) => String(allocation.specific_date || '') === String(selectedDate))
            : [];
          const recurringAllocations = roomAllocations
            .filter((allocation) => !allocation.specific_date && Number(allocation.day_of_week) === weekdayIndex)
            .filter((allocation) => !selectedDateOverrides.some((overrideAllocation) => (
              doScheduleTimeRangesOverlap(
                allocation.start_time,
                allocation.end_time,
                overrideAllocation.start_time,
                overrideAllocation.end_time,
              )
            )));

          return {
            weekday,
            selectedDate: selectedDateWeekday === weekdayIndex ? selectedDate : '',
            allocations: [
              ...recurringAllocations.map((allocation) => ({ ...allocation, _scheduleSource: 'weekly' })),
              ...selectedDateOverrides.map((allocation) => ({ ...allocation, _scheduleSource: 'override' })),
            ].sort((left, right) => String(left.start_time || '').localeCompare(String(right.start_time || ''))),
          };
        }),
        specificDateAllocations,
      };
    });

    const unassignedAllocations = allocations
      .filter((allocation) => !allocation.room || !allocatedRoomIds.has(String(allocation.room || '')))
      .sort((left, right) => {
        if (left.specific_date && right.specific_date) {
          const dateDiff = String(left.specific_date || '').localeCompare(String(right.specific_date || ''));
          if (dateDiff !== 0) {
            return dateDiff;
          }
        }

        const weekdayDiff = Number(left.day_of_week || 0) - Number(right.day_of_week || 0);
        if (weekdayDiff !== 0) {
          return weekdayDiff;
        }

        return String(left.start_time || '').localeCompare(String(right.start_time || ''));
      });

    return { rooms, groups, unassignedAllocations };
  };

  // Cache the heavy Staff Room Allocations computation (filter + sort across rooms x weekdays)
  // per location so it only recomputes when the underlying slots, locations, filters, or
  // preview dates change, not on every unrelated re-render (drag/resize previews, snackbars).
  const roomAllocationDataByLocation = useMemo(() => {
    const result = {};
    (locations || []).forEach((loc) => {
      const locationAllocations = (availabilitySlots || []).filter(
        (allocation) => String(allocation.clinic_location) === String(loc.id),
      );
      const selectedScheduleDate = roomScheduleDates[loc.id] || '';
      const roomFilters = roomScheduleRoomFilters[loc.id] || [];
      const staffFilters = roomScheduleStaffFilters[loc.id] || [];
      const showClassRooms = roomScheduleShowClassRooms[loc.id] === true;
      const visibleLocationAllocations = locationAllocations.filter((allocation) => {
        const allocationRoom = (loc.rooms || []).find((room) => String(room.id) === String(allocation.room || '')) || null;
        if (!showClassRooms && allocationRoom?.supports_classes === true) {
          return false;
        }
        if (roomFilters.length > 0 && !roomFilters.includes(String(allocation.room || ''))) {
          return false;
        }
        if (staffFilters.length > 0 && !staffFilters.includes(String(allocation.staff_member || ''))) {
          return false;
        }
        return true;
      });
      result[loc.id] = {
        selectedScheduleDate,
        visibleLocationAllocations,
        ...getRoomAllocationGroups(loc, visibleLocationAllocations, selectedScheduleDate, showClassRooms),
      };
    });
    return result;
  }, [locations, availabilitySlots, roomScheduleDates, roomScheduleRoomFilters, roomScheduleStaffFilters, roomScheduleShowClassRooms]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCalendarHoursSummary = (openingHours = {}) => {
    const activeDays = WEEKDAY_OPTIONS
      .map(({ key, label }) => {
        const day = openingHours[key] || {};
        if (!day.enabled) {
          return null;
        }

        return `${label.slice(0, 3)} ${String(day.start || '09:00')} - ${String(day.end || '17:00')}`;
      })
      .filter(Boolean);

    return activeDays.length > 0 ? activeDays.join(', ') : 'Closed all week';
  };

  const getAllocationOpeningHoursConflicts = (draft = {}, staffOpeningHoursSnapshot = staffOpeningHours) => {
    const staffId = String(draft.staff_member || '').trim();
    if (!staffId) {
      return [];
    }

    const staffHours = staffOpeningHoursSnapshot?.[staffId] || openingHours;
    if (!staffHours || typeof staffHours !== 'object') {
      return [];
    }

    const allocationStart = parseScheduleTimeToMinutes(draft.start_time);
    const allocationEnd = parseScheduleTimeToMinutes(draft.end_time);
    if (!Number.isFinite(allocationStart) || !Number.isFinite(allocationEnd) || allocationEnd <= allocationStart) {
      return [];
    }

    const weekdayIndexes = draft.specific_date
      ? [getScheduleWeekdayIndex(draft.specific_date)]
      : (Array.isArray(draft.day_of_week) ? draft.day_of_week : [draft.day_of_week])
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6);

    return Array.from(new Set(weekdayIndexes)).flatMap((weekdayIndex) => {
      const weekday = WEEKDAY_OPTIONS[weekdayIndex];
      if (!weekday) {
        return [];
      }

      const daySchedule = staffHours[weekday.key] || {};
      const dayStart = parseScheduleTimeToMinutes(daySchedule.start);
      const dayEnd = parseScheduleTimeToMinutes(daySchedule.end);
      const isOpen = Boolean(daySchedule.enabled)
        && Number.isFinite(dayStart)
        && Number.isFinite(dayEnd)
        && dayEnd > dayStart;

      if (!isOpen) {
        return [{
          weekdayIndex,
          weekdayKey: weekday.key,
          weekdayLabel: weekday.label,
          reason: 'closed',
          allocationStart,
          allocationEnd,
        }];
      }

      if (allocationStart < dayStart || allocationEnd > dayEnd) {
        return [{
          weekdayIndex,
          weekdayKey: weekday.key,
          weekdayLabel: weekday.label,
          reason: 'outside_hours',
          allocationStart,
          allocationEnd,
          dayStart,
          dayEnd,
        }];
      }

      return [];
    });
  };

  const buildAdjustedStaffOpeningHours = (draft = {}, conflicts = [], staffOpeningHoursSnapshot = staffOpeningHours) => {
    const staffId = String(draft.staff_member || '').trim();
    if (!staffId) {
      return staffOpeningHoursSnapshot;
    }

    const nextStaffHours = normalizeOpeningHoursForForm(
      staffOpeningHoursSnapshot?.[staffId] || openingHours,
    );

    conflicts.forEach((conflict) => {
      const currentDay = nextStaffHours[conflict.weekdayKey] || { enabled: false, start: '09:00', end: '17:00' };
      const currentStart = parseScheduleTimeToMinutes(currentDay.start);
      const currentEnd = parseScheduleTimeToMinutes(currentDay.end);

      nextStaffHours[conflict.weekdayKey] = {
        enabled: true,
        start: formatScheduleMinutesLabel(Number.isFinite(currentStart) ? Math.min(currentStart, conflict.allocationStart) : conflict.allocationStart),
        end: formatScheduleMinutesLabel(Number.isFinite(currentEnd) ? Math.max(currentEnd, conflict.allocationEnd) : conflict.allocationEnd),
      };
    });

    return {
      ...staffOpeningHoursSnapshot,
      [staffId]: nextStaffHours,
    };
  };

  const getStaffOpeningHoursAllocationConflicts = (staffId, nextStaffHours = {}, slots = availabilitySlots, locationsSnapshot = locations) => {
    const normalizedStaffId = String(staffId || '').trim();
    if (!normalizedStaffId) {
      return [];
    }

    const staffHours = normalizeOpeningHoursForForm(nextStaffHours || openingHours);
    const allSlots = Array.isArray(slots) ? slots : [];
    const allLocations = Array.isArray(locationsSnapshot) ? locationsSnapshot : [];

    return allSlots
      .filter((slot) => String(slot.staff_member || '') === normalizedStaffId)
      .flatMap((slot) => {
        const slotStart = parseScheduleTimeToMinutes(slot.start_time);
        const slotEnd = parseScheduleTimeToMinutes(slot.end_time);
        if (!Number.isFinite(slotStart) || !Number.isFinite(slotEnd) || slotEnd <= slotStart) {
          return [];
        }

        const slotSpecificDate = String(slot.specific_date || '').trim();
        const weekdayIndexes = slotSpecificDate
          ? [getScheduleWeekdayIndex(slotSpecificDate)]
          : [Number(slot.day_of_week)]
            .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6);

        const slotLocation = allLocations.find((loc) => String(loc.id) === String(slot.clinic_location)) || null;
        const room = (slotLocation?.rooms || []).find((entry) => String(entry.id) === String(slot.room)) || null;
        const staff = (slotLocation?.staff_members_detail || []).find((entry) => String(entry.id) === normalizedStaffId) || null;

        return Array.from(new Set(weekdayIndexes)).flatMap((weekdayIndex) => {
          const weekday = WEEKDAY_OPTIONS[weekdayIndex];
          if (!weekday) {
            return [];
          }

          const daySchedule = staffHours[weekday.key] || {};
          const dayStart = parseScheduleTimeToMinutes(daySchedule.start);
          const dayEnd = parseScheduleTimeToMinutes(daySchedule.end);
          const isOpen = Boolean(daySchedule.enabled)
            && Number.isFinite(dayStart)
            && Number.isFinite(dayEnd)
            && dayEnd > dayStart;

          if (!isOpen) {
            return [{
              slotId: slot.id,
              location: slotLocation,
              room,
              staff,
              weekdayIndex,
              weekdayKey: weekday.key,
              weekdayLabel: weekday.label,
              reason: 'closed',
              allocationStart: slotStart,
              allocationEnd: slotEnd,
              dayStart: null,
              dayEnd: null,
              suggestion: null,
            }];
          }

          if (slotStart >= dayEnd || slotEnd <= dayStart) {
            return [{
              slotId: slot.id,
              location: slotLocation,
              room,
              staff,
              weekdayIndex,
              weekdayKey: weekday.key,
              weekdayLabel: weekday.label,
              reason: 'outside_hours',
              allocationStart: slotStart,
              allocationEnd: slotEnd,
              dayStart,
              dayEnd,
              suggestion: null,
            }];
          }

          const suggestionStart = Math.max(slotStart, dayStart);
          const suggestionEnd = Math.min(slotEnd, dayEnd);
          return [{
            slotId: slot.id,
            location: slotLocation,
            room,
            staff,
            weekdayIndex,
            weekdayKey: weekday.key,
            weekdayLabel: weekday.label,
            reason: 'outside_hours',
            allocationStart: slotStart,
            allocationEnd: slotEnd,
            dayStart,
            dayEnd,
            suggestion: suggestionEnd > suggestionStart
              ? {
                  start_time: formatScheduleMinutesLabel(suggestionStart),
                  end_time: formatScheduleMinutesLabel(suggestionEnd),
                  start: suggestionStart,
                  end: suggestionEnd,
                }
              : null,
          }];
        });
      });
  };

  // Detect when the selected staff member would be allocated to a different room at an
  // overlapping time (a clinician cannot physically be in two rooms at once). Returns the
  // conflicting allocation slots so the UI can offer to adjust the time or override them.
  const getAllocationRoomConflicts = (draft = {}, location = null, slots = availabilitySlots) => {
    const staffId = String(draft.staff_member || '').trim();
    const roomId = String(draft.room || '').trim();
    if (!staffId) {
      return [];
    }

    const allocationStart = parseScheduleTimeToMinutes(draft.start_time);
    const allocationEnd = parseScheduleTimeToMinutes(draft.end_time);
    if (!Number.isFinite(allocationStart) || !Number.isFinite(allocationEnd) || allocationEnd <= allocationStart) {
      return [];
    }

    const editingSlotId = String(draft.editing_slot_id || '');
    const specificDate = String(draft.specific_date || '').trim();
    const requestedWeekdaySet = new Set(
      (Array.isArray(draft.day_of_week) ? draft.day_of_week : [draft.day_of_week])
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6),
    );

    const slotAppliesToRequest = (slot) => {
      const slotSpecificDate = String(slot.specific_date || '').trim();
      if (specificDate) {
        if (slotSpecificDate) {
          return slotSpecificDate === specificDate;
        }
        return Number(slot.day_of_week) === getScheduleWeekdayIndex(specificDate);
      }
      if (slotSpecificDate) {
        return requestedWeekdaySet.has(getScheduleWeekdayIndex(slotSpecificDate));
      }
      return requestedWeekdaySet.has(Number(slot.day_of_week));
    };

    return (slots || [])
      .filter((slot) => {
        if (String(slot.staff_member || '') !== staffId) {
          return false;
        }
        // Same-room overlaps are a different concern; only block multi-room collisions.
        if (roomId && String(slot.room || '') === roomId) {
          return false;
        }
        if (editingSlotId && String(slot.id || '') === editingSlotId) {
          return false;
        }
        const slotStart = parseScheduleTimeToMinutes(slot.start_time);
        const slotEnd = parseScheduleTimeToMinutes(slot.end_time);
        if (!Number.isFinite(slotStart) || !Number.isFinite(slotEnd)) {
          return false;
        }
        const overlaps = slotStart < allocationEnd && allocationStart < slotEnd;
        return overlaps && slotAppliesToRequest(slot);
      })
      .map((slot) => {
        const slotLocation = (locations || []).find((loc) => String(loc.id) === String(slot.clinic_location)) || location;
        const room = (slotLocation?.rooms || []).find((entry) => String(entry.id) === String(slot.room));
        const staff = (slotLocation?.staff_members_detail || []).find((entry) => String(entry.id) === String(slot.staff_member));
        const slotSpecificDate = String(slot.specific_date || '').trim();
        const weekdayIndex = Number.isInteger(Number(slot.day_of_week)) ? Number(slot.day_of_week) : null;
        return {
          slotId: slot.id,
          roomId: String(slot.room || ''),
          roomName: room?.name || 'this room',
          staffName: staff ? formatCalendarStaffLabel(staff) : 'This staff member',
          locationName: slotLocation?.name || '',
          whenLabel: slotSpecificDate || (weekdayIndex !== null ? (WEEKDAY_OPTIONS[weekdayIndex]?.label || '') : ''),
          start: parseScheduleTimeToMinutes(slot.start_time),
          end: parseScheduleTimeToMinutes(slot.end_time),
        };
      });
  };

  // Suggest the largest free sub-interval of the requested window that avoids every conflict,
  // so the same clinician can keep an overlapping-but-trimmed allocation if they prefer.
  const suggestAllocationFittingInterval = (draft = {}, conflicts = []) => {
    const allocationStart = parseScheduleTimeToMinutes(draft.start_time);
    const allocationEnd = parseScheduleTimeToMinutes(draft.end_time);
    if (!Number.isFinite(allocationStart) || !Number.isFinite(allocationEnd) || allocationEnd <= allocationStart) {
      return null;
    }

    const busy = conflicts
      .map((conflict) => [Math.max(conflict.start, allocationStart), Math.min(conflict.end, allocationEnd)])
      .filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end > start)
      .sort((left, right) => left[0] - right[0]);

    const merged = [];
    busy.forEach(([start, end]) => {
      const last = merged[merged.length - 1];
      if (last && start <= last[1]) {
        last[1] = Math.max(last[1], end);
      } else {
        merged.push([start, end]);
      }
    });

    const gaps = [];
    let cursor = allocationStart;
    merged.forEach(([start, end]) => {
      if (start > cursor) {
        gaps.push([cursor, start]);
      }
      cursor = Math.max(cursor, end);
    });
    if (cursor < allocationEnd) {
      gaps.push([cursor, allocationEnd]);
    }

    if (gaps.length === 0) {
      return null;
    }

    const [bestStart, bestEnd] = gaps.reduce(
      (best, gap) => ((gap[1] - gap[0]) > (best[1] - best[0]) ? gap : best),
      gaps[0],
    );
    if (bestEnd - bestStart < 5) {
      return null;
    }

    return {
      start_time: formatScheduleMinutesLabel(bestStart),
      end_time: formatScheduleMinutesLabel(bestEnd),
    };
  };

  const buildAllocationDraft = (location) => ({
    staff_member: String(location?.staff_members_detail?.[0]?.id || ''),
    room: String((Array.isArray(location?.rooms) ? location.rooms.find((room) => room.is_active !== false) : null)?.id || location?.rooms?.[0]?.id || ''),
    color: getAllocationDefaultColor(location?.staff_members_detail?.[0]?.id || location?.staff_members_detail?.[0]?.email || '')
      || '#3174ad',
    recurrence: 'weekly',
    day_of_week: ['0'],
    specific_date: '',
    start_time: '09:00',
    end_time: '17:00',
    is_available: true,
    is_bookable: true,
    editing_slot_id: '',
  });

  const buildAllocationDraftFromSlot = (location, allocation = null) => {
    if (!allocation) {
      return buildAllocationDraft(location);
    }

    const dayIndex = allocation.specific_date
      ? getScheduleWeekdayIndex(allocation.specific_date)
      : Number(allocation.day_of_week || 0);

    return {
      staff_member: String(allocation.staff_member || location?.staff_members_detail?.[0]?.id || ''),
      room: String(allocation.room || (Array.isArray(location?.rooms) ? location.rooms.find((room) => room.is_active !== false)?.id : '') || location?.rooms?.[0]?.id || ''),
      color: normalizeAllocationColor(allocation.color)
        || getAllocationDefaultColor(allocation.staff_member || location?.staff_members_detail?.[0]?.id || location?.staff_members_detail?.[0]?.email || '')
        || '#3174ad',
      recurrence: String(allocation.recurrence || 'weekly'),
      day_of_week: [String(Number.isFinite(dayIndex) ? dayIndex : 0)],
      specific_date: allocation.specific_date || '',
      start_time: String(allocation.start_time || '09:00').slice(0, 5),
      end_time: String(allocation.end_time || '17:00').slice(0, 5),
      is_available: allocation.is_available !== false,
      is_bookable: allocation.is_bookable !== false,
      editing_slot_id: String(allocation.id || ''),
    };
  };

  const buildDraggedAllocationPreview = (location, allocation, targetRoomId, targetWeekdayIndex, targetSpecificDate, dropEvent) => {
    const currentStartMinutes = parseScheduleTimeToMinutes(allocation?.start_time);
    const currentEndMinutes = parseScheduleTimeToMinutes(allocation?.end_time);
    const durationMinutes = Number.isFinite(currentStartMinutes) && Number.isFinite(currentEndMinutes) && currentEndMinutes > currentStartMinutes
      ? currentEndMinutes - currentStartMinutes
      : ROOM_SCHEDULE_SLOT_MINUTES;
    const dropRect = dropEvent.currentTarget.getBoundingClientRect();
    const relativeY = dropEvent.clientY - dropRect.top;
    const slotIndex = Math.max(0, Math.min(ROOM_SCHEDULE_SLOT_COUNT - 1, Math.round(relativeY / ROOM_SCHEDULE_ROW_HEIGHT)));
    const proposedStartMinutes = ROOM_SCHEDULE_START_MINUTES + (slotIndex * ROOM_SCHEDULE_SLOT_MINUTES);
    const maxStartMinutes = ROOM_SCHEDULE_END_MINUTES - durationMinutes;
    const startMinutes = Math.max(
      ROOM_SCHEDULE_START_MINUTES,
      Math.min(Number.isFinite(maxStartMinutes) ? maxStartMinutes : ROOM_SCHEDULE_START_MINUTES, proposedStartMinutes),
    );
    const endMinutes = Math.min(ROOM_SCHEDULE_END_MINUTES, startMinutes + durationMinutes);
    const nextSpecificDate = targetSpecificDate || '';
    const nextDayOfWeek = nextSpecificDate ? getScheduleWeekdayIndex(nextSpecificDate) : targetWeekdayIndex;

    return {
      ...allocation,
      clinic_location: location.id,
      room: String(targetRoomId || allocation?.room || ''),
      day_of_week: Number.isFinite(nextDayOfWeek) ? nextDayOfWeek : allocation?.day_of_week,
      specific_date: nextSpecificDate,
      start_time: formatScheduleMinutesLabel(startMinutes),
      end_time: formatScheduleMinutesLabel(endMinutes),
    };
  };

  const updateRoomAllocationResizePreview = (nextAllocation) => {
    roomAllocationResizePreviewRef.current = nextAllocation;
    setRoomAllocationResizePreview(nextAllocation);
  };

  const startRoomScheduleSelection = (location, room, weekdayIndex, selectedDate, event) => {
    if (event.button !== 0 || !location || !room) {
      return;
    }

    if (event.target?.closest?.('[data-room-allocation-card="true"]')) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const anchorIndex = getRoomScheduleSlotIndexFromClientY(event.clientY, rect);
    const range = buildRoomScheduleRangeFromIndices(anchorIndex, anchorIndex);
    roomScheduleSelectionRef.current = {
      location,
      room,
      weekdayIndex,
      selectedDate,
      anchorIndex,
      currentIndex: anchorIndex,
      columnElement: event.currentTarget,
    };
    setRoomScheduleSelectionPreview({
      locationId: String(location.id),
      roomId: String(room.id),
      weekdayIndex,
      selectedDate: selectedDate || '',
      ...range,
    });
  };

  const openAllocationDialogFromSelection = (location, room, weekdayIndex, selectedDate, startMinutes, endMinutes) => {
    if (!location || !room) {
      return;
    }

    const baseDraft = buildAllocationDraft(location);
    openAllocationDialog(location, {
      ...baseDraft,
      room: String(room.id || baseDraft.room || ''),
      day_of_week: [String(weekdayIndex)],
      specific_date: '',
      start_time: formatScheduleMinutesLabel(startMinutes),
      end_time: formatScheduleMinutesLabel(endMinutes),
      editing_slot_id: '',
    });
  };

  openAllocationDialogFromSelectionRef.current = openAllocationDialogFromSelection;

  const startRoomAllocationResize = (event, location, allocation, mode) => {
    const originalStartMinutes = parseScheduleTimeToMinutes(allocation?.start_time);
    const originalEndMinutes = parseScheduleTimeToMinutes(allocation?.end_time);
    if (!location || !allocation || !Number.isFinite(originalStartMinutes) || !Number.isFinite(originalEndMinutes)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    cancelRoomAllocationLongPress();
    roomAllocationClickSuppressedRef.current = true;
    roomAllocationResizeRef.current = {
      location,
      allocation,
      mode,
      originalStartMinutes,
      originalEndMinutes,
      startClientY: event.clientY,
    };
    updateRoomAllocationResizePreview(null);
  };

  // Propagate a color change to all other allocations for the same staff member in the same location.
  const syncStaffAllocationColors = async (staffMemberId, locationId, newColor) => {
    const resolvedColor = normalizeAllocationColor(newColor);
    if (!staffMemberId || !locationId || !resolvedColor) {
      return;
    }

    const slotsToSync = (availabilitySlots || []).filter(
      (slot) => String(slot.staff_member || '') === String(staffMemberId)
        && String(slot.clinic_location || '') === String(locationId)
        && normalizeAllocationColor(slot.color) !== resolvedColor,
    );

    if (slotsToSync.length === 0) {
      return;
    }

    await Promise.allSettled(
      slotsToSync.map((slot) => fetch(`${backendBaseUrl}/api/calendar/availability/${slot.id}/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ color: resolvedColor }),
      })),
    );

    await fetchAvailabilitySlots();
  };

  const persistRoomAllocationChange = async (location, allocation, updatedAllocation, successMessage = 'Allocation updated.', options = {}) => {
    if (!location || !allocation || !updatedAllocation) {
      return false;
    }

    const startMinutes = parseScheduleTimeToMinutes(updatedAllocation.start_time);
    const endMinutes = parseScheduleTimeToMinutes(updatedAllocation.end_time);
    if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
      alert('End time must be later than start time.');
      return false;
    }

    const staffOpeningHoursSnapshot = options.staffOpeningHoursOverride || staffOpeningHours;
    const skipOpeningHoursConflictCheck = Boolean(options.skipOpeningHoursConflictCheck);
    if (!skipOpeningHoursConflictCheck) {
      const conflictDraft = {
        staff_member: updatedAllocation.staff_member || allocation.staff_member,
        start_time: updatedAllocation.start_time,
        end_time: updatedAllocation.end_time,
        specific_date: updatedAllocation.specific_date || allocation.specific_date || '',
        day_of_week: updatedAllocation.specific_date
          ? undefined
          : (updatedAllocation.day_of_week ?? allocation.day_of_week),
      };
      const openingHoursConflicts = getAllocationOpeningHoursConflicts(conflictDraft, staffOpeningHoursSnapshot);
      if (openingHoursConflicts.length > 0) {
        setAllocationConflictDetails({
          location,
          draft: conflictDraft,
          conflicts: openingHoursConflicts,
          persist: (persistOptions = {}) => persistRoomAllocationChange(
            location,
            allocation,
            updatedAllocation,
            successMessage,
            { ...options, ...persistOptions },
          ),
        });
        setAllocationConflictDialogOpen(true);
        return false;
      }
    }

    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/availability/${allocation.id}/`, {
        method: 'PUT',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          staff_member: updatedAllocation.staff_member || allocation.staff_member,
          clinic_location: location.id,
          room: updatedAllocation.room || allocation.room,
          color: updatedAllocation.color || allocation.color || null,
          day_of_week: updatedAllocation.specific_date
            ? getScheduleWeekdayIndex(updatedAllocation.specific_date)
            : (updatedAllocation.day_of_week ?? allocation.day_of_week),
          specific_date: updatedAllocation.specific_date || null,
          start_time: updatedAllocation.start_time,
          end_time: updatedAllocation.end_time,
          is_available: updatedAllocation.is_available !== false,
          is_bookable: updatedAllocation.is_bookable !== false,
        }),
      });

      if (!response.ok) {
        throw new Error(await formatApiErrorMessage(response, 'Unable to update staff room allocation.'));
      }

      const responseData = await response.json().catch(() => ({}));
      const warningMessages = extractLocationWarnings(responseData);

      const savedColor = normalizeAllocationColor(updatedAllocation.color || allocation.color);
      if (savedColor && normalizeAllocationColor(allocation.color) !== savedColor) {
        const staffId = updatedAllocation.staff_member || allocation.staff_member;
        await syncStaffAllocationColors(staffId, location.id, savedColor);
      } else {
        await fetchAvailabilitySlots();
      }

      showLocationSnackbar(
        warningMessages.length > 0
          ? `${successMessage.replace(/\.$/, '')} with warning${warningMessages.length > 1 ? 's' : ''}: ${warningMessages.join(' ')}`
          : successMessage,
        warningMessages.length > 0 ? 'warning' : 'success',
      );
      return true;
    } catch (error) {
      const message = error?.message || 'Unable to update staff room allocation.';
      showLocationSnackbar(message, 'error');
      return false;
    }
  };

  persistRoomAllocationChangeRef.current = persistRoomAllocationChange;

  useEffect(() => {
    const handleWindowMouseMove = (event) => {
      const selection = roomScheduleSelectionRef.current;
      if (selection?.columnElement) {
        const nextIndex = getRoomScheduleSlotIndexFromClientY(event.clientY, selection.columnElement.getBoundingClientRect());
        if (nextIndex !== selection.currentIndex) {
          selection.currentIndex = nextIndex;
          const range = buildRoomScheduleRangeFromIndices(selection.anchorIndex, nextIndex);
          setRoomScheduleSelectionPreview({
            locationId: String(selection.location.id),
            roomId: String(selection.room.id),
            weekdayIndex: selection.weekdayIndex,
            selectedDate: selection.selectedDate || '',
            ...range,
          });
        }
        return;
      }

      const resize = roomAllocationResizeRef.current;
      if (!resize) {
        return;
      }

      const deltaSlots = Math.round((event.clientY - resize.startClientY) / ROOM_SCHEDULE_ROW_HEIGHT);
      const deltaMinutes = deltaSlots * ROOM_SCHEDULE_SLOT_MINUTES;
      let startMinutes = resize.originalStartMinutes;
      let endMinutes = resize.originalEndMinutes;

      if (resize.mode === 'start') {
        startMinutes = Math.max(
          ROOM_SCHEDULE_START_MINUTES,
          Math.min(resize.originalEndMinutes - ROOM_SCHEDULE_SLOT_MINUTES, resize.originalStartMinutes + deltaMinutes),
        );
      } else {
        endMinutes = Math.min(
          ROOM_SCHEDULE_END_MINUTES,
          Math.max(resize.originalStartMinutes + ROOM_SCHEDULE_SLOT_MINUTES, resize.originalEndMinutes + deltaMinutes),
        );
      }

      updateRoomAllocationResizePreview({
        ...resize.allocation,
        start_time: formatScheduleMinutesLabel(startMinutes),
        end_time: formatScheduleMinutesLabel(endMinutes),
      });
    };

    const handleWindowMouseUp = () => {
      const selection = roomScheduleSelectionRef.current;
      if (selection) {
        const range = buildRoomScheduleRangeFromIndices(selection.anchorIndex, selection.currentIndex);
        roomScheduleSelectionRef.current = null;
        setRoomScheduleSelectionPreview(null);
        openAllocationDialogFromSelectionRef.current?.(
          selection.location,
          selection.room,
          selection.weekdayIndex,
          selection.selectedDate,
          range.startMinutes,
          range.endMinutes,
        );
      }

      const resize = roomAllocationResizeRef.current;
      if (resize) {
        const nextAllocation = roomAllocationResizePreviewRef.current;
        roomAllocationResizeRef.current = null;
        updateRoomAllocationResizePreview(null);
        if (
          nextAllocation
          && (nextAllocation.start_time !== resize.allocation.start_time || nextAllocation.end_time !== resize.allocation.end_time)
        ) {
          void persistRoomAllocationChangeRef.current?.(resize.location, resize.allocation, nextAllocation, 'Allocation updated.');
        }
      }
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, []);

  const openRoomAllocationAdjust = (location, allocation, anchorEl) => {
    if (!location || !allocation) {
      return;
    }

    setRoomAllocationAdjustLocationId(String(location.id));
    setRoomAllocationAdjustSlot(allocation);
    setRoomAllocationAdjustStart(String(allocation.start_time || '09:00').slice(0, 5));
    setRoomAllocationAdjustEnd(String(allocation.end_time || '17:00').slice(0, 5));
    setRoomAllocationAdjustAnchor(anchorEl);
  };

  const closeRoomAllocationAdjust = () => {
    setRoomAllocationAdjustAnchor(null);
    setRoomAllocationAdjustLocationId('');
    setRoomAllocationAdjustSlot(null);
    setRoomAllocationAdjustSaving(false);
  };

  const adjustRoomAllocationTime = (field, minutes) => {
    const setter = field === 'start' ? setRoomAllocationAdjustStart : setRoomAllocationAdjustEnd;
    setter((previousValue) => {
      const [hours, mins] = String(previousValue || '00:00').split(':').map((part) => Number(part));
      if (!Number.isFinite(hours) || !Number.isFinite(mins)) {
        return previousValue;
      }

      const startLimit = ROOM_SCHEDULE_START_MINUTES;
      const endLimit = ROOM_SCHEDULE_END_MINUTES - (field === 'start' && roomAllocationAdjustSlot
        ? Math.max(
          ROOM_SCHEDULE_SLOT_MINUTES,
          (parseScheduleTimeToMinutes(roomAllocationAdjustSlot.end_time) || ROOM_SCHEDULE_SLOT_MINUTES) - (parseScheduleTimeToMinutes(roomAllocationAdjustSlot.start_time) || 0),
        )
        : ROOM_SCHEDULE_SLOT_MINUTES);

      let totalMinutes = (hours * 60) + mins + minutes;
      if (field === 'start') {
        totalMinutes = Math.max(startLimit, Math.min(endLimit, totalMinutes));
      } else {
        totalMinutes = Math.max(startLimit + ROOM_SCHEDULE_SLOT_MINUTES, Math.min(ROOM_SCHEDULE_END_MINUTES, totalMinutes));
      }

      return formatScheduleMinutesLabel(totalMinutes);
    });
  };

  const saveRoomAllocationAdjust = async () => {
    const location = locations.find((candidate) => String(candidate.id) === String(roomAllocationAdjustLocationId)) || null;
    if (!location || !roomAllocationAdjustSlot) {
      return;
    }

    const startMinutes = parseScheduleTimeToMinutes(roomAllocationAdjustStart);
    const endMinutes = parseScheduleTimeToMinutes(roomAllocationAdjustEnd);
    if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
      alert('End time must be later than start time.');
      return;
    }

    setRoomAllocationAdjustSaving(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/availability/${roomAllocationAdjustSlot.id}/`, {
        method: 'PUT',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          staff_member: roomAllocationAdjustSlot.staff_member,
          clinic_location: location.id,
          room: roomAllocationAdjustSlot.room,
          color: roomAllocationAdjustSlot.color || null,
          day_of_week: roomAllocationAdjustSlot.specific_date
            ? getScheduleWeekdayIndex(roomAllocationAdjustSlot.specific_date)
            : roomAllocationAdjustSlot.day_of_week,
          specific_date: roomAllocationAdjustSlot.specific_date || null,
          start_time: roomAllocationAdjustStart,
          end_time: roomAllocationAdjustEnd,
          is_available: roomAllocationAdjustSlot.is_available !== false,
          is_bookable: roomAllocationAdjustSlot.is_bookable !== false,
        }),
      });

      if (!response.ok) {
        throw new Error(await formatApiErrorMessage(response, 'Unable to update staff room allocation time.'));
      }

      const responseData = await response.json().catch(() => ({}));
      const warningMessages = extractLocationWarnings(responseData);

      const updatedAllocation = {
        ...roomAllocationAdjustSlot,
        start_time: roomAllocationAdjustStart,
        end_time: roomAllocationAdjustEnd,
      };

      setAllocationDrafts((prev) => ({
        ...prev,
        [location.id]: buildAllocationDraftFromSlot(location, updatedAllocation),
      }));
      await fetchAvailabilitySlots();
      showLocationSnackbar(
        warningMessages.length > 0
          ? `Allocation time updated with warning${warningMessages.length > 1 ? 's' : ''}: ${warningMessages.join(' ')}`
          : 'Allocation time updated.',
        warningMessages.length > 0 ? 'warning' : 'success',
      );
      closeRoomAllocationAdjust();
    } catch (error) {
      alert(error.message || 'Unable to update staff room allocation time.');
    } finally {
      setRoomAllocationAdjustSaving(false);
    }
  };

  const handleRoomAllocationLongPressStart = (location, allocation, event) => {
    roomAllocationLongPressMovedRef.current = false;
    roomAllocationLongPressTargetRef.current = event.currentTarget;
    if (roomAllocationLongPressTimerRef.current) {
      clearTimeout(roomAllocationLongPressTimerRef.current);
    }

    roomAllocationLongPressTimerRef.current = setTimeout(() => {
      if (!roomAllocationLongPressMovedRef.current && allocation) {
        roomAllocationClickSuppressedRef.current = true;
        openRoomAllocationAdjust(location, allocation, roomAllocationLongPressTargetRef.current);
      }
    }, 600);
  };

  const cancelRoomAllocationLongPress = () => {
    roomAllocationLongPressMovedRef.current = true;
    if (roomAllocationLongPressTimerRef.current) {
      clearTimeout(roomAllocationLongPressTimerRef.current);
      roomAllocationLongPressTimerRef.current = null;
    }
  };

  const getAllocationDayLabel = (allocation = {}) => {
    if (allocation.specific_date) {
      return allocation.specific_date;
    }

    const dayIndex = Number(allocation.day_of_week);
    return WEEKDAY_OPTIONS[dayIndex]?.label || 'Unknown day';
  };

  const allocationDialogLocation = locations.find((location) => String(location.id) === String(allocationDialogLocationId)) || null;
  const allocationDialogDraft = allocationDialogLocation
    ? (allocationDrafts[allocationDialogLocation.id] || buildAllocationDraft(allocationDialogLocation))
    : null;
  const allocationDialogRooms = allocationDialogLocation
    ? [...(allocationDialogLocation.rooms || [])].sort((left, right) => {
      const activeLeft = left.is_active !== false;
      const activeRight = right.is_active !== false;
      if (activeLeft !== activeRight) {
        return activeLeft ? -1 : 1;
      }

      const orderDiff = Number(left.order || 0) - Number(right.order || 0);
      if (orderDiff !== 0) {
        return orderDiff;
      }

      return String(left.name || '').localeCompare(String(right.name || ''));
    })
    : [];

  const buildCalendarDraft = (location, calendar = null) => {
    const locationStaffIds = Array.isArray(location?.staff_members_detail)
      ? location.staff_members_detail.map((staff) => String(staff.id))
      : [];
    const allowedStaffIds = calendar
      ? (Array.isArray(calendar.allowed_staff_ids)
        ? calendar.allowed_staff_ids.map((staffId) => String(staffId))
        : Array.isArray(calendar.allowed_staff_detail)
          ? calendar.allowed_staff_detail.map((staff) => String(staff.id))
          : [])
      : locationStaffIds;

    return {
      locationId: String(location?.id || ''),
      calendarId: calendar?.id ? String(calendar.id) : '',
      name: calendar?.name || '',
      order: String(calendar?.order ?? ((location?.calendars || []).length + 1 || 1)),
      allowedStaffIds: allowedStaffIds.length > 0 ? allowedStaffIds : locationStaffIds,
    };
  };

  const handleOpenCalendarDraft = (location, calendar = null) => {
    setCalendarDraft(buildCalendarDraft(location, calendar));
    setCalendarError('');
  };

  const handleCancelCalendarDraft = () => {
    setCalendarDraft(null);
    setCalendarError('');
  };

  const handleCalendarAllowedStaffChange = (event) => {
    const { value } = event.target;
    const nextValue = typeof value === 'string' ? value.split(',') : value;

    setCalendarDraft((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        allowedStaffIds: nextValue,
      };
    });
  };

  const handleSaveCalendarDraft = async () => {
    if (!calendarDraft || !calendarDraft.locationId) {
      return;
    }

    if (!String(calendarDraft.name || '').trim()) {
      setCalendarError('Calendar name is required.');
      return;
    }

    setCalendarSaving(true);
    setCalendarError('');

    try {
      const isEdit = Boolean(calendarDraft.calendarId);
      const url = isEdit
        ? `${backendBaseUrl}/api/profile/clinic/locations/${calendarDraft.locationId}/calendars/${calendarDraft.calendarId}/`
        : `${backendBaseUrl}/api/profile/clinic/locations/${calendarDraft.locationId}/calendars/`;
      const response = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: String(calendarDraft.name || '').trim(),
          order: Number(calendarDraft.order || 0),
          allowed_staff_ids: calendarDraft.allowedStaffIds,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.detail || errorBody.error || 'Unable to save calendar.');
      }

      handleCancelCalendarDraft();
      fetchLocations();
    } catch (error) {
      setCalendarError(error.message || 'Unable to save calendar.');
    } finally {
      setCalendarSaving(false);
    }
  };

  const handleDeleteCalendar = async (locationId, calendarId) => {
    if (!window.confirm('Delete this calendar?')) {
      return;
    }

    await fetch(`${backendBaseUrl}/api/profile/clinic/locations/${locationId}/calendars/${calendarId}/`, {
      method: 'DELETE',
      headers: { 'Authorization': `Token ${token}` },
    });

    if (calendarDraft && String(calendarDraft.calendarId) === String(calendarId)) {
      handleCancelCalendarDraft();
    }

    fetchLocations();
  };

  const handleSave = async () => {
    try {
      const normalizedPhaseProgressionPayload = phaseProgressionDefaults.map((rule, ruleIndex) => {
        const sourcePhaseIndex = parseInt(rule.source_phase_index, 10);
        const targetPhaseIndex = parseInt(rule.target_phase_index, 10);

        if (!Number.isInteger(sourcePhaseIndex) || !Number.isInteger(targetPhaseIndex)) {
          throw new Error(`Rule ${ruleIndex + 1} requires numeric source and target phase values.`);
        }

        if (targetPhaseIndex <= sourcePhaseIndex) {
          throw new Error(`Rule ${ruleIndex + 1} must target a later phase.`);
        }

        const conditions = [];
        const conditionCombos = Array.isArray(rule.conditionCombos) ? rule.conditionCombos : [];
        if (conditionCombos.length === 0) {
          throw new Error(`Rule ${ruleIndex + 1} needs at least one rule combo.`);
        }

        conditionCombos.forEach((combo, comboIndex) => {
          const comboConditions = Array.isArray(combo.conditions) ? combo.conditions : [];
          if (comboConditions.length === 0) {
            throw new Error(`Rule ${ruleIndex + 1}, combo ${comboIndex + 1} needs at least one condition.`);
          }

          if (!PHASE_PROGRESSION_ANCHOR_KEYS.includes(comboConditions[0]?.metric_key)) {
            throw new Error(`Rule ${ruleIndex + 1}, combo ${comboIndex + 1} must start with weeks in phase or sessions completed.`);
          }

          const metricKeys = comboConditions.map((condition) => condition.metric_key).filter(Boolean);
          if (new Set(metricKeys).size !== metricKeys.length) {
            throw new Error(`Rule ${ruleIndex + 1}, combo ${comboIndex + 1} cannot contain duplicate metrics.`);
          }

          comboConditions.forEach((condition, conditionIndex) => {
            const thresholdValue = parseFloat(condition.threshold_value);
            if (!Number.isFinite(thresholdValue)) {
              throw new Error(`Rule ${ruleIndex + 1}, combo ${comboIndex + 1}, condition ${conditionIndex + 1} needs a numeric threshold.`);
            }

            conditions.push({
              metric_key: condition.metric_key,
              operator: condition.operator,
              threshold_value: thresholdValue,
              combo_index: comboIndex,
              combo_join_operator: comboIndex === 0 ? 'and' : combo.combo_join_operator,
              exercise_identifiers: condition.exercise_identifiers_text
                ? condition.exercise_identifiers_text.split(',').map((item) => item.trim()).filter(Boolean)
                : [],
            });
          });
        });

        return {
          source_phase_index: sourcePhaseIndex,
          target_phase_index: targetPhaseIndex,
          conditions,
        };
      });

      validateOpeningHoursSchedule(openingHours, '', patientBookingOpeningHoursEnabled);

      const staffOpeningHoursPayload = buildStaffOpeningHoursPayload(
        staffOpeningHours,
        videoconsultationCatalog.staffOptions,
        openingHours
      );

      if (openingHoursTab !== 'clinic') {
        const staffId = String(openingHoursTab || '').trim();
        const conflicts = getStaffOpeningHoursAllocationConflicts(
          staffId,
          staffOpeningHoursPayload[staffId] || openingHours,
          availabilitySlots,
          locations,
        );

        if (conflicts.length > 0) {
          setOpeningHoursConflictDetails({
            staffId,
            staffLabel: (videoconsultationCatalog.staffOptions || []).find((staff) => String(staff.id) === staffId)?.label || 'This staff member',
            conflicts,
          });
          setOpeningHoursConflictOpen(true);
          return;
        }
      }

      (videoconsultationCatalog.staffOptions || []).forEach((staff) => {
        validateOpeningHoursSchedule(
          staffOpeningHoursPayload[staff.id] || {},
          formatStaffTabInitials(staff),
          false
        );
      });

      setSaving(true);
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/settings/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appointment_interval: parseInt(appointmentInterval),
          same_day_max_gap_hours: parseInt(sameDayMaxGapHours, 10),
          late_cancellation_threshold_hours: parseInt(lateCancellationThresholdHours, 10),
          late_cancellation_fee_amount: lateCancellationFeeAmount === '' ? 0 : parseFloat(lateCancellationFeeAmount),
          public_listing_enabled: publicListingEnabled,
          patient_booking_opening_hours_enabled: patientBookingOpeningHoursEnabled,
          opening_hours: buildOpeningHoursPayload(openingHours),
          staff_opening_hours: staffOpeningHoursPayload,
          exercise_adjustment_mode_default: sanitizeClinicAdjustmentMode(exerciseAdjustmentModeDefault),
          videoconsultation_settings: buildVideoconsultationPayload(videoconsultationSettings),
          insurance_support_settings: buildInsuranceSupportPayload(insuranceSupportSettings),
          phase_progression_defaults: normalizedPhaseProgressionPayload,
          // Billing configuration
          payment_terms_default: parseInt(paymentTermsDefault),
          payment_terms_patient: parseInt(paymentTermsPatient),
          payment_terms_insurance: parseInt(paymentTermsInsurance),
          stripe_fee_display_mode: stripeFeeDisplayMode,
          enable_payment_reminders: enablePaymentReminders,
          reminder_1_days_before_due: parseInt(reminder1DaysBeforeDue),
          reminder_2_days_after_due: parseInt(reminder2DaysAfterDue),
          reminder_3_days_after_due: parseInt(reminder3DaysAfterDue),
          inkasso_days_after_due: parseInt(inkassoDaysAfterDue),
          insurance_payment_terms: insurancePaymentTerms.map((item) => ({
            insurance_provider: item.insurance_provider,
            payer_details: normalizePayerDetails(item).map((detail) => ({
              policy_type: detail.policy_type,
              custom_payment_terms_days: detail.custom_payment_terms_days === '' ? null : parseInt(detail.custom_payment_terms_days),
              billing_email: detail.billing_email || null,
              ean_number: detail.ean_number || null,
              business_registration_number: detail.business_registration_number || null,
              reminder_1_days_before_due: detail.reminder_1_days_before_due === '' ? null : parseInt(detail.reminder_1_days_before_due),
              reminder_2_days_after_due: detail.reminder_2_days_after_due === '' ? null : parseInt(detail.reminder_2_days_after_due),
              reminder_3_days_after_due: detail.reminder_3_days_after_due === '' ? null : parseInt(detail.reminder_3_days_after_due),
              inkasso_days_after_due: detail.inkasso_days_after_due === '' ? null : parseInt(detail.inkasso_days_after_due),
            })),
          })),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const snapshot = buildSettingsSnapshot(data);
        applyGeneralSettingsSnapshot(snapshot);
        setInsurancePaymentTerms(cloneInsuranceOverrides(snapshot.insurancePaymentTerms));
        setOriginalSettings(snapshot);
        alert('Settings saved successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to save settings. Please try again.');
      }
    } catch (error) {
      console.error('Error saving clinic settings:', error);
      alert(error.message || 'Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneralCancel = () => {
    if (!originalSettings) return;
    applyGeneralSettingsSnapshot(originalSettings);
  };

  const handleInsuranceOverridesCancel = () => {
    if (!originalSettings) return;
    setInsurancePaymentTerms(cloneInsuranceOverrides(originalSettings.insurancePaymentTerms));
  };

  const selectedPendingPatientInsurance = useMemo(
    () => pendingPatientInsurances.find((item) => String(item.id) === String(pendingPatientInsuranceSelectedId)) || pendingPatientInsurances[0] || null,
    [pendingPatientInsurances, pendingPatientInsuranceSelectedId]
  );

  const getExampleTimes = (interval) => {
    const times = [];
    const start = 8 * 60; // 8:00 AM in minutes
    const end = 18 * 60; // 6:00 PM in minutes
    const intervalNum = parseInt(interval);
    
    for (let time = start; time <= end; time += intervalNum) {
      const hours = Math.floor(time / 60);
      const minutes = time % 60;
      times.push(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
    }
    return times.slice(0, 8).join(', ') + '...';
  };

  if (loading) {
    return (
      <Card className={classes.root}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={classes.root} {...rest}>
        <Paper elevation={0} className={classes.tabsWrapper}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            classes={{ root: classes.tabsControlRoot, indicator: classes.tabsIndicator }}
          >
            <Tab label="General Settings" value="general" className={classes.tabRoot} />
            <Tab label="Insurance Payers" value="insurance" className={classes.tabRoot} />
            <Tab label="Other Payers" value="other" className={classes.tabRoot} />
            <Tab label="Locations & Rooms" value="locations" className={classes.tabRoot} />
            <Tab label="Resource Utilization" value="utilization" className={classes.tabRoot} />
          </Tabs>
        </Paper>
        <CardContent className={classes.cardContent}>
          {/* ======= GENERAL SETTINGS TAB ======= */}
          {activeTab === 'general' && (
            <Box className={classes.tabContent}>
              <Box style={{ marginTop: 16, marginBottom: 16 }}>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <ScheduleIcon style={{ color: '#FF7F27' }} />
                  <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                    General Settings
                  </Typography>
                </Box>
                <Typography className={classes.description} style={{ marginTop: 16, marginBottom: 0 }}>
                  Control your clinic&apos;s general settings here. Press Save Settings when you are done.
                </Typography>
              </Box>
              <GeneralSettingsDrawer
                classes={classes}
                expanded={expandedGeneralSetting === 'appointment-time-intervals'}
                onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'appointment-time-intervals' ? false : 'appointment-time-intervals')}
                icon={<ScheduleIcon style={{ color: '#FF7F27' }} />}
                title="Appointment Time Intervals"
              >
                <Typography className={classes.description}>
                  Configure how appointment time slots are displayed in the booking calendar.
                  This setting affects all booking interfaces for patients and staff.
                </Typography>

                <FormControl component="fieldset">
                  <FormLabel component="legend" style={{ color: '#002D55', fontWeight: 600, marginBottom: 8 }}>
                    Select Time Interval
                  </FormLabel>
                  <RadioGroup
                    value={appointmentInterval}
                    onChange={(e) => setAppointmentInterval(e.target.value)}
                  >
                    <FormControlLabel value="15" control={<Radio />} label="Every 15 minutes (more flexibility)" className={classes.radioLabel} />
                    <FormControlLabel value="30" control={<Radio />} label="Every 30 minutes (recommended)" className={classes.radioLabel} />
                    <FormControlLabel value="45" control={<Radio />} label="Every 45 minutes" className={classes.radioLabel} />
                    <FormControlLabel value="60" control={<Radio />} label="Every 60 minutes (hourly)" className={classes.radioLabel} />
                  </RadioGroup>
                </FormControl>

                <Box className={classes.exampleBox}>
                  <Typography className={classes.exampleTitle}>Preview of available time slots:</Typography>
                  <Typography className={classes.exampleTimes}>{getExampleTimes(appointmentInterval)}</Typography>
                </Box>

                <Box style={{ marginTop: 24 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" style={{ color: '#002D55', fontWeight: 600, marginBottom: 8 }}>
                      Maximum gap between same-day appointments
                    </FormLabel>
                    <RadioGroup
                      value={sameDayMaxGapHours}
                      onChange={(e) => setSameDayMaxGapHours(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio />} label="Disabled (no same-day gap limit)" className={classes.radioLabel} />
                      <FormControlLabel value="1" control={<Radio />} label="1 hour" className={classes.radioLabel} />
                      <FormControlLabel value="2" control={<Radio />} label="2 hours" className={classes.radioLabel} />
                      <FormControlLabel value="3" control={<Radio />} label="3 hours" className={classes.radioLabel} />
                      <FormControlLabel value="4" control={<Radio />} label="4 hours" className={classes.radioLabel} />
                      <FormControlLabel value="6" control={<Radio />} label="6 hours" className={classes.radioLabel} />
                    </RadioGroup>
                  </FormControl>
                </Box>

                <Box style={{ marginTop: 24 }}>
                  <Typography style={{ color: '#002D55', fontWeight: 600, marginBottom: 8 }}>
                    Late cancellation threshold (hours)
                  </Typography>
                  <TextField
                    type="number"
                    value={lateCancellationThresholdHours}
                    onChange={(e) => setLateCancellationThresholdHours(e.target.value)}
                    inputProps={{ min: 0, max: 168, step: 1 }}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    placeholder="24"
                  />
                  <Typography style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                    Appointments cancelled within this many hours of the start time are treated as late cancellations.
                  </Typography>
                </Box>

                <Box style={{ marginTop: 24 }}>
                  <Typography style={{ color: '#002D55', fontWeight: 600, marginBottom: 8 }}>
                    Late cancellation fee amount
                  </Typography>
                  <TextField
                    type="number"
                    value={lateCancellationFeeAmount}
                    onChange={(e) => setLateCancellationFeeAmount(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    placeholder="0.00"
                  />
                  <Typography style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                    Patients will see this amount when they cancel within 24 hours.
                  </Typography>
                </Box>
              </GeneralSettingsDrawer>

              <GeneralSettingsDrawer
                classes={classes}
                expanded={expandedGeneralSetting === 'patient-booking-opening-hours'}
                onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'patient-booking-opening-hours' ? false : 'patient-booking-opening-hours')}
                icon={<ScheduleIcon style={{ color: '#FF7F27' }} />}
                title="Patient Booking Opening Hours"
              >
                <Typography className={classes.description}>
                  Set the clinic's standard weekly booking window, then refine it per staff. The clinic schedule controls booking enforcement,
                  while each staff tab drives that staff member's calendar view.
                </Typography>

                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={patientBookingOpeningHoursEnabled}
                      onChange={(e) => setPatientBookingOpeningHoursEnabled(e.target.checked)}
                      style={{ color: patientBookingOpeningHoursEnabled ? '#FF7F27' : '#002D55' }}
                    />
                  )}
                  label="Enforce opening hours for patient and public booking"
                  style={{ marginBottom: 12, color: '#002D55', fontWeight: 600 }}
                />

                <Box display="flex" justifyContent="flex-end" style={{ marginBottom: 12 }}>
                  <Button variant="outlined" size="small" className={classes.cancelButton} onClick={() => handleApplyStandardOpeningHours(openingHoursTab)}>
                    Use standard Mon-Fri hours
                  </Button>
                </Box>

                <Box style={{ marginBottom: 16 }}>
                  <Tabs
                    value={openingHoursTab}
                    onChange={(_, value) => setOpeningHoursTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    classes={{ root: classes.tabsControlRoot, indicator: classes.tabsIndicator }}
                  >
                    {openingHoursTabs.map((tab) => (
                      <Tab
                        key={tab.id}
                        value={tab.id}
                        label={tab.id === 'clinic' ? 'Clinic' : tab.initials}
                        title={tab.label}
                        className={classes.tabRoot}
                      />
                    ))}
                  </Tabs>
                </Box>

                <Typography style={{ color: '#6f7b87', fontSize: 12, fontWeight: 600, marginBottom: 12 }}>
                  {openingHoursTab === 'clinic'
                    ? 'Clinic hours apply to patient and public booking enforcement.'
                    : 'This staff schedule is used when the selected staff member is shown in the calendar.'}
                </Typography>

                <Grid container spacing={2}>
                  {WEEKDAY_OPTIONS.map(({ key, label }) => {
                    const day = activeOpeningHours[key] || {};
                    const dayEnabled = day.enabled !== undefined ? Boolean(day.enabled) : false;

                    return (
                      <Grid item xs={12} key={key}>
                        <Paper
                          variant="outlined"
                          style={{
                            borderRadius: 14,
                            borderColor: dayEnabled ? 'rgba(255, 127, 39, 0.3)' : '#E6EEF5',
                            background: dayEnabled ? '#fff' : '#FAFCFE',
                            padding: 16,
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                              <FormControlLabel
                                control={(
                                  <Checkbox
                                    checked={dayEnabled}
                                    onChange={(e) => updateOpeningHoursDay(openingHoursTab, key, 'enabled', e.target.checked)}
                                    style={{ color: dayEnabled ? '#FF7F27' : '#002D55' }}
                                  />
                                )}
                                label={<span style={{ fontWeight: 600, color: '#002D55' }}>{label}</span>}
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextField
                                fullWidth
                                type="time"
                                label="Start"
                                value={day.start || '09:00'}
                                onChange={(e) => updateOpeningHoursDay(openingHoursTab, key, 'start', e.target.value)}
                                disabled={!dayEnabled}
                                variant="outlined"
                                size="small"
                                className={classes.orangeFocused}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }}
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextField
                                fullWidth
                                type="time"
                                label="End"
                                value={day.end || '17:00'}
                                onChange={(e) => updateOpeningHoursDay(openingHoursTab, key, 'end', e.target.value)}
                                disabled={!dayEnabled}
                                variant="outlined"
                                size="small"
                                className={classes.orangeFocused}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography style={{ color: dayEnabled ? '#2e7d32' : '#6f7b87', fontSize: 12, fontWeight: 600 }}>
                                {dayEnabled ? 'Patient booking is allowed during this window.' : 'Closed for patient/public booking.'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </GeneralSettingsDrawer>

              <GeneralSettingsDrawer
                classes={classes}
                expanded={expandedGeneralSetting === 'internal-staff-room-allocation'}
                onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'internal-staff-room-allocation' ? false : 'internal-staff-room-allocation')}
                icon={<PeopleIcon style={{ color: '#FF7F27' }} />}
                title="Internal Staff & Room Allocation"
              >
                <Typography className={classes.description}>
                  Patient booking opening hours above are used for public booking rules. Internal staff work hours,
                  room assignments, and recurring split-day room schedules are managed per location under the
                  Locations &amp; Rooms tab.
                </Typography>
                <Box display="flex" justifyContent="flex-start">
                  <Button
                    variant="contained"
                    className={classes.saveButton}
                    onClick={() => setActiveTab('locations')}
                  >
                    Open Locations &amp; Rooms
                  </Button>
                </Box>
              </GeneralSettingsDrawer>

              <GeneralSettingsDrawer
                classes={classes}
                expanded={expandedGeneralSetting === 'public-booking-listing'}
                onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'public-booking-listing' ? false : 'public-booking-listing')}
                icon={<BusinessIcon style={{ color: '#FF7F27' }} />}
                title="Public Booking Listing"
              >
                <Typography className={classes.description}>
                  Control whether this clinic appears in public clinician search and booking match results.
                  Disable this if the clinic should stay private or only use Virtus internally.
                </Typography>

                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={publicListingEnabled}
                      onChange={(e) => setPublicListingEnabled(e.target.checked)}
                      style={{ color: publicListingEnabled ? '#FF7F27' : '#002D55' }}
                    />
                  )}
                  label="Enable public clinician listing"
                  style={{ marginTop: 8, color: '#002D55', fontWeight: 600 }}
                />
              </GeneralSettingsDrawer>

              <GeneralSettingsDrawer
                classes={classes}
                expanded={expandedGeneralSetting === 'insurance-support'}
                onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'insurance-support' ? false : 'insurance-support')}
                icon={<BusinessIcon style={{ color: '#FF7F27' }} />}
                title="Insurance Support"
              >
                <Typography className={classes.description}>
                  Choose which insurers the clinic cooperates with, then optionally hide specific insurer clients from selected staff members.
                </Typography>

                <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                  Clinic-wide supported insurers
                </Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 20 }}>
                  {(insuranceSupportCatalog.networkOptions || []).map((option) => {
                    const selected = insuranceSupportSettings.supportedNetworks.includes(option.value);
                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        clickable
                        onClick={() => handleInsuranceSupportClinicToggle(option.value)}
                        style={{
                          backgroundColor: selected ? '#002D55' : '#eef4fb',
                          color: selected ? '#ffffff' : '#002D55',
                          fontWeight: 600,
                        }}
                      />
                    );
                  })}
                </Box>

                <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                  Staff-level exclusions
                </Typography>
                <Typography className={classes.description} style={{ marginBottom: 12 }}>
                  Use this when a clinician should not receive clients from one or more supported insurance networks.
                </Typography>

                {(insuranceSupportCatalog.staffOptions || []).length === 0 ? (
                  <Typography color="textSecondary">No clinic staff were returned for insurance exclusions.</Typography>
                ) : (
                  (insuranceSupportCatalog.staffOptions || []).map((staff) => {
                    const override = findInsuranceSupportStaffOverride(staff.id);
                    const overrideEnabled = Boolean(override);
                    return (
                      <Accordion key={`insurance-${staff.id}`} elevation={0} style={{ marginBottom: 10, border: '1px solid #d7e3ef', borderRadius: 16, overflow: 'hidden' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ backgroundColor: '#f7fafd' }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" style={{ gap: 12, paddingRight: 8 }}>
                            <Box>
                              <Typography style={{ color: '#002D55', fontWeight: 700 }}>
                                {staff.label}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={overrideEnabled ? 'Exclusions active' : 'Using clinic default'}
                              style={{
                                backgroundColor: overrideEnabled ? '#002D55' : '#eef4fb',
                                color: overrideEnabled ? '#ffffff' : '#002D55',
                                fontWeight: 700,
                              }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails style={{ display: 'block' }}>
                          <FormControlLabel
                            control={(
                              <Checkbox
                                checked={overrideEnabled}
                                onChange={(e) => handleInsuranceSupportStaffOverrideEnabled(staff.id, e.target.checked)}
                                style={{ color: overrideEnabled ? '#FF7F27' : '#002D55' }}
                              />
                            )}
                            label="Exclude selected insurers for this staff member"
                            style={{ marginBottom: overrideEnabled ? 16 : 0, color: '#002D55', fontWeight: 600 }}
                          />

                          {overrideEnabled && (
                            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                              {insuranceSupportSettings.supportedNetworks.map((networkValue) => {
                                const option = (insuranceSupportCatalog.networkOptions || []).find((item) => item.value === networkValue);
                                const selected = (override?.excludedNetworks || []).includes(networkValue);
                                return (
                                  <Chip
                                    key={`${staff.id}-${networkValue}`}
                                    label={option?.label || networkValue}
                                    clickable
                                    onClick={() => handleInsuranceSupportStaffToggle(staff.id, networkValue)}
                                    style={{
                                      backgroundColor: selected ? '#002D55' : '#eef4fb',
                                      color: selected ? '#ffffff' : '#002D55',
                                      fontWeight: 600,
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                )}
              </GeneralSettingsDrawer>

              <GeneralSettingsDrawer
                classes={classes}
                expanded={expandedGeneralSetting === 'videoconsultation-candidate-rules'}
                onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'videoconsultation-candidate-rules' ? false : 'videoconsultation-candidate-rules')}
                icon={<PeopleIcon style={{ color: '#FF7F27' }} />}
                title="Videoconsultation Candidate Rules"
              >
                <Typography className={classes.description}>
                  Control which intakes should still be recommended for videoconsultation. Clinic defaults apply to everyone,
                  and staff overrides can only add extra exclusions on top of the clinic baseline.
                </Typography>

                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={videoconsultationSettings.enabled}
                      onChange={(e) => setVideoconsultationSettings((prev) => ({ ...prev, enabled: e.target.checked }))}
                      style={{ color: videoconsultationSettings.enabled ? '#FF7F27' : '#002D55' }}
                    />
                  )}
                  label="Enable videoconsultation recommendation guidance"
                  style={{ marginTop: 8, color: '#002D55', fontWeight: 600 }}
                />

                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={videoconsultationSettings.defaultRequiresNoRedFlags}
                      onChange={(e) => setVideoconsultationSettings((prev) => ({ ...prev, defaultRequiresNoRedFlags: e.target.checked }))}
                      disabled={!videoconsultationSettings.enabled}
                      style={{ color: videoconsultationSettings.defaultRequiresNoRedFlags ? '#FF7F27' : '#002D55' }}
                    />
                  )}
                  label="Require no inferred red flags for a positive recommendation"
                  style={{ marginBottom: 16, color: '#002D55', fontWeight: 600 }}
                />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                      Clinic-wide excluded specialities
                    </Typography>
                    <Typography className={classes.description} style={{ marginBottom: 12 }}>
                      These body areas will not be recommended for videoconsultation when inferred from the intake.
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                      {(videoconsultationCatalog.specialityOptions || []).map((option) => {
                        const selected = videoconsultationSettings.excludedSpecialities.includes(option.value);
                        return (
                          <Chip
                            key={option.value}
                            label={option.label}
                            clickable
                            onClick={() => handleVideoconsultationClinicToggle('excludedSpecialities', option.value, videoconsultationCatalog.specialityOptions, 'value')}
                            style={{
                              backgroundColor: selected ? '#002D55' : '#eef4fb',
                              color: selected ? '#ffffff' : '#002D55',
                              fontWeight: 600,
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                      Clinic-wide excluded symptom tags
                    </Typography>
                    <Typography className={classes.description} style={{ marginBottom: 12 }}>
                      Use this for symptom patterns you want the QuestionHandler inference to treat as not suitable for video.
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                      {(videoconsultationCatalog.symptomTagOptions || []).map((option) => {
                        const selected = videoconsultationSettings.excludedSymptomTags.includes(option.value);
                        return (
                          <Chip
                            key={option.value}
                            label={option.label}
                            clickable
                            onClick={() => handleVideoconsultationClinicToggle('excludedSymptomTags', option.value, videoconsultationCatalog.symptomTagOptions, 'value')}
                            style={{
                              backgroundColor: selected ? '#002D55' : '#eef4fb',
                              color: selected ? '#ffffff' : '#002D55',
                              fontWeight: 600,
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Grid>
                </Grid>

                <Box className={classes.exampleBox} style={{ marginTop: 20 }}>
                  <Typography className={classes.exampleTitle}>How these rules behave</Typography>
                  <Typography className={classes.exampleTimes}>
                    Recommendations are advisory only. A positive recommendation currently requires no inferred red flags,
                    unless you disable that requirement above. Staff rules below only add more exclusions for individual clinicians.
                  </Typography>
                </Box>

                <Box style={{ marginTop: 24 }}>
                  <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                    Per-staff narrowing
                  </Typography>
                  <Typography className={classes.description} style={{ marginBottom: 12 }}>
                    Activate staff-specific overrides when a clinician wants stricter videoconsultation criteria than the clinic default.
                  </Typography>

                  {(videoconsultationCatalog.staffOptions || []).length === 0 ? (
                    <Typography color="textSecondary">No clinic staff were returned for override configuration.</Typography>
                  ) : (
                    (videoconsultationCatalog.staffOptions || []).map((staff) => {
                      const override = findVideoconsultationStaffOverride(staff.id);
                      const overrideEnabled = Boolean(override);
                      return (
                        <Accordion key={staff.id} elevation={0} style={{ marginBottom: 10, border: '1px solid #d7e3ef', borderRadius: 16, overflow: 'hidden' }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ backgroundColor: '#f7fafd' }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" style={{ gap: 12, paddingRight: 8 }}>
                              <Box>
                                <Typography style={{ color: '#002D55', fontWeight: 700 }}>
                                  {staff.label}
                                </Typography>
                                <Typography style={{ color: '#5f6c7b', fontSize: 12 }}>
                                  {(staff.role_type || '').replace(/_/g, ' ') || 'Staff member'}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label={overrideEnabled ? 'Override active' : 'Using clinic default'}
                                style={{
                                  backgroundColor: overrideEnabled ? '#002D55' : '#eef4fb',
                                  color: overrideEnabled ? '#ffffff' : '#002D55',
                                  fontWeight: 700,
                                }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails style={{ display: 'block' }}>
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  checked={overrideEnabled}
                                  onChange={(e) => handleVideoconsultationStaffOverrideEnabled(staff.id, e.target.checked)}
                                  style={{ color: overrideEnabled ? '#FF7F27' : '#002D55' }}
                                />
                              )}
                              label="Add staff-specific exclusions"
                              style={{ marginBottom: overrideEnabled ? 16 : 0, color: '#002D55', fontWeight: 600 }}
                            />

                            {overrideEnabled && (
                              <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                  <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                                    Extra excluded specialities
                                  </Typography>
                                  <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                                    {(videoconsultationCatalog.specialityOptions || []).map((option) => {
                                      const selected = (override?.excludedSpecialities || []).includes(option.value);
                                      return (
                                        <Chip
                                          key={`${staff.id}-${option.value}`}
                                          label={option.label}
                                          clickable
                                          onClick={() => handleVideoconsultationStaffToggle(staff.id, 'excludedSpecialities', option.value, videoconsultationCatalog.specialityOptions, 'value')}
                                          style={{
                                            backgroundColor: selected ? '#002D55' : '#eef4fb',
                                            color: selected ? '#ffffff' : '#002D55',
                                            fontWeight: 600,
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                                    Extra excluded symptom tags
                                  </Typography>
                                  <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                                    {(videoconsultationCatalog.symptomTagOptions || []).map((option) => {
                                      const selected = (override?.excludedSymptomTags || []).includes(option.value);
                                      return (
                                        <Chip
                                          key={`${staff.id}-${option.value}-symptom`}
                                          label={option.label}
                                          clickable
                                          onClick={() => handleVideoconsultationStaffToggle(staff.id, 'excludedSymptomTags', option.value, videoconsultationCatalog.symptomTagOptions, 'value')}
                                          style={{
                                            backgroundColor: selected ? '#002D55' : '#eef4fb',
                                            color: selected ? '#ffffff' : '#002D55',
                                            fontWeight: 600,
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                </Grid>
                              </Grid>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  )}
                </Box>
              </GeneralSettingsDrawer>

              <GeneralSettingsDrawer
                classes={classes}
                expanded={expandedGeneralSetting === 'adaptive-exercise-adjustments'}
                onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'adaptive-exercise-adjustments' ? false : 'adaptive-exercise-adjustments')}
                icon={<NotificationsIcon style={{ color: '#FF7F27' }} />}
                title="Adaptive Exercise Adjustments"
              >
                <Typography className={classes.description}>
                  Choose the default adaptive adjustment behavior for all patient treatments in this clinic.
                  Patient treatments can inherit this setting or override it individually.
                </Typography>

                <FormControl component="fieldset">
                  <FormLabel component="legend" style={{ color: '#002D55', fontWeight: 600, marginBottom: 8 }}>
                    Clinic Default Adjustment Mode
                  </FormLabel>
                  <RadioGroup
                    value={exerciseAdjustmentModeDefault}
                    onChange={(e) => setExerciseAdjustmentModeDefault(e.target.value)}
                  >
                    {EXERCISE_ADJUSTMENT_MODE_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={`${option.label} - ${option.description}`}
                        className={classes.radioLabel}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>

                <Box className={classes.exampleBox}>
                  <Typography className={classes.exampleTitle}>How this is used</Typography>
                  <Typography className={classes.exampleTimes}>
                    New and existing patient treatments inherit this clinic default until a patient-specific override is set.
                  </Typography>
                </Box>
              </GeneralSettingsDrawer>

                <GeneralSettingsDrawer
                  classes={classes}
                  expanded={expandedGeneralSetting === 'global-phase-progression-defaults'}
                  onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'global-phase-progression-defaults' ? false : 'global-phase-progression-defaults')}
                  icon={<NotificationsIcon style={{ color: '#FF7F27' }} />}
                  title="Global Phase Progression Defaults"
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography className={classes.description} style={{ marginBottom: 0 }}>
                        Configure the clinic-wide fallback rules used when a treatment does not have diagnosis-specific or patient-specific phase progression settings.
                      </Typography>
                    </Box>
                    <Button variant="outlined" onClick={handleAddPhaseProgressionRule} style={{ borderColor: '#FF7F27', color: '#002D55', borderRadius: 14, fontWeight: 700, textTransform: 'none' }} startIcon={<AddIcon />}>
                      Add Global Rule
                    </Button>
                  </Box>

                  {phaseProgressionDefaults.length === 0 ? (
                    <Box>
                      <Box className={classes.exampleBox} style={{ marginBottom: 16 }}>
                        <Typography className={classes.exampleTitle}>No global progression defaults configured yet</Typography>
                        <Typography className={classes.exampleTimes}>
                          Treatments will fall back to their built-in progression behavior until you add a clinic default rule. You can start from the current legacy fallback presets below.
                        </Typography>
                      </Box>
                      {(phaseProgressionFallbackPresets || []).map((preset) => (
                        <Box key={preset.localId} className={classes.insuranceDetailCard}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                            <Box>
                              <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 16 }}>
                                Phase {preset.source_phase_index} {'->'} Phase {preset.target_phase_index}
                              </Typography>
                              <Typography style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
                                Legacy fallback preset
                              </Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              onClick={() => handleUseFallbackPreset(preset)}
                              style={{ borderColor: '#FF7F27', color: '#002D55', borderRadius: 14, fontWeight: 700, textTransform: 'none' }}
                            >
                              Use as starting point
                            </Button>
                          </Box>
                          {preset.summary ? (
                            <Typography style={{ color: '#5f6f82', fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
                              {preset.summary}
                            </Typography>
                          ) : null}
                          {(preset.conditions || []).length > 0 ? (
                            <Box style={{ padding: '10px 12px', borderRadius: 12, background: '#f4f7fb', marginBottom: 10 }}>
                              {(preset.conditions || []).map((condition, index) => {
                                const metricOption = phaseProgressionAvailableMetrics.find((option) => option.key === condition.metric_key);
                                const operatorLabel = PHASE_PROGRESSION_OPERATOR_OPTIONS.find((option) => option.value === condition.operator)?.label || condition.operator;
                                return (
                                  <Typography key={`${preset.localId}-condition-${index}`} style={{ color: '#002D55', fontSize: 12, lineHeight: 1.45 }}>
                                    {metricOption?.label || condition.metric_key} - {operatorLabel} {condition.threshold_value}
                                  </Typography>
                                );
                              })}
                            </Box>
                          ) : null}
                          {(preset.notes || []).map((note, index) => (
                            <Typography key={`${preset.localId}-note-${index}`} style={{ color: '#5f6f82', fontSize: 12, lineHeight: 1.45, marginBottom: 6 }}>
                              {note}
                            </Typography>
                          ))}
                          {(preset.unrepresented_checks || []).map((item, index) => (
                            <Typography key={`${preset.localId}-check-${index}`} style={{ color: '#8a5200', fontSize: 12, lineHeight: 1.45, marginBottom: 6 }}>
                              {item}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box>
                      {phaseProgressionDefaults.map((rule, ruleIndex) => {
                        const isExpanded = expandedPhaseProgressionRule === rule.localId;
                        return (
                          <Accordion
                            key={rule.localId}
                            className={`${classes.stackedAccordion} ${classes.insuranceAccordion}`}
                            expanded={isExpanded}
                            onChange={() => setExpandedPhaseProgressionRule(isExpanded ? null : rule.localId)}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.insuranceAccordionSummary}>
                              <Box style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 16, flexWrap: 'wrap' }}>
                                <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: 16, minWidth: 200 }}>
                                  Phase {rule.source_phase_index || '?'} {'->'} Phase {rule.target_phase_index || '?'}
                                </Typography>
                                <Typography style={{ color: '#666', fontSize: 12, flex: 1 }}>
                                  {(rule.conditionCombos || []).length} combo{(rule.conditionCombos || []).length !== 1 ? 's' : ''} / {(rule.conditionCombos || []).reduce((total, combo) => total + ((combo.conditions || []).length), 0)} condition{(rule.conditionCombos || []).reduce((total, combo) => total + ((combo.conditions || []).length), 0) !== 1 ? 's' : ''}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  style={{ color: '#c62828', borderColor: '#ef9a9a', fontSize: 11, borderRadius: 14 }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleRemovePhaseProgressionRule(rule.localId);
                                  }}
                                >
                                  Remove
                                </Button>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails style={{ display: 'block', padding: 24 }}>
                              <Grid container spacing={2} style={{ marginBottom: 12 }}>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    label="From Phase"
                                    value={rule.source_phase_index}
                                    onChange={(event) => handlePhaseProgressionRuleChange(rule.localId, 'source_phase_index', event.target.value)}
                                    variant="outlined"
                                    size="small"
                                    className={classes.orangeFocused}
                                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    label="To Phase"
                                    value={rule.target_phase_index}
                                    onChange={(event) => handlePhaseProgressionRuleChange(rule.localId, 'target_phase_index', event.target.value)}
                                    variant="outlined"
                                    size="small"
                                    className={classes.orangeFocused}
                                    InputProps={{ inputProps: { min: 1, step: 1 } }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Box className={classes.exampleBox} style={{ marginTop: 0, padding: 12 }}>
                                    <Typography className={classes.exampleTitle} style={{ marginBottom: 6 }}>Rule scope</Typography>
                                    <Typography className={classes.exampleTimes}>
                                      These defaults apply only when no diagnosis-specific template and no treatment override exist.
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>

                              <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <Box style={{ padding: '10px 12px', borderRadius: 14, background: '#f4f7fb' }}>
                                  <Typography style={{ fontSize: 12, color: '#5f6f82', lineHeight: 1.45 }}>
                                    The first required field in each rule combo must use either Sessions completed or Duration in phase (weeks). Criteria inside a combo are ANDed, and combos can be joined with AND or OR.
                                  </Typography>
                                </Box>
                                {(rule.conditionCombos || []).map((combo, comboIndex) => (
                                  <Box key={combo.localId} style={{ border: '1px solid #d8e2ef', borderRadius: 16, padding: 12, background: '#fff' }}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                                      <Box>
                                        <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 13 }}>
                                          Rule combo {comboIndex + 1}
                                        </Typography>
                                        <Typography style={{ color: '#5f6f82', fontSize: 12 }}>
                                          All criteria in this combo must pass.
                                        </Typography>
                                      </Box>
                                      <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap', flex: 1, justifyContent: 'center' }}>
                                        {comboIndex > 0 ? (
                                          <TextField
                                            select
                                            label="Join"
                                            value={combo.combo_join_operator}
                                            onChange={(event) => handlePhaseProgressionComboJoinChange(rule.localId, combo.localId, event.target.value)}
                                            SelectProps={{ MenuProps: PHASE_PROGRESSION_SELECT_MENU_PROPS }}
                                            variant="outlined"
                                            size="small"
                                            className={`${classes.orangeFocused} ${classes.phaseProgressionCompactField} ${classes.phaseProgressionSelectField}`}
                                            style={{ minWidth: 120 }}
                                            InputLabelProps={{ shrink: true }}
                                          >
                                            {PHASE_PROGRESSION_COMBO_JOIN_OPTIONS.map((option) => (
                                              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                          </TextField>
                                        ) : null}
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          style={{ color: '#c62828', borderColor: '#ef9a9a', fontSize: 11, borderRadius: 14 }}
                                          onClick={() => handleRemovePhaseProgressionConditionCombo(rule.localId, combo.localId)}
                                          disabled={(rule.conditionCombos || []).length <= 1}
                                        >
                                          Remove combo
                                        </Button>
                                      </Box>
                                    </Box>
                                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                      {combo.conditions.map((condition, conditionIndex) => (
                                        <Box key={`${combo.localId}-condition-${conditionIndex}`} style={{ border: '1px solid #d8e2ef', borderRadius: 16, padding: 12, background: '#fff' }}>
                                          <Grid container spacing={1} alignItems="center">
                                            <Grid item xs={12} md={5}>
                                              <TextField
                                                select
                                                fullWidth
                                                label={conditionIndex === 0 ? 'Time' : 'Metric'}
                                                value={condition.metric_key}
                                                onChange={(event) => handlePhaseProgressionConditionChange(rule.localId, combo.localId, conditionIndex, 'metric_key', event.target.value)}
                                                SelectProps={{ MenuProps: PHASE_PROGRESSION_SELECT_MENU_PROPS }}
                                                variant="outlined"
                                                size="small"
                                                className={`${classes.orangeFocused} ${classes.phaseProgressionCompactField} ${classes.phaseProgressionSelectField}`}
                                              >
                                                {getAvailableClinicPhaseMetricOptions(combo.conditions, conditionIndex, phaseProgressionAvailableMetrics).map((option) => (
                                                  <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                                                ))}
                                              </TextField>
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                              <TextField
                                                select
                                                fullWidth
                                                label="Operator"
                                                value={condition.operator}
                                                onChange={(event) => handlePhaseProgressionConditionChange(rule.localId, combo.localId, conditionIndex, 'operator', event.target.value)}
                                                SelectProps={{ MenuProps: PHASE_PROGRESSION_SELECT_MENU_PROPS }}
                                                variant="outlined"
                                                size="small"
                                                className={`${classes.orangeFocused} ${classes.phaseProgressionCompactField} ${classes.phaseProgressionSelectField}`}
                                              >
                                                {PHASE_PROGRESSION_OPERATOR_OPTIONS.map((option) => (
                                                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                ))}
                                              </TextField>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                              <TextField
                                                fullWidth
                                                type="number"
                                                label={getClinicPhaseThresholdLabel(condition)}
                                                value={condition.threshold_value}
                                                onChange={(event) => handlePhaseProgressionConditionChange(rule.localId, combo.localId, conditionIndex, 'threshold_value', event.target.value)}
                                                variant="outlined"
                                                size="small"
                                                className={`${classes.orangeFocused} ${classes.phaseProgressionCompactField}`}
                                              />
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                              <Box display="flex" justifyContent="flex-end">
                                                <Button
                                                  size="small"
                                                  style={{ color: '#FF7F27', minWidth: 0 }}
                                                  onClick={() => handleRemovePhaseProgressionCondition(rule.localId, combo.localId, conditionIndex)}
                                                  disabled={combo.conditions.length <= 1 || conditionIndex === 0}
                                                >
                                                  Remove
                                                </Button>
                                              </Box>
                                            </Grid>
                                            {condition.metric_key === 'exercise_level_progressions' && (
                                              <Grid item xs={12}>
                                                <TextField
                                                  fullWidth
                                                  label="Exercise identifiers (comma separated)"
                                                  value={condition.exercise_identifiers_text}
                                                  onChange={(event) => handlePhaseProgressionConditionChange(rule.localId, combo.localId, conditionIndex, 'exercise_identifiers_text', event.target.value)}
                                                  variant="outlined"
                                                  size="small"
                                                  className={classes.orangeFocused}
                                                  helperText="Optional: limit this rule to specific exercises."
                                                />
                                              </Grid>
                                            )}
                                          </Grid>
                                        </Box>
                                      ))}
                                    </Box>
                                    <Box display="flex" justifyContent="flex-end" style={{ marginTop: 12 }}>
                                      <Button
                                        onClick={() => handleAddPhaseProgressionCondition(rule.localId, combo.localId)}
                                        disabled={combo.conditions.length >= 3}
                                        variant="outlined"
                                        style={{ borderColor: '#FF7F27', color: '#002D55', textTransform: 'none', borderRadius: 14, fontWeight: 700 }}
                                      >
                                        Add criterion
                                      </Button>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>

                              <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginTop: 12, gap: 12, flexWrap: 'wrap' }}>
                                <Typography style={{ color: '#5f6f82', fontSize: 12 }}>
                                  Include either weeks in phase or sessions completed as the first field in every combo.
                                </Typography>
                                <Button
                                  onClick={() => handleAddPhaseProgressionConditionCombo(rule.localId)}
                                  variant="outlined"
                                  style={{ borderColor: '#FF7F27', color: '#002D55', textTransform: 'none', borderRadius: 14, fontWeight: 700 }}
                                >
                                  Add combo
                                </Button>
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  )}
                </GeneralSettingsDrawer>

                {/* Stripe Connect Section */}
                <GeneralSettingsDrawer
                  classes={classes}
                  expanded={expandedGeneralSetting === 'stripe-connect-setup'}
                  onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'stripe-connect-setup' ? false : 'stripe-connect-setup')}
                  icon={<BusinessIcon style={{ color: '#FF7F27' }} />}
                  title="Stripe Connect Setup"
                >
                  <Typography className={classes.description}>
                    Complete this section to let the clinic receive card payments. If Stripe was skipped during signup, you can start it here and we will reuse the clinic details already saved in Virtus.
                  </Typography>
                  {!stripeConnectStatus.stripeAccountId ? (
                    <Typography style={{ color: '#6f7b87', fontSize: 13, marginBottom: 12 }}>
                      Stripe onboarding will open a hosted Connect flow to finish verification and payouts setup.
                    </Typography>
                  ) : null}
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                    <Chip
                      label={stripeConnectStatus.loading ? 'Refreshing status...' : (stripeConnectStatus.stripeAccountId ? 'Connected account found' : 'No Connect account yet')}
                      style={{ backgroundColor: stripeConnectStatus.stripeAccountId ? '#002D55' : '#e6eaf0', color: stripeConnectStatus.stripeAccountId ? '#fff' : '#002D55', fontWeight: 600 }}
                    />
                    <Chip
                      label={stripeConnectStatus.chargesEnabled ? 'Charges enabled' : 'Charges disabled'}
                      style={{ backgroundColor: stripeConnectStatus.chargesEnabled ? '#4caf50' : '#f2f4f7', color: stripeConnectStatus.chargesEnabled ? '#fff' : '#002D55', fontWeight: 600 }}
                    />
                    <Chip
                      label={stripeConnectStatus.payoutsEnabled ? 'Payouts enabled' : 'Payouts disabled'}
                      style={{ backgroundColor: stripeConnectStatus.payoutsEnabled ? '#4caf50' : '#f2f4f7', color: stripeConnectStatus.payoutsEnabled ? '#fff' : '#002D55', fontWeight: 600 }}
                    />
                    <Chip
                      label={stripeConnectStatus.onboardingComplete ? 'Onboarding complete' : 'Onboarding pending'}
                      style={{ backgroundColor: stripeConnectStatus.onboardingComplete ? '#4caf50' : '#f2f4f7', color: stripeConnectStatus.onboardingComplete ? '#fff' : '#002D55', fontWeight: 600 }}
                    />
                  </Box>
                  {stripeConnectStatus.stripeAccountId ? (
                    <Typography style={{ color: '#002D55', fontSize: 13, marginBottom: 12 }}>
                      Stripe account: {stripeConnectStatus.stripeAccountId}
                    </Typography>
                  ) : null}
                  {stripeConnectStatus.error ? (
                    <Typography style={{ color: '#d32f2f', fontSize: 13, marginBottom: 12 }}>
                      {stripeConnectStatus.error}
                    </Typography>
                  ) : null}
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <Button
                      variant="contained"
                      className={classes.saveButton}
                      onClick={handleStartStripeOnboarding}
                      disabled={stripeConnectStatus.loading}
                    >
                      {stripeConnectStatus.stripeAccountId ? 'Resume Stripe Connect onboarding' : 'Start Stripe Connect onboarding'}
                    </Button>
                    <Button
                      variant="outlined"
                      className={classes.cancelButton}
                      onClick={handleOpenStripeDashboard}
                      disabled={stripeConnectStatus.loading || !stripeConnectStatus.stripeAccountId}
                    >
                      Open Stripe dashboard
                    </Button>
                    <Button
                      variant="outlined"
                      className={classes.cancelButton}
                      onClick={fetchStripeConnectStatus}
                      disabled={stripeConnectStatus.loading}
                    >
                      Refresh status
                    </Button>
                  </Box>
                </GeneralSettingsDrawer>

              {/* Payment Terms Section */}
              <GeneralSettingsDrawer
                classes={classes}
                expanded={expandedGeneralSetting === 'default-payment-terms'}
                onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'default-payment-terms' ? false : 'default-payment-terms')}
                icon={<ReceiptIcon style={{ color: '#FF7F27' }} />}
                title="Default Payment Terms (Invoice Due Dates)"
              >
                <Typography className={classes.description}>
                  Configure default payment terms in days for different payer types.
                  When an invoice is created, the due date will be calculated based on these settings.
                  <strong> Patient</strong> = Direct billing to patients.
                  <strong> Insurance</strong> = Billing through referral letters.
                </Typography>
                <Box style={{ marginBottom: 20 }}>
                  <TextField
                    select
                    fullWidth
                    label="Stripe fee presentation"
                    value={stripeFeeDisplayMode}
                    onChange={(e) => setStripeFeeDisplayMode(e.target.value)}
                    variant="outlined"
                    size="small"
                    className={classes.orangeFocused}
                    helperText="Choose whether the Stripe percentage is shown as a patient surcharge or as a platform fee deduction in the payment dialog."
                  >
                    <MenuItem value="platform_fee_deduction">Platform fee deduction</MenuItem>
                    <MenuItem value="patient_surcharge">Patient surcharge</MenuItem>
                  </TextField>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth type="number" label="Patient Payment Terms (days)" value={paymentTermsPatient} onChange={(e) => setPaymentTermsPatient(e.target.value)} variant="outlined" size="small" className={classes.orangeFocused} InputProps={{ inputProps: { min: 1, max: 365 } }} helperText="Days until payment due for patient billing" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth type="number" label="Insurance Payment Terms (days)" value={paymentTermsInsurance} onChange={(e) => setPaymentTermsInsurance(e.target.value)} variant="outlined" size="small" className={classes.orangeFocused} InputProps={{ inputProps: { min: 1, max: 365 } }} helperText="Days until payment due for insurance billing" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth type="number" label="Default Payment Terms (days)" value={paymentTermsDefault} onChange={(e) => setPaymentTermsDefault(e.target.value)} variant="outlined" size="small" className={classes.orangeFocused} InputProps={{ inputProps: { min: 1, max: 365 } }} helperText="Fallback payment terms for other cases" />
                  </Grid>
                </Grid>
              </GeneralSettingsDrawer>

              {/* Payment Reminders Section */}
              <GeneralSettingsDrawer
                classes={classes}
                expanded={expandedGeneralSetting === 'default-payment-reminder-schedule'}
                onChange={() => setExpandedGeneralSetting(expandedGeneralSetting === 'default-payment-reminder-schedule' ? false : 'default-payment-reminder-schedule')}
                icon={<NotificationsIcon style={{ color: '#FF7F27' }} />}
                title="Default Payment Reminder Schedule"
              >
                <Typography className={classes.description}>
                  Configure when payment reminders should be triggered. Reminders are currently managed manually
                  by updating the invoice status to 'reminder_1', 'reminder_2', 'reminder_3', or 'inkasso'.
                  These settings define the recommended timeline for each reminder stage.
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={enablePaymentReminders} onChange={(e) => setEnablePaymentReminders(e.target.checked)} style={{ color: enablePaymentReminders ? '#FF7F27' : '#002D55' }} />}
                  label="Enable payment reminder tracking (for manual workflow)"
                  style={{ marginBottom: 16, color: '#002D55', fontWeight: 600 }}
                />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth type="number" label="1st Reminder (days before due)" value={reminder1DaysBeforeDue} onChange={(e) => setReminder1DaysBeforeDue(e.target.value)} variant="outlined" size="small" className={classes.orangeFocused} InputProps={{ inputProps: { min: 0, max: 365 } }} helperText="Send before due date" disabled={!enablePaymentReminders} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth type="number" label="2nd Reminder (days after due)" value={reminder2DaysAfterDue} onChange={(e) => setReminder2DaysAfterDue(e.target.value)} variant="outlined" size="small" className={classes.orangeFocused} InputProps={{ inputProps: { min: 0, max: 365 } }} helperText="Send after overdue" disabled={!enablePaymentReminders} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth type="number" label="3rd Reminder (days after due)" value={reminder3DaysAfterDue} onChange={(e) => setReminder3DaysAfterDue(e.target.value)} variant="outlined" size="small" className={classes.orangeFocused} InputProps={{ inputProps: { min: 0, max: 365 } }} helperText="Final reminder" disabled={!enablePaymentReminders} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth type="number" label="Inkasso Threshold (days after due)" value={inkassoDaysAfterDue} onChange={(e) => setInkassoDaysAfterDue(e.target.value)} variant="outlined" size="small" className={classes.orangeFocused} InputProps={{ inputProps: { min: 0, max: 365 } }} helperText="Send to collections" disabled={!enablePaymentReminders} />
                  </Grid>
                </Grid>
                <Box className={classes.exampleBox} style={{ marginTop: 16 }}>
                  <Typography className={classes.exampleTitle}>
                    Example Reminder Timeline (for {paymentTermsPatient}-day patient payment terms):
                  </Typography>
                  <Typography className={classes.exampleTimes}>
                    Day 0: Invoice created -> Day {Math.max(0, parseInt(paymentTermsPatient) - parseInt(reminder1DaysBeforeDue))}: 1st Reminder ->{' '}
                    Day {paymentTermsPatient}: Payment Due -> Day {parseInt(paymentTermsPatient) + parseInt(reminder2DaysAfterDue)}: 2nd Reminder ->{' '}
                    Day {parseInt(paymentTermsPatient) + parseInt(reminder3DaysAfterDue)}: 3rd Reminder ->{' '}
                    Day {parseInt(paymentTermsPatient) + parseInt(inkassoDaysAfterDue)}: Inkasso
                  </Typography>
                </Box>
              </GeneralSettingsDrawer>

              <Divider className={classes.footerDivider} />

              <Box className={classes.footerActions}>
                <Button variant="contained" className={`${classes.saveButton} ${classes.footerActionButton}`} fullWidth style={{ flex: 1 }} startIcon={saving ? <CircularProgress size={20} style={{ color: '#fff' }} /> : <SaveIcon />} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
                <Button variant="outlined" className={`${classes.cancelButton} ${classes.footerActionButton}`} fullWidth style={{ flex: 1 }} onClick={handleGeneralCancel} disabled={saving || !originalSettings}>
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          {/* ======= INSURANCE PAYERS TAB ======= */}
          {activeTab === 'insurance' && (
            <Box className={classes.tabContent}>
              <Box className={classes.settingSection}>
                <Typography className={classes.sectionTitle}>
                  <BusinessIcon style={{ color: '#FF7F27' }} />
                  Danish Partner Templates
                </Typography>
                <Typography className={classes.description}>
                  Click a partner to add the seeded insurer agreement and its contact data to this clinic.
                </Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 8 }}>
                  {insurancePartnerTemplates.map((template) => (
                    <Chip
                      key={template.value}
                      label={template.label}
                      clickable
                      onClick={() => handleOpenInsurancePartnerTemplate(template)}
                      disabled={saving}
                      style={{ backgroundColor: '#eef4fb', color: '#002D55', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Section A: Clinic-Registered Insurer Agreements */}
              <Box className={classes.settingSection}>
                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 16 }}>
                  <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                    <BusinessIcon style={{ color: '#FF7F27' }} />
                    Clinic-Registered Insurers
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={openAddInsurerDialog}
                    className={classes.saveButton}
                  >
                    Add Insurer
                  </Button>
                </Box>
                <Typography className={classes.description}>
                  Formal insurer agreements registered by your clinic. These payers are used for invoice routing and can be assigned to patients.
                </Typography>
                {agreementsLoading ? (
                  <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
                ) : agreements.length === 0 ? (
                  <Typography style={{ color: '#666', fontSize: 13 }}>
                    No insurer agreements registered yet. Click "Add Insurer" to create one.
                  </Typography>
                ) : (
                  <Box>
                    {agreements.map((agreement) => {
                      const isExpanded = expandedAgreement === agreement.id;
                      return (
                        <Accordion key={agreement.id} className={`${classes.stackedAccordion} ${classes.insuranceAccordion}`} expanded={isExpanded} onChange={() => setExpandedAgreement(isExpanded ? null : agreement.id)}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.insuranceAccordionSummary}>
                            <Box style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 16, flexWrap: 'wrap' }}>
                              <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: 16, minWidth: 200 }}>
                                {agreement.insurance_provider_name}
                              </Typography>
                              {agreement.business_registration_number && (
                                <Typography style={{ color: '#666', fontSize: 12 }}>CVR: {agreement.business_registration_number}</Typography>
                              )}
                              <Chip
                                label={agreement.is_active ? 'Active' : 'Inactive'}
                                size="small"
                                style={{ backgroundColor: agreement.is_active ? '#e8f5e9' : '#fce4ec', color: agreement.is_active ? '#2e7d32' : '#c62828', fontWeight: 600, borderRadius: 14 }}
                              />
                              <Typography style={{ color: '#666', fontSize: 12, flex: 1 }}>
                                {(agreement.payer_details || []).length} policy type{(agreement.payer_details || []).length !== 1 ? 's' : ''} configured
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                style={{ marginLeft: 'auto', color: agreement.is_active ? '#FF7F27' : '#2e7d32', borderColor: agreement.is_active ? '#FF7F27' : '#2e7d32', fontSize: 11, borderRadius: 14 }}
                                onClick={(e) => { e.stopPropagation(); handleToggleAgreementActive(agreement.id, agreement.is_active); }}
                              >
                                {agreement.is_active ? 'Deactivate' : 'Reactivate'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                style={{ color: '#002D55', borderColor: '#002D55', fontSize: 11, borderRadius: 14 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditInsurerDialog(agreement);
                                }}
                              >
                                Edit
                              </Button>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails style={{ padding: 24 }}>
                            <Box style={{ width: '100%' }}>
                              {POLICY_TYPES.map((policyType) => {
                                const detailKey = `${agreement.id}-${policyType}`;
                                const editData = agreementDetailEdits[detailKey] || {};
                                const isSaving = !!savingAgreementDetail[detailKey];
                                return (
                                  <Box key={policyType} style={{ marginBottom: 16, border: '1px solid #e5e9ef', borderRadius: 8, padding: 16 }}>
                                    <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 12 }}>
                                      {policyTypeLabel(policyType)}
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} sm={3}>
                                        <TextField fullWidth size="small" variant="outlined" label="EAN Number" value={editData.ean_number || ''} onChange={(e) => handleAgreementDetailChange(agreement.id, policyType, 'ean_number', e.target.value)} className={classes.orangeFocused} helperText="13-digit electronic billing number" />
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <TextField fullWidth size="small" variant="outlined" label="Billing Email" type="email" value={editData.billing_email || ''} onChange={(e) => handleAgreementDetailChange(agreement.id, policyType, 'billing_email', e.target.value)} className={classes.orangeFocused} helperText="Invoice delivery email" />
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <TextField fullWidth size="small" variant="outlined" label="Max Coverage / Appointment" type="number" value={editData.max_coverage_per_appointment || ''} onChange={(e) => handleAgreementDetailChange(agreement.id, policyType, 'max_coverage_per_appointment', e.target.value)} className={classes.orangeFocused} helperText="Leave blank for full coverage" />
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <TextField fullWidth size="small" variant="outlined" label="Payment Terms (days)" type="number" value={editData.payment_terms_days ?? ''} onChange={(e) => handleAgreementDetailChange(agreement.id, policyType, 'payment_terms_days', e.target.value)} className={classes.orangeFocused} InputProps={{ inputProps: { min: 1, max: 365 } }} helperText="Days until invoice due" />
                                      </Grid>
                                    </Grid>
                                    <Box display="flex" justifyContent="flex-end" mt={1}>
                                      <Button size="small" variant="contained" className={classes.saveButton} onClick={() => handleSaveAgreementDetail(agreement.id, policyType)} disabled={isSaving}>
                                        {isSaving ? 'Saving...' : 'Save'}
                                      </Button>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                )}
              </Box>

              <Divider className={classes.sectionDivider} />

            </Box>
          )}

          {/* ======= OTHER PAYERS TAB ======= */}
          {activeTab === 'other' && (
            <Box className={classes.tabContent}>
              <Box className={classes.settingSection}>
                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 16 }}>
                  <Typography className={classes.sectionTitle} style={{ marginBottom: 0, position: 'relative', top: -2 }}>
                    <BusinessIcon style={{ color: '#FF7F27' }} />
                    Other Payer Configuration
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={openNewOtherPayerDialog}
                    className={classes.saveButton}
                  >
                    Add Other Payer
                  </Button>
                </Box>
                <Typography className={classes.description}>
                  Non-insurance billing entities such as corporate accounts, public subsidy schemes, and other third-party payers.
                  These records are stored separately from insurer agreements.
                </Typography>

                {otherPayersLoading ? (
                  <Box display="flex" justifyContent="center" p={3}><CircularProgress size={28} /></Box>
                ) : otherPayers.length === 0 ? (
                  <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, borderColor: '#dbe7f2', background: '#fafcff' }}>
                    <Typography style={{ color: '#002D55', fontWeight: 600, marginBottom: 4 }}>
                      No other payers have been added yet.
                    </Typography>
                    <Typography variant="body2" style={{ color: '#6f7b87' }}>
                      Use the button above to add a payer without mixing it into the insurer agreements list.
                    </Typography>
                  </Paper>
                ) : (
                  <Box style={{ display: 'grid', gap: 12 }}>
                    {otherPayers.map((payer) => (
                      <Paper
                        key={payer.id}
                        variant="outlined"
                        style={{
                          padding: 16,
                          borderRadius: 12,
                          borderColor: '#dbe7f2',
                          background: '#fff',
                        }}
                      >
                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 12 }}>
                          <Box style={{ flex: 1 }}>
                            <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                              {payer.name}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#46607a' }}>
                              {payer.address || 'No address'}{payer.city ? `, ${payer.city}` : ''}{payer.postal_code ? ` ${payer.postal_code}` : ''}
                            </Typography>
                            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                              {payer.cvr_number && <Chip label={`CVR ${payer.cvr_number}`} size="small" style={{ background: '#edf5fc', color: '#002D55' }} />}
                              {payer.ean_number && <Chip label={`EAN ${payer.ean_number}`} size="small" style={{ background: '#edf5fc', color: '#002D55' }} />}
                              {payer.billing_email && <Chip label={payer.billing_email} size="small" style={{ background: '#f5f8fb', color: '#46607a' }} />}
                              {payer.phone && <Chip label={payer.phone} size="small" style={{ background: '#f5f8fb', color: '#46607a' }} />}
                            </Box>
                          </Box>
                          <Box style={{ display: 'flex', gap: 8 }}>
                            <Button size="small" variant="outlined" onClick={() => openEditOtherPayerDialog(payer)} className={classes.cancelButton}>
                              Edit
                            </Button>
                            <Button size="small" variant="text" onClick={() => handleDeleteOtherPayer(payer.id)} style={{ color: '#e53935' }}>
                              Delete
                            </Button>
                          </Box>
                        </Box>
                        {payer.person_reference && (
                          <Typography variant="body2" style={{ color: '#6f7b87', marginTop: 10 }}>
                            Reference: {payer.person_reference}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* ======= LOCATIONS & ROOMS TAB ======= */}
          {activeTab === 'locations' && (
            <Box className={classes.tabContent}>
              <Box className={classes.settingSection}>
                <Typography className={classes.sectionTitle}>
                  <BusinessIcon style={{ color: '#FF7F27' }} />
                  Clinic Locations &amp; Rooms
                </Typography>
                <Typography className={classes.description}>
                  Manage your clinic locations and the rooms within each. These are used in the calendar resource view and bookings.
                </Typography>

                {locationsLoading ? (
                  <Box display="flex" justifyContent="center" p={3}><CircularProgress size={28} /></Box>
                ) : (
                  <>
                    {locationsError && (
                      <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, borderColor: '#ffd4bf', background: '#fff8f3', marginBottom: 12 }}>
                        <Typography style={{ color: '#8a4b15', fontWeight: 600 }}>{locationsError}</Typography>
                      </Paper>
                    )}
                    {!locationsError && locations.length === 0 && (
                      <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, borderColor: '#dbe7f2', background: '#fafcff', marginBottom: 12 }}>
                        <Typography style={{ color: '#002D55', fontWeight: 600, marginBottom: 4 }}>
                          No visible locations returned.
                        </Typography>
                        <Typography variant="body2" style={{ color: '#6f7b87' }}>
                          If this clinic already has locations, refresh the page. If you are signed in as staff, make sure the clinic admin has assigned you to a location.
                        </Typography>
                      </Paper>
                    )}
                    {locations.map((loc) => (
                    <Accordion
                      key={loc.id}
                      expanded={String(expandedLocationId || '') === String(loc.id)}
                      onChange={(_, isExpanded) => setExpandedLocationId(isExpanded ? String(loc.id) : null)}
                      TransitionProps={{ unmountOnExit: true }}
                      style={{ marginBottom: 8, borderRadius: 16, border: '1px solid #e5e9ef', overflow: 'hidden' }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box display="flex" alignItems="center" style={{ gap: 8, width: '100%' }}>
                          <Typography component="div" style={{ fontWeight: 600, color: '#002D55', flex: 1, display: 'flex', alignItems: 'center' }}>
                            {loc.name || 'Unnamed Location'}
                            {loc.is_primary && <Chip label="Primary" size="small" style={{ marginLeft: 8, backgroundColor: '#FF7F27', color: '#fff', height: 20, fontSize: '0.7rem' }} />}
                          </Typography>
                          <Typography variant="body2" style={{ color: '#888' }}>{loc.address}{loc.city ? `, ${loc.city}` : ''}</Typography>
                          <Chip
                            label={`${(loc.rooms || []).length} room${(loc.rooms || []).length === 1 ? '' : 's'}`}
                            size="small"
                            variant="outlined"
                            style={{ borderColor: '#002D55', color: '#002D55', fontWeight: 600 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails style={{ flexDirection: 'column' }}>
                        <Typography style={{ fontWeight: 600, color: '#002D55', marginBottom: 8 }}>
                          Edit location details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Location Name" variant="outlined" size="small" value={loc.name}
                              onChange={(e) => setLocations((prev) => prev.map((l) => l.id === loc.id ? { ...l, name: e.target.value } : l))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Address" variant="outlined" size="small" value={loc.address}
                              onChange={(e) => setLocations((prev) => prev.map((l) => l.id === loc.id ? { ...l, address: e.target.value } : l))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField fullWidth label="City" variant="outlined" size="small" value={loc.city}
                              onChange={(e) => setLocations((prev) => prev.map((l) => l.id === loc.id ? { ...l, city: e.target.value } : l))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField fullWidth label="Postal Code" variant="outlined" size="small" value={loc.postal_code}
                              onChange={(e) => setLocations((prev) => prev.map((l) => l.id === loc.id ? { ...l, postal_code: e.target.value } : l))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField fullWidth label="Phone" variant="outlined" size="small" value={loc.phone}
                              onChange={(e) => setLocations((prev) => prev.map((l) => l.id === loc.id ? { ...l, phone: e.target.value } : l))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField fullWidth label="Email" variant="outlined" size="small" value={loc.email}
                              onChange={(e) => setLocations((prev) => prev.map((l) => l.id === loc.id ? { ...l, email: e.target.value } : l))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={12}>
                            <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                              <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={() => handleDeleteLocation(loc.id)} style={{ color: '#e53935' }}>Remove Location</Button>
                              <Button size="small" variant="contained" className={classes.saveButton} onClick={() => handleSaveLocation(loc)} disabled={saving}>
                                Save Location
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Assigned Staff */}
                        <Accordion
                          expanded={expandedLocationSection === `staff-${loc.id}`}
                          onChange={(_, isExpanded) => setExpandedLocationSection(isExpanded ? `staff-${loc.id}` : null)}
                          TransitionProps={{ unmountOnExit: true }}
                          style={{ marginTop: 16, marginBottom: 8, borderRadius: 16, border: '1px solid #e5e9ef', boxShadow: 'none', overflow: 'hidden' }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography style={{ fontWeight: 600, color: '#002D55' }}>
                              Assigned Staff at location
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails style={{ display: 'block' }}>
                            {(loc.staff_members_detail || []).length === 0 && (
                              <Typography variant="body2" style={{ color: '#999', marginBottom: 8 }}>No staff assigned to this location yet.</Typography>
                            )}
                            {(loc.staff_members_detail || []).map((staff) => (
                              <Box key={staff.id} display="flex" alignItems="center" style={{ gap: 8, marginBottom: 4 }}>
                                <Chip
                                  label={`${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.email}
                                  variant="outlined"
                                  style={{ borderColor: '#002D55' }}
                                  onDelete={() => handleRemoveStaffFromLocation(loc.id, staff.id)}
                                />
                              </Box>
                            ))}
                            {(() => {
                              const assignedIds = (loc.staff_members_detail || []).map(s => s.id);
                              const available = allStaff.filter(s => !assignedIds.includes(s.id));
                              if (available.length === 0) return null;
                              return (
                                <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 8 }}>
                                  <TextField
                                    select
                                    size="small"
                                    variant="outlined"
                                    label="Add staff member"
                                    value=""
                                    onChange={(e) => handleAddStaffToLocation(loc.id, e.target.value)}
                                    className={classes.orangeFocused}
                                    style={{ minWidth: 200 }}
                                  >
                                    {available.map((s) => (
                                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                    ))}
                                  </TextField>
                                </Box>
                              );
                            })()}
                          </AccordionDetails>
                        </Accordion>

                        {renderRoomDrawerSection(
                          loc,
                          'treatment-rooms',
                          'Treatment Rooms',
                          'Rooms in this drawer are used for treatment bookings and cannot be class rooms.',
                          (loc.rooms || []).filter((room) => room.supports_classes !== true),
                          false,
                        )}

                        {renderRoomDrawerSection(
                          loc,
                          'class-rooms',
                          'Class Rooms',
                          'Rooms in this drawer are reserved for recurring class bookings.',
                          (loc.rooms || []).filter((room) => room.supports_classes === true),
                          true,
                        )}

                        {/* Staff Room Allocations */}
                        <Accordion
                          expanded={expandedLocationSection === `allocations-${loc.id}`}
                          onChange={(_, isExpanded) => setExpandedLocationSection(isExpanded ? `allocations-${loc.id}` : null)}
                          TransitionProps={{ unmountOnExit: true }}
                          style={{ marginBottom: 8, borderRadius: 16, border: '1px solid #e5e9ef', boxShadow: 'none', overflow: 'hidden' }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography style={{ fontWeight: 600, color: '#002D55' }}>Staff Room Allocations</Typography>
                          </AccordionSummary>
                          <AccordionDetails style={{ display: 'block' }}>
                        {(() => {
                          const allocationDraft = allocationDrafts[loc.id] || buildAllocationDraft(loc);
                          const roomFilters = roomScheduleRoomFilters[loc.id] || [];
                          const staffFilters = roomScheduleStaffFilters[loc.id] || [];
                          const cachedLocationData = roomAllocationDataByLocation[loc.id] || {};
                          const selectedScheduleDate = cachedLocationData.selectedScheduleDate || '';
                          const sortedRooms = cachedLocationData.rooms || [];
                          const roomAllocationGroups = cachedLocationData.groups || [];
                          const unassignedAllocations = cachedLocationData.unassignedAllocations || [];

                          return (
                            <Box mt={2}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                                <Typography style={{ fontWeight: 600, color: '#002D55' }}>Staff Room Allocations</Typography>
                                {availabilityLoading ? <CircularProgress size={18} /> : null}
                              </Box>
                              <Typography variant="body2" style={{ color: '#6f7b87', marginBottom: 12 }}>
                                Define recurring weekly room allocations or one-off date overrides for staff working hours in this location.
                              </Typography>
                              <FormControlLabel
                                control={(
                                  <Checkbox
                                    checked={roomScheduleShowClassRooms[loc.id] === true}
                                    onChange={(e) => setRoomScheduleShowClassRooms((prev) => ({
                                      ...prev,
                                      [loc.id]: e.target.checked,
                                    }))}
                                    style={{ color: roomScheduleShowClassRooms[loc.id] ? '#FF7F27' : '#002D55' }}
                                  />
                                )}
                                label={<span style={{ color: '#002D55', fontWeight: 600 }}>Show class rooms</span>}
                                style={{ marginBottom: 8 }}
                              />

                              <Paper
                                variant="outlined"
                                style={{ padding: 16, borderRadius: 12, borderColor: '#dbe7f2', background: '#fafcff', marginBottom: 16 }}
                              >
                                <Typography style={{ fontWeight: 600, color: '#002D55', marginBottom: 12 }}>
                                  New Allocation
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      select
                                      fullWidth
                                      label="Staff member"
                                      variant="outlined"
                                      size="small"
                                      value={allocationDraft.staff_member || ''}
                                      onChange={(e) => handleAllocationDraftChange(loc, 'staff_member', e.target.value)}
                                      className={classes.orangeFocused}
                                    >
                                      {(loc.staff_members_detail || []).map((staff) => (
                                        <MenuItem key={staff.id} value={String(staff.id)}>
                                          {formatCalendarStaffLabel(staff)}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      select
                                      fullWidth
                                      label="Room"
                                      variant="outlined"
                                      size="small"
                                      value={allocationDraft.room || ''}
                                      onChange={(e) => handleAllocationDraftChange(loc, 'room', e.target.value)}
                                      className={classes.orangeFocused}
                                    >
                                      {sortedRooms.map((room) => (
                                        <MenuItem key={room.id} value={String(room.id)}>
                                          {room.name}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      type="color"
                                      label="Color"
                                      variant="outlined"
                                      size="small"
                                      value={allocationDraft.color || getAllocationDefaultColor(allocationDraft.staff_member || '')}
                                      onChange={(e) => handleAllocationDraftChange(loc, 'color', e.target.value)}
                                      className={classes.orangeFocused}
                                      helperText="Pick a distinct staff color for this room allocation."
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      select
                                      fullWidth
                                      label="Recurrence"
                                      variant="outlined"
                                      size="small"
                                      value={allocationDraft.recurrence || 'weekly'}
                                      onChange={(e) => handleAllocationDraftChange(loc, 'recurrence', e.target.value)}
                                      className={classes.orangeFocused}
                                      helperText="Choose how often this allocation repeats."
                                    >
                                      {ALLOCATION_RECURRENCE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                          {option.label}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      select
                                      fullWidth
                                      label="Recurring weekdays"
                                      variant="outlined"
                                      size="small"
                                      value={allocationDraft.day_of_week || []}
                                      onChange={(e) => handleAllocationDraftChange(loc, 'day_of_week', e.target.value)}
                                      className={classes.orangeFocused}
                                      helperText="Select one or more weekdays for the recurring allocation. Overlaps with other allocations are allowed with a warning, but real bookings still block saves."
                                      SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => {
                                          const selectedValues = Array.isArray(selected) ? selected : [selected];
                                          const labels = selectedValues
                                            .map((value) => WEEKDAY_OPTIONS[Number(value)]?.label)
                                            .filter(Boolean);
                                          return labels.length > 0 ? labels.join(', ') : 'Select weekdays';
                                        },
                                      }}
                                    >
                                      {WEEKDAY_OPTIONS.map(({ label }, index) => (
                                        <MenuItem key={label} value={String(index)}>
                                          <Checkbox
                                            checked={(allocationDraft.day_of_week || []).includes(String(index))}
                                            style={{ color: '#FF7F27' }}
                                          />
                                          <Typography variant="body2" style={{ marginLeft: 8 }}>
                                            {label}
                                          </Typography>
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      type="date"
                                      label="Specific date override"
                                      variant="outlined"
                                      size="small"
                                      value={allocationDraft.specific_date || ''}
                                      onChange={(e) => handleAllocationDraftChange(loc, 'specific_date', e.target.value)}
                                      className={classes.orangeFocused}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <TextField
                                      fullWidth
                                      type="time"
                                      label="Start"
                                      variant="outlined"
                                      size="small"
                                      value={allocationDraft.start_time || '09:00'}
                                      onChange={(e) => handleAllocationDraftChange(loc, 'start_time', e.target.value)}
                                      className={classes.orangeFocused}
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ step: 300 }}
                                    />
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <TextField
                                      fullWidth
                                      type="time"
                                      label="End"
                                      variant="outlined"
                                      size="small"
                                      value={allocationDraft.end_time || '17:00'}
                                      onChange={(e) => handleAllocationDraftChange(loc, 'end_time', e.target.value)}
                                      className={classes.orangeFocused}
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ step: 300 }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                      control={(
                                        <Checkbox
                                          checked={allocationDraft.is_available !== false}
                                          onChange={(e) => handleAllocationDraftChange(loc, 'is_available', e.target.checked)}
                                          style={{ color: allocationDraft.is_available !== false ? '#FF7F27' : '#002D55' }}
                                        />
                                      )}
                                      label={<span style={{ color: '#002D55', fontWeight: 600 }}>Available for work</span>}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                      control={(
                                        <Checkbox
                                          checked={allocationDraft.is_bookable !== false}
                                          onChange={(e) => handleAllocationDraftChange(loc, 'is_bookable', e.target.checked)}
                                          style={{ color: allocationDraft.is_bookable !== false ? '#FF7F27' : '#002D55' }}
                                        />
                                      )}
                                      label={<span style={{ color: '#002D55', fontWeight: 600 }}>Bookable interval</span>}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Box display="flex" justifyContent="flex-end" style={{ gap: 8, flexWrap: 'wrap' }}>
                                      <Button
                                        size="small"
                                        onClick={() => setAllocationDrafts((prev) => ({ ...prev, [loc.id]: buildAllocationDraft(loc) }))}
                                        className={classes.cancelButton}
                                      >
                                        Reset
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        className={classes.saveButton}
                                        onClick={() => handleCreateAllocation(loc)}
                                        disabled={sortedRooms.length === 0 || (loc.staff_members_detail || []).length === 0}
                                      >
                                        {String(allocationDraft.editing_slot_id || '') ? 'Update Allocation' : 'Save Allocation'}
                                      </Button>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Paper>

                              <Box className={classes.roomScheduleToolbar}>
                                <Box>
                                  <Typography className={classes.roomScheduleHeader}>Allocation schedule</Typography>
                                  <Typography className={classes.roomScheduleHint}>
                                    The weekly grid shows recurring staff room allocations. Filter by staff or room to focus on a subset, or select a date to merge overrides into the matching weekday column.
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
                                  <TextField
                                    select
                                    label="Rooms"
                                    variant="outlined"
                                    size="small"
                                    value={roomFilters}
                                    onChange={(e) => {
                                      const nextValue = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                                      setRoomScheduleRoomFilters((prev) => ({
                                        ...prev,
                                        [loc.id]: nextValue,
                                      }));
                                    }}
                                    className={classes.orangeFocused}
                                    style={{ minWidth: 180 }}
                                    SelectProps={{
                                      multiple: true,
                                      renderValue: (selected) => {
                                        const selectedIds = Array.isArray(selected) ? selected.map((value) => String(value)) : [];
                                        if (selectedIds.length === 0) {
                                          return 'All rooms';
                                        }

                                        return selectedIds
                                          .map((roomId) => (loc.rooms || []).find((room) => String(room.id) === roomId)?.name || roomId)
                                          .join(', ');
                                      },
                                      MenuProps: { classes: { list: classes.dialogMenuList } },
                                    }}
                                  >
                                    {(loc.rooms || []).map((room) => (
                                      <MenuItem key={room.id} value={String(room.id)}>
                                        <Checkbox
                                          checked={(roomFilters || []).includes(String(room.id))}
                                          style={{ color: '#FF7F27' }}
                                        />
                                        <Typography variant="body2" style={{ marginLeft: 8 }}>
                                          {room.name}
                                        </Typography>
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <TextField
                                    select
                                    label="Staff"
                                    variant="outlined"
                                    size="small"
                                    value={staffFilters}
                                    onChange={(e) => {
                                      const nextValue = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                                      setRoomScheduleStaffFilters((prev) => ({
                                        ...prev,
                                        [loc.id]: nextValue,
                                      }));
                                    }}
                                    className={classes.orangeFocused}
                                    style={{ minWidth: 180 }}
                                    SelectProps={{
                                      multiple: true,
                                      renderValue: (selected) => {
                                        const selectedIds = Array.isArray(selected) ? selected.map((value) => String(value)) : [];
                                        if (selectedIds.length === 0) {
                                          return 'All staff';
                                        }

                                        return selectedIds
                                          .map((staffId) => formatCalendarStaffLabel((loc.staff_members_detail || []).find((staff) => String(staff.id) === staffId) || {}))
                                          .join(', ');
                                      },
                                      MenuProps: { classes: { list: classes.dialogMenuList } },
                                    }}
                                  >
                                    {(loc.staff_members_detail || []).map((staff) => (
                                      <MenuItem key={staff.id} value={String(staff.id)}>
                                        <Checkbox
                                          checked={(staffFilters || []).includes(String(staff.id))}
                                          style={{ color: '#FF7F27' }}
                                        />
                                        <Typography variant="body2" style={{ marginLeft: 8 }}>
                                          {formatCalendarStaffLabel(staff)}
                                        </Typography>
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <TextField
                                    type="date"
                                    label="Preview date"
                                    variant="outlined"
                                    size="small"
                                    value={selectedScheduleDate}
                                    onChange={(e) => setRoomScheduleDates((prev) => ({ ...prev, [loc.id]: e.target.value }))}
                                    className={classes.orangeFocused}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                  <IconButton
                                    size="small"
                                    className={classes.roomWeekNavButton}
                                    aria-label="Previous week"
                                    onClick={() => {
                                      const nextDate = shiftScheduleDateByDays(selectedScheduleDate, -7);
                                      setRoomScheduleDates((prev) => ({ ...prev, [loc.id]: nextDate }));
                                    }}
                                  >
                                    <ChevronLeftIcon fontSize="small" />
                                  </IconButton>
                                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
                                    {selectedScheduleDate
                                      ? `Week ${getScheduleIsoWeekNumber(selectedScheduleDate)}`
                                      : 'Week (current)'}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    className={classes.roomWeekNavButton}
                                    aria-label="Next week"
                                    onClick={() => {
                                      const nextDate = shiftScheduleDateByDays(selectedScheduleDate, 7);
                                      setRoomScheduleDates((prev) => ({ ...prev, [loc.id]: nextDate }));
                                    }}
                                  >
                                    <ChevronRightIcon fontSize="small" />
                                  </IconButton>
                                  {selectedScheduleDate ? (
                                    <Button
                                      size="small"
                                      onClick={() => setRoomScheduleDates((prev) => ({ ...prev, [loc.id]: '' }))}
                                      className={classes.cancelButton}
                                    >
                                      Clear date
                                    </Button>
                                  ) : null}
                                </Box>
                              </Box>

                              {roomAllocationGroups.map(({ room, weekly, specificDateAllocations }) => {
                                const overrideCount = specificDateAllocations.length;
                                const selectedWeekNumber = selectedScheduleDate ? getScheduleIsoWeekNumber(selectedScheduleDate) : null;

                                return (
                                  <Paper
                                    key={room.id}
                                    variant="outlined"
                                    style={{ padding: 16, borderRadius: 12, borderColor: '#dbe7f2', marginBottom: 14 }}
                                  >
                                    <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 12, width: '100%' }}>
                                      <Box style={{ minWidth: 0, flex: '0 0 auto' }}>
                                        <Typography style={{ fontWeight: 700, color: '#002D55' }}>
                                          {room.name}
                                        </Typography>
                                        <Typography variant="body2" style={{ color: '#6f7b87' }}>
                                          {room.is_active !== false ? 'Active room' : 'Inactive room'}
                                        </Typography>
                                      </Box>
                                      <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 8, flex: 1 }}>
                                        <IconButton
                                          size="small"
                                          className={classes.roomWeekNavButton}
                                          aria-label="Previous week"
                                          onClick={() => {
                                            const nextDate = shiftScheduleDateByDays(selectedScheduleDate, -7);
                                            setRoomScheduleDates((prev) => ({ ...prev, [loc.id]: nextDate }));
                                          }}
                                        >
                                          <ChevronLeftIcon fontSize="small" />
                                        </IconButton>
                                        <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
                                          {selectedWeekNumber ? `Week ${selectedWeekNumber}` : 'Week (current)'}
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          className={classes.roomWeekNavButton}
                                          aria-label="Next week"
                                          onClick={() => {
                                            const nextDate = shiftScheduleDateByDays(selectedScheduleDate, 7);
                                            setRoomScheduleDates((prev) => ({ ...prev, [loc.id]: nextDate }));
                                          }}
                                        >
                                          <ChevronRightIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                      <Box style={{ flex: '0 0 auto' }}>
                                        <Chip
                                          label={`${overrideCount} date override${overrideCount === 1 ? '' : 's'}`}
                                          size="small"
                                          variant="outlined"
                                          style={{ borderColor: room.color || '#3174ad', color: '#002D55', fontWeight: 700 }}
                                        />
                                      </Box>
                                    </Box>

                                    <Box className={classes.roomScheduleWrapper}>
                                      <Box className={classes.roomScheduleScroll}>
                                        <Box className={classes.roomScheduleGrid}>
                                          <Box className={classes.roomScheduleCorner} />
                                          {weekly.map(({ weekday, selectedDate }, weekdayIndex) => (
                                            <Box
                                              key={`${room.id}-header-${weekdayIndex}`}
                                              className={classes.roomDayHeader}
                                              style={selectedDate ? { background: '#fff7ef' } : undefined}
                                            >
                                              <Typography className={classes.roomDayHeaderLabel}>{weekday.label}</Typography>
                                              <Typography className={classes.roomDayHeaderSubLabel}>
                                                  {selectedScheduleDate
                                                    ? getScheduleWeekdayDateLabel(selectedScheduleDate, weekdayIndex)
                                                    : 'Recurring template'}
                                              </Typography>
                                            </Box>
                                          ))}

                                          <Box className={classes.roomTimeAxis}>
                                            {ROOM_SCHEDULE_HOUR_MARKERS.map((marker, markerIndex) => (
                                              <Typography
                                                key={`${room.id}-time-${marker}`}
                                                className={classes.roomTimeAxisLabel}
                                                style={{
                                                  top: markerIndex === 0
                                                    ? 10
                                                    : ((marker - ROOM_SCHEDULE_START_MINUTES) / ROOM_SCHEDULE_SLOT_MINUTES) * ROOM_SCHEDULE_ROW_HEIGHT,
                                                }}
                                              >
                                                {formatScheduleMinutesLabel(marker)}
                                              </Typography>
                                            ))}
                                          </Box>

                                          {weekly.map(({ weekday, allocations, selectedDate }, weekdayIndex) => (
                                            <Box
                                              key={`${room.id}-column-${weekdayIndex}`}
                                              className={classes.roomTimeColumn}
                                              onMouseDown={(event) => startRoomScheduleSelection(loc, room, weekdayIndex, selectedDate, event)}
                                              onDragOver={(event) => {
                                                if (!roomAllocationDragRef.current?.allocation) {
                                                  return;
                                                }

                                                event.preventDefault();
                                                event.dataTransfer.dropEffect = 'move';
                                              }}
                                              onDrop={(event) => {
                                                const draggedAllocation = roomAllocationDragRef.current?.allocation;
                                                if (!draggedAllocation || String(draggedAllocation.clinic_location || '') !== String(loc.id)) {
                                                  return;
                                                }

                                                event.preventDefault();
                                                const nextAllocation = buildDraggedAllocationPreview(
                                                  loc,
                                                  draggedAllocation,
                                                  room.id,
                                                  weekdayIndex,
                                                  selectedDate,
                                                  event,
                                                );
                                                void persistRoomAllocationChange(loc, draggedAllocation, nextAllocation, 'Allocation moved.');
                                                roomAllocationClickSuppressedRef.current = true;
                                                roomAllocationDragRef.current = null;
                                              }}
                                              style={selectedDate ? { background: 'linear-gradient(180deg, #fffdf9 0%, #fff7ef 100%)' } : undefined}
                                            >
                                              <RoomScheduleSlotBackground
                                                slotClassName={classes.roomTimeSlot}
                                                hourSlotClassName={classes.roomTimeSlotHour}
                                              />

                                              {roomScheduleSelectionPreview
                                                && String(roomScheduleSelectionPreview.locationId) === String(loc.id)
                                                && String(roomScheduleSelectionPreview.roomId) === String(room.id)
                                                && roomScheduleSelectionPreview.weekdayIndex === weekdayIndex ? (
                                                  <Box
                                                    className={classes.roomSelectionPreview}
                                                    style={getRoomAllocationBlockLayout({
                                                      start_time: formatScheduleMinutesLabel(roomScheduleSelectionPreview.startMinutes),
                                                      end_time: formatScheduleMinutesLabel(roomScheduleSelectionPreview.endMinutes),
                                                    })}
                                                  >
                                                    <Typography className={classes.roomSelectionPreviewLabel}>
                                                      {formatScheduleMinutesLabel(roomScheduleSelectionPreview.startMinutes)} - {formatScheduleMinutesLabel(roomScheduleSelectionPreview.endMinutes)}
                                                    </Typography>
                                                  </Box>
                                                ) : null}

                                              {allocations.length === 0 ? (
                                                <Typography
                                                  variant="body2"
                                                  style={{ position: 'absolute', top: 8, left: 10, color: '#a0afbf', fontSize: 12 }}
                                                >
                                                  No allocation
                                                </Typography>
                                              ) : null}

                                              {allocations.map((allocation) => {
                                                const displayedAllocation = roomAllocationResizePreview
                                                  && String(roomAllocationResizePreview.id || '') === String(allocation.id || '')
                                                  ? roomAllocationResizePreview
                                                  : allocation;
                                                const layout = getRoomAllocationBlockLayout(displayedAllocation);
                                                if (!layout) {
                                                  return null;
                                                }

                                                const staff = getAllocationStaff(allocation, loc);
                                                const available = allocation.is_available !== false;
                                                const isBookable = allocation.is_bookable !== false;
                                                const isOverride = allocation._scheduleSource === 'override';
                                                const roomColor = normalizeAllocationColor(displayedAllocation.color)
                                                  || getAllocationDefaultColor(staff?.id || allocation.staff_member || '')
                                                  || '#3174ad';

                                                return (
                                                  <Box
                                                    key={`${allocation.id}-${allocation._scheduleSource || 'weekly'}`}
                                                    className={`${classes.roomAllocationCard} ${isOverride ? classes.roomAllocationCardOverride : ''}`}
                                                    data-room-allocation-card="true"
                                                    draggable
                                                    role="button"
                                                    tabIndex={0}
                                                    title="Click to edit or drag to another time slot"
                                                    onMouseDown={(event) => handleRoomAllocationLongPressStart(loc, allocation, event)}
                                                    onMouseMove={cancelRoomAllocationLongPress}
                                                    onMouseUp={cancelRoomAllocationLongPress}
                                                    onTouchStart={(event) => handleRoomAllocationLongPressStart(loc, allocation, event)}
                                                    onTouchMove={cancelRoomAllocationLongPress}
                                                    onTouchEnd={cancelRoomAllocationLongPress}
                                                    onClick={() => {
                                                      if (roomAllocationClickSuppressedRef.current) {
                                                        roomAllocationClickSuppressedRef.current = false;
                                                        return;
                                                      }

                                                      handleEditAllocation(loc, allocation);
                                                    }}
                                                    onKeyDown={(event) => {
                                                      if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        handleEditAllocation(loc, allocation);
                                                      }
                                                    }}
                                                    onDragStart={(event) => {
                                                      roomAllocationDragRef.current = { locationId: loc.id, allocation };
                                                      roomAllocationClickSuppressedRef.current = true;
                                                      event.dataTransfer.effectAllowed = 'move';
                                                      event.dataTransfer.setData('text/plain', String(allocation.id || ''));
                                                    }}
                                                    onDragEnd={() => {
                                                      cancelRoomAllocationLongPress();
                                                      roomAllocationDragRef.current = null;
                                                    }}
                                                    style={{
                                                      top: layout.top,
                                                      height: layout.height,
                                                      '--room-allocation-color': roomColor,
                                                      '--room-allocation-background': colorWithScheduleAlpha(
                                                        roomColor,
                                                        available ? (isBookable ? 0.18 : 0.11) : 0.07,
                                                      ),
                                                      opacity: available ? 1 : 0.82,
                                                    }}
                                                  >
                                                    <Box
                                                      className={`${classes.roomAllocationResizeHandle} ${classes.roomAllocationResizeHandleTop}`}
                                                      data-room-allocation-resize="true"
                                                      onMouseDown={(event) => startRoomAllocationResize(event, loc, allocation, 'start')}
                                                    >
                                                      {Array.from({ length: 3 }, (_, gripIndex) => (
                                                        <Box key={`top-grip-${allocation.id}-${gripIndex}`} className={classes.roomAllocationResizeGrip} />
                                                      ))}
                                                    </Box>
                                                    <Box className={classes.roomAllocationActions} data-room-allocation-action="true">
                                                      <Button
                                                        size="small"
                                                        variant="contained"
                                                        className={classes.roomAllocationActionButton}
                                                        onClick={(event) => {
                                                          event.stopPropagation();
                                                          handleEditAllocation(loc, allocation);
                                                        }}
                                                      >
                                                        Edit
                                                      </Button>
                                                      <Button
                                                        size="small"
                                                        variant="outlined"
                                                        className={classes.roomAllocationActionButton}
                                                        style={{ color: '#c62828', borderColor: 'rgba(198,40,40,0.35)', background: 'rgba(255,255,255,0.82)' }}
                                                        onClick={(event) => {
                                                          event.stopPropagation();
                                                          if (window.confirm('Delete this allocation?')) {
                                                            handleDeleteAllocation(allocation.id);
                                                          }
                                                        }}
                                                      >
                                                        Delete
                                                      </Button>
                                                    </Box>
                                                    <Typography className={classes.roomAllocationTitle}>
                                                      {formatCalendarStaffOverlayLabel(staff || {})}
                                                    </Typography>
                                                    <Typography className={classes.roomAllocationMeta}>
                                                      {formatAllocationTimeRange(displayedAllocation)}
                                                    </Typography>
                                                    <Typography className={classes.roomAllocationMeta}>
                                                      {available ? 'Available' : 'Unavailable'}{isBookable ? '' : ' | Not bookable'}{isOverride ? ' | Override' : ''}
                                                    </Typography>
                                                    <Box
                                                      className={`${classes.roomAllocationResizeHandle} ${classes.roomAllocationResizeHandleBottom}`}
                                                      data-room-allocation-resize="true"
                                                      onMouseDown={(event) => startRoomAllocationResize(event, loc, allocation, 'end')}
                                                    >
                                                      {Array.from({ length: 3 }, (_, gripIndex) => (
                                                        <Box key={`bottom-grip-${allocation.id}-${gripIndex}`} className={classes.roomAllocationResizeGrip} />
                                                      ))}
                                                    </Box>
                                                  </Box>
                                                );
                                              })}
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>

                                      {specificDateAllocations.length > 0 ? (
                                        <Box className={classes.roomSpecificDateSection}>
                                          {/*<Typography className={classes.roomSpecificDateLabel}>
                                            {selectedScheduleDate
                                              ? 'Selected date merges matching overrides into the highlighted weekday.'
                                              : 'Select a preview date to merge saved overrides into the weekly grid.'}
                                          </Typography>*/}
                                          {selectedScheduleDate ? (
                                            <Typography variant="body2" style={{ color: '#6f7b87' }}>
                                              Week {getScheduleIsoWeekNumber(selectedScheduleDate)}: {getScheduleWeekRangeLabel(selectedScheduleDate)}
                                            </Typography>
                                          ) : null}
                                        </Box>
                                      ) : null}
                                    </Box>
                                  </Paper>
                                );
                              })}

                              {unassignedAllocations.length > 0 && (
                                <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, borderColor: '#f1d7d7', background: '#fcf7f7', marginBottom: 14 }}>
                                  <Typography style={{ fontWeight: 700, color: '#8a4b15', marginBottom: 8 }}>
                                    Unassigned room allocations
                                  </Typography>
                                  <Typography variant="body2" style={{ color: '#6f7b87', marginBottom: 12 }}>
                                    These allocations are missing a room reference and should be reassigned.
                                  </Typography>
                                  <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                                    {unassignedAllocations.map((allocation) => {
                                      const staff = getAllocationStaff(allocation, loc);
                                      return (
                                        <Paper key={allocation.id} variant="outlined" style={{ padding: '8px 10px', borderRadius: 10, borderColor: '#f1d7d7', background: '#fff' }}>
                                          <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 13 }}>
                                            {formatCalendarStaffLabel(staff || {})}
                                          </Typography>
                                          <Typography variant="body2" style={{ color: '#6f7b87', fontSize: 12 }}>
                                            {allocation.specific_date || getAllocationDayLabel(allocation)} | {formatAllocationTimeRange(allocation)}
                                          </Typography>
                                        </Paper>
                                      );
                                    })}
                                  </Box>
                                </Paper>
                              )}
                            </Box>
                          );
                        })()}
                          </AccordionDetails>
                        </Accordion>

                        {/* Added Calendars */}
                        <Accordion
                          expanded={expandedLocationSection === `calendars-${loc.id}`}
                          onChange={(_, isExpanded) => setExpandedLocationSection(isExpanded ? `calendars-${loc.id}` : null)}
                          TransitionProps={{ unmountOnExit: true }}
                          style={{ marginBottom: 8, borderRadius: 16, border: '1px solid #e5e9ef', boxShadow: 'none', overflow: 'hidden' }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography style={{ fontWeight: 600, color: '#002D55' }}>Added Calendars</Typography>
                          </AccordionSummary>
                          <AccordionDetails style={{ display: 'block' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                              <Typography style={{ fontWeight: 600, color: '#002D55' }}>Staff calendar filters</Typography>
                              <Button
                                size="small"
                                variant="contained"
                                className={classes.saveButton}
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenCalendarDraft(loc)}
                              >
                                Add Group
                              </Button>
                            </Box>

                          {(loc.calendars || []).length === 0 && (
                            <Typography variant="body2" style={{ color: '#999', marginBottom: 8 }}>
                              No staff calendar filters configured yet.
                            </Typography>
                          )}

                          {(loc.calendars || []).map((calendar) => (
                            <Paper
                              key={calendar.id}
                              variant="outlined"
                              style={{ padding: 12, marginBottom: 8, borderRadius: 10, borderColor: '#dbe7f2' }}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 12 }}>
                                <Box style={{ flex: 1 }}>
                                  <Typography style={{ fontWeight: 600, color: '#002D55' }}>
                                    {calendar.name || 'Unnamed Calendar'}
                                  </Typography>
                                  <Typography variant="body2" style={{ color: '#6f7b87', marginTop: 4 }}>
                                    Order {calendar.order || 0} | {(calendar.allowed_staff_detail || []).length} staff
                                  </Typography>
                                  <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginTop: 8 }}>
                                    {(calendar.allowed_staff_detail || []).length > 0 ? (
                                      (calendar.allowed_staff_detail || []).map((staff) => (
                                        <Chip
                                          key={staff.id}
                                          label={formatCalendarStaffLabel(staff)}
                                          size="small"
                                          variant="outlined"
                                          style={{ borderColor: '#FF7F27' }}
                                        />
                                      ))
                                    ) : (
                                      <Chip
                                        label="No allowed staff"
                                        size="small"
                                        variant="outlined"
                                        style={{ borderColor: '#d0d7e2' }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                <Box display="flex" style={{ gap: 6, flexShrink: 0 }}>
                                  <Button size="small" onClick={() => handleOpenCalendarDraft(loc, calendar)} style={{ color: '#002D55' }}>
                                    Edit
                                  </Button>
                                  <Button size="small" onClick={() => handleDeleteCalendar(loc.id, calendar.id)} style={{ color: '#e53935' }}>
                                    Delete
                                  </Button>
                                </Box>
                              </Box>
                            </Paper>
                          ))}

                          {calendarDraft?.locationId === String(loc.id) && (
                            <Paper
                              variant="outlined"
                              style={{ padding: 16, borderRadius: 12, borderColor: '#FF7F27', background: '#fffaf5', marginTop: 8 }}
                            >
                              <Typography style={{ fontWeight: 600, color: '#002D55', marginBottom: 12 }}>
                                {calendarDraft.calendarId ? 'Edit Group' : 'New Group'}
                              </Typography>

                              {calendarError && (
                                <Typography variant="body2" style={{ color: '#d32f2f', marginBottom: 12 }}>
                                  {calendarError}
                                </Typography>
                              )}

                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    label="Group Name"
                                    variant="outlined"
                                    size="small"
                                    value={calendarDraft.name}
                                    onChange={(e) => setCalendarDraft((prev) => ({ ...prev, name: e.target.value }))}
                                    className={classes.orangeFocused}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                  <TextField
                                    fullWidth
                                    label="Order"
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    value={calendarDraft.order}
                                    onChange={(e) => setCalendarDraft((prev) => ({ ...prev, order: e.target.value }))}
                                    className={classes.orangeFocused}
                                    inputProps={{ min: 0, step: 1 }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    select
                                    fullWidth
                                    label="Show Staff Calendars"
                                    variant="outlined"
                                    size="small"
                                    value={calendarDraft.allowedStaffIds}
                                    onChange={handleCalendarAllowedStaffChange}
                                    className={classes.orangeFocused}
                                    helperText={(loc.staff_members_detail || []).length === 0
                                      ? 'Assign staff to this location before creating a group.'
                                      : 'This filter preset only controls which staff calendars appear in Calendar.'}
                                    SelectProps={{
                                      multiple: true,
                                      renderValue: (selected) => {
                                        const selectedIds = Array.isArray(selected) ? selected.map((value) => String(value)) : [];
                                        if (selectedIds.length === 0) {
                                          return 'Select staff';
                                        }

                                        return selectedIds
                                          .map((staffId) => formatCalendarStaffLabel((loc.staff_members_detail || []).find((staff) => String(staff.id) === staffId) || {}))
                                          .join(', ');
                                      },
                                      MenuProps: { classes: { list: classes.dialogMenuList } },
                                    }}
                                  >
                                    {(loc.staff_members_detail || []).map((staff) => (
                                      <MenuItem key={staff.id} value={String(staff.id)}>
                                        <Checkbox
                                          checked={(calendarDraft.allowedStaffIds || []).includes(String(staff.id))}
                                          style={{ color: '#FF7F27' }}
                                        />
                                        <Typography variant="body2" style={{ marginLeft: 8 }}>
                                          {formatCalendarStaffLabel(staff)}
                                        </Typography>
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box display="flex" justifyContent="flex-end" style={{ gap: 8, flexWrap: 'wrap' }}>
                                    {calendarDraft.calendarId && (
                                      <Button
                                        size="small"
                                        onClick={() => handleDeleteCalendar(calendarDraft.locationId, calendarDraft.calendarId)}
                                        style={{ color: '#e53935' }}
                                      >
                                        Delete Group
                                      </Button>
                                    )}
                                    <Button size="small" onClick={handleCancelCalendarDraft} className={classes.cancelButton} disabled={calendarSaving}>
                                      Cancel
                                    </Button>
                                    <Button size="small" variant="contained" className={classes.saveButton} onClick={handleSaveCalendarDraft} disabled={calendarSaving}>
                                      {calendarSaving ? 'Saving...' : 'Save Group'}
                                    </Button>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Paper>
                          )}
                          </AccordionDetails>
                        </Accordion>

                      </AccordionDetails>
                    </Accordion>
                    ))}
                    {/* Add Location inline form */}
                    {newLocation && (
                      <Box mt={2} p={2} style={{ border: '1px dashed #FF7F27', borderRadius: 8, background: '#fffaf5' }}>
                        <Typography style={{ fontWeight: 600, color: '#002D55', marginBottom: 12 }}>New Location</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Location Name" variant="outlined" size="small" value={newLocation.name}
                              onChange={(e) => setNewLocation((prev) => ({ ...prev, name: e.target.value }))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Address" variant="outlined" size="small" value={newLocation.address}
                              onChange={(e) => setNewLocation((prev) => ({ ...prev, address: e.target.value }))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField fullWidth label="City" variant="outlined" size="small" value={newLocation.city}
                              onChange={(e) => setNewLocation((prev) => ({ ...prev, city: e.target.value }))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField fullWidth label="Postal Code" variant="outlined" size="small" value={newLocation.postal_code}
                              onChange={(e) => setNewLocation((prev) => ({ ...prev, postal_code: e.target.value }))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField fullWidth label="Phone" variant="outlined" size="small" value={newLocation.phone}
                              onChange={(e) => setNewLocation((prev) => ({ ...prev, phone: e.target.value }))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField fullWidth label="Email" variant="outlined" size="small" value={newLocation.email}
                              onChange={(e) => setNewLocation((prev) => ({ ...prev, email: e.target.value }))}
                              className={classes.orangeFocused} />
                          </Grid>
                          <Grid item xs={12}>
                            <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                              <Button size="small" onClick={() => setNewLocation(null)} style={{ color: '#888' }}>Cancel</Button>
                              <Button size="small" variant="contained" className={classes.saveButton}
                                disabled={saving || !newLocation.name.trim()}
                                onClick={async () => {
                                  const savedLocation = await handleSaveLocation(newLocation);
                                  if (savedLocation) {
                                    setNewLocation(null);
                                  }
                                }}>
                                Create Location
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    <Divider className={classes.footerDivider} />
                    <Box className={classes.footerActions}>
                      <Button variant="contained" className={classes.saveButton}
                        onClick={() => setNewLocation({ name: '', address: '', city: '', postal_code: '', country: '', phone: '', email: '' })}
                        startIcon={<AddIcon />}
                        disabled={!!newLocation}>
                        Add Location
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* ======= RESOURCE UTILIZATION TAB ======= */}
          {activeTab === 'utilization' && (
            <Box className={classes.tabContent}>
              <Box className={classes.settingSection}>
                <Typography className={classes.sectionTitle}>
                  <BusinessIcon style={{ color: '#FF7F27' }} />
                  Resource Utilization
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 16, flexWrap: 'wrap' }}>
                  <Typography className={classes.description} style={{ marginBottom: 16, flex: '1 1 480px' }}>
                    Measure how effectively your calendar resources are used overall and per room,
                    including the unused (idle) hours. Choose a calculation basis, time period, and
                    planning sandbox below to visualize staffing capacity.
                  </Typography>
                  <Tabs
                    value={utilizationPanelTab}
                    onChange={(_, value) => setUtilizationPanelTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    className={classes.tabsControlRoot}
                    style={{ minHeight: 38, height: 38 }}
                    TabIndicatorProps={{ className: classes.tabsIndicator }}
                  >
                    <Tab value="utilization" label="Utilization" className={classes.tabRoot} />
                    <Tab value="vacation" label="Absence planner" className={classes.tabRoot} />
                  </Tabs>
                </Box>

                {utilizationPanelTab === 'utilization' && (
                  <>
                    <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                      <Typography className={classes.description} style={{ margin: 0 }}>
                        {utilizationReport.basisOption.description}
                        {' '}Period: {utilizationReport.startDateValue} ? {utilizationReport.endDateValue}.
                      </Typography>
                      {utilizationBasis !== 'allocation_vs_opening' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={fetchUtilizationBookings}
                          disabled={utilizationBookingsLoading}
                          style={{ borderColor: '#FF7F27', color: '#FF7F27' }}
                        >
                          {utilizationBookingsLoading ? 'Refreshing...' : 'Refresh bookings'}
                        </Button>
                      )}
                    </Box>

                    {!(utilizationBasis !== 'allocation_vs_opening' && utilizationBookingsLoading) && utilizationReport.hasOpenCapacity && (
                      <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, marginBottom: 20 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="baseline">
                          <Typography style={{ fontWeight: 700, color: '#002D55' }}>Overall utilization</Typography>
                          <Typography style={{ fontWeight: 700, color: '#FF7F27', fontSize: 20 }}>
                            {utilizationReport.totals.utilizationPct}%
                          </Typography>
                        </Box>
                        <Box style={{ marginTop: 8, height: 10, borderRadius: 6, background: '#eef2f6', overflow: 'hidden' }}>
                          <Box style={{ width: `${utilizationReport.totals.utilizationPct}%`, height: '100%', background: '#FF7F27' }} />
                        </Box>
                      </Paper>
                    )}

                    <Grid container spacing={2} style={{ marginBottom: 8 }}>
                      <Grid item xs={12} md={5}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          variant="outlined"
                          label="Calculation basis"
                          value={utilizationBasis}
                          onChange={(e) => setUtilizationBasis(e.target.value)}
                          InputProps={{ classes: { focused: classes.orangeFocused } }}
                          className={classes.orangeFocused}
                        >
                          {UTILIZATION_BASIS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          variant="outlined"
                          label="Time period"
                          value={utilizationPeriod}
                          onChange={(e) => setUtilizationPeriod(e.target.value)}
                          InputProps={{ classes: { focused: classes.orangeFocused } }}
                          className={classes.orangeFocused}
                        >
                          {UTILIZATION_PERIOD_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          variant="outlined"
                          label="Location"
                          value={utilizationLocationId}
                          onChange={(e) => setUtilizationLocationId(e.target.value)}
                          InputProps={{ classes: { focused: classes.orangeFocused } }}
                          className={classes.orangeFocused}
                        >
                          <MenuItem value="">All locations</MenuItem>
                          {(locations || []).map((loc) => (
                            <MenuItem key={loc.id} value={String(loc.id)}>{loc.name || 'Unnamed location'}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>

                    <Paper variant="outlined" style={{ padding: 16, borderRadius: 14, borderColor: '#dbe7f2', background: '#fafcff', marginBottom: 12 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                        <Box>
                          <Typography style={{ fontWeight: 700, color: '#002D55' }}>Settings</Typography>
                          <Typography className={classes.description} style={{ margin: 0 }}>
                            Select weekdays and time range to test for room capacity.
                          </Typography>
                        </Box>
                        <Typography style={{ fontWeight: 700, color: '#2e7d32', fontSize: 13 }}>
                          {normalizeUtilizationSandboxWeekdays(utilizationSandboxWeekdaysDraft)
                            .map((weekdayKey) => WEEKDAY_OPTIONS.find((option) => option.key === weekdayKey)?.label || weekdayKey)
                            .join(', ')}
                          {' '}| {utilizationSandboxStartTimeDraft} - {utilizationSandboxEndTimeDraft}
                        </Typography>
                      </Box>

                      <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 12 }}>
                        {WEEKDAY_OPTIONS.map((option) => {
                          const checked = normalizeUtilizationSandboxWeekdays(utilizationSandboxWeekdaysDraft).includes(option.key);
                          return (
                            <FormControlLabel
                              key={`sandbox-day-${option.key}`}
                              control={(
                                <Checkbox
                                  style={{ color: '#FF7F27' }}
                                  checked={checked}
                                  onChange={() => handleUtilizationSandboxWeekdayToggle(option.key)}
                                />
                              )}
                              label={option.label}
                              style={{ marginRight: 0 }}
                            />
                          );
                        })}
                      </Box>

                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="time"
                            label="Start time"
                            value={utilizationSandboxStartTimeDraft}
                            onChange={(e) => setUtilizationSandboxStartTimeDraft(e.target.value)}
                            className={classes.orangeFocused}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="time"
                            label="End time"
                            value={utilizationSandboxEndTimeDraft}
                            onChange={(e) => setUtilizationSandboxEndTimeDraft(e.target.value)}
                            className={classes.orangeFocused}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography className={classes.description} style={{ margin: 0 }}>
                            The utilization heatmap below only counts capacity inside this sandbox, so it can be used as a visual hiring guide.
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="number"
                            label="Continuity min hours"
                            value={utilizationContinuityMinHoursDraft}
                            onChange={(e) => setUtilizationContinuityMinHoursDraft(e.target.value)}
                            className={classes.orangeFocused}
                            inputProps={{ min: 0, step: 0.5 }}
                            helperText="Rule threshold for continuity totals"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="number"
                            label="Max gap (minutes)"
                            value={utilizationContinuityMaxGapMinutesDraft}
                            onChange={(e) => setUtilizationContinuityMaxGapMinutesDraft(e.target.value)}
                            className={classes.orangeFocused}
                            inputProps={{ min: 0, step: 5 }}
                            helperText="Intervals within this gap are merged"
                          />
                        </Grid>
                        {utilizationSettingsDirty && (
                          <Grid item xs={12}>
                            <Box display="flex" style={{ gap: 8, marginTop: 4 }}>
                              <Button
                                fullWidth
                                variant="contained"
                                className={classes.saveButton}
                                onClick={applyUtilizationHeatmapSettings}
                                style={{ flex: 1 }}
                              >
                                Update heatmaps
                              </Button>
                              <Button
                                fullWidth
                                variant="outlined"
                                className={classes.cancelButton}
                                onClick={resetUtilizationHeatmapSettingsDraft}
                                style={{ flex: 1 }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>

                    {utilizationBasis !== 'allocation_vs_opening' && utilizationBookingsError && (
                      <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, borderColor: '#ffd4bf', background: '#fff8f3', marginBottom: 12 }}>
                        <Typography style={{ color: '#8a4b15', fontWeight: 600 }}>{utilizationBookingsError}</Typography>
                      </Paper>
                    )}

                    {utilizationBasis !== 'allocation_vs_opening' && utilizationBookingsLoading ? (
                      <Box display="flex" justifyContent="center" p={3}><CircularProgress size={28} /></Box>
                    ) : !utilizationReport.hasOpenCapacity ? (
                      <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, borderColor: '#d6e4f0', background: '#f4f9fd' }}>
                        <Typography style={{ color: '#002D55', fontWeight: 600 }}>
                          No capacity to report for this period.
                        </Typography>
                        <Typography className={classes.description} style={{ marginTop: 4 }}>
                          Adjust the sandbox weekdays or time range, and make sure the selected location has active rooms.
                        </Typography>
                      </Paper>
                    ) : (
                      <>
                    {/* Overall summary */}
                    {/*<Grid container spacing={2} style={{ marginBottom: 16 }}>
                      {[
                        { label: utilizationReport.basisOption.capacityLabel, minutes: utilizationReport.totals.capacityMinutes, color: '#002D55' },
                        { label: utilizationReport.basisOption.usedLabel, minutes: utilizationReport.totals.usedMinutes, color: '#1f8b4c' },
                        { label: utilizationReport.basisOption.unusedLabel, minutes: utilizationReport.totals.unusedMinutes, color: '#c0392b' },
                      ].map((card) => (
                        <Grid item xs={12} sm={4} key={card.label}>
                          <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, height: '100%' }}>
                            <Typography style={{ fontSize: 12, color: '#6f7b87', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                              {card.label}
                            </Typography>
                            <Typography style={{ fontSize: 26, fontWeight: 700, color: card.color, marginTop: 4 }}>
                              {formatUtilizationHours(card.minutes)}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>*/}

                    {/* Room-based breakdown */}
                    {/*<Box style={{ marginTop: 24 }}>
                      <Typography style={{ fontWeight: 700, color: '#002D55', marginBottom: 4 }}>
                        Room-based utilization
                      </Typography>
                      <Typography className={classes.description} style={{ marginBottom: 10 }}>
                        Detailed utilization grouped by location, then by room.
                      </Typography>

                      {utilizationReport.roomGroups.length === 0 ? (
                        <Typography className={classes.description}>No active rooms in scope.</Typography>
                      ) : (
                        utilizationReport.roomGroups.map((group) => (
                          <Paper key={group.locationId} variant="outlined" style={{ padding: 16, borderRadius: 12, marginBottom: 12, borderColor: '#dbe7f2' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                              <Box style={{ minWidth: 0 }}>
                                <Typography style={{ fontWeight: 700, color: '#002D55' }} noWrap>
                                  {group.locationName}
                                </Typography>
                                <Typography style={{ fontSize: 12, color: '#6f7b87' }} noWrap>
                                  {group.rooms.length} room{group.rooms.length === 1 ? '' : 's'}
                                </Typography>
                              </Box>
                              <Typography style={{ fontWeight: 700, color: '#FF7F27' }}>{group.utilizationPct}%</Typography>
                            </Box>
                            <Box display="flex" style={{ gap: 18, marginBottom: 12, flexWrap: 'wrap' }}>
                              <Typography style={{ fontSize: 12, color: '#6f7b87' }}>
                                {utilizationReport.basisOption.capacityLabel}: <strong style={{ color: '#002D55' }}>{formatUtilizationHours(group.capacityMinutes)}</strong>
                              </Typography>
                              <Typography style={{ fontSize: 12, color: '#6f7b87' }}>
                                {utilizationReport.basisOption.usedLabel}: <strong style={{ color: '#1f8b4c' }}>{formatUtilizationHours(group.usedMinutes)}</strong>
                              </Typography>
                              <Typography style={{ fontSize: 12, color: '#6f7b87' }}>
                                {utilizationReport.basisOption.unusedLabel}: <strong style={{ color: '#c0392b' }}>{formatUtilizationHours(group.unusedMinutes)}</strong>
                              </Typography>
                            </Box>
                            <Box style={{ marginBottom: 14, height: 8, borderRadius: 5, background: '#eef2f6', overflow: 'hidden' }}>
                              <Box style={{ width: `${group.utilizationPct}%`, height: '100%', background: '#FF7F27' }} />
                            </Box>

                            {group.rooms.map((room) => (
                              <Paper key={room.key} variant="outlined" style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 8, background: '#fff' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12, flexWrap: 'wrap' }}>
                                  <Box display="flex" alignItems="center" style={{ gap: 10, minWidth: 0 }}>
                                    <Box style={{ width: 12, height: 12, borderRadius: 3, background: room.roomColor, flexShrink: 0 }} />
                                    <Typography style={{ fontWeight: 600, color: '#002D55' }} noWrap>{room.roomName}</Typography>
                                  </Box>
                                  <Typography style={{ fontWeight: 700, color: '#FF7F27' }}>{room.utilizationPct}%</Typography>
                                </Box>
                                <Box style={{ marginTop: 8, height: 8, borderRadius: 5, background: '#eef2f6', overflow: 'hidden' }}>
                                  <Box style={{ width: `${room.utilizationPct}%`, height: '100%', background: '#FF7F27' }} />
                                </Box>
                                <Box display="flex" style={{ gap: 18, marginTop: 8, flexWrap: 'wrap' }}>
                                  <Typography style={{ fontSize: 12, color: '#6f7b87' }}>
                                    {utilizationReport.basisOption.capacityLabel}: <strong style={{ color: '#002D55' }}>{formatUtilizationHours(room.capacityMinutes)}</strong>
                                  </Typography>
                                  <Typography style={{ fontSize: 12, color: '#6f7b87' }}>
                                    {utilizationReport.basisOption.usedLabel}: <strong style={{ color: '#1f8b4c' }}>{formatUtilizationHours(room.usedMinutes)}</strong>
                                  </Typography>
                                  <Typography style={{ fontSize: 12, color: '#6f7b87' }}>
                                    {utilizationReport.basisOption.unusedLabel}: <strong style={{ color: '#c0392b' }}>{formatUtilizationHours(room.unusedMinutes)}</strong>
                                  </Typography>
                                </Box>
                              </Paper>
                            ))}
                          </Paper>
                        ))
                      )}
                    </Box>*/}

                    {/* Weekly unused-hours breakdown */}
                    {/*
                    {utilizationReport.weeklyBreakdown.length > 0 && (
                      <Box style={{ marginTop: 24 }}>
                        <Typography style={{ fontWeight: 700, color: '#002D55', marginBottom: 4 }}>
                          Unused hours per week
                        </Typography>
                        <Typography className={classes.description} style={{ marginBottom: 10 }}>
                          Week-by-week idle (unused) hours across the selected period.
                        </Typography>
                        {utilizationReport.weeklyBreakdown.map((week) => (
                          <Paper key={week.weekStart} variant="outlined" style={{ padding: '10px 16px', borderRadius: 12, marginBottom: 8 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12, flexWrap: 'wrap' }}>
                              <Box style={{ minWidth: 0 }}>
                                <Typography style={{ fontWeight: 600, color: '#002D55' }} noWrap>
                                  Week {week.weekNumber}
                                </Typography>
                                <Typography style={{ fontSize: 12, color: '#6f7b87' }} noWrap>{week.rangeLabel}</Typography>
                              </Box>
                              <Box display="flex" alignItems="center" style={{ gap: 18, flexWrap: 'wrap' }}>
                                <Typography style={{ fontSize: 12, color: '#6f7b87' }}>
                                  Unused: <strong style={{ color: '#c0392b' }}>{formatUtilizationHours(week.unusedMinutes)}</strong>
                                </Typography>
                                <Typography style={{ fontSize: 12, color: '#6f7b87' }}>
                                  Used: <strong style={{ color: '#1f8b4c' }}>{formatUtilizationHours(week.usedMinutes)}</strong>
                                </Typography>
                                <Typography style={{ fontWeight: 700, color: '#FF7F27' }}>{week.utilizationPct}%</Typography>
                              </Box>
                            </Box>
                            <Box style={{ marginTop: 8, height: 8, borderRadius: 5, background: '#eef2f6', overflow: 'hidden' }}>
                              <Box style={{ width: `${week.utilizationPct}%`, height: '100%', background: '#FF7F27' }} />
                            </Box>
                          </Paper>
                        ))}
                      </>
                    )}
                    */}

                    {/* Weekday-by-time unused-capacity heatmap */}
                    {utilizationReport.timeBuckets.length > 0 && utilizationReport.weekdayGrid.length > 0 && (
                      <Box style={{ marginTop: 24 }}>
                        {/*<Typography style={{ fontWeight: 700, color: '#002D55', marginBottom: 4 }}>
                          Unused capacity by weekday &amp; time ({utilizationReport.scopedLocationName})
                        </Typography>
                        <Typography className={classes.description} style={{ marginBottom: 10 }}>
                          Summary is scoped to the opened location drawer (primary location by default).
                          Continuity is calculated per room only using your settings threshold.
                          Combined continuity below always shows all non-overlapping intervals across rooms,
                          even when they are shorter than the minimum threshold.
                        </Typography>

                        <Paper variant="outlined" style={{ padding: '10px 14px', borderRadius: 12, borderColor: '#cdeccf', background: '#f1fbf2', marginBottom: 12 }}>
                          <Typography style={{ color: '#2e7d32', fontWeight: 700, fontSize: 13 }}>
                            Combined continuity free (current settings, non-overlapping): {formatUtilizationHours(utilizationReport.combinedContinuityMinutesTotal)}
                          </Typography>
                          {(utilizationReport.combinedContinuityByWeekday || []).length > 0 ? (
                            <Typography style={{ color: '#2e7d32', fontSize: 12, marginTop: 4 }}>
                              {(utilizationReport.combinedContinuityByWeekday || [])
                                .map((entry) => `${entry.weekdayLabel}: ${formatUtilizationHours(entry.continuityMinutes)}${entry.intervalLabels.length ? ` (${entry.intervalLabels.join(', ')})` : ''}`)
                                .join(' | ')}
                            </Typography>
                          ) : (
                            <Typography style={{ color: '#2e7d32', fontSize: 12, marginTop: 4 }}>
                              No rule-based continuity windows currently match your settings.
                            </Typography>
                          )}
                          {(utilizationReport.combinedContinuityBookingGuide || []).length > 0 ? (
                            <Typography style={{ color: '#2e7d32', fontSize: 11, marginTop: 6 }}>
                              Booking guide: {(utilizationReport.combinedContinuityBookingGuide || []).join(' | ')}
                            </Typography>
                          ) : null}
                          <Typography style={{ color: '#2e7d32', fontSize: 11, marginTop: 6 }}>
                            All intervals regardless minimum (max gap {utilizationReport.continuityRules.maxGapMinutes}m): {formatUtilizationHours(utilizationReport.combinedAllContinuityMinutesTotal)}
                          </Typography>
                          {(utilizationReport.combinedAllContinuityByWeekday || []).length > 0 ? (
                            <Typography style={{ color: '#2e7d32', fontSize: 11, marginTop: 4 }}>
                              {(utilizationReport.combinedAllContinuityByWeekday || [])
                                .map((entry) => `${entry.weekdayLabel}: ${entry.intervalCompactLabels.length ? entry.intervalCompactLabels.join(' ') : ''}`)
                                .join(' | ')}
                            </Typography>
                          ) : null}
                          <Typography style={{ color: '#2e7d32', fontSize: 11, marginTop: 4 }}>
                            Rule settings: min {utilizationReport.continuityRules.minHours}h, max gap {utilizationReport.continuityRules.maxGapMinutes}m.
                          </Typography>
                        </Paper>

                        {utilizationReport.peakIdleCell && utilizationReport.peakIdleCell.unusedMinutes > 0 && (
                          <Paper variant="outlined" style={{ padding: '10px 14px', borderRadius: 12, borderColor: '#cdeccf', background: '#f1fbf2', marginBottom: 12 }}>
                            <Typography style={{ color: '#2e7d32', fontWeight: 600, fontSize: 13 }}>
                              Most idle slot: {utilizationReport.peakIdleCell.weekdayLabel} {utilizationReport.peakIdleCell.timeLabel}
                              {' '}| {formatUtilizationHours(utilizationReport.peakIdleCell.unusedMinutes)} unused
                              {' '}({utilizationReport.peakIdleCell.utilizationPct}% used).
                            </Typography>
                          </Paper>
                        )}

                        <Box style={{ overflowX: 'auto' }}>
                          <table style={{ borderCollapse: 'separate', borderSpacing: 2, width: '100%' }}>
                            <thead>
                              <tr>
                                <th style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, padding: '4px 8px', textAlign: 'left', fontSize: 11, color: '#6f7b87' }} />
                                {utilizationReport.timeBuckets.map((timeBucket) => (
                                  <th
                                    key={`head-${timeBucket.start}`}
                                    style={{ padding: '4px 6px', fontSize: 11, color: '#6f7b87', fontWeight: 600, minWidth: 44, textAlign: 'center' }}
                                  >
                                    {timeBucket.label}
                                  </th>
                                ))}
                                <th style={{ padding: '4px 8px', fontSize: 11, color: '#6f7b87', fontWeight: 700, textAlign: 'right', minWidth: 64 }}>
                                  Unused
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {utilizationReport.weekdayGrid.map((row) => {
                                const dayContinuityEntry = (utilizationReport.combinedContinuityByWeekday || [])
                                  .find((entry) => entry.weekdayIndex === row.weekdayIndex);
                                const dayContinuityIntervals = dayContinuityEntry?.intervals || [];
                                return (
                                  <tr key={`row-${row.weekdayIndex}`}>
                                    <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, padding: '4px 8px', fontSize: 12, fontWeight: 600, color: '#002D55', whiteSpace: 'nowrap' }}>
                                      {row.label}
                                    </td>
                                    {row.cells.map((cell) => {
                                      const bucket = utilizationReport.timeBuckets[cell.bucketIndex];
                                      const continuityInterval = bucket
                                        ? dayContinuityIntervals.find((interval) => bucket.start >= interval.start && bucket.end <= interval.end)
                                        : null;
                                      const hasCapacity = cell.capacityMinutes > 0;
                                      // Idle fraction ? green intensity; fully used slots tint red.
                                      const idleFraction = hasCapacity ? (cell.unusedMinutes / cell.capacityMinutes) : 0;
                                      const isContinuityCell = Boolean(continuityInterval);
                                      let background = '#f2f4f7';
                                      if (hasCapacity) {
                                        background = idleFraction <= 0.001
                                          ? 'rgba(192, 57, 43, 0.18)'
                                          : `rgba(31, 139, 76, ${0.12 + (idleFraction * 0.68)})`;
                                      }
                                      return (
                                        <td
                                          title={hasCapacity
                                            ? `${row.label} ${cell.label}: ${formatUtilizationHours(cell.unusedMinutes)} unused of ${formatUtilizationHours(cell.capacityMinutes)} (${cell.utilizationPct}% used)${isContinuityCell ? ' | continuity highlight' : ''}`
                                            : `${row.label} ${cell.label}: closed`}
                                          style={{
                                            background,
                                            borderRadius: 4,
                                            padding: '4px 2px',
                                            textAlign: 'center',
                                            fontSize: 10,
                                            fontWeight: isContinuityCell ? 700 : 600,
                                            color: hasCapacity && idleFraction > 0.55 ? '#fff' : '#33414f',
                                            minWidth: 44,
                                            boxShadow: isContinuityCell ? 'inset 0 0 0 2px #f0a33a' : 'none',
                                          }}
                                        >
                                          {hasCapacity ? formatUtilizationHours(cell.unusedMinutes) : '-'}
                                        </td>
                                      );
                                    })}
                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#2e7d32', whiteSpace: 'nowrap' }}>
                                      {formatUtilizationHours(row.unusedMinutes)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </Box>*/}

                        {utilizationReport.roomHeatmaps.length > 0 && (
                          <Box style={{ marginTop: 24 }}>
                            <Typography style={{ fontWeight: 700, color: '#002D55', marginBottom: 4 }}>
                              Per-room unused capacity by weekday &amp; time
                            </Typography>
                            <Typography className={classes.description} style={{ marginBottom: 10 }}>
                              Each room gets its own heatmap so you can see where idle time sits inside the clinic.
                            </Typography>

                            {utilizationReport.roomHeatmapGroups.map((group) => (
                              <Accordion key={`util-loc-${group.locationId}`} defaultExpanded={group.isPrimary} elevation={0} style={{ marginBottom: 12, border: '1px solid #d7e3ef', borderRadius: 16, overflow: 'hidden' }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ backgroundColor: '#f7fafd' }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%', gap: 12, flexWrap: 'wrap', paddingRight: 8 }}>
                                    <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0 }}>
                                      <BusinessIcon style={{ color: '#002D55', fontSize: 18 }} />
                                      <Typography style={{ fontWeight: 700, color: '#002D55' }} noWrap>{group.locationName}</Typography>
                                      <Typography style={{ fontSize: 12, color: '#6f7b87' }}>
                                        {group.rooms.length} {group.rooms.length === 1 ? 'room' : 'rooms'}
                                      </Typography>
                                    </Box>
                                    <Typography style={{ fontWeight: 700, color: '#FF7F27' }}>{group.utilizationPct}%</Typography>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails style={{ display: 'block', backgroundColor: '#fff', padding: 16 }}>
                                  {group.rooms.map((room) => (
                                    <Paper key={room.key} variant="outlined" style={{ padding: 16, borderRadius: 12, marginBottom: 12, borderColor: (room.continuityMinutesTotal || 0) > 0 ? '#cdeccf' : '#dbe7f2' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                                  <Box display="flex" alignItems="center" style={{ gap: 10, minWidth: 0 }}>
                                    <Box style={{ width: 12, height: 12, borderRadius: 3, background: room.roomColor, flexShrink: 0 }} />
                                    <Box style={{ minWidth: 0 }}>
                                      <Typography style={{ fontWeight: 700, color: '#002D55' }} noWrap>
                                        {room.roomName}
                                      </Typography>
                                      <Typography style={{ fontSize: 12, color: '#6f7b87' }} noWrap>
                                        {room.locationName}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Typography style={{ fontWeight: 700, color: '#FF7F27' }}>{room.utilizationPct}%</Typography>
                                </Box>

                                {(room.continuityByWeekday || []).some((entry) => (entry.intervals || []).length > 0) ? (
                                  <Paper variant="outlined" style={{ padding: '10px 14px', borderRadius: 12, borderColor: '#cdeccf', background: '#f1fbf2', marginBottom: 10 }}>
                                    <Typography style={{ color: '#2e7d32', fontWeight: 700, fontSize: 13 }}>
                                      Continuity highlights ({utilizationReport.continuityRules.minHours}h+, max {utilizationReport.continuityRules.maxGapMinutes}m gaps): {(room.continuityByWeekday || []).reduce((sum, entry) => sum + ((entry.intervals || []).length), 0)} {((room.continuityByWeekday || []).reduce((sum, entry) => sum + ((entry.intervals || []).length), 0) === 1 ? 'window' : 'windows')}.
                                    </Typography>
                                    <Typography style={{ color: '#2e7d32', fontSize: 12, marginTop: 4 }}>
                                      {(room.continuityByWeekday || [])
                                        .filter((entry) => (entry.intervals || []).length > 0)
                                        .map((entry) => `${entry.weekdayLabel}: ${(entry.intervals || []).map((interval) => formatContinuityIntervalCompact(interval.start, interval.end)).join(' ')}`)
                                        .join(' | ')}
                                    </Typography>
                                  </Paper>
                                ) : null}

                                {room.peakIdleCell && room.peakIdleCell.unusedMinutes > 0 ? (
                                  <Typography style={{ color: '#2e7d32', fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                                    Most idle slot: {room.peakIdleCell.weekdayLabel} {room.peakIdleCell.timeLabel}
                                    {' '}| {formatUtilizationHours(room.peakIdleCell.unusedMinutes)} unused.
                                  </Typography>
                                ) : null}

                                <Paper variant="outlined" style={{ padding: '10px 14px', borderRadius: 12, borderColor: '#cdeccf', background: '#f1fbf2', marginBottom: 10 }}>
                                  <Typography style={{ color: '#2e7d32', fontWeight: 700, fontSize: 13 }}>
                                    Continuity free ({utilizationReport.continuityRules.minHours}h+, max {utilizationReport.continuityRules.maxGapMinutes}m gaps): {formatUtilizationHours(room.continuityMinutesTotal || 0)}
                                  </Typography>
                                  {(room.continuityByWeekday || []).length > 0 ? (
                                    <Typography style={{ color: '#2e7d32', fontSize: 12, marginTop: 4 }}>
                                      {(room.continuityByWeekday || [])
                                        .map((entry) => `${entry.weekdayLabel}: ${formatUtilizationHours(entry.continuityMinutes)}${entry.intervalLabels.length ? ` (${entry.intervalLabels.join(', ')})` : ''}`)
                                        .join(' | ')}
                                    </Typography>
                                  ) : (
                                    <Typography style={{ color: '#2e7d32', fontSize: 12, marginTop: 4 }}>
                                      No qualifying continuity windows for this room.
                                    </Typography>
                                  )}
                                </Paper>

                                <Box style={{ overflowX: 'auto' }}>
                                  <table style={{ borderCollapse: 'separate', borderSpacing: 2, width: '100%' }}>
                                    <thead>
                                      <tr>
                                        <th style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, padding: '4px 8px', textAlign: 'left', fontSize: 11, color: '#6f7b87' }} />
                                        {utilizationReport.timeBuckets.map((timeBucket) => (
                                          <th
                                            key={`room-head-${room.key}-${timeBucket.start}`}
                                            style={{ padding: '4px 6px', fontSize: 11, color: '#6f7b87', fontWeight: 600, minWidth: 44, textAlign: 'center' }}
                                          >
                                            {timeBucket.label}
                                          </th>
                                        ))}
                                        <th style={{ padding: '4px 8px', fontSize: 11, color: '#6f7b87', fontWeight: 700, textAlign: 'right', minWidth: 88 }}>
                                          Continuity
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {room.weekdayGrid.map((row) => (
                                        <tr key={`room-row-${room.key}-${row.weekdayIndex}`}>
                                          <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, padding: '4px 8px', fontSize: 12, fontWeight: 600, color: '#002D55', whiteSpace: 'nowrap' }}>
                                            {row.label}
                                          </td>
                                          {(() => {
                                            const dayContinuityEntry = (room.continuityByWeekday || []).find((entry) => entry.weekdayIndex === row.weekdayIndex);
                                            const dayContinuityIntervals = dayContinuityEntry?.intervals || [];
                                            return row.cells.map((cell) => {
                                              const bucket = utilizationReport.timeBuckets[cell.bucketIndex];
                                              const continuityInterval = bucket
                                                ? dayContinuityIntervals.find((interval) => bucket.start >= interval.start && bucket.end <= interval.end)
                                                : null;
                                              const continuityMinutes = continuityInterval ? continuityInterval.durationMinutes : 0;
                                              const hasCapacity = cell.capacityMinutes > 0;
                                              const idleFraction = hasCapacity ? (cell.unusedMinutes / cell.capacityMinutes) : 0;
                                              const isContinuityCell = Boolean(continuityInterval);
                                              let background = '#f2f4f7';
                                              if (hasCapacity) {
                                                background = idleFraction <= 0.001
                                                  ? 'rgba(192, 57, 43, 0.18)'
                                                  : `rgba(31, 139, 76, ${0.12 + (idleFraction * 0.68)})`;
                                              }
                                              return (
                                                <td
                                                  key={`room-cell-${room.key}-${row.weekdayIndex}-${cell.bucketIndex}`}
                                                  title={hasCapacity
                                                    ? `${row.label} ${cell.label}: ${continuityMinutes > 0 ? `${formatUtilizationHours(continuityMinutes)} continuity` : 'no continuity interval'} (unused ${formatUtilizationHours(cell.unusedMinutes)} of ${formatUtilizationHours(cell.capacityMinutes)}, ${cell.utilizationPct}% used)`
                                                    : `${row.label} ${cell.label}: closed`}
                                                  style={{
                                                    background,
                                                    borderRadius: 4,
                                                    padding: '4px 2px',
                                                    textAlign: 'center',
                                                    fontSize: 10,
                                                    fontWeight: isContinuityCell ? 700 : 600,
                                                    color: hasCapacity && idleFraction > 0.55 ? '#fff' : '#33414f',
                                                    minWidth: 44,
                                                    boxShadow: isContinuityCell ? 'inset 0 0 0 2px #f0a33a' : 'none',
                                                  }}
                                                >
                                                  {hasCapacity ? (continuityMinutes > 0 ? formatUtilizationHours(continuityMinutes) : '-') : '-'}
                                                </td>
                                              );
                                            });
                                          })()}
                                          <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#2e7d32', whiteSpace: 'nowrap' }}>
                                            {formatUtilizationHours(((room.continuityByWeekday || []).find((entry) => entry.weekdayIndex === row.weekdayIndex)?.continuityMinutes) || 0)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </Box>
                                    </Paper>
                                  ))}
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </Box>
                        )}
                        {utilizationPanelTab === 'vacation' && (
                          <Box style={{ marginTop: 24 }}>
                            <Accordion
                              elevation={0}
                              defaultExpanded
                              style={{ marginBottom: 20, border: '1px solid #d7e3ef', borderRadius: 16, overflow: 'hidden' }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ backgroundColor: '#f7fafd' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%', gap: 12, flexWrap: 'wrap', paddingRight: 8 }}>
                                  <Box>
                                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                      <NotificationsIcon style={{ color: '#002D55', fontSize: 18 }} />
                                      <Typography style={{ fontWeight: 700, color: '#002D55' }}>Absence planner</Typography>
                                      {reportedVacations.length > 0 && (
                                        <Box style={{ padding: '2px 8px', borderRadius: 999, background: '#ffe8bf', color: '#8a5a00', fontSize: 11, fontWeight: 700 }}>
                                          {reportedVacations.length} pending
                                        </Box>
                                      )}
                                    </Box>
                                    <Typography className={classes.description} style={{ margin: 0 }}>
                                      Reported staff absences across the clinic. Allocations are freed automatically for these dates.
                                    </Typography>
                                  </Box>
                                  <Typography style={{ fontSize: 12, color: '#6f7b87', fontWeight: 600 }}>
                                    {vacationCalendar.label}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails style={{ display: 'block', backgroundColor: '#fff' }}>
                                <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 12 }}>
                                  {currentMonthAbsenceCards.map((card) => (
                                    <Paper key={`top-${card.key}`} variant="outlined" style={{ padding: 12, borderRadius: 12, background: card.color, borderColor: 'rgba(0,45,85,0.08)' }}>
                                      <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                                        <Box style={{ width: 12, height: 12, borderRadius: '50%', background: card.accent, flexShrink: 0 }} />
                                        <Typography style={{ color: '#002D55', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                          {card.label}
                                        </Typography>
                                      </Box>
                                      <Typography style={{ color: '#002D55', fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>
                                        {card.value}
                                      </Typography>
                                      <Typography style={{ color: '#6f7b87', fontSize: 12, marginTop: 4 }}>
                                        {card.fullLabel} this month
                                      </Typography>
                                    </Paper>
                                  ))}
                                </Box>

                                {reportedVacations.length > 0 && (
                                  <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, borderColor: '#f2c56b', background: '#fff8eb', marginBottom: 12 }}>
                                    <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                                      <NotificationsIcon style={{ color: '#8a5a00', fontSize: 20 }} />
                                      <Typography style={{ fontWeight: 700, color: '#8a5a00' }}>
                                        {reportedVacationReports.length} vacation {reportedVacationReports.length === 1 ? 'report' : 'reports'} and {reportedSickDayReports.length} sick day {reportedSickDayReports.length === 1 ? 'report' : 'reports'} to review
                                      </Typography>
                                    </Box>
                                    <Typography style={{ color: '#8a5a00', fontSize: 13, marginBottom: 10 }}>
                                      Staff allocations for these dates have been automatically freed. Acknowledge to clear the alert.
                                    </Typography>
                                    {reportedVacations.map((vacation) => (
                                      <Box
                                        key={`reported-${vacation.id}`}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        style={{ gap: 12, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid #f0e2c0' }}
                                      >
                                        <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0 }}>
                                          <Box style={{ width: 10, height: 10, borderRadius: '50%', background: vacationStaffColor(vacation.staff_member), flexShrink: 0 }} />
                                          <Typography style={{ fontWeight: 600, color: '#002D55' }} noWrap>
                                            {vacation.staff_member_name || 'Staff member'}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            label={String(vacation.absence_type || 'vacation') === 'sick_day' ? 'Sick day' : 'Vacation'}
                                            style={{ backgroundColor: String(vacation.absence_type || 'vacation') === 'sick_day' ? '#FFF3E0' : '#E3F2FD', color: '#002D55', fontWeight: 700, height: 22 }}
                                          />
                                          <Typography style={{ color: '#6f7b87', fontSize: 13 }}>
                                            {formatVacationRange(vacation)}
                                          </Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            disableElevation
                                            disabled={vacationActionId === vacation.id}
                                            onClick={() => acknowledgeVacation(vacation.id)}
                                            style={{ backgroundColor: '#002D55', color: '#fff', textTransform: 'none', fontWeight: 600, borderRadius: 8 }}
                                          >
                                            Acknowledge
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            disabled={vacationActionId === vacation.id}
                                            onClick={() => deleteVacation(vacation.id)}
                                            style={{ borderColor: '#c0392b', color: '#c0392b', textTransform: 'none', fontWeight: 600, borderRadius: 8 }}
                                          >
                                            Remove
                                          </Button>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Paper>
                                )}

                                <Paper variant="outlined" style={{ padding: 16, borderRadius: 12 }}>
                                  <Typography style={{ fontWeight: 700, color: '#002D55', marginBottom: 10 }}>
                                    Monthly absence summary
                                  </Typography>
                                  <Box style={{ overflowX: 'auto', marginBottom: 12 }}>
                                    <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                                      <thead>
                                        <tr>
                                          <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4' }}>Legend</th>
                                          <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4' }}>Count</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {currentMonthAbsenceCards.map((card) => (
                                          <tr key={`absence-row-${card.key}`}>
                                            <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', fontWeight: 600, borderBottom: '1px solid #f1f4f8' }}>
                                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                                <Box style={{ width: 10, height: 10, borderRadius: '50%', background: card.accent, flexShrink: 0 }} />
                                                <Typography style={{ fontSize: 12, fontWeight: 700, color: '#002D55' }}>
                                                  {card.label}
                                                </Typography>
                                                <Typography style={{ fontSize: 12, color: '#6f7b87' }}>
                                                  {card.fullLabel}
                                                </Typography>
                                              </Box>
                                            </td>
                                            <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', fontWeight: 800, textAlign: 'right', borderBottom: '1px solid #f1f4f8' }}>
                                              {card.value}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </Box>

                                  <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                                    <Box>
                                      <Typography style={{ fontWeight: 700, color: '#002D55' }}>Absence planner calendar</Typography>
                                      <Typography className={classes.description} style={{ margin: 0 }}>
                                        Reported staff absences across the clinic. Allocations are freed automatically for these dates.
                                      </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                      <IconButton size="small" onClick={() => shiftVacationPlannerMonth(-1)} style={{ backgroundColor: '#e6eaf0', borderRadius: 8 }}>
                                        <ChevronLeftIcon fontSize="small" style={{ color: '#002D55' }} />
                                      </IconButton>
                                      <Typography style={{ fontWeight: 700, color: '#002D55', minWidth: 140, textAlign: 'center' }}>
                                        {vacationCalendar.label}
                                      </Typography>
                                      <IconButton size="small" onClick={() => shiftVacationPlannerMonth(1)} style={{ backgroundColor: '#e6eaf0', borderRadius: 8 }}>
                                        <ChevronRightIcon fontSize="small" style={{ color: '#002D55' }} />
                                      </IconButton>
                                    </Box>
                                  </Box>

                                  {vacationsLoading ? (
                                    <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
                                  ) : (
                                    <>
                                      <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginTop: 12 }}>
                                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayLabel) => (
                                          <Typography key={dayLabel} style={{ fontSize: 11, fontWeight: 700, color: '#6f7b87', textAlign: 'center', padding: '2px 0' }}>
                                            {dayLabel}
                                          </Typography>
                                        ))}
                                        {vacationCalendar.weeks.map((week, weekIndex) => (
                                          week.map((cell) => (
                                            <Box
                                              key={`vac-cell-${weekIndex}-${cell.date.toISOString()}`}
                                              style={{
                                                minHeight: 64,
                                                border: '1px solid #e6edf4',
                                                borderRadius: 8,
                                                padding: 4,
                                                background: cell.inMonth ? '#fff' : '#f7fafd',
                                                opacity: cell.inMonth ? 1 : 0.6,
                                              }}
                                            >
                                              <Typography style={{ fontSize: 11, fontWeight: 600, color: '#6f7b87' }}>
                                                {cell.date.getDate()}
                                              </Typography>
                                              {cell.vacations.slice(0, 3).map((vacation) => (
                                                <Box
                                                  key={`vac-chip-${cell.date.toISOString()}-${vacation.id}`}
                                                  title={`${vacation.staff_member_name || 'Staff'} - ${String(vacation.absence_type || 'vacation') === 'sick_day' ? 'Sick day' : 'Vacation'} (${formatVacationRange(vacation)})`}
                                                  style={{
                                                    marginTop: 2,
                                                    padding: '1px 4px',
                                                    borderRadius: 4,
                                                    background: String(vacation.absence_type || 'vacation') === 'sick_day' ? '#FF7F27' : vacation.color,
                                                    color: '#fff',
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                  }}
                                                >
                                                  {String(vacation.absence_type || 'vacation') === 'sick_day' ? 'Sick day' : 'Vacation'}
                                                </Box>
                                              ))}
                                              {cell.vacations.length > 3 && (
                                                <Typography style={{ fontSize: 10, color: '#6f7b87', marginTop: 2 }}>
                                                  +{cell.vacations.length - 3} more
                                                </Typography>
                                              )}
                                            </Box>
                                          ))
                                        ))}
                                      </Box>
                                      {staffVacations.length === 0 && (
                                        <Typography className={classes.description} style={{ marginTop: 12 }}>
                                                        No staff absences have been reported yet. When a staff member books a "Vacation", "Sick day", "Paid leave" or "Agreed absence" event in the
                                          calendar it will appear here and their allocations will be freed automatically.
                                        </Typography>
                                      )}
                                    </>
                                  )}
                                </Paper>
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        )}

                        <Typography className={classes.description} style={{ marginTop: 12, fontSize: 11 }}>
                          Note: recurring staff allocations are treated as a weekly schedule, and room
                          capacity is based on the sandbox window above. Specific-date allocations override
                          the weekly schedule for that day.
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}

                {utilizationPanelTab === 'vacation' && (
                  <Box style={{ marginTop: 24 }}>
                    <Accordion
                      elevation={0}
                      defaultExpanded
                      style={{ marginBottom: 20, border: '1px solid #d7e3ef', borderRadius: 16, overflow: 'hidden' }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ backgroundColor: '#f7fafd' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%', gap: 12, flexWrap: 'wrap', paddingRight: 8 }}>
                          <Box>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                              <NotificationsIcon style={{ color: '#002D55', fontSize: 18 }} />
                              <Typography style={{ fontWeight: 700, color: '#002D55' }}>Absence planner</Typography>
                              {reportedVacations.length > 0 && (
                                <Box style={{ padding: '2px 8px', borderRadius: 999, background: '#ffe8bf', color: '#8a5a00', fontSize: 11, fontWeight: 700 }}>
                                  {reportedVacations.length} pending
                                </Box>
                              )}
                            </Box>
                            <Typography className={classes.description} style={{ margin: 0 }}>
                              Reported staff absences across the clinic. Allocations are freed automatically for these dates.
                            </Typography>
                          </Box>
                          <Typography style={{ fontSize: 12, color: '#6f7b87', fontWeight: 600 }}>
                            {vacationCalendar.label}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails style={{ display: 'block', backgroundColor: '#fff' }}>
                                <Box display="flex" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setAbsenceStatsTab('monthly')}
                                    style={{ textTransform: 'none', fontWeight: 700, color: absenceStatsTab === 'monthly' ? '#002D55' : '#6f7b87', borderBottom: absenceStatsTab === 'monthly' ? '2px solid #002D55' : '2px solid transparent', borderRadius: 0, padding: '4px 2px' }}
                                  >
                                    Monthly absence summary
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setAbsenceStatsTab('yearly')}
                                    style={{ textTransform: 'none', fontWeight: 700, color: absenceStatsTab === 'yearly' ? '#002D55' : '#6f7b87', borderBottom: absenceStatsTab === 'yearly' ? '2px solid #002D55' : '2px solid transparent', borderRadius: 0, padding: '4px 2px' }}
                                  >
                                    Yearly absence statistics
                                  </Button>
                                </Box>

                                <Box display="flex" style={{ gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                                  <TextField
                                    select
                                    size="small"
                                    variant="outlined"
                                    label="Employee group"
                                    value={absenceStaffGroupFilter}
                                    onChange={(e) => {
                                      setAbsenceStaffGroupFilter(e.target.value);
                                      setAbsenceStaffMemberFilter('all');
                                    }}
                                    style={{ minWidth: 220 }}
                                    className={classes.orangeFocused}
                                  >
                                    <MenuItem value="all">All staff groups</MenuItem>
                                    <MenuItem value="physiotherapist">Physiotherapists</MenuItem>
                                    <MenuItem value="subcontractor">Subcontractors</MenuItem>
                                    <MenuItem value="administrative">Administrative employees</MenuItem>
                                    <MenuItem value="other">Other staff</MenuItem>
                                  </TextField>

                                  <TextField
                                    select
                                    size="small"
                                    variant="outlined"
                                    label="Employee"
                                    value={absenceStaffMemberFilter}
                                    onChange={(e) => setAbsenceStaffMemberFilter(e.target.value)}
                                    style={{ minWidth: 260 }}
                                    className={classes.orangeFocused}
                                  >
                                    <MenuItem value="all">All employees</MenuItem>
                                    {(allStaff || [])
                                      .filter((staff) => absenceStaffGroupFilter === 'all' || getAbsenceStaffGroup(staff) === absenceStaffGroupFilter)
                                      .map((staff) => (
                                        <MenuItem key={staff.id} value={staff.id}>{staff.name}</MenuItem>
                                      ))}
                                  </TextField>
                                </Box>

                                {absenceStatsTab === 'monthly' ? (
                                  <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, marginBottom: 12 }}>
                                    <Typography style={{ fontWeight: 700, color: '#002D55', marginBottom: 10 }}>
                                      Monthly absence by staff
                                    </Typography>
                                    <Box style={{ overflowX: 'auto' }}>
                                      <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                                        <thead>
                                          <tr>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4' }}>Staff member</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4' }}>Group</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4' }}>Vacation</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4' }}>Sick days</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4' }}>Paid leave</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4' }}>Agreed absence</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4' }}>Dates</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(staffAbsenceStats.length > 0 ? staffAbsenceStats : [{ staffId: 'none', staffName: 'No staff match the selected filters', group: '-', vacation: 0, sick_day: 0, paid_leave: 0, agreed_absence: 0 }]).map((row) => (
                                            <tr key={row.staffId}>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', fontWeight: 600, borderBottom: '1px solid #f1f4f8' }}>{row.staffName}</td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#6f7b87', borderBottom: '1px solid #f1f4f8' }}>{getAbsenceStaffGroupLabel(row.group)}</td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', textAlign: 'right', borderBottom: '1px solid #f1f4f8' }}>{row.vacation}</td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', textAlign: 'right', borderBottom: '1px solid #f1f4f8' }}>{row.sick_day}</td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', textAlign: 'right', borderBottom: '1px solid #f1f4f8' }}>{row.paid_leave}</td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', textAlign: 'right', borderBottom: '1px solid #f1f4f8' }}>{row.agreed_absence}</td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#6f7b87', borderBottom: '1px solid #f1f4f8', minWidth: 260 }}>
                                                {(row.monthlyDates || []).length > 0 ? (row.monthlyDates.map((item, index) => (
                                                  <Box key={`${row.staffId}-monthly-date-${index}`} style={{ marginBottom: 4 }}>
                                                    <strong style={{ color: '#002D55' }}>{item.type.replace(/_/g, ' ')}</strong>: {item.range}
                                                  </Box>
                                                ))) : 'No dates in the selected month'}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </Box>
                                  </Paper>
                                ) : (
                                  <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, marginBottom: 12 }}>
                                    <Typography style={{ fontWeight: 700, color: '#002D55', marginBottom: 10 }}>
                                      Yearly absence statistics
                                    </Typography>
                                    <Box style={{ overflowX: 'auto' }}>
                                      <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                                        <thead>
                                          <tr>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4', minWidth: 160 }}>Staff member / month</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4', minWidth: 72 }}>Vacation</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4', minWidth: 72 }}>Sick</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4', minWidth: 72 }}>Paid</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4', minWidth: 92 }}>Agreed</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#6f7b87', borderBottom: '1px solid #e6edf4', minWidth: 240 }}>Dates</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(
                                            staffAbsenceStats.length > 0
                                              ? staffAbsenceStats
                                              : [{ staffId: 'none', staffName: 'No staff match the selected filters', group: '-', yearly: Array.from({ length: 12 }, () => ({ vacation: 0, sick_day: 0, paid_leave: 0, agreed_absence: 0 })) }]
                                          ).flatMap((staff) => staff.yearly.map((monthStats, monthIndex) => (
                                            <tr key={`${staff.staffId}-${monthIndex}`}>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', fontWeight: monthIndex === 0 ? 700 : 500, borderBottom: '1px solid #f1f4f8' }}>
                                                {monthIndex === 0 ? `${staff.staffName} (${getAbsenceStaffGroupLabel(staff.group)})` : staff.staffName}
                                                <span style={{ color: '#6f7b87', fontWeight: 500, marginLeft: 8 }}>{yearlyAbsenceStats[monthIndex]?.label}</span>
                                              </td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', textAlign: 'right', borderBottom: '1px solid #f1f4f8' }}>{monthStats.vacation}</td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', textAlign: 'right', borderBottom: '1px solid #f1f4f8' }}>{monthStats.sick_day}</td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', textAlign: 'right', borderBottom: '1px solid #f1f4f8' }}>{monthStats.paid_leave}</td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#002D55', textAlign: 'right', borderBottom: '1px solid #f1f4f8' }}>{monthStats.agreed_absence}</td>
                                              <td style={{ padding: '8px 10px', fontSize: 12, color: '#6f7b87', borderBottom: '1px solid #f1f4f8', minWidth: 240 }}>
                                                {(staffAbsenceStats.find((entry) => entry.staffId === staff.staffId)?.yearlyDates?.[monthIndex] || []).length > 0
                                                  ? (staffAbsenceStats.find((entry) => entry.staffId === staff.staffId)?.yearlyDates?.[monthIndex] || []).map((item, index) => (
                                                    <Box key={`${staff.staffId}-yearly-date-${monthIndex}-${index}`} style={{ marginBottom: 4 }}>
                                                      <strong style={{ color: '#002D55' }}>{item.type.replace(/_/g, ' ')}</strong>: {item.range}
                                                    </Box>
                                                  ))
                                                  : 'No dates in this month'}
                                              </td>
                                            </tr>
                                          )))
                                          }
                                        </tbody>
                                      </table>
                                    </Box>
                                  </Paper>
                                )}

                        {reportedVacations.length > 0 && (
                          <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, borderColor: '#f2c56b', background: '#fff8eb', marginBottom: 12 }}>
                            <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                              <NotificationsIcon style={{ color: '#8a5a00', fontSize: 20 }} />
                              <Typography style={{ fontWeight: 700, color: '#8a5a00' }}>
                                {reportedVacationReports.length} vacation {reportedVacationReports.length === 1 ? 'report' : 'reports'} and {reportedSickDayReports.length} sick day {reportedSickDayReports.length === 1 ? 'report' : 'reports'} to review
                              </Typography>
                            </Box>
                            <Typography style={{ color: '#8a5a00', fontSize: 13, marginBottom: 10 }}>
                              Staff allocations for these dates have been automatically freed. Acknowledge to clear the alert.
                            </Typography>
                            {reportedVacations.map((vacation) => (
                              <Box
                                key={`reported-${vacation.id}`}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ gap: 12, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid #f0e2c0' }}
                              >
                                <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0 }}>
                                  <Box style={{ width: 10, height: 10, borderRadius: '50%', background: vacationStaffColor(vacation.staff_member), flexShrink: 0 }} />
                                  <Typography style={{ fontWeight: 600, color: '#002D55' }} noWrap>
                                    {vacation.staff_member_name || 'Staff member'}
                                  </Typography>
                                  <Typography style={{ color: '#6f7b87', fontSize: 13 }}>
                                    {formatVacationRange(vacation)}
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disableElevation
                                    disabled={vacationActionId === vacation.id}
                                    onClick={() => acknowledgeVacation(vacation.id)}
                                    style={{ backgroundColor: '#002D55', color: '#fff', textTransform: 'none', fontWeight: 600, borderRadius: 8 }}
                                  >
                                    Acknowledge
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={vacationActionId === vacation.id}
                                    onClick={() => deleteVacation(vacation.id)}
                                    style={{ borderColor: '#c0392b', color: '#c0392b', textTransform: 'none', fontWeight: 600, borderRadius: 8 }}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              </Box>
                            ))}
                          </Paper>
                        )}

                        <Paper variant="outlined" style={{ padding: 16, borderRadius: 12 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                            <Box>
                              <Typography style={{ fontWeight: 700, color: '#002D55' }}>Absence planner calendar</Typography>
                              <Typography className={classes.description} style={{ margin: 0 }}>
                                Reported staff absences across the clinic. Allocations are freed automatically for these dates.
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                              <IconButton size="small" onClick={() => shiftVacationPlannerMonth(-1)} style={{ backgroundColor: '#e6eaf0', borderRadius: 8 }}>
                                <ChevronLeftIcon fontSize="small" style={{ color: '#002D55' }} />
                              </IconButton>
                              <Typography style={{ fontWeight: 700, color: '#002D55', minWidth: 140, textAlign: 'center' }}>
                                {vacationCalendar.label}
                              </Typography>
                              <IconButton size="small" onClick={() => shiftVacationPlannerMonth(1)} style={{ backgroundColor: '#e6eaf0', borderRadius: 8 }}>
                                <ChevronRightIcon fontSize="small" style={{ color: '#002D55' }} />
                              </IconButton>
                            </Box>
                          </Box>

                          {vacationsLoading ? (
                            <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
                          ) : (
                            <>
                              <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 12, marginBottom: 12 }}>
                                {currentMonthAbsenceCards.map((card) => (
                                  <Box key={`legend-${card.key}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderRadius: 999, background: card.color, border: `1px solid ${card.accent}33` }}>
                                    <Box style={{ width: 10, height: 10, borderRadius: '50%', background: card.accent, flexShrink: 0 }} />
                                    <Typography style={{ fontSize: 11, fontWeight: 700, color: '#002D55' }}>{card.label}</Typography>
                                    <Typography style={{ fontSize: 11, color: '#46607a' }}>{card.fullLabel}</Typography>
                                  </Box>
                                ))}
                              </Box>

                              <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginTop: 0 }}>
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayLabel) => (
                                  <Typography key={dayLabel} style={{ fontSize: 11, fontWeight: 700, color: '#6f7b87', textAlign: 'center', padding: '2px 0' }}>
                                    {dayLabel}
                                  </Typography>
                                ))}
                                {vacationCalendar.weeks.map((week, weekIndex) => (
                                  week.map((cell) => (
                                    <Box
                                      key={`vac-cell-${weekIndex}-${cell.date.toISOString()}`}
                                      style={{
                                        minHeight: 64,
                                        border: '1px solid #e6edf4',
                                        borderRadius: 8,
                                        padding: 4,
                                        background: cell.inMonth ? '#fff' : '#f7fafd',
                                        opacity: cell.inMonth ? 1 : 0.6,
                                      }}
                                    >
                                      <Typography style={{ fontSize: 11, fontWeight: 600, color: '#6f7b87' }}>
                                        {cell.date.getDate()}
                                      </Typography>
                                      {cell.vacations.slice(0, 3).map((vacation) => (
                                        <Box
                                          key={`vac-chip-${cell.date.toISOString()}-${vacation.id}`}
                                          title={`${vacation.staff_member_name || 'Staff'} - ${String(vacation.absence_type || 'vacation') === 'sick_day' ? 'Sick day' : 'Vacation'} (${formatVacationRange(vacation)})`}
                                          style={{
                                            marginTop: 2,
                                            padding: '1px 4px',
                                            borderRadius: 4,
                                            background: String(vacation.absence_type || 'vacation') === 'sick_day' ? '#FF7F27' : vacation.color,
                                            color: '#fff',
                                            fontSize: 10,
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                          }}
                                        >
                                          {String(vacation.absence_type || 'vacation') === 'sick_day' ? 'Sick day' : 'Vacation'}
                                        </Box>
                                      ))}
                                      {cell.vacations.length > 3 && (
                                        <Typography style={{ fontSize: 10, color: '#6f7b87', marginTop: 2 }}>
                                          +{cell.vacations.length - 3} more
                                        </Typography>
                                      )}
                                    </Box>
                                  ))
                                ))}
                              </Box>
                              {staffVacations.length === 0 && (
                                <Typography className={classes.description} style={{ marginTop: 12 }}>
                                  No staff absences have been reported yet. When a staff member books a "Vacation" or "Sick day" event in the
                                  calendar it will appear here and their allocations will be freed automatically.
                                </Typography>
                              )}
                            </>
                          )}
                        </Paper>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}

              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Popover
        open={Boolean(roomAllocationAdjustAnchor)}
        anchorEl={roomAllocationAdjustAnchor}
        onClose={closeRoomAllocationAdjust}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ style: { borderRadius: 14, padding: '16px 20px', minWidth: 240, boxShadow: '0 6px 32px rgba(0,45,85,0.18)' } }}
      >
        <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: '0.85rem', marginBottom: 12 }}>
          Adjust Time
        </Typography>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[{ label: 'Start', value: roomAllocationAdjustStart, field: 'start' }, { label: 'End', value: roomAllocationAdjustEnd, field: 'end' }].map(({ label, value, field }) => (
            <Box key={field} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography style={{ color: '#002D55', fontSize: '0.78rem', fontWeight: 600, minWidth: 36 }}>{label}</Typography>
              <IconButton size="small" onClick={() => adjustRoomAllocationTime(field, -5)} style={{ backgroundColor: '#e6eaf0', borderRadius: 8, padding: 4 }}>
                <RemoveIcon fontSize="small" style={{ color: '#002D55' }} />
              </IconButton>
              <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: '0.95rem', minWidth: 44, textAlign: 'center' }}>{value}</Typography>
              <IconButton size="small" onClick={() => adjustRoomAllocationTime(field, 5)} style={{ backgroundColor: '#e6eaf0', borderRadius: 8, padding: 4 }}>
                <AddIcon fontSize="small" style={{ color: '#002D55' }} />
              </IconButton>
            </Box>
          ))}
        </Box>
        <Box style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <Button onClick={closeRoomAllocationAdjust} size="small" style={{ backgroundColor: '#e6eaf0', color: '#002D55', borderRadius: 10, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', boxShadow: 'none' }}>Cancel</Button>
          <Button onClick={saveRoomAllocationAdjust} disabled={roomAllocationAdjustSaving} size="small" style={{ backgroundColor: '#002D55', color: '#fff', borderRadius: 10, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', boxShadow: 'none' }}>
            {roomAllocationAdjustSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Popover>

      <Dialog
        open={allocationDialogOpen && Boolean(allocationDialogLocation && allocationDialogDraft)}
        onClose={closeAllocationDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.dialogTitleRow}>
            <Typography className={classes.dialogTitleText}>
              {String(allocationDialogDraft?.editing_slot_id || '') ? 'Edit Allocation' : 'New Allocation'}
            </Typography>
            <IconButton onClick={closeAllocationDialog} className={classes.closeButton} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        {allocationDialogLocation && allocationDialogDraft ? (
          <>
            <DialogContent dividers className={classes.dialogContent}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Staff member"
                    variant="outlined"
                    size="small"
                    value={allocationDialogDraft.staff_member || ''}
                    onChange={(e) => handleAllocationDraftChange(allocationDialogLocation, 'staff_member', e.target.value)}
                    className={classes.orangeFocused}
                  >
                    {(allocationDialogLocation.staff_members_detail || []).map((staff) => (
                      <MenuItem key={staff.id} value={String(staff.id)}>
                        {formatCalendarStaffLabel(staff)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Room"
                    variant="outlined"
                    size="small"
                    value={allocationDialogDraft.room || ''}
                    onChange={(e) => handleAllocationDraftChange(allocationDialogLocation, 'room', e.target.value)}
                    className={classes.orangeFocused}
                  >
                    {allocationDialogRooms.map((room) => (
                      <MenuItem key={room.id} value={String(room.id)}>
                        {room.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="color"
                    label="Color"
                    variant="outlined"
                    size="small"
                    value={allocationDialogDraft.color || getAllocationDefaultColor(allocationDialogDraft.staff_member || '')}
                    onChange={(e) => handleAllocationDraftChange(allocationDialogLocation, 'color', e.target.value)}
                    className={classes.orangeFocused}
                    helperText="Pick a distinct staff color for this room allocation."
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Recurrence"
                    variant="outlined"
                    size="small"
                    value={allocationDialogDraft.recurrence || 'weekly'}
                    onChange={(e) => handleAllocationDraftChange(allocationDialogLocation, 'recurrence', e.target.value)}
                    className={classes.orangeFocused}
                    helperText="Choose how often this allocation repeats."
                  >
                    {ALLOCATION_RECURRENCE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Recurring weekdays"
                    variant="outlined"
                    size="small"
                    value={allocationDialogDraft.day_of_week || []}
                    onChange={(e) => handleAllocationDraftChange(allocationDialogLocation, 'day_of_week', e.target.value)}
                    className={classes.orangeFocused}
                    helperText="Select one or more weekdays for the recurring allocation. Overlaps with other allocations are allowed with a warning, but real bookings still block saves."
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => {
                        const selectedValues = Array.isArray(selected) ? selected : [selected];
                        const labels = selectedValues
                          .map((value) => WEEKDAY_OPTIONS[Number(value)]?.label)
                          .filter(Boolean);
                        return labels.length > 0 ? labels.join(', ') : 'Select weekdays';
                      },
                    }}
                  >
                    {WEEKDAY_OPTIONS.map(({ label }, index) => (
                      <MenuItem key={label} value={String(index)}>
                        <Checkbox
                          checked={(allocationDialogDraft.day_of_week || []).includes(String(index))}
                          style={{ color: '#FF7F27' }}
                        />
                        <Typography variant="body2" style={{ marginLeft: 8 }}>
                          {label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Specific date override"
                    variant="outlined"
                    size="small"
                    value={allocationDialogDraft.specific_date || ''}
                    onChange={(e) => handleAllocationDraftChange(allocationDialogLocation, 'specific_date', e.target.value)}
                    inputRef={allocationSpecificDateInputRef}
                    className={classes.orangeFocused}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Start"
                    variant="outlined"
                    size="small"
                    value={allocationDialogDraft.start_time || '09:00'}
                    onChange={(e) => handleAllocationDraftChange(allocationDialogLocation, 'start_time', e.target.value)}
                    className={classes.orangeFocused}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="time"
                    label="End"
                    variant="outlined"
                    size="small"
                    value={allocationDialogDraft.end_time || '17:00'}
                    onChange={(e) => handleAllocationDraftChange(allocationDialogLocation, 'end_time', e.target.value)}
                    className={classes.orangeFocused}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={allocationDialogDraft.is_available !== false}
                        onChange={(e) => handleAllocationDraftChange(allocationDialogLocation, 'is_available', e.target.checked)}
                        style={{ color: allocationDialogDraft.is_available !== false ? '#FF7F27' : '#002D55' }}
                      />
                    )}
                    label={<span style={{ color: '#002D55', fontWeight: 600 }}>Available for work</span>}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={allocationDialogDraft.is_bookable !== false}
                        onChange={(e) => handleAllocationDraftChange(allocationDialogLocation, 'is_bookable', e.target.checked)}
                        style={{ color: allocationDialogDraft.is_bookable !== false ? '#FF7F27' : '#002D55' }}
                      />
                    )}
                    label={<span style={{ color: '#002D55', fontWeight: 600 }}>Bookable interval</span>}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              {String(allocationDialogDraft.editing_slot_id || '') ? (
                <Button
                  onClick={() => {
                    if (window.confirm('Delete this allocation?')) {
                      handleDeleteAllocation(allocationDialogDraft.editing_slot_id);
                    }
                  }}
                  style={{ color: '#e53935' }}
                >
                  Delete
                </Button>
              ) : null}
              <Button onClick={closeAllocationDialog} className={classes.cancelButton} classes={{ root: classes.dialogActionButton }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                className={classes.saveButton}
                classes={{ root: classes.dialogActionButton }}
                onClick={() => handleCreateAllocation(allocationDialogLocation, { closeDialogOnSuccess: true })}
                disabled={allocationDialogRooms.length === 0 || (allocationDialogLocation.staff_members_detail || []).length === 0}
              >
                {String(allocationDialogDraft.editing_slot_id || '') ? 'Update Allocation' : 'Save Allocation'}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog
        open={allocationConflictDialogOpen && Boolean(allocationConflictDetails)}
        onClose={closeAllocationConflictDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.dialogTitleRow}>
            <Typography className={classes.dialogTitleText}>
              Staff opening hours conflict
            </Typography>
            <IconButton onClick={closeAllocationConflictDialog} className={classes.closeButton} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        {allocationConflictDetails ? (
          <>
            <DialogContent dividers className={classes.dialogContent}>
              <Typography style={{ color: '#002D55', fontWeight: 600, marginBottom: 12 }}>
                This allocation falls outside the selected staff member&apos;s opening hours.
              </Typography>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                {allocationConflictDetails.conflicts.map((conflict) => (
                  <Paper
                    key={`${conflict.weekdayKey}-${conflict.reason}`}
                    variant="outlined"
                    style={{ borderRadius: 12, borderColor: '#dce8f5', padding: 12, background: '#fff' }}
                  >
                    <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>
                      {conflict.weekdayLabel}
                    </Typography>
                    <Typography style={{ color: '#46607a', fontSize: 13 }}>
                      {conflict.reason === 'closed'
                        ? 'The staff member is closed on this day.'
                        : `Opening hours are ${formatScheduleMinutesLabel(conflict.dayStart)} - ${formatScheduleMinutesLabel(conflict.dayEnd)}.`}
                    </Typography>
                    <Typography style={{ color: '#46607a', fontSize: 13 }}>
                      Allocation time: {formatScheduleMinutesLabel(conflict.allocationStart)} - {formatScheduleMinutesLabel(conflict.allocationEnd)}
                    </Typography>
                  </Paper>
                ))}
              </Box>
              <Typography style={{ color: '#6f7b87', fontSize: 13 }}>
                If this is only once, add a specific date override. Otherwise, adjust the staff opening hours.
              </Typography>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button onClick={closeAllocationConflictDialog} className={classes.cancelButton} classes={{ root: classes.dialogActionButton }}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={handleAllocationConflictOneOff}
                className={classes.cancelButton}
                classes={{ root: classes.dialogActionButton }}
              >
                One-off event
              </Button>
              <Button
                variant="contained"
                className={classes.saveButton}
                classes={{ root: classes.dialogActionButton }}
                onClick={handleAllocationConflictAdjustHours}
              >
                Adjust staff hours
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog
        open={openingHoursConflictOpen && Boolean(openingHoursConflictDetails)}
        onClose={closeOpeningHoursConflictDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.dialogTitleRow}>
            <Typography className={classes.dialogTitleText}>
              Staff opening hours conflict
            </Typography>
            <IconButton onClick={closeOpeningHoursConflictDialog} className={classes.closeButton} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        {openingHoursConflictDetails ? (
          <>
            <DialogContent dividers className={classes.dialogContent}>
              <Typography style={{ color: '#002D55', fontWeight: 600, marginBottom: 12 }}>
                {openingHoursConflictDetails.staffLabel || 'This staff member'} has room allocations that extend beyond the hours you are trying to save.
              </Typography>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                {openingHoursConflictDetails.conflicts.map((conflict) => (
                  <Paper
                    key={`${conflict.slotId}-${conflict.weekdayKey}-${conflict.reason}`}
                    variant="outlined"
                    style={{ borderRadius: 12, borderColor: '#dce8f5', padding: 12, background: '#fff' }}
                  >
                    <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>
                      {conflict.weekdayLabel}
                      {conflict.room?.name ? ` | ${conflict.room.name}` : ''}
                    </Typography>
                    <Typography style={{ color: '#46607a', fontSize: 13 }}>
                      Allocation time: {formatScheduleMinutesLabel(conflict.allocationStart)} - {formatScheduleMinutesLabel(conflict.allocationEnd)}
                    </Typography>
                    <Typography style={{ color: '#46607a', fontSize: 13 }}>
                      Saving these hours would leave this allocation outside the staff schedule.
                    </Typography>
                  </Paper>
                ))}
              </Box>
              <Typography style={{ color: '#6f7b87', fontSize: 13 }}>
                Adjusting will expand the staff opening hours so the existing room allocations remain valid.
              </Typography>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button onClick={closeOpeningHoursConflictDialog} className={classes.cancelButton} classes={{ root: classes.dialogActionButton }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                className={classes.saveButton}
                classes={{ root: classes.dialogActionButton }}
                onClick={handleOpeningHoursConflictAdjust}
              >
                Adjust hours and save
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog
        open={allocationRoomConflictOpen && Boolean(allocationRoomConflictDetails)}
        onClose={closeAllocationRoomConflictDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.dialogTitleRow}>
            <Typography className={classes.dialogTitleText}>
              Staff double-booking conflict
            </Typography>
            <IconButton onClick={closeAllocationRoomConflictDialog} className={classes.closeButton} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        {allocationRoomConflictDetails ? (
          <>
            <DialogContent dividers className={classes.dialogContent}>
              <Typography style={{ color: '#002D55', fontWeight: 600, marginBottom: 12 }}>
                {allocationRoomConflictDetails.conflicts[0]?.staffName || 'This staff member'} is already allocated to another room during this time and cannot be in two rooms at once.
              </Typography>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                {allocationRoomConflictDetails.conflicts.map((conflict) => (
                  <Paper
                    key={conflict.slotId}
                    variant="outlined"
                    style={{ borderRadius: 12, borderColor: '#f3d6d6', padding: 12, background: '#fff' }}
                  >
                    <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>
                      {conflict.roomName}
                      {conflict.whenLabel ? ` | ${conflict.whenLabel}` : ''}
                    </Typography>
                    <Typography style={{ color: '#46607a', fontSize: 13 }}>
                      Existing allocation: {formatScheduleMinutesLabel(conflict.start)} - {formatScheduleMinutesLabel(conflict.end)}
                      {conflict.locationName ? ` | ${conflict.locationName}` : ''}
                    </Typography>
                  </Paper>
                ))}
              </Box>
              <Typography style={{ color: '#6f7b87', fontSize: 13 }}>
                {allocationRoomConflictDetails.suggestion
                  ? `Adjust this allocation to ${allocationRoomConflictDetails.suggestion.start_time} - ${allocationRoomConflictDetails.suggestion.end_time} to avoid the overlap, or override to remove the conflicting allocation${allocationRoomConflictDetails.conflicts.length === 1 ? '' : 's'}.`
                  : `No free interval fits the requested time. Override to remove the conflicting allocation${allocationRoomConflictDetails.conflicts.length === 1 ? '' : 's'}, or change the time manually.`}
              </Typography>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button onClick={closeAllocationRoomConflictDialog} className={classes.cancelButton} classes={{ root: classes.dialogActionButton }}>
                Cancel
              </Button>
              {allocationRoomConflictDetails.suggestion ? (
                <Button
                  variant="outlined"
                  onClick={handleAllocationRoomConflictAdjust}
                  className={classes.cancelButton}
                  classes={{ root: classes.dialogActionButton }}
                >
                  {`Adjust to ${allocationRoomConflictDetails.suggestion.start_time} - ${allocationRoomConflictDetails.suggestion.end_time}`}
                </Button>
              ) : null}
              <Button
                variant="contained"
                className={classes.saveButton}
                classes={{ root: classes.dialogActionButton }}
                onClick={handleAllocationRoomConflictOverride}
              >
                Override &amp; remove
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Snackbar
        open={locationSnackbar.open}
        autoHideDuration={3200}
        onClose={closeLocationSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarContent
          style={{
            backgroundColor: locationSnackbar.severity === 'warning'
              ? '#ed6c02'
              : (locationSnackbar.severity === 'error' ? '#d32f2f' : '#2e7d32'),
            color: '#fff',
            borderRadius: 12,
          }}
          message={locationSnackbar.message}
          action={(
            <IconButton size="small" onClick={closeLocationSnackbar} style={{ color: '#fff' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        />
      </Snackbar>

      {/* ======= ADD INSURER DIALOG ======= */}
      <Dialog open={addInsurerOpen} onClose={closeInsurerDialog} maxWidth="md" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.dialogTitleRow}>
            <Typography className={classes.dialogTitleText}>{editingInsurerId ? 'Edit Insurer Agreement' : 'Add Insurer Agreement'}</Typography>
            <IconButton onClick={closeInsurerDialog} className={classes.closeButton} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Fill from Danish partner list"
                variant="outlined"
                size="small"
                value={selectedInsuranceTemplate}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  if (!selectedValue) {
                    resetNewInsurerForm();
                    return;
                  }

                  const template = insurancePartnerTemplates.find((item) => item.value === selectedValue);
                  if (template) {
                    applyInsurancePartnerTemplateToForm(template);
                  } else {
                    resetNewInsurerForm();
                  }
                }}
                className={classes.orangeFocused}
                helperText="Choose a default Danish insurer to auto-fill the fields below"
                SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
              >
                <MenuItem value="" className={classes.menuItem}>
                  Custom / blank
                </MenuItem>
                {insurancePartnerTemplates.map((template) => (
                  <MenuItem key={template.value} value={template.value} className={classes.menuItem}>
                    {template.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Provider Name *"
                variant="outlined"
                size="small"
                value={newInsurer.insurance_provider_name}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, insurance_provider_name: e.target.value }))}
                className={classes.orangeFocused}
                helperText="Name as it appears on the insurance card"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Registration / CVR Number"
                variant="outlined"
                size="small"
                value={newInsurer.business_registration_number}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, business_registration_number: e.target.value }))}
                className={classes.orangeFocused}
                helperText="Top-level company identifier"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address"
                variant="outlined"
                size="small"
                value={newInsurer.address}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, address: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Postal Code"
                variant="outlined"
                size="small"
                value={newInsurer.postal_code}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, postal_code: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                size="small"
                value={newInsurer.city}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, city: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                size="small"
                value={newInsurer.email}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, email: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="EAN Number"
                variant="outlined"
                size="small"
                value={newInsurer.ean_number}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, ean_number: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Person Reference"
                variant="outlined"
                size="small"
                value={newInsurer.person_reference}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, person_reference: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                variant="outlined"
                size="small"
                value={newInsurer.phone}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, phone: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Effective From"
                type="date"
                variant="outlined"
                size="small"
                value={newInsurer.effective_from}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, effective_from: e.target.value }))}
                className={classes.orangeFocused}
                InputLabelProps={{ shrink: true }}
                helperText="Leave blank if no start date"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Effective Until"
                type="date"
                variant="outlined"
                size="small"
                value={newInsurer.effective_until}
                onChange={(e) => setNewInsurer((prev) => ({ ...prev, effective_until: e.target.value }))}
                className={classes.orangeFocused}
                InputLabelProps={{ shrink: true }}
                helperText="Leave blank if open-ended"
              />
            </Grid>
          </Grid>

          <Typography style={{ color: '#002D55', fontWeight: 600, marginBottom: 12 }}>
            Payer Details by Policy Type
          </Typography>
          {POLICY_TYPES.map((policyType) => {
            const d = newInsurer.payer_details[policyType];
            const setter = (field, value) =>
              setNewInsurer((prev) => ({
                ...prev,
                payer_details: { ...prev.payer_details, [policyType]: { ...prev.payer_details[policyType], [field]: value } },
              }));
            return (
              <Box key={policyType} style={{ border: '1px solid #e5e9ef', borderRadius: 8, padding: 16, marginBottom: 12 }}>
                <Typography style={{ color: '#002D55', fontWeight: 600, marginBottom: 12 }}>{policyTypeLabel(policyType)}</Typography>
                <Grid container spacing={2}>
                  
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="EAN Number" variant="outlined" size="small" value={d.ean_number} onChange={(e) => setter('ean_number', e.target.value)} className={classes.orangeFocused} helperText="13 digits" />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="Billing Email" type="email" variant="outlined" size="small" value={d.billing_email} onChange={(e) => setter('billing_email', e.target.value)} className={classes.orangeFocused} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="Max Coverage / Appointment" type="number" variant="outlined" size="small" value={d.max_coverage_per_appointment} onChange={(e) => setter('max_coverage_per_appointment', e.target.value)} className={classes.orangeFocused} helperText="Leave blank for full coverage" />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="Payment Terms (days)" type="number" variant="outlined" size="small" value={d.payment_terms_days} onChange={(e) => setter('payment_terms_days', e.target.value)} className={classes.orangeFocused} InputProps={{ inputProps: { min: 1 } }} />
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={closeInsurerDialog} className={classes.cancelButton} classes={{ root: classes.dialogActionButton }}>Cancel</Button>
          <Button
            variant="contained"
            className={classes.saveButton}
            onClick={handleSaveNewInsurer}
            disabled={saving || !newInsurer.insurance_provider_name.trim()}
            classes={{ root: classes.dialogActionButton }}
            startIcon={saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : null}
          >
            {saving ? 'Saving...' : (editingInsurerId ? 'Save Changes' : 'Save Insurer')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={otherPayerDialogOpen} onClose={() => setOtherPayerDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.dialogTitleRow}>
            <Typography className={classes.dialogTitleText}>{editingOtherPayerId ? 'Edit Other Payer' : 'Add Other Payer'}</Typography>
            <IconButton onClick={() => setOtherPayerDialogOpen(false)} className={classes.closeButton} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payer Name *"
                variant="outlined"
                size="small"
                value={newOtherPayer.name}
                onChange={(e) => setNewOtherPayer((prev) => ({ ...prev, name: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CVR Number"
                variant="outlined"
                size="small"
                value={newOtherPayer.cvr_number}
                onChange={(e) => setNewOtherPayer((prev) => ({ ...prev, cvr_number: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="EAN Number"
                variant="outlined"
                size="small"
                value={newOtherPayer.ean_number}
                onChange={(e) => setNewOtherPayer((prev) => ({ ...prev, ean_number: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                variant="outlined"
                size="small"
                value={newOtherPayer.address}
                onChange={(e) => setNewOtherPayer((prev) => ({ ...prev, address: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Postal Code"
                variant="outlined"
                size="small"
                value={newOtherPayer.postal_code}
                onChange={(e) => setNewOtherPayer((prev) => ({ ...prev, postal_code: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                size="small"
                value={newOtherPayer.city}
                onChange={(e) => setNewOtherPayer((prev) => ({ ...prev, city: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Billing Email"
                variant="outlined"
                size="small"
                value={newOtherPayer.billing_email}
                onChange={(e) => setNewOtherPayer((prev) => ({ ...prev, billing_email: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                variant="outlined"
                size="small"
                value={newOtherPayer.phone}
                onChange={(e) => setNewOtherPayer((prev) => ({ ...prev, phone: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Person Reference"
                variant="outlined"
                size="small"
                value={newOtherPayer.person_reference}
                onChange={(e) => setNewOtherPayer((prev) => ({ ...prev, person_reference: e.target.value }))}
                className={classes.orangeFocused}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setOtherPayerDialogOpen(false)} className={classes.cancelButton} classes={{ root: classes.dialogActionButton }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className={classes.saveButton}
            onClick={handleSaveOtherPayer}
            disabled={savingOtherPayer || !newOtherPayer.name.trim()}
            classes={{ root: classes.dialogActionButton }}
            startIcon={savingOtherPayer ? <CircularProgress size={18} style={{ color: '#fff' }} /> : null}
          >
            {savingOtherPayer ? 'Saving...' : 'Save Other Payer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ClinicSettings.propTypes = {
  className: PropTypes.string,
};

export default ClinicSettings;
